// ═══════════════════════════════════════════════════════════════════════
// GOOP ZONE — Automated Playtester Bot  v3.0
// ═══════════════════════════════════════════════════════════════════════
// Drop-in module that plays the game autonomously. Hooks into GameState
// via the engine's exported refs. Reads enemy positions, player HP, items,
// doorways, chests, shrines — makes movement/combat/upgrade decisions.
//
// Toggle with F9 key. Runs inside the existing game loop (no separate thread).
// Logs every decision and outcome for post-run analysis.
// ═══════════════════════════════════════════════════════════════════════

import type { GameState, Enemy, UpgradeChoice } from './types';
import { canMoveTo, getGreenZoneRedirect, hasLineOfSight as hasProjectileLOS } from './types';
import { getDoorwayMagnet, pointInPolygon as pointInPoly } from './roomCollisionGrid';
import { findPath, hasLineOfSight } from './navGraph';
import { getFlowDirection, isFlowFieldReady, markCellAsTrap } from './flowField';
import { trackStuckEvent } from './weaponLab';
// BETA 17.21 — PSBOT cycle-30ai B2: projectile dodging.
import { getActiveEnemyProjectiles as _getActiveEnemyProjectiles } from './enemySystem';
import type { PlacedRoom } from './roomMapgen';
import {
  getLevelUpOptionDisplayData, getShrineBuffDisplayOptions, getChestOptionDisplayData,
  setLevelUpSelectedIndex, handleLevelUpSelection,
  setShrineSelectedIndex, handleShrineSelection,
  setChestHoverIndex, handleChestItemSelection,
  getItemState, getPercState,
  rerollLevelUpOptions, rerollShrineOptions, rerollChestOptions,
} from './itemsAdapter';
import { getRunDamageDealt, resetRunDamageDealt, getWeaponSystemState } from './weaponAdapter';
// BETA 17.26 — PSBOT cycle-30am: weapon-specific aim leading needs projectile.speed
// per active weapon. Fetched via getWeaponDef so constant `projSpeed=600`
// (which over-led Revolver speed 900) is replaced with the highest-damage
// active weapon's actual projectile speed.
import { getWeaponDef as _getWeaponDef, WEAPON_DATABASE as _WEAPON_DATABASE } from './weapons';
// BETA 17.07 — Karen Drone diagnostic counter (PSBOT cycle-30w Protocol B).
import { getKarenDroneTrace as _getKarenDroneTraceForBot } from './weaponSystem';
// BETA 17.08 — PSBOT cycle-30t Tier C closing batch (6 final counters).
import { getLocalItemTracker as _getLocalItemTrackerForBot } from './weaponLab';
// BETA 20.68 — BRAIN v2: live weapon×enemy accuracy feeds threat-aware kiting.
import { getLocalWeaponEnemyMatrix as _getWpnEnemyMatrixForBrain } from './weaponLab';
import { getAnusRocketsFiredThisRun as _getAnusRocketsForBot } from './itemEffectSystem';
import { getSillyJuicePeakStack as _getSillyJuicePeakForBot } from './itemsAdapter';
import { GAME_VERSION } from './engine';
import { debugTimeScale } from './debugState';
import { notePerfFrame } from './performanceTracker';
// BETA 15.31 — perc-pick rarity counters (snapshot at finalizeRun, reset at startNewRun)
import { getPercPickRarityCounts, resetPercPickRarityCounts } from './peanutPercSystem';
// BETA 15.25 — Supabase telemetry pipeline. recordBotRunEvent fires on every
// completed run + every 5-min in-progress snapshot. recordPerfEvent fires on
// long frames (≥1s = lag spike). All async, all best-effort, all bounded.
import { recordBotRunEvent, recordPerfEvent, ERR } from '../lib/perfTelemetry';
// BETA 16.26 — PSBOT cycle-27 URGENT: bot_run schema catch-up to live_play.
// Shared helper returns pickEvents + finalShield + finalEvasionPct +
// finalMoveSpeedPct + finalMaxHpDebug for parallel landing in bot runs.
import { buildExtendedTelemetryFields } from './livePlayTracker';
// BETA 15.26 — character passive run-end trackers (Layer 1 final + Layer 2)
import {
  getPassiveBonusFinalThisRun,
  getSignatureProcsThisRun,
  getSignatureMaxStacksThisRun,
  getSignatureActiveSecondsThisRun,
  getSignatureBonusDamageThisRun,
} from './characterPassives';
// BETA 15.34 — pick audit (per-pick offers + values + stats) for god-mode detection
import {
  resetPickAudit, getPickAuditEntries, getPickAuditCount,
  getMaxBuffSpike, getLegendaryPickCount, getEpicPlusPickCount,
  getPickAuditCountsBySource,
} from './pickAudit';
// BETA 14.70 — v1 BotFarm CSV converge. Pulls weaponLab tracker getters so
// the master.csv inherits all the per-mechanic JSON the WeaponLab CSV has.
// BETA 14.72 — bot diagnostic getters + per-frame trackers.
import {
  getLocalPercTracker, getLocalItemTracker, getLocalShrineTracker,
  getLocalFloorEffectTracker, getLocalEventLog, getLocalDeathContext,
  getLocalNearMissEvents, getLocalRetreatEvents,
  getLocalWeaponEnemyMatrix, getLocalSynergyTracker,
  computeTopWeaponSummary,
  trackBotPosition, trackBotMapCell,
  getLocalBotPositionTrail, getLocalStuckEvents, getLocalBotMapCellVisits,
  getLocalDecisionTrace,
  // BETA 14.74 — per-run reset to fix tracker cumulative bleed between runs
  // (Phase 0/1 sanity check on cycle-9.5 CSV revealed run 2's eventLog
  // contained run 1's events; same for botPositionTrail / stuckEvents /
  // botMapCellVisits / percTracker / itemTracker / shrineTracker).
  clearTrackers,
} from './weaponLab';

// ─── Bot Brain Version ──────────────────────────────────────────────
export const BOT_BRAIN_VERSION = '3.18';

// ─── Build Archetypes ──────────────────────────────────────────────
// PS Major's 7-build system. Each build biases card-pick scoring + bot config.
// Tiered keyword scoring: Tier 1 (ideal) = perfect match, Tier 2 (good) = related,
// Tier 3 (acceptable) = generic survival fallback. The picker scores all tiers and
// always picks a card — never returns nothing — so RNG offer pools that lack ideal
// cards still get a sensible second-best pick.
export type BuildType =
  | 'adaptive'        // default — generic brain, no build bias
  | 'chad_aura'
  | 'peanut_sniper'
  | 'rpk_suppressor'
  | 'spatula_melee'
  | 'fireball_kite'
  | 'tank_sustain'
  | 'holy_trinity';

interface BuildKeywordTiers {
  tier1: Record<string, number>;  // ideal — direct build keywords (+12 each)
  tier2: Record<string, number>;  // good — related stats (+6 each)
  tier3: Record<string, number>;  // acceptable — generic survival fallback (+3 each)
}

// BETA 13.69: tier weights bumped (T1 12→20, T2 6→10) per cycle-1 audit of 25 Chad
// runs + 787 picks. Old +12 for T1 was dominated by survival keyword bias (+22-25
// for hp/lifesteal at wave≤5) AND by rarity scoring (+20-100 by tier), so build
// preference rarely flipped a same-rarity tiebreaker. New +20 puts T1 keystones
// (Not Viagra, Adderall, etc.) above the +5-8 late-game survival fallback so build-
// specific perks reliably win when rarity is equal. Still loses to higher-rarity
// off-build picks (correct — bot should grab a legendary Reflexes over a common
// Not Viagra). T3 fallback unchanged (3) as a safety floor.
// BETA 13.99b: keyword tiers expanded for the 11 new weapons (BETA 13.94-97)
// + 8 new items (BETA 13.98-99). Each new card name has at least one tier-1
// or tier-2 keyword in EXACTLY ONE build so the bot has a clear preference,
// preventing "everyone picks everything" averaging that would obscure
// per-build effectiveness signals.
// BETA 14.05: extended for the 14 new percs + 7 new items (BETA 13.103). See
// BOT_TRAINING_LOG.md "MISSING CHECKS" section for the rationale (cycle-7
// produced 0 picks of the new content because keyword tiers weren't synced
// — fixed here).
const BUILD_KEYWORD_TIERS: Record<BuildType, BuildKeywordTiers> = {
  adaptive: { tier1: {}, tier2: {}, tier3: {} },
  chad_aura: {
    tier1: { aura: 20, area: 20, size: 20, pulse: 20, 'not viagra': 20, beam: 20, 'ultra beam': 20,
      // 14.05: chad's contact-range play loves swarm + named-elite bonuses
      bungulazine: 20, stompatine: 20 },
    tier2: { 'attack speed': 10, damage: 10, adderall: 10, steroids: 10, karen: 10, drones: 10, orbital: 10, bungulators: 10, brawler: 10,
      // 14.05: low-HP scaling + long-game scaling fit chad's mid-late strength
      'not blue chew': 10, 'puberty blockers': 10,
      // 14.05: heal-on-kill + burn DoT fit chad's high-kill volume
      'mom\'s cooking': 10, 'big mac': 10, 'special sauce': 10 },
    tier3: { health: 3, hp: 3, armor: 3, regen: 3, lifesteal: 3, voogoo: 3, sheeesh: 3 },
  },
  peanut_sniper: {
    tier1: { crit: 20, pierce: 20, sniper: 20, rifle: 20, vision: 20, frozen: 20, darts: 20, 'frozen darts': 20, girlfriend: 20, 'ex girlfriend': 20,
      // 14.05: kite-friendly perks (alone bonus + escape after damage) fit sniper's stationary play
      'let\'s ride': 20, 'lets ride': 20, alone: 20, 'not acid': 20, phase: 20,
      // BETA 17.24 PSBOT cycle-30ak flaw #2: tactical + lebron assigned
      // peanut_sniper but their starters weren't in tier1.
      // — Tactical: peanut_rifle + headshot bonus (already covered via 'rifle')
      tactical: 20, headshot: 20, 'tactical peanut': 20,
      // — LeBron: tactical_basketball + king's court signature
      basketball: 20, 'tactical basketball': 20, lebron: 20, "king's court": 20, kings: 20, court: 20, bounce: 20 },
    tier2: { damage: 10, luck: 10, 'projectile speed': 10, steroids: 10, terminator: 10, boomerang: 10, iranian: 10, bird: 10, drone: 10,
      // 14.05: fragile sniper benefits from defensive utility + glass cannon trade-offs
      'not xanax': 10, 'sponge me daddy': 10, absorbatol: 10, 'puberty blockers': 10,
      // 14.05: write-that-down marks elites on minimap (sniper benefits)
      'write that down': 10 },
    tier3: { health: 3, hp: 3, armor: 3, regen: 3, dodge: 3, sheeesh: 3, 'liar deceiver': 3, 'false prophet': 3 },
  },
  rpk_suppressor: {
    tier1: { 'attack speed': 20, 'fire rate': 20, reload: 20, adderall: 20, squirter: 20, water: 20, hurray: 20, confetti: 20,
      // 14.05: rpk's spammy opener loves the +50% AS first-60s burst from Not Ritalin
      'not ritalin': 20, ritalin: 20,
      // BETA 17.24 PSBOT cycle-30ak flaw #2: cowboy + viking + killa all
      // assigned rpk_suppressor but their starter weapons + signatures
      // weren't in this tier list. Bot picked random rares over upgrading
      // its own starter. Fix:
      // — Cowboy: revolver + quick draw signature + crit identity
      revolver: 20, 'quick draw': 20, 'six shooter': 20, cowboy: 20,
      // — Viking: viking_axe + berserker rage (low-HP atk speed)
      viking: 20, axe: 20, 'viking axe': 20, berserker: 20, rage: 20,
      // — Killa: rpk machine gun + suppression fire
      rpk: 20, 'machine gun': 20, killa: 20, suppression: 20 },
    tier2: { damage: 10, lifesteal: 10, steroids: 10, 'pre workout': 10, iranian: 10, bird: 10, bungulators: 10, brawler: 10,
      // 14.05: rpk mid-range works in mixed density + uses MS for repositioning
      bungulazine: 10, 'mr silly pills': 10, 'silly pills': 10,
      // 14.05: high fire rate = many hit attempts = many burn proc rolls
      'big mac': 10, 'special sauce': 10, 'stay frosty': 10 },
    tier3: { health: 3, hp: 3, armor: 3, regen: 3 },
  },
  spatula_melee: {
    tier1: { melee: 20, spatula: 20, slash: 20, area: 20, size: 20, drac: 20, fang: 20, "drac's fang": 20, vampire: 20,
      // 14.05: melee = always in swarm, plus tank trade-off fits melee identity
      bungulazine: 20, "hutch's armor": 20, 'hutchs armor': 20, plating: 20 },
    tier2: { 'attack speed': 10, armor: 10, dodge: 10, damage: 10, 'gas mask': 10, "drac's gas mask": 10, bungulators: 10, brawler: 10, voogoo: 10,
      // 14.05: glass-cannon melee + escape after taking hits
      'sponge me daddy': 10, absorbatol: 10, 'not xanax': 10,
      // 14.05: heal-on-kill scales hard with melee kill volume
      'mom\'s cooking': 10 },
    tier3: { health: 3, hp: 3, regen: 3, lifesteal: 3 },
  },
  fireball_kite: {
    tier1: { 'move speed': 20, movement: 20, fireball: 20, 'projectile speed': 20, 'not crack': 20, caffeine: 20, shit: 20, trail: 20, banana: 20, "asmon's desk banana": 20, knockback: 20, trap: 20, frozen: 20, freeze: 20,
      // 14.05: kite-rewarding (alone bonus, diagonal MS, escape) + stay-frosty slow makes kiting easier
      'let\'s ride': 20, 'lets ride': 20, alone: 20, 'mr silly pills': 20, 'silly pills': 20,
      'not xanax': 20, xanax: 20, 'stay frosty': 20, frosty: 20,
      // BETA 17.24 PSBOT cycle-30ak flaw #2: wizard + mr_silly assigned
      // fireball_kite but their starter weapon + signatures weren't here.
      // — Wizard: signature item Not Viagra (+2.5% atk area)
      // BETA 17.26 — PSBOT cycle-30am: Wizard "not viagra" picked 0 times
      // in 5 runs despite 17.24 keyword add. Wizard is dying too fast to
      // get many picks (median 66s); bumped weight 20 → 32 (1.6×) so the
      // few picks the bot does get prioritize the wizard kit.
      'not viagra': 32, wizard: 32, arcane: 32, surge: 32,
      // — Mr Silly: voogoo_rager_toss + silly_juice signature
      voogoo: 20, rager: 20, 'rager toss': 20, 'voogoo rager': 20, silly: 20, 'silly juice': 20 },
    tier2: { dodge: 10, damage: 10, 'pre workout': 10, whoopie: 10, cushion: 10, 'whoopie cushion': 10, stun: 10, poop: 10, 'poop sling': 10, slow: 10, hurray: 10,
      // 14.05: long-game scaling rewards kite that survives long
      'puberty blockers': 10, 'not acid': 10, phase: 10 },
    tier3: { health: 3, hp: 3, regen: 3, armor: 3 },
  },
  tank_sustain: {
    tier1: { health: 20, hp: 20, regen: 20, armor: 20, shield: 20, 'damage reduction': 20, vitamins: 20, ibuprofen: 20, tums: 20, voogoo: 20, rager: 20, 'voogoo rager': 20, heal: 20, sheeesh: 20, revive: 20, drac: 20, fang: 20, "drac's fang": 20,
      // 14.05: tank-perfect armor trade-off + long-game scaling + double-revive
      "hutch's armor": 20, 'hutchs armor': 20, plating: 20, 'puberty blockers': 20,
      'liar deceiver': 20, 'false prophet': 20,
      // 14.05: heal-on-kill + Timmy/named damage reduction (combined with Stompatine perc) for tank survivability
      'mom\'s cooking': 20,
      // BETA 17.24 PSBOT cycle-30ak flaw #2: pirate assigned tank_sustain
      // but blunderbuss + scavenger + pirate's greed not in tier1.
      blunderbuss: 20, pirate: 20, scavenger: 20, "pirate's greed": 20, greed: 20, gold: 20 },
    tier2: { lifesteal: 10, 'underwear': 10, 'big ol': 10, 'stim pod': 10, 'gas mask': 10, "drac's gas mask": 10, bungulators: 10, brawler: 10, minion: 10,
      // 14.05: tank takes hits in swarms + has time to spend gold for HP
      bungulazine: 10, debt: 10, stompatine: 10 },
    tier3: { damage: 3, dodge: 3, iranian: 3, bird: 3,
      // 14.05: glass cannon contradicts tank identity — keep tier 3 (low priority)
      'sponge me daddy': 3, absorbatol: 3 },
  },
  holy_trinity: {
    tier1: { xp: 20, experience: 20, luck: 20, ambition: 20, 'lady luck': 20, level: 20,
      // 14.05: gold-economy perks are PERFECT fit for holy_trinity
      'greedy tums': 20, greedy: 20, debt: 20, 'puberty blockers': 20,
      // 14.05: write-that-down +XP gain fits holy_trinity's level-focused build
      'write that down': 20 },
    tier2: { gold: 10, greed: 10, 'rabbit foot': 10, horseshoe: 10, sheeesh: 10, revive: 10,
      // 14.05: long-game survival utility
      'liar deceiver': 10, 'false prophet': 10, 'mystery meat': 10 },
    tier3: { health: 3, hp: 3, damage: 3, regen: 3,
      // 14.05: stepmom's pills ANTI-SYNERGY with holy_trinity (kills gold gain — explicitly tier 3)
      'stepmom\'s pills': 3, 'stepmoms pills': 3 },
  },
};

// BETA 13.58: character → build mapping. Auto-applied at run start unless the user
// has manually overridden via window.__setBotBuild(). Lebron, Viking, Mr Silly, and
// Cowboy don't have perfect-fit builds in PS Major's 7 — they get the closest match.
const CHARACTER_BUILD: Record<string, BuildType> = {
  // BETA 13.61: CRITICAL FIX. The CHARACTERS array in Home.tsx uses SHORT IDs
  // ('cowboy', 'chad') but I had been using the LONG weapon-owner IDs
  // ('cowboy_peanut', 'chad_peanut'). Auto-build was silently broken since
  // BETA 13.58 — every character defaulted to 'adaptive' because no key matched.
  cowboy:    'rpk_suppressor',  // Six Shooter + Quick Draw passive (atk speed)
  tactical:  'peanut_sniper',   // Peanut Rifle + Headshot Bonus
  sponge:    'spatula_melee',   // Spatula Slash + Sponge Absorb
  killa:     'rpk_suppressor',  // RPK Machine Gun + Suppression Fire
  wizard:    'fireball_kite',   // Fireball Wand + Arcane Surge
  viking:    'rpk_suppressor',  // Viking Axe + Berserker Rage (atk speed when low HP)
  mr_silly:  'fireball_kite',   // VooGoo Rager Toss + Silly Juice
  lebron:    'peanut_sniper',   // Tactical Basketball + King's Court (chain → focus)
  chad:      'chad_aura',       // Chad Aura + Golden Aura
  // BETA 13.75: Pirate re-re-mapped from holy_trinity → tank_sustain. 2 holy_trinity
  // Pirate runs ended at LV1 with 2-3 kills — bot picked XP/Luck/Ambition farming
  // instead of damage/HP. Blunderbuss is close-range shotgun + Scavenger passive
  // matters only if you SURVIVE long enough to loot. tank_sustain biases HP/regen/
  // armor/shield/lifesteal which keeps Pirate alive at close range.
  pirate:    'tank_sustain',    // Blunderbuss + Scavenger (close-range shotgun = tank role)
};

const BUILD_CONFIG_OVERRIDES: Partial<Record<BuildType, Partial<BotConfig>>> = {
  // BETA 17.09 — Bot AI investigation (Dylan: "make bots as smart as me").
  // PSBOT cycle-30z showed all 55 bots dying at 60-72s in first minute,
  // but Dylan's own 309s Killa run had firstDamageTakenTime=67s and
  // longestStretchBetweenHitsMs=114,645. The human kites at 50-60% HP;
  // the bot was waiting until 22% HP to flee. By then runs are mathematically
  // lost. Bumped every fleeThreshold +15-18pts to match human-equivalent
  // panic point. Heal-seek raised commensurately so bot picks up health
  // orbs proactively, not after taking last-stand chip damage.
  chad_aura:      { kiteAggression: 0.30, fleeThreshold: 0.40, healSeekThreshold: 0.60 },
  peanut_sniper:  { kiteAggression: 0.90, fleeThreshold: 0.45, healSeekThreshold: 0.70 },
  rpk_suppressor: { kiteAggression: 0.55, fleeThreshold: 0.40, healSeekThreshold: 0.65 },
  spatula_melee:  { kiteAggression: 0.25, fleeThreshold: 0.35, healSeekThreshold: 0.60 },
  fireball_kite:  { kiteAggression: 0.85, fleeThreshold: 0.40, healSeekThreshold: 0.65 },
  tank_sustain:   { kiteAggression: 0.20, fleeThreshold: 0.30, healSeekThreshold: 0.70 },
  holy_trinity:   { kiteAggression: 0.60, fleeThreshold: 0.40, healSeekThreshold: 0.65 },
};

// Persisted across reloads; user can change via console: window.__setBotBuild('chad_aura')
// BETA 13.87 — CRITICAL FIX for build leak in BotFarm Electron mode.
// Cycle-5 data showed ALL 10 game windows reporting `build=fireball_kite` regardless
// of their assigned character, contaminating per-character balance metrics. Root
// cause: 10 same-origin Electron windows share localStorage. Each iframe's
// `setBotBuild(URL_BUILD)` wrote to `goopzone_bot_build` — last write wins, all
// windows read the shared value at next initBot.
//
// Fix: in BotFarm mode, READ build from URL params (per-iframe correct), and
// SKIP writes to localStorage so iframes don't clobber each other's settings.
// Non-BotFarm sessions keep working exactly as before.
function loadStoredBuildType(): BuildType {
  // BETA 13.87 — botfarm mode: each iframe has its own URL param for build, ignore shared localStorage
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('botfarm') === 'true') {
        const urlBuild = params.get('build');
        if (urlBuild && urlBuild in BUILD_KEYWORD_TIERS) return urlBuild as BuildType;
      }
    } catch { /* noop */ }
  }
  try {
    const raw = localStorage.getItem('goopzone_bot_build');
    if (raw && raw in BUILD_KEYWORD_TIERS) return raw as BuildType;
  } catch { /* SSR / private mode */ }
  return 'adaptive';
}

function saveStoredBuildType(b: BuildType): void {
  // BETA 13.87 — botfarm mode: don't pollute shared localStorage from racing iframes
  if (typeof window !== 'undefined') {
    try {
      if (new URLSearchParams(window.location.search).get('botfarm') === 'true') return;
    } catch { /* noop */ }
  }
  try { localStorage.setItem('goopzone_bot_build', b); } catch { /* noop */ }
}

// ─── Pick Bridge: v1 exports its last pick so v2 can record it ──────
export interface V1PickInfo {
  type: 'levelup' | 'chest' | 'shrine';
  pickedIndex: number;
  pickedName: string;
  pickedRarity?: string;
  offered: string[];
  reason: string;
  timestamp: number;
}
let _lastPick: V1PickInfo | null = null;
let _lastPickReason = '';
export function getLastV1Pick(): V1PickInfo | null {
  const pick = _lastPick;
  _lastPick = null; // consume on read
  return pick;
}

// ─── Configuration ──────────────────────────────────────────────────
export interface BotConfig {
  /** Enable/disable the bot */
  enabled: boolean;
  /** How aggressively the bot kites (0 = stand and fight, 1 = always run) */
  kiteAggression: number;
  /** HP percentage threshold to prioritize fleeing */
  fleeThreshold: number;
  /** HP percentage threshold to seek health pickups */
  healSeekThreshold: number;
  /** Maximum runs for batch testing (0 = infinite) */
  maxRuns: number;
  /** Log verbosity: 'silent' | 'summary' | 'verbose' */
  logLevel: 'silent' | 'summary' | 'verbose';
  /** Auto-restart on death */
  autoRestart: boolean;
  /** Delay between runs in ms */
  restartDelayMs: number;
  /** Active build archetype — biases card scoring + movement config (BETA 13.56) */
  buildType: BuildType;
}

const DEFAULT_CONFIG: BotConfig = {
  enabled: false,
  kiteAggression: 0.6,
  fleeThreshold: 0.20,
  healSeekThreshold: 0.50,
  maxRuns: 10,
  logLevel: 'summary',
  autoRestart: true,
  restartDelayMs: 2000,
  buildType: 'adaptive',
};

// ─── Run Log (one per playthrough) ──────────────────────────────────
export interface RunLog {
  runNumber: number;
  gameVersion: string;           // e.g. 'BETA 13.5' — which build produced this run
  buildType: BuildType;          // BETA 13.56: archetype that drove card-pick scoring this run
  characterId?: string;          // BETA 13.73: actual character id (cowboy/killa/viking/etc.) — separates shared-build cohorts in CSV
  // BETA 13.59: invisible-behavior counters — promoted from bot state to RunLog at finalize
  blineTriggers?: number;        // total drift-circle b-line activations
  trailTeleports?: number;       // total trail-enemy fog-of-war teleports
  namedEliteFlees?: number;      // total Timmy/CaseOh awareness triggers
  // BETA 13.60: per-system frame-time buckets (cumulative ms over the run; CSV shows ms/sec)
  // BETA 13.70 (PS Major FPS pass): added `flowfield` bucket
  perfBuckets?: { enemies: number; weapons: number; items: number; bot: number; render: number; flowfield: number };
  // BETA 13.71: stat-effect verification summary (computed at finalize from choiceLog)
  brokenPickCount?: number;      // picks where statDeltas was empty (no measurable effect)
  brokenPickNames?: string[];    // names of those picks for spot-checking
  totalPicksTracked?: number;    // total picks where statsBefore + statsAfter both captured
  // BETA 13.80: pathfinding handoff Fix 1 + hitbox near-miss tracker
  trapCellsMarked?: number;      // total times bot called markCellAsTrap (stuck > 4 attempts at same cell)
  enemyNearMissFrames?: number;  // frames where any enemy was within (hitbox + player.size + 5wu) but no damage applied
  weaponNearMisses?: number;     // projectiles that despawned having passed within (proj.size + 10wu) of an enemy without hitting
  // BETA 13.82: item coverage tracker — picks of items never seen in prior runs
  noveltyPicks?: number;         // count of picks where the chosen item had never been picked across any prior run
  // BETA 15.26 — character passive tracking. Layer 1 (per-level) + Layer 2 (signature ability).
  // characterPassiveLayer1Final: total Layer 1 bonus at run end (e.g. Wizard
  //   level 40 with +1% Attack Size/lvl = 40). Direct read of perLevel × level.
  // signatureProcsThisRun: count of signature ability activations (Sniper crits,
  //   Quick Draw shots, Wizard Surges, Scavenger drops, Chad gold-in-aura, etc).
  //   "Continuous" signatures (Berserker, Suppression, Silly Juice) report 0
  //   here — see their stack/seconds columns instead.
  // signatureMaxStacks: peak stacks reached (Sponge 0-3, Silly Juice 0-25,
  //   Killa Suppression rounded to 0-10).
  // signatureActiveSeconds: time spent in active signature window (Wizard Surge).
  // signatureBonusDamage: cumulative bonus damage from signature (LeBron bounce-chain).
  characterPassiveLayer1Final?: number;
  signatureProcsThisRun?: number;
  signatureMaxStacks?: number;
  signatureActiveSeconds?: number;
  signatureBonusDamage?: number;
  // BETA 15.28 — spawn timing tracker. Answers "overwhelmed too soon?"
  // Buckets count enemies that newly spawned in each gameTime window.
  // First-spawn-time = -1 if that enemy type never spawned in this run.
  enemiesSpawned_0to30s?: number;
  enemiesSpawned_30to60s?: number;
  enemiesSpawned_60to120s?: number;
  enemiesSpawned_120to300s?: number;
  peakAliveCount?: number;
  /** BETA 19.39 — Per-minute alive-enemy samples (DEPRECATED in 19.40).
   *  Always empty; replaced by samples10s. Kept for older PSBOT scripts. */
  aliveSamplesPerMinute?: number[];
  /** BETA 19.40 — 10-second granular time-series. Each entry = one frame
   *  sampled at the t mark. Used to pinpoint exactly when bot HP starts
   *  cratering / enemy density spikes vs the run timeline. */
  samples10s?: Array<{ t: number; alive: number; hp: number; dmgTaken: number }>;
  peakAliveTime?: number;
  firstZombieSpawnTime?: number;
  firstTimmySpawnTime?: number;
  firstCaseohSpawnTime?: number;
  // BETA 15.28 — final luck stat at run end. Cross-reference with chest/perc
  // pick rarities to verify luck actually steers offers / picks.
  finalLuckPct?: number;
  // BETA 15.34 — Pick audit. Per-pick log of every shrine / perc / chest /
  // weapon level-up the player took, with picked id + value + offers + stats.
  // The full JSON is in pickAuditJson; the summary cols below let you filter
  // god-mode runs in Supabase without parsing JSON.
  pickAuditCount?: number;             // total picks this run
  pickAuditLegendaryCount?: number;    // count of legendary picks across all sources
  pickAuditEpicPlusCount?: number;     // count of epic OR legendary picks
  pickAuditMaxBuffValue?: number;      // largest single buff value seen this run
  pickAuditMaxBuffSource?: string;     // 'shrine' | 'levelup_perc' | 'chest_free' | etc
  pickAuditMaxBuffId?: string;         // which thing dropped that buff (e.g. '+40% Damage')
  pickAuditMaxBuffRarity?: string;
  pickAuditPickCounts_levelupPerc?: number;
  pickAuditPickCounts_levelupWeapon?: number;
  pickAuditPickCounts_shrine?: number;
  pickAuditPickCounts_chestFree?: number;
  pickAuditPickCounts_chestPaid?: number;
  pickAuditJson?: string;              // full per-pick log; ~3-7KB per run typical
  // BETA 15.31 — per-run perc-pick rarity counts. Pairs with finalLuckPct to
  // answer "is luck actually steering offers?" via correlation analysis.
  percPicks_common?: number;
  percPicks_uncommon?: number;
  percPicks_rare?: number;
  percPicks_epic?: number;
  percPicks_legendary?: number;
  percPicks_total?: number;
  // BETA 13.99b — new-content adoption tracking (11 weapons + 8 items shipped 13.94-99)
  newWeapPicks?: number;         // pick events where chosen card was one of the 11 new weapons
  newItemPicks?: number;         // pick events where chosen card was one of the new items (BETA 13.98-99 + 13.103)
  newPickNames?: string[];       // names of new-content picks for spot-checking
  // BETA 14.05 — separate tracker for the 14 new percs (BETA 13.103). Adoption-rate
  // tracking by build is the cycle-7b verification target.
  newPercPicks?: number;         // pick events where chosen card was one of the 14 new percs
  // BETA 14.05 — floor-effect tracking. User: "are you able to track all the new
  // floor sprite and their damage or slowing and such?" The weaponSystemState has
  // a `groundEffects: GroundEffect[]` array (item_shit, item_asmons_desk_banana,
  // item_whoopie_cushion_of_doom, wpn_poop_sling impact, wpn_shitty_cannon impact,
  // CaseOh slam crack). Per-frame sweep counts spawns + first-step triggers + a
  // per-source breakdown so cycle-7b can answer "did the SHIT trail actually slow
  // anything" / "did the banana trap fire" / etc.
  groundEffectsSpawned?: number; // count of unique GroundEffect IDs observed this run
  groundEffectsTriggered?: number; // count of unique IDs that flipped wasSteppedOn=true
  groundEffectSources?: string[]; // pipe list like "item_shit:8|wpn_poop_sling:3|caseoh_slam_crack:2" — STEP-ONS per source
  // BETA 16.55 — PSBOT cycle-30l: spawn-side per-source breakdown. Distinct
  // from groundEffectSources (which counts step-ons). Use this to answer
  // "did banana auto-drop fire at all?" without inferring from triggers.
  groundEffectSpawnSources?: string[]; // pipe list of "item_asmons_desk_banana:42|wpn_poop_sling:18"
  // BETA 14.05 — Ugly Kittens trap tracking. The weapon's projectiles have
  // speed 0 + lifetime 999, so they sit on the ground until an enemy contact
  // kills them. Bot sweeps state.projectiles (weaponSystem) once per frame and
  // counts unique kitten IDs seen + how many disappeared (trap fired).
  kittensPlaced?: number;        // unique projectile IDs seen with weaponId=wpn_ugly_kittens
  kittensTriggered?: number;     // count of placed kittens that disappeared from list (trap fired)
  // BETA 14.06 — per-weapon accuracy + damage rollup. The engine already tracks
  // weapon.totalShotsFired / totalShotsHit / totalDamageDealt / totalCrits per
  // WeaponInstance (BETA 13.43). Snapshot at finalize so the analyst pass can
  // tell which weapon did the work, accuracy per weapon, crit rate per weapon.
  // User: "are you tracking accuracy on weapons and projectiles..."
  weaponTelemetry?: string[];    // pipe-list "wpn_X:lvlN,dmg=D,hit=H/F(P%),crit=C"
  // BETA 14.06 — per-perc level rollup. Tells which percs the bot ended with
  // and at what level (1-99). Required to compute conditional/scaling perc
  // contribution at end-of-run. Without this, all 14 new perc mechanics
  // (Bungulazine swarm, Puberty Blockers ramp, Greedy Tums gold-scale, etc.)
  // are invisible to the analyst pass.
  percLevels?: string[];         // pipe-list "T01:5|T03:3|N01:2|N05:4"
  // BETA 14.06 — final player stat snapshot. Captures the cumulative result of
  // all the scaling mechanics (perc per-level + condition uptime + time scale +
  // gold scale + missing-HP scale + Mystery Meat random rolls + trade-off
  // penalties). User: "are you tracking all scaling? how why when linear aoe."
  finalStats?: string;           // packed "maxHp=125,dmg=42,as=15,arm=18,..." for all relevant stats
  // BETA 14.06 (audit pass) — revive detection (Sheeesh + Liar Deceiver).
  // Inferred from player.hp transitions; engine flags _sheeeshUsedThisRun /
  // _liarUsedThisRun aren't exposed to the bot.
  sheeeshTriggered?: number;     // 0/1 — did Sheeesh fire this run
  liarTriggered?: number;        // 0/1 — did Liar Deceiver fire this run
  // BETA 14.07 — projectile chase / homing tracking. User: "the projectile or
  // bullet sometimes follows an enemy when it should just shoot straight... how
  // long did it chase, and such." Per-weapon turn-event count. Folded into
  // weaponTelemetry as turn= field so the analyst pass can flag any weapon
  // with non-zero turns that's NOT supposed to home (Squirter, Frozen Darts,
  // straight projectiles). Boomerang (Terminator) and orbital weapons (Karen,
  // Iranian Bird) are EXPECTED to turn — high counts there are by design.
  // Already in weaponTelemetry; no separate column needed.
  // BETA 14.07 — additional precision metrics (none needed engine hooks).
  timeToFirstKillSec?: number;   // game-time when killCount first reached 1 (-1 if no kills)
  phaseTimePlaying?: number;     // accumulated dt while state.phase === 'playing'
  phaseTimeLevelup?: number;     // accumulated dt while state.phase === 'levelup'
  phaseTimeChest?: number;       // accumulated dt while state.phase === 'chest_choice' or 'chest_spin'
  phaseTimeShrine?: number;      // accumulated dt while state.phase === 'shrine_choice'
  distanceTraveled?: number;     // sum of frame-to-frame player position deltas (wu)
  maxItemStack?: number;         // largest stack count any single item reached
  avgEnemiesAlive?: number;      // running average of state.enemies.filter(alive).length
  avgKiteDistance?: number;      // running average of distance to nearest enemy (wu)
  // Status-effect adoption: per-frame sweep of state.enemies counts active statuses
  freezeFrames?: number;         // sum of (enemy-frames frozenUntil > 0)  — Ugly Kittens 3s + Frozen Darts 2s
  slowFrames?: number;           // sum of (enemy-frames slowedUntil > 0 AND not frozen) — Poop / Shitty Cannon / Hurray / SHIT trail
  wetFrames?: number;            // sum of (enemy-frames wetUntil > 0)  — Squirter (Phase 3 = electric synergy)
  synergyDracsBoth?: number;     // 0/1 — did the bot end the run holding BOTH Drac's Fang AND Drac's Gas Mask
  timeScale: number;             // debugTimeScale at run start (1 = normal, 5 = 5× speed)
  startTime: number;             // Date.now() wall-clock at run start
  endTime: number;               // Date.now() wall-clock at run end
  gameTimeStart: number;         // state.time at run start (game time, scale-aware)
  survivalTimeSec: number;       // GAME time elapsed (state.time delta) — not wall-clock
  wallClockSec: number;          // real elapsed seconds (for reference)
  deathCause: string;           // 'enemy:<type>' | 'boss:<name>' | 'unknown' | 'won'
  floorReached: number;
  waveReached: number;
  killCount: number;
  killsByType: Record<string, number>;
  damageDealt: number;
  damageTaken: number;
  healingReceived: number;
  goldCollected: number;
  chestsOpened: number;
  shrinesActivated: number;
  itemsCollected: string[];
  weaponsUsed: string[];
  levelReached: number;
  upgradesPicked: string[];     // IDs of chosen upgrades in order
  upgradesSkipped: string[];    // IDs of upgrades that were offered but not picked
  choiceLog: ChoiceRecord[];    // Every level-up / chest / shrine decision with full context
  roomsVisited: number;
  deathPosition: { x: number; y: number };
  hpAtDeath: number;
  // ── Anomalies / Bugs detected ──
  anomalies: Anomaly[];

  // ── BETA 14.70 — v1 BotFarm CSV converge ──
  // PSBOT RUN ask: "v1 add — unify the trackers into the v1 master CSV
  // writer so every standard BotFarm run inherits them. Avoids the
  // v1/v2 mode-switch confusion." Pulls all the deep per-mechanic
  // trackers from weaponLab module-state at finalizeRun and emits
  // them in the master.csv so cycle-8+ analyst pass works without
  // remembering which CSV format to use.
  percTrackerJson?: string;
  itemTrackerJson?: string;
  shrineTrackerJson?: string;
  floorEffectTrackerJson?: string;
  weaponEnemyMatrixJson?: string;
  synergyTrackerJson?: string;
  eventLogJson?: string;
  deathContextJson?: string;
  nearMissEventsLogJson?: string;
  retreatEventsLogJson?: string;
  topWeaponVsZombie?: string;
  topWeaponVsTimmy?: string;
  topWeaponVsCaseoh?: string;
  topWeaponVsElite?: string;
  weakestWeaponOverall?: string;
  // Phase 1 event counters (per-run cumulative)
  reviveEventsThisRun?: number;
  healEventsThisRun?: number;
  dodgeEventsThisRun?: number;
  phaseEventsThisRun?: number;
  knockbackEventsThisRun?: number;
  // Phase 1 enemy-attack outcomes
  enemyAttacksAttempted?: number;
  enemyAttacksHit?: number;
  enemyAttacksMissedByEvasion?: number;
  enemyAttacksMissedByIframe?: number;
  enemyAttacksMissedByPhase?: number;
  enemyAttacksMissedByOther?: number;  // BETA 14.74 — shield-fully-absorbed (state._enemyAttacksMissedByOther). Was on engine state since BETA 14.30 BUG-05 but missing from BotFarm CSV header until now.
  // BETA 14.82 — sprite-touch-no-damage tracking (user: "enemy sprites are touching bot and no damage being dealt")
  enemySpriteOverlapFrames?: number;     // any enemy's sprite overlapped player's sprite (visual touch)
  enemySpriteOnlyTouchFrames?: number;   // sprite overlapped but hitbox didn't (visual-vs-hitbox gap)
  enemyTouchCooldownGated?: number;      // hitbox touched but contactCooldown gated the attack
  // BETA 15.14 — N10 Debt mechanic trackers
  debtSavesTriggered?: number;           // count of Debt window activations
  debtGoldSpent?: number;                // total gold drained by Debt across all windows
  debtHitsAbsorbed?: number;             // count of hits redirected from HP → gold
  debtForceEnded?: number;               // times Debt collapsed early because gold ran out
  // BETA 15.15 — N13 Not Xanax trackers (slow on hit-taken)
  notXanaxSlowsApplied?: number;         // count of slow applications to attackers
  notXanaxTotalSlowSeconds?: number;     // sum of slow-frame durations applied
  // BETA 15.15 — N06 Let's Ride XXX trackers (perk-source bungulator summon)
  perkBungulatorsSpawned?: number;       // peak count of perk-source bungulators ever active
  // Phase 5 timing baselines
  firstBloodTime?: number;
  firstDamageTakenTime?: number;
  longestStretchBetweenHitsMs?: number;
  // Engine perf
  engineFramePerfJson?: string;
  // Stat snapshots (final values)
  finalDamagePct?: number;
  finalCritChancePct?: number;
  finalAttackSpeedPct?: number;
  finalMaxHP?: number;
  // BETA 15.13 N-perk stat anchors (added to verify new perks actually apply)
  finalLifestealPct?: number;        // N01 Sponge Me Daddy
  finalArmor?: number;                // N02 Hutch's Armor
  finalThornsPct?: number;            // N03 Stepmom's Pills
  finalCritDamagePct?: number;        // N04 Not Blue Chew
  finalProjectileCount?: number;      // N05 Bungulazine
  finalOverheal?: number;             // N07 Puberty Blockers (lazy field on player as 'overheal')
  finalProjectilePierce?: number;     // N08 Not Ritalin (lazy field as 'projectilePierceBonus')
  finalPickupRangePct?: number;       // N09 Greedy Tums
  finalEliteDamagePct?: number;       // N11 Stompatine
  finalProjectileBounce?: number;     // N12 Mr Silly Pills
  finalPhaseChancePct?: number;       // N14 Not Acid
  // Run-end metadata
  runGoldSpent?: number;

  // ── BETA 14.72 — Bot diagnostics ──
  // User: "are we tracking the bots movements and such and location on map
  // where it is and what is around it and possibilities to why its getting
  // stuck and also have a way to make the bot better from the information
  // you get?" Six diagnostic streams snapshot at finalizeRun:
  botPositionTrailJson?: string;       // sampled coords every 5s of game-time
  stuckEventsJson?: string;            // per-stuck-event context + categorization
  botMapCellVisitsJson?: string;       // 280wu grid heatmap (cell → visit count)
  decisionTraceJson?: string;          // bot brain decision events (currently empty)
  // BETA 15.42 (re-add) — long-frame / freeze detection. Originally shipped in
  // BETA 15.26, wiped by the BETA 15.22 ROLLBACK (commit 1406244), restored
  // here. Tracks wall-clock frames > 250ms during state.phase === 'playing';
  // snapshots context (player/enemies/goal) at the WORST frame so freezes show
  // up in CSV + Supabase rather than being invisible.
  longFrameCount?: number;
  longestFrameMs?: number;
  longFrameLastContext?: string;       // JSON snapshot at worst-frame moment
}

/** A single card-pick decision the bot made during a run. */
export interface ChoiceRecord {
  type: 'levelup' | 'chest' | 'shrine';
  wave: number;
  level: number;
  hpPct: number;             // player HP% at decision time
  offered: string[];         // names of all offered cards/options
  pickedIndex: number;       // which index was chosen (0-based)
  pickedName: string;        // human-readable name of chosen option
  pickedRarity?: string;     // rarity of chosen card if available
  // BETA 13.58: outcome stamping for regret analyzer.
  // Stamped at logChoice time so we know WHEN a pick was made.
  pickedAtSurvivalSec?: number; // game time when this pick happened
  pickedAtKills?: number;       // total kill count when this pick happened
  // Back-filled at finalizeRun: how the run played out AFTER this pick.
  survivalAfterPickSec?: number; // delta survivalTimeSec - pickedAtSurvivalSec
  killsAfterPick?: number;        // delta killCount - pickedAtKills
  runDeathCause?: string;         // copied from run.deathCause (e.g. enemy:zombie:elite)
  runBuildType?: BuildType;       // build active for this run — for cross-run grouping
  // BETA 13.70: stat-effect verification. Snapshots player stats before + after
  // each pick so we can verify the upgrade actually had an effect. If you pick
  // "+10% Damage" and damageBonus delta is 0, that's a broken upgrade pipeline.
  statsBefore?: Record<string, number>;   // captured at pick time
  statsAfter?: Record<string, number>;    // captured ~500ms later (after itemsAdapter recalc)
  statDeltas?: Record<string, number>;    // per-stat diff (only fields that changed)
  topStatDelta?: { stat: string; delta: number }; // strongest change for CSV summary
}

export interface Anomaly {
  timestamp: number;
  severity: 'info' | 'warning' | 'bug';
  category: string;
  description: string;
  data?: any;
}

// ═══════════════════════════════════════════════════════════════════════
// BOT BRAIN — Adaptive learning system that persists across runs
// ═══════════════════════════════════════════════════════════════════════

// BETA 17.22 — PSBOT cycle-30ai B4: per-character brain. Sponge's optimal
// kite aggression is different from Killa's. Previously one global brain
// averaged across all characters. Now each character gets a sub-brain
// that learns its own optimums. Global brain remains as fallback for
// characters that haven't been played enough yet (<5 runs).
export interface PerCharacterBrain {
  fleeThreshold: number;
  healSeekThreshold: number;
  kiteAggression: number;
  cardScores: Record<string, { total: number; count: number }>;
  recentSurvivalTimes: number[];
  totalRuns: number;
  bestSurvivalSec: number;
  avgSurvivalSec: number;
}

// BETA 17.22 — PSBOT cycle-30ai B7: safe-zone memory. Track positions
// where bot recovered HP from < 50% → > 70%. When HP is low and no
// health orb visible, the bot navigates to nearest remembered safe spot
// instead of wandering.
export interface SafeZone {
  x: number;
  y: number;
  quality: number; // how much HP was recovered there (running average)
  lastUsed: number; // timestamp for LRU pruning
}

export interface BotBrain {
  // ── Adaptive survival parameters (tuned by learning) ──
  fleeThreshold: number;       // flee when HP% < this  (default 0.20)
  healSeekThreshold: number;   // seek health when HP% < this (default 0.50)
  kiteAggression: number;      // 0=stand+fight, 1=always circle-strafe (default 0.6)

  // ── Strategic flags ──
  avoidBossShrines: boolean;   // true = skip boss shrines (learned from bad experiences)
  preferDefense: boolean;      // true = weight health/armor cards higher when picking
  preferOffense: boolean;      // true = weight damage cards higher when picking

  // ── Card keyword scoring (survival-time-weighted) ──
  // For each keyword, tracks: totalSurvivalSec accumulated when that keyword was chosen, and count.
  // Higher ratio = keyword correlates with longer runs → prefer it.
  cardScores: Record<string, { total: number; count: number }>;

  // ── Run history for trend analysis ──
  recentSurvivalTimes: number[];     // last 10 run survival seconds
  recentDeathCauses: string[];       // last 10 death cause strings
  recentHpAtDeath: number[];         // last 10 hp% at death

  // ── Meta ──
  totalRunsLearned: number;
  bestSurvivalSec: number;
  avgSurvivalSec: number;
  lastAdaptedAt: number;     // run number of last param adjustment
  createdAt: number;

  // ── BETA 17.22 PSBOT cycle-30ai B4: per-character brain ──
  perCharacter?: Record<string, PerCharacterBrain>;

  // ── BETA 17.22 PSBOT cycle-30ai B7: safe-zone memory (top 5 LRU) ──
  safeZones?: SafeZone[];

  // ── BETA 17.22 PSBOT cycle-30ai B3: server-side aggregated brain blend ──
  // Fetched async from Supabase on initBot. Captures global average flee/
  // heal/kite thresholds across all bot runs by all dev machines. Blended
  // into the active brain at 40% weight. Skipped if fetch fails (no
  // network) — bot still works on local brain only.
  globalBlend?: {
    fleeThreshold: number;
    healSeekThreshold: number;
    kiteAggression: number;
    fetchedAt: number;
    sampleSize: number;
  };
}

const BRAIN_STORAGE_KEY  = 'goopzone_bot_brain_v2';
const BOT_RUNS_KEY       = 'goopzone_bot_runs_v1';   // completed run logs
const BOT_RUN_COUNT_KEY  = 'goopzone_bot_run_count'; // global run counter (survives page reload)
const MEMORY_KEY         = 'goopzone_bot_memory';    // StrategyMemory persistence
// BETA 13.82 — item coverage tracking. Persistent sets of every item EVER offered
// to the bot AND every item EVER picked by the bot, across all runs/sessions.
// The bot biases scoring toward never-picked items (+15 in pickFromCards) so
// every item in the catalog gets test coverage at least once. Also surfaces
// "offered but never picked" (catalog gaps) and "in catalog but never offered"
// (drop-rate / unlock issues) for the analyst report.
const COVERAGE_PICKED_KEY  = 'goopzone_coverage_picked_v1';
const COVERAGE_OFFERED_KEY = 'goopzone_coverage_offered_v1';
const MAX_STORED_RUNS    = 200;                        // cap so we don't blow localStorage
const BRAIN_WINDOW = 10;      // rolling window size for trend analysis

// ═══════════════════════════════════════════════════════════════════════
// BETA 13.82 — ITEM COVERAGE TRACKER
// ═══════════════════════════════════════════════════════════════════════
// User direction: "we should make sure we are getting to the point where we
// are getting all items and such able to be chosen to test. so keep track of
// past and future items and what has never been chosen and make it so that
// our bots will choose it at least once."
//
// Two persistent sets across all sessions:
//   - PICKED  — every item name the bot ever PICKED (chose at level-up/shrine/chest)
//   - OFFERED — every item name the bot ever SAW in an offer pool
//
// In pickFromCards, the bot adds +15 score to any offered item that's not yet
// in PICKED — this is between Tier 2 (+10) and Tier 1 (+20), so it breaks
// ties between similar-tier picks but won't override a build keystone.
//
// The bias only applies after 5s into a run AND when HP > 50% — we never
// sabotage early-game / low-HP picks just for coverage.
//
// Reports:
//   getCoverageReport() — { picked, offered, neverPicked, neverOffered }
//   - "neverPicked" = offered but never chosen (bot scoring blind spot)
//   - "neverOffered" = could be in catalog but never appeared in any pool
//     (drop-rate / unlock issue — currently we can only detect this if user
//      maintains a known-catalog list externally; we surface "neverPicked"
//      only here)
//
// Reset for new game version: window.__resetCoverage() — call when shipping
// a new item that should re-bias toward novel testing.
// ═══════════════════════════════════════════════════════════════════════

let _coveragePicked: Set<string> | null = null;
let _coverageOffered: Set<string> | null = null;

function _normalizeItemName(name: string): string {
  return (name ?? '').toLowerCase().trim();
}

function _loadCoverageSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function _saveCoverageSet(key: string, s: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(s)));
  } catch { /* storage full / private mode */ }
}

function _ensureCoverageLoaded(): void {
  if (!_coveragePicked) _coveragePicked = _loadCoverageSet(COVERAGE_PICKED_KEY);
  if (!_coverageOffered) _coverageOffered = _loadCoverageSet(COVERAGE_OFFERED_KEY);
}

/** Mark item names as offered to the bot (called from logChoice — every pick screen). */
export function markItemsOffered(names: string[]): void {
  _ensureCoverageLoaded();
  let changed = false;
  for (const n of names) {
    const k = _normalizeItemName(n);
    if (k && !_coverageOffered!.has(k)) { _coverageOffered!.add(k); changed = true; }
  }
  if (changed) _saveCoverageSet(COVERAGE_OFFERED_KEY, _coverageOffered!);
}

/** Mark a single item as picked (chosen). Called when a pick is finalized. */
export function markItemPicked(name: string): void {
  _ensureCoverageLoaded();
  const k = _normalizeItemName(name);
  if (!k || _coveragePicked!.has(k)) return;
  _coveragePicked!.add(k);
  _saveCoverageSet(COVERAGE_PICKED_KEY, _coveragePicked!);
}

/** True if this item has never been picked across any run. */
export function isNoveltyItem(name: string): boolean {
  _ensureCoverageLoaded();
  return !_coveragePicked!.has(_normalizeItemName(name));
}

// BETA 13.99b — adoption tracking for the 11 new weapons (BETA 13.94-97) and
// 8 new items (BETA 13.98-99). Lowercased name fragments — match against any
// substring of the pick's lowercased name, since cards label as either
// the weapon's display name ("Frozen Darts") or the item's name ("Sheeesh").
// BETA 14.05 — extended for the 7 NEW items + 14 NEW percs from BETA 13.103.
// Cycle-7 surfaced 0 picks of the new content because the name-list was
// out-of-sync with what shipped. Synced here.
const NEW_WEAPON_NAME_FRAGMENTS = [
  'ultra beam', 'terminator', 'ugly kittens', 'karen drones', 'poop sling',
  'squirter', 'frozen darts', "drac's fang", 'shitty cannon', 'ex girlfriend', 'hurray',
];
const NEW_ITEM_NAME_FRAGMENTS = [
  // BETA 13.98-99 items
  'voogoo rager', "asmon's desk banana", 'whoopie cushion', 'sheeesh',
  'iranian bird', 'bungulators', 'shit', "drac's gas mask",
  // BETA 13.103 items (7 new passive items)
  'stay frosty', "mom's cooking", 'write that down',
  'mystery meat', 'big mac', 'special sauce',
  'liar deceiver', 'false prophet',
];
// BETA 14.05 — track new percs separately so the adoption-by-build analyst pass
// can correlate per-build pick rates against BOT_KEYWORD_TIER_ADDITIONS targets.
const NEW_PERC_NAME_FRAGMENTS = [
  // BETA 13.103 percs (14 new — N01-N14)
  'sponge me daddy', 'absorbatol',
  "hutch's armor", 'hutchs armor', 'plating',
  "stepmom's pills", 'stepmoms pills',
  'not blue chew',
  'bungulazine',
  "let's ride", 'lets ride',
  'puberty blockers',
  'not ritalin',
  'greedy tums',
  'debt',
  'stompatine',
  'mr silly pills', 'silly pills',
  'not xanax',
  'not acid',
];
function isNewPercName(name: string): boolean {
  const n = name.toLowerCase();
  return NEW_PERC_NAME_FRAGMENTS.some(f => n.includes(f));
}
function isNewWeaponName(name: string): boolean {
  const n = name.toLowerCase();
  return NEW_WEAPON_NAME_FRAGMENTS.some(f => n.includes(f));
}
function isNewItemName(name: string): boolean {
  const n = name.toLowerCase();
  return NEW_ITEM_NAME_FRAGMENTS.some(f => n.includes(f));
}

/** Coverage report for the analyst handoff + dev panel. */
export function getCoverageReport(): {
  pickedCount: number;
  offeredCount: number;
  neverPickedFromOffered: string[];
  pickedItems: string[];
  offeredItems: string[];
} {
  _ensureCoverageLoaded();
  const picked = _coveragePicked!;
  const offered = _coverageOffered!;
  const neverPicked: string[] = [];
  // Array.from() to avoid downlevelIteration TS error (same pattern used elsewhere in this file).
  for (const o of Array.from(offered)) if (!picked.has(o)) neverPicked.push(o);
  return {
    pickedCount: picked.size,
    offeredCount: offered.size,
    neverPickedFromOffered: neverPicked.sort(),
    pickedItems: Array.from(picked).sort(),
    offeredItems: Array.from(offered).sort(),
  };
}

/** Reset both sets — call when shipping a new item version that should re-bias toward novel testing. */
export function resetCoverage(): void {
  _coveragePicked = new Set();
  _coverageOffered = new Set();
  try {
    localStorage.removeItem(COVERAGE_PICKED_KEY);
    localStorage.removeItem(COVERAGE_OFFERED_KEY);
  } catch { /* noop */ }
}

// ═══════════════════════════════════════════════════════════════════════
// BETA 13.83 — TRACKER SELF-VALIDATION
// ═══════════════════════════════════════════════════════════════════════
// Cycle-4 surfaced a new failure mode: PS Major wired `Player.projectileBounceBonus`
// in 13.82 (correctly), but the stat-effect tracker's `_snapshotPlayerStats` didn't
// include the field, so 27 picks of "+1 Projectile Bounce" shrine were FLAGGED as
// broken even though the wire was working. Same class of bug as 13.77's
// eliteDamageBonus/projectileSpeedBonus/attackAreaBonus gaps.
//
// This helper runs at game start (and on demand via window.__validateTracker())
// to compare the live `state.player` keys against `_snapshotPlayerStats` keys.
// Any numeric Player field NOT covered by the snapshot is logged loudly so the
// gap is caught BEFORE the next cycle's data accumulates 27 false positives.
//
// "If invisible, build a tracker" — and validate the tracker itself.
// ═══════════════════════════════════════════════════════════════════════

let _trackerValidated = false;

export function validateTrackerCoverage(player: Record<string, unknown>): {
  ok: boolean;
  trackedFields: string[];
  uncoveredNumericFields: string[];
} {
  const dummy = _snapshotPlayerStats(player as Parameters<typeof _snapshotPlayerStats>[0]);
  const trackedFields = Object.keys(dummy);
  const uncovered: string[] = [];
  for (const k of Object.keys(player)) {
    if (typeof player[k] !== 'number') continue;
    // Skip per-frame ephemeral fields that aren't stat-pick targets.
    if (k === 'x' || k === 'y' || k === 'vx' || k === 'vy' || k === 'invincibleTimer'
        || k === 'goopCount' || k === 'spriteSize' || k === 'bobPhase' || k === 'facing'
        || k === 'gold' || k === 'xp' || k === 'level' || k === 'xpToNext'
        || k === 'hypeTrainShield') continue;
    if (!(k in dummy)) uncovered.push(k);
  }
  const ok = uncovered.length === 0;
  if (!ok && !_trackerValidated) {
    console.warn(
      `[Tracker self-validation] Player has ${uncovered.length} numeric field(s) NOT covered by _snapshotPlayerStats: ${uncovered.join(', ')}. ` +
      `Picks targeting these fields will silently false-positive as "broken". Add them to _snapshotPlayerStats.`,
    );
  }
  _trackerValidated = true;
  return { ok, trackedFields, uncoveredNumericFields: uncovered };
}

// ═══════════════════════════════════════════════════════════════════════
// STRATEGY MEMORY — Per-item and per-weapon correlation tracking
// Persists across sessions; separate from BotBrain so it can be inspected
// and reset independently.
// ═══════════════════════════════════════════════════════════════════════

export interface ItemMemoryEntry {
  /** Runs where this item was held AND survival was above the baseline. */
  goodRuns: number;
  /** Runs where this item was held AND survival was below the baseline. */
  badRuns: number;
  /** Cumulative survival seconds across all runs this item appeared in. */
  totalSurvivalSec: number;
  /** Total times this item was picked by the bot. */
  pickCount: number;
}

export interface WeaponMemoryEntry {
  /** Cumulative survival seconds across all runs this weapon was equipped. */
  totalSurvivalSec: number;
  /** Total runs this weapon was used. */
  pickCount: number;
  /** Runs ended in 'won' state while this weapon was held. */
  wins: number;
}

export interface StrategyMemory {
  /** Per-item tracking keyed on normalized item name. */
  itemStats: Record<string, ItemMemoryEntry>;
  /** Per-weapon tracking keyed on weapon id. */
  weaponStats: Record<string, WeaponMemoryEntry>;
  /** Number of runs sampled into this memory. */
  totalRunsSampled: number;
  /**
   * Rolling average survival baseline — used to classify a run as "good"
   * or "bad" for a given item.  Starts at 120s (empirical floor-1 average).
   */
  avgSurvivalBaseline: number;
}

// BETA 13.85 — botfarm-mode localStorage isolation. When the URL has
// `?botfarm=true` (set by /bot-farm dashboard's iframe URLs), this iframe is
// one of 10 racing siblings sharing the same origin's localStorage. If each
// iframe wrote to BOT_RUNS_KEY or BOT_RUN_COUNT_KEY, they'd clobber each
// other's results. So in botfarm mode we skip BOTH save and load — `bot.allRuns`
// is the in-memory source of truth, and the dashboard reads each iframe's CSV
// via postMessage (apMod.exportCSVAsString(bot.allRuns)). User's regular
// non-botfarm sessions keep working as before because the flag only fires when
// ?botfarm=true. Declared here above loadRunCount/saveRunCount to avoid TDZ.
const _isBotFarmMode = (() => {
  try {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('botfarm') === 'true';
  } catch { return false; }
})();

function loadRunCount(): number {
  if (_isBotFarmMode) return 0; // BETA 13.85: independent counters per tile
  try { return parseInt(localStorage.getItem(BOT_RUN_COUNT_KEY) ?? '0', 10) || 0; }
  catch { return 0; }
}
function saveRunCount(n: number): void {
  if (_isBotFarmMode) return; // BETA 13.85: don't clobber shared counter from racing iframes
  try { localStorage.setItem(BOT_RUN_COUNT_KEY, String(n)); } catch { /* skip */ }
}

function saveRunsToStorage(runs: RunLog[]): void {
  if (_isBotFarmMode) return; // BETA 13.85: skip — keep in-memory only
  try {
    const toSave = runs.slice(-MAX_STORED_RUNS);
    localStorage.setItem(BOT_RUNS_KEY, JSON.stringify(toSave));
  } catch { /* storage full — skip silently */ }
}

function loadRunsFromStorage(): RunLog[] {
  if (_isBotFarmMode) return []; // BETA 13.85: each iframe starts with empty in-memory bot.allRuns
  try {
    const raw = localStorage.getItem(BOT_RUNS_KEY);
    return raw ? (JSON.parse(raw) as RunLog[]) : [];
  } catch { return []; }
}

export function clearStoredRuns(): void {
  localStorage.removeItem(BOT_RUNS_KEY);
  localStorage.removeItem(BOT_RUN_COUNT_KEY);
  if (bot) { bot.allRuns = []; bot.runCount = 0; }
}

/**
 * createDefaultBrain — Pre-seeded with knowledge from 7 observed bot runs.
 *
 * Run data (BETA 12.6 / 7 runs, all floor 1):
 *   Avg survival ~120s | Avg level 6.9 | Avg kills 100 | Avg gold 923
 *   All deaths: enemy:zombie (never reached floor 2 or bosses)
 *   Key insight: bot collected good gold but rarely spent it on chests.
 *
 * cardScores are pre-seeded with the known roguelite meta so the bot starts
 * making smart picks immediately rather than learning from scratch.
 * Values represent (total survival seconds accumulated, pick count) —
 * higher ratio = keyword correlates with surviving longer.
 */
function createDefaultBrain(): BotBrain {
  return {
    fleeThreshold: 0.22,        // slightly higher than default — zombies burst quickly
    healSeekThreshold: 0.55,    // seek healing earlier (died to zombie swarms at ~35% HP)
    kiteAggression: 0.65,       // slightly more kite-heavy vs stand-and-fight
    avoidBossShrines: false,
    preferDefense: false,
    preferOffense: true,        // offense is the best defense at floor 1
    // ── Pre-seeded card keyword scores ──────────────────────────────
    // Tuned for zombie-heavy floor 1: burst damage > sustained > defense > utility
    cardScores: {
      'damage':       { total: 1800, count: 12 }, // #1 priority — more damage = faster kills
      'fire rate':    { total: 1600, count: 11 }, // #2 — more shots = more kills = more xp
      'crit':         { total: 1500, count: 10 }, // burst spikes kill packs instantly
      'reload':       { total: 1400, count: 10 }, // less downtime = more dps
      'health':       { total: 1300, count: 9  }, // #5 — survive longer
      'armor':        { total: 1100, count: 8  }, // reduce incoming damage
      'regen':        { total: 1000, count: 7  }, // passive sustain
      'speed':        { total: 900,  count: 7  }, // kiting and loot collection
      'xp':           { total: 700,  count: 6  }, // more xp = more card picks
      'gold':         { total: 600,  count: 6  }, // more gold = more chests
      'magnet':       { total: 650,  count: 5  }, // auto-collect XP/gold = big value
      'evasion':      { total: 500,  count: 5  }, // dodge incoming hits
      'defense':      { total: 750,  count: 6  }, // general defense
      'attack speed': { total: 1500, count: 11 }, // alias for fire rate
      'atk spd':      { total: 1400, count: 10 }, // alias
      'lifesteal':    { total: 800,  count: 5  }, // healing through offense
    },
    recentSurvivalTimes: [153, 78, 158, 106, 115, 103, 130], // seeded from 7 real runs
    recentDeathCauses: ['enemy:zombie', 'enemy:zombie', 'enemy:zombie', 'enemy:zombie', 'enemy:zombie', 'enemy:zombie', 'enemy:zombie'],
    recentHpAtDeath: [0.35, 0.28, 0.30, 0.22, 0.40, 0.25, 0.33],
    totalRunsLearned: 7,        // bot "knows" it has 7 runs of experience
    bestSurvivalSec: 158,       // from the 7 runs
    avgSurvivalSec: 120.5,      // average of 7 runs
    lastAdaptedAt: Date.now(),
    createdAt: Date.now(),
  };
}

function loadBrain(): BotBrain {
  try {
    const raw = localStorage.getItem(BRAIN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<BotBrain>;
      return { ...createDefaultBrain(), ...parsed };
    }
  } catch { /* ignore */ }
  return createDefaultBrain();
}

// BETA 17.22 — PSBOT cycle-30ai B3: fetch aggregate stats from Supabase
// and blend into local brain. Each bot_run row contains finalDamagePct,
// hpAtDeath, survivalTimeSec — we average across recent rows to get
// "global optimum" thresholds. Blended at 40% weight into local brain
// so new bots benefit from cross-device experience without overriding
// learned local preferences. Fire-and-forget; failures are silent.
async function _fetchAndBlendGlobalBrain(brain: BotBrain): Promise<void> {
  // Skip if recently fetched (cache for 1 hour)
  if (brain.globalBlend && (Date.now() - brain.globalBlend.fetchedAt) < 3_600_000) return;
  try {
    // BETA 17.32 — PSBOT cycle-30aq found this query was 400-ing since
    // 17.22 because: (1) it asked for `select=metadata` but that column
    // doesn't exist in `bug_reports` (postgres error 42703), and
    // (2) bot_run telemetry is actually stored as a JSON STRING in the
    // `description` column. Diagnosed via Chrome DevTools MCP smoke test.
    // The bot has been silently falling back to local-only brain this
    // whole time. Fix: select `description`, parse JSON per row, pull
    // hpAtDeath / survivalTimeSec from the parsed object.
    const url = 'https://wslppieqghdyaodhbrbl.supabase.co/rest/v1/bug_reports' +
      '?select=description' +
      '&category=eq.bot_run' +
      '&order=created_at.desc&limit=200' +
      '&apikey=sb_publishable_7uHojLtW1sWE9risVX2CcA_Tmqk6sgY';
    const resp = await fetch(url);
    if (!resp.ok) return;
    const rows = await resp.json() as Array<{ description?: string }>;
    if (!Array.isArray(rows) || rows.length < 5) return; // not enough data to blend
    // Aggregate: average hpAtDeath% (proxy for fleeThreshold) + median survival
    let hpSum = 0, hpCount = 0, survivals: number[] = [];
    for (const row of rows) {
      if (!row.description) continue;
      let md: { hpAtDeath?: number; survivalTimeSec?: number } | null = null;
      try { md = JSON.parse(row.description); } catch { continue; }
      if (!md) continue;
      if (typeof md.hpAtDeath === 'number' && md.hpAtDeath > 0 && md.hpAtDeath < 100) {
        hpSum += md.hpAtDeath; hpCount++;
      }
      if (typeof md.survivalTimeSec === 'number' && md.survivalTimeSec > 5 && md.survivalTimeSec < 1200) {
        survivals.push(md.survivalTimeSec);
      }
    }
    if (hpCount < 5) return;
    const avgHpDeath = hpSum / hpCount / 100; // 0-1
    survivals.sort((a, b) => a - b);
    const medianSurvival = survivals[Math.floor(survivals.length / 2)] ?? 60;
    // Derive global thresholds: if bots die at 25% HP on average, the
    // optimal flee threshold is probably 35% (panic before that lethal
    // hit). Heal-seek slightly above flee. Kite aggression scales inversely
    // with survival — better runs justify more strafe.
    const globalFlee = Math.min(0.50, Math.max(0.15, avgHpDeath + 0.10));
    const globalHeal = Math.min(0.75, Math.max(0.35, avgHpDeath + 0.35));
    const globalKite = medianSurvival > 150 ? 0.75 : 0.55;
    brain.globalBlend = {
      fleeThreshold: globalFlee,
      healSeekThreshold: globalHeal,
      kiteAggression: globalKite,
      fetchedAt: Date.now(),
      sampleSize: rows.length,
    };
    // Apply 40% blend if local brain has < 10 runs (kickstart). Once local
    // brain has plenty of samples, prefer local.
    if (brain.totalRunsLearned < 10) {
      // BETA 20.68 — BRAIN v2: 60/40 → 85/15. The old blend dragged 40%
      // toward the fleet average on EVERY boot, washing out everything this
      // machine had learned (a major reason the bot "never learned").
      brain.fleeThreshold     = brain.fleeThreshold     * 0.85 + globalFlee * 0.15;
      brain.healSeekThreshold = brain.healSeekThreshold * 0.85 + globalHeal * 0.15;
      brain.kiteAggression    = brain.kiteAggression    * 0.85 + globalKite * 0.15;
    }
    saveBrain(brain);
    log(`[BRAIN] Global blend from Supabase: ${rows.length} rows, avg hpDeath=${(avgHpDeath*100).toFixed(0)}%, medianSurvival=${medianSurvival.toFixed(0)}s`);
  } catch (_err) {
    // Network / parse error — silent fail. Bot works on local brain only.
  }
}

// BETA 17.22 — PSBOT cycle-30ai B4: apply per-character brain overrides
// to the active brain when starting a run with a specific character.
// Only applies if perCharacter[id] has 5+ runs (statistical confidence).
// Called from finalizeRun's resetForNextRun path AND from initBot when
// character is set externally.
function _applyPerCharacterBrain(characterId: string | undefined): void {
  if (!characterId || !brain.perCharacter) return;
  const pc = brain.perCharacter[characterId];
  if (!pc || pc.totalRuns < 5) return;
  brain.fleeThreshold = pc.fleeThreshold;
  brain.healSeekThreshold = pc.healSeekThreshold;
  brain.kiteAggression = pc.kiteAggression;
  log(`[BRAIN] Applied ${characterId}-specific brain (${pc.totalRuns} runs, avg ${pc.avgSurvivalSec.toFixed(0)}s)`);
}

function saveBrain(brain: BotBrain): void {
  try {
    localStorage.setItem(BRAIN_STORAGE_KEY, JSON.stringify(brain));
  } catch { /* storage full or unavailable */ }
}

/** Learn from a completed run and adapt brain parameters. */
function learnFromRun(run: RunLog, brain: BotBrain): void {
  // ── Ghost run filter — skip runs with impossible or useless data ──
  if (run.survivalTimeSec < 5) {
    log(`[BRAIN] Skipping micro run #${run.runNumber} (only ${run.survivalTimeSec.toFixed(1)}s)`);
    return;
  }
  if (run.survivalTimeSec > 500 && run.killCount < 20) {
    log(`[BRAIN] Skipping ghost run #${run.runNumber} (${run.survivalTimeSec.toFixed(0)}s but only ${run.killCount} kills — likely paused/backgrounded)`);
    return;
  }

  brain.totalRunsLearned++;
  const t = run.survivalTimeSec;
  const hpPct = run.hpAtDeath / 100;

  // ── Update rolling history ──
  brain.recentSurvivalTimes.push(t);
  brain.recentDeathCauses.push(run.deathCause);
  brain.recentHpAtDeath.push(hpPct);
  if (brain.recentSurvivalTimes.length > BRAIN_WINDOW) {
    brain.recentSurvivalTimes.shift();
    brain.recentDeathCauses.shift();
    brain.recentHpAtDeath.shift();
  }

  // ── Update best/avg ──
  if (t > brain.bestSurvivalSec) brain.bestSurvivalSec = t;
  brain.avgSurvivalSec = brain.recentSurvivalTimes.reduce((a, b) => a + b, 0) / brain.recentSurvivalTimes.length;

  // ── Card keyword scoring: credit all categories that appeared in chosen upgrades ──
  for (const upgradeId of run.upgradesPicked) {
    const keywords = extractKeywords(upgradeId);
    for (const kw of keywords) {
      if (!brain.cardScores[kw]) brain.cardScores[kw] = { total: 0, count: 0 };
      brain.cardScores[kw].total += t;
      brain.cardScores[kw].count++;
    }
  }

  // ── Adaptive parameter adjustment ──
  brain.lastAdaptedAt = brain.totalRunsLearned;

  const diedFast = t < 60;
  const diedMedium = t >= 60 && t < 150;
  const survivedWell = t >= 150;
  const survivedGreat = t >= 300;
  const diedToBoss = run.deathCause.includes('boss'); // E1 — dead caseo/timmy substrings deleted (deathCause can't contain them)
  const diedAtHighHp = hpPct > 0.3;    // something one-shot or burst the bot
  const diedAtLowHp = hpPct < 0.1;     // died correctly, just wasn't strong enough

  if (diedFast) {
    // Died very quickly → defensive emergency
    brain.fleeThreshold = Math.min(0.45, brain.fleeThreshold + 0.05);
    brain.healSeekThreshold = Math.min(0.75, brain.healSeekThreshold + 0.05);
    brain.kiteAggression = Math.max(0.25, brain.kiteAggression - 0.08);
    brain.preferDefense = true;
    brain.preferOffense = false;
  } else if (diedMedium || survivedWell) {
    // BETA 20.68 — BRAIN v2: this band (where MOST deaths land) was an
    // UNCONDITIONAL defensive nudge — and 150-300s had no branch at all —
    // so the brain ratcheted cowardly forever and looked like it "never
    // learned". Now RELATIVE to the bot's own rolling average: beat your
    // average → lean aggressive; underperform it → lean defensive.
    if (t > brain.avgSurvivalSec * 1.15) {
      brain.kiteAggression = Math.min(0.9, brain.kiteAggression + 0.03);
      brain.fleeThreshold = Math.max(0.12, brain.fleeThreshold - 0.01);
    } else if (t < brain.avgSurvivalSec * 0.85) {
      brain.fleeThreshold = Math.min(0.35, brain.fleeThreshold + 0.02);
      brain.healSeekThreshold = Math.min(0.65, brain.healSeekThreshold + 0.02);
      brain.kiteAggression = Math.max(0.35, brain.kiteAggression - 0.03);
    }
  } else if (survivedGreat) {
    // Survived well → allow more aggression
    brain.fleeThreshold = Math.max(0.12, brain.fleeThreshold - 0.03);
    brain.healSeekThreshold = Math.max(0.30, brain.healSeekThreshold - 0.03);
    brain.kiteAggression = Math.min(0.90, brain.kiteAggression + 0.05);
    brain.preferDefense = false;
    brain.preferOffense = true;
  } else if (survivedWell) {
    // Survived decently → small offensive nudge
    brain.fleeThreshold = Math.max(0.15, brain.fleeThreshold - 0.01);
    brain.healSeekThreshold = Math.max(0.40, brain.healSeekThreshold - 0.01);
    brain.kiteAggression = Math.min(0.80, brain.kiteAggression + 0.02);
  }

  // Boss-specific learning
  if (diedToBoss) {
    brain.kiteAggression = Math.min(0.90, brain.kiteAggression + 0.05);
    brain.fleeThreshold = Math.min(0.40, brain.fleeThreshold + 0.03);
  }

  // High HP at death = something burst us, we weren't fleeing early enough
  if (diedAtHighHp && !run.deathCause.includes('won')) {
    brain.fleeThreshold = Math.min(0.45, brain.fleeThreshold + 0.04);
  }

  // Low HP at death = we were defending correctly, problem is damage output
  if (diedAtLowHp && !diedFast) {
    brain.preferOffense = true;
  }

  // Trend: last 3 all died fast → emergency defensive reset
  if (brain.recentSurvivalTimes.length >= 3) {
    const last3 = brain.recentSurvivalTimes.slice(-3);
    if (last3.every(x => x < 60)) {
      log('[BRAIN] 3 fast deaths in a row — defensive emergency reset');
      brain.fleeThreshold = 0.40;
      brain.healSeekThreshold = 0.70;
      brain.kiteAggression = 0.25;
      brain.preferDefense = true;
      brain.preferOffense = false;
    }
    // Trend: last 3 all good → try going full offense
    if (last3.every(x => x >= 200)) {
      log('[BRAIN] 3 good runs in a row — switching to offense mode');
      brain.preferOffense = true;
      brain.preferDefense = false;
    }
  }

  // Boss shrine avoidance: if a boss shrine was activated and we died within 90s
  if (run.shrinesActivated > 0 && t < 90) {
    brain.avoidBossShrines = true;
    log('[BRAIN] Boss shrine activated + died quickly — avoiding boss shrines');
  }
  // Clear avoidance after a successful run
  if (survivedGreat) {
    brain.avoidBossShrines = false;
  }

  // BETA 17.22 — PSBOT cycle-30ai B4: per-character brain tracking.
  // Write character-specific copies of the adapted params + history so
  // each character converges to its own optimum independently. Read path
  // in initBot blends per-character brain INTO the active brain when
  // running that character (≥5 runs sample size required).
  if (run.characterId) {
    if (!brain.perCharacter) brain.perCharacter = {};
    const cid = run.characterId;
    const pc = brain.perCharacter[cid] ?? {
      fleeThreshold: brain.fleeThreshold,
      healSeekThreshold: brain.healSeekThreshold,
      kiteAggression: brain.kiteAggression,
      cardScores: {},
      recentSurvivalTimes: [],
      totalRuns: 0,
      bestSurvivalSec: 0,
      avgSurvivalSec: 0,
    };
    pc.fleeThreshold = brain.fleeThreshold;
    pc.healSeekThreshold = brain.healSeekThreshold;
    pc.kiteAggression = brain.kiteAggression;
    pc.totalRuns++;
    pc.recentSurvivalTimes.push(t);
    if (pc.recentSurvivalTimes.length > BRAIN_WINDOW) pc.recentSurvivalTimes.shift();
    pc.avgSurvivalSec = pc.recentSurvivalTimes.reduce((a, b) => a + b, 0) / pc.recentSurvivalTimes.length;
    if (t > pc.bestSurvivalSec) pc.bestSurvivalSec = t;
    for (const upgradeId of run.upgradesPicked) {
      const keywords = extractKeywords(upgradeId);
      for (const kw of keywords) {
        if (!pc.cardScores[kw]) pc.cardScores[kw] = { total: 0, count: 0 };
        pc.cardScores[kw].total += t;
        pc.cardScores[kw].count++;
      }
    }
    brain.perCharacter[cid] = pc;
  }

  log(`[BRAIN] Run #${brain.totalRunsLearned} learned — avg ${brain.avgSurvivalSec.toFixed(0)}s | flee=${brain.fleeThreshold.toFixed(2)} heal=${brain.healSeekThreshold.toFixed(2)} kite=${brain.kiteAggression.toFixed(2)}`);
  saveBrain(brain);

  // ── Strategy memory: item and weapon correlation learning ──
  learnItemCorrelation(run, memory);
}

/** Extract searchable keyword categories from an upgrade/item name or id. */
function extractKeywords(nameOrId: string): string[] {
  const n = nameOrId.toLowerCase();
  const kws: string[] = [];
  // BETA 17.25 — PSBOT cycle-30ak flaw #23: 5 coarse buckets meant brain
  // couldn't distinguish 'lifesteal' from 'raw damage' from 'crit'. Now
  // 15+ specific buckets. Each pick contributes to one or more — brain
  // learns which fine-grained mix correlates with longer runs.
  // Offensive sub-types
  if (n.includes('crit')) kws.push('crit');
  if (n.includes('lifesteal') || n.includes('life steal')) kws.push('lifesteal');
  if (n.includes('attack speed') || n.includes('fire rate') || n.includes('reload') || n.includes('atk spd')) kws.push('atkspeed');
  if (n.includes('pierce') || n.includes('penetration')) kws.push('pierce');
  if (n.includes('bounce') || n.includes('ricochet')) kws.push('bounce');
  if (n.includes('projectile') || n.includes('bullet') || n.includes('shot')) kws.push('projectile');
  if (n.includes('damage') || n.includes('dmg') || n.includes('attack') && !n.includes('attack speed')) kws.push('damage');
  if (n.includes('area') || n.includes('aoe') || n.includes('size') || n.includes('radius')) kws.push('area');
  if (n.includes('elite') || n.includes('boss')) kws.push('elite');
  // Defensive sub-types
  if (n.includes('health') || n.includes('hp') || n.includes('heal') || n.includes('regen')) kws.push('health');
  if (n.includes('armor')) kws.push('armor');
  if (n.includes('shield')) kws.push('shield');
  if (n.includes('dodge') || n.includes('evasion')) kws.push('evasion');
  if (n.includes('thorns') || n.includes('reflect')) kws.push('thorns');
  if (n.includes('damage reduction') || n.includes('dr ')) kws.push('damreduction');
  // Utility sub-types
  if (n.includes('move speed') || n.includes('movement')) kws.push('movespeed');
  if (n.includes('luck')) kws.push('luck');
  if (n.includes('gold')) kws.push('gold');
  if (n.includes('xp') || n.includes('experience')) kws.push('xp');
  if (n.includes('magnet') || n.includes('pickup')) kws.push('magnet');
  if (n.includes('phase')) kws.push('phase');
  if (kws.length === 0) kws.push('other');
  return kws;
}

/** Get the brain's learned score multiplier for a keyword (1.0 = neutral). */
function brainKeywordMultiplier(brain: BotBrain, keyword: string): number {
  const entry = brain.cardScores[keyword];
  if (!entry || entry.count < 2) return 1.0;
  const keywordAvg = entry.total / entry.count;
  // Compare to overall avg survival; ratio > 1 means this keyword correlated with longer runs
  const overallAvg = brain.avgSurvivalSec > 0 ? brain.avgSurvivalSec : 120;
  return Math.max(0.5, Math.min(2.5, keywordAvg / overallAvg));
}

/** Export brain state for external inspection. */
export function getBotBrain(): BotBrain | null {
  return brain;
}

/** Reset the brain back to defaults. */
export function resetBotBrain(): void {
  brain = createDefaultBrain();
  saveBrain(brain);
  log('[BRAIN] Brain reset to defaults');
}

// ─── Module-level brain singleton (persists across runs) ────────────
let brain: BotBrain = loadBrain();

// ═══════════════════════════════════════════════════════════════════════
// STRATEGY MEMORY — load / save / reset / learn
// ═══════════════════════════════════════════════════════════════════════

function createDefaultStrategyMemory(): StrategyMemory {
  return {
    itemStats: {},
    weaponStats: {},
    totalRunsSampled: 0,
    avgSurvivalBaseline: 120, // seeded from 7 observed runs avg ~120s
  };
}

function loadStrategyMemory(): StrategyMemory {
  try {
    const raw = localStorage.getItem(MEMORY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StrategyMemory>;
      return {
        ...createDefaultStrategyMemory(),
        ...parsed,
        itemStats:   parsed.itemStats   ?? {},
        weaponStats: parsed.weaponStats ?? {},
      };
    }
  } catch { /* ignore */ }
  return createDefaultStrategyMemory();
}

function saveStrategyMemory(mem: StrategyMemory): void {
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify(mem));
  } catch { /* storage full or unavailable */ }
}

/** Reset strategy memory back to blank (preserves brain). */
export function resetStrategyMemory(): void {
  memory = createDefaultStrategyMemory();
  saveStrategyMemory(memory);
  log('[MEMORY] Strategy memory reset to defaults');
}

/** Export memory state for external inspection. */
export function getStrategyMemory(): StrategyMemory | null {
  return memory;
}

/**
 * Learn item and weapon correlations from a completed run.
 * Called at the end of learnFromRun().
 */
function learnItemCorrelation(run: RunLog, mem: StrategyMemory): void {
  // ── Ghost run filter — same criteria as learnFromRun ──
  if (run.survivalTimeSec < 5) return;
  if (run.survivalTimeSec > 500 && run.killCount < 20) return;

  const t = run.survivalTimeSec;

  // ── Update baseline (exponential moving average, α = 0.15) ──
  mem.avgSurvivalBaseline = mem.avgSurvivalBaseline * 0.85 + t * 0.15;
  mem.totalRunsSampled++;

  const isGoodRun = t >= mem.avgSurvivalBaseline;

  // ── Per-item correlation ──
  for (const itemName of run.itemsCollected) {
    const key = itemName.toLowerCase().trim();
    if (!key) continue;
    if (!mem.itemStats[key]) {
      mem.itemStats[key] = { goodRuns: 0, badRuns: 0, totalSurvivalSec: 0, pickCount: 0 };
    }
    const entry = mem.itemStats[key];
    entry.totalSurvivalSec += t;
    entry.pickCount++;
    if (isGoodRun) entry.goodRuns++; else entry.badRuns++;
  }

  // ── Per-weapon correlation ──
  for (const weaponId of run.weaponsUsed) {
    const key = weaponId.toLowerCase().trim();
    if (!key) continue;
    if (!mem.weaponStats[key]) {
      mem.weaponStats[key] = { totalSurvivalSec: 0, pickCount: 0, wins: 0 };
    }
    const entry = mem.weaponStats[key];
    entry.totalSurvivalSec += t;
    entry.pickCount++;
    if (run.deathCause === 'won') entry.wins++;
  }

  saveStrategyMemory(mem);
  log(`[MEMORY] Sampled run #${mem.totalRunsSampled} (${isGoodRun ? 'GOOD' : 'bad'}, ${t.toFixed(0)}s) — baseline ${mem.avgSurvivalBaseline.toFixed(0)}s`);
}

/**
 * Returns a memory-derived score bonus for a card name.
 * Checks both itemStats (good/bad run correlation) and weaponStats (avg survival).
 * Positive = correlates with longer runs.  Negative = correlates with shorter.
 * Zero = insufficient data (< 3 picks).
 */
function memoryScoreBonus(name: string = ''): number {
  if (!memory || !name) return 0;
  const key = name.toLowerCase().trim();

  // ── Item stats: good/bad run win-rate ──
  const itemEntry = memory.itemStats[key];
  if (itemEntry && itemEntry.pickCount >= 3) {
    const total = itemEntry.goodRuns + itemEntry.badRuns;
    if (total > 0) {
      const winRate = itemEntry.goodRuns / total;
      return (winRate - 0.5) * 12; // range: −6 to +6
    }
  }

  // ── Weapon stats: avg survival vs baseline ──
  const weaponEntry = memory.weaponStats[key];
  if (weaponEntry && weaponEntry.pickCount >= 3) {
    const avgSurvival = weaponEntry.totalSurvivalSec / weaponEntry.pickCount;
    const baseline = memory.avgSurvivalBaseline || 120;
    // +6 if this weapon's avg is 2× baseline; −6 if it's 0.5× baseline
    return Math.max(-6, Math.min(6, ((avgSurvival / baseline) - 1) * 6));
  }

  return 0;
}

// ─── Module-level memory singleton ──────────────────────────────────
let memory: StrategyMemory = loadStrategyMemory();

// ─── Pre-run Nav Graph ───────────────────────────────────────────────────────
// Built once when a run starts. Nodes = room centers + gap centers.
// Edges connect room centers to their gap nodes, and gap nodes to the
// adjacent room on the other side. Bot uses BFS to find waypoint paths
// through actual doorways rather than probing blindly into walls.

interface NavNode { id: string; x: number; y: number; }
interface NavGraph {
  nodes: Map<string, NavNode>;
  adj:   Map<string, string[]>;
}

function buildNavGraph(state: GameState): NavGraph {
  const rooms = state.map.roomMap?.rooms ?? [];
  const RS    = state.map.ROOM_SIZE ?? 768;
  const nodes = new Map<string, NavNode>();
  const adj   = new Map<string, string[]>();

  const addNode = (n: NavNode) => { nodes.set(n.id, n); adj.set(n.id, []); };
  const addEdge = (a: string, b: string) => {
    if (!adj.get(a)?.includes(b)) adj.get(a)?.push(b);
    if (!adj.get(b)?.includes(a)) adj.get(b)?.push(a);
  };

  // Build a quick room lookup by grid position
  const roomByPos = new Map<string, string>(); // "gx,gy" → room.id
  for (const room of rooms) {
    const gx = Math.round(room.x / RS);
    const gy = Math.round(room.y / RS);
    roomByPos.set(`${gx},${gy}`, room.id);
    addNode({ id: `r_${room.id}`, x: room.x + RS / 2, y: room.y + RS / 2 });
  }

  // Add gap nodes and wire them up
  for (const room of rooms) {
    const rNodeId = `r_${room.id}`;
    const gx0 = Math.round(room.x / RS);
    const gy0 = Math.round(room.y / RS);

    for (let gi = 0; gi < (room.gaps ?? []).length; gi++) {
      const gap = room.gaps[gi];
      const cx  = gap.x + gap.w / 2;
      const cy  = gap.y + gap.h / 2;
      const gNodeId = `g_${room.id}_${gi}`;
      addNode({ id: gNodeId, x: cx, y: cy });
      addEdge(rNodeId, gNodeId);

      // Find the room on the other side based on gap direction
      const dir: string = (gap as any).dir ?? '';
      const neighbour =
        dir === 'N' ? roomByPos.get(`${gx0},${gy0-1}`) :
        dir === 'S' ? roomByPos.get(`${gx0},${gy0+1}`) :
        dir === 'E' ? roomByPos.get(`${gx0+1},${gy0}`) :
        dir === 'W' ? roomByPos.get(`${gx0-1},${gy0}`) : undefined;
      if (neighbour) addEdge(gNodeId, `r_${neighbour}`);
    }
  }

  return { nodes, adj };
}

function findNavPath(
  graph: NavGraph,
  fromX: number, fromY: number,
  toX:   number, toY:   number,
): { x: number; y: number }[] {
  if (!graph.nodes.size) return [{ x: toX, y: toY }];

  // Nearest node to start and end
  let fromId = '', toId = '', fd = Infinity, td = Infinity;
  for (const [id, n] of Array.from(graph.nodes)) {
    const d1 = Math.hypot(n.x - fromX, n.y - fromY);
    const d2 = Math.hypot(n.x - toX,   n.y - toY);
    if (d1 < fd) { fd = d1; fromId = id; }
    if (d2 < td) { td = d2; toId   = id; }
  }
  if (!fromId || !toId || fromId === toId) return [{ x: toX, y: toY }];

  // BFS
  const visited = new Set<string>([fromId]);
  const parent  = new Map<string, string>();
  const queue   = [fromId];
  while (queue.length) {
    const cur = queue.shift()!;
    if (cur === toId) break;
    for (const nb of (graph.adj.get(cur) ?? [])) {
      if (!visited.has(nb)) { visited.add(nb); parent.set(nb, cur); queue.push(nb); }
    }
  }

  // Reconstruct
  if (!parent.has(toId)) return [{ x: toX, y: toY }];
  const path: { x: number; y: number }[] = [];
  let cur = toId;
  while (cur !== fromId) {
    const n = graph.nodes.get(cur)!;
    path.unshift({ x: n.x, y: n.y });
    cur = parent.get(cur)!;
  }
  path.push({ x: toX, y: toY });
  return path;
}

// ─── Bot Brain State ────────────────────────────────────────────────
interface BotState {
  config: BotConfig;
  currentRun: RunLog | null;
  allRuns: RunLog[];
  runCount: number;
  // Decision state
  targetPos: { x: number; y: number } | null;
  currentGoal: 'explore' | 'kite' | 'flee' | 'seek_chest' | 'seek_shrine' | 'seek_health' | 'seek_portal' | 'seek_loot' | 'seek_chick_magnet' | 'seek_barrel' | 'engage_melee';
  goalTimer: number;
  goalLockTimer: number;        // BETA 13.49 (PS Major Pattern 6): prevent goal thrashing — locks current goal for 300ms after a switch
  // BETA 19.45 — Reactive-flee timer. When > state.time, the bot's goal is
  // forced to 'flee' regardless of normal selection. Set when the bot takes
  // damage so it bails out of engage_melee / seek_chest / etc. on the FIRST
  // hit instead of staying committed and eating a 3-4 hit cascade. Live
  // evidence (Sponge BETA 19.44 death cascade): 5 hits in 6s at descending
  // distance because bot kept engaging after the first 32-damage hit.
  reactiveFleeUntil: number;
  lastHp: number;
  lastEnemyCount: number;
  lastDamageFrame: number;
  damageDealtAccum: number;
  damageTakenAccum: number;
  healingAccum: number;
  killsByType: Record<string, number>;
  roomsVisitedSet: Set<string>;
  upgradesPicked: string[];
  upgradesSkipped: string[];
  choiceLog: ChoiceRecord[];
  itemsCollected: string[];
  weaponsUsed: Set<string>;
  // Anomaly tracking
  lastPhase: string;
  stuckTimer: number;
  lastPos: { x: number; y: number };
  /** BETA 15.57 — anti-camp tracking. Per Dylan: "bots are just chilling
   *  standing still." Camping at a chokepoint with static AOE (chad aura,
   *  sponge absorb) is mathematically optimal but looks broken. This timer
   *  accumulates when the bot moves <30wu/s sustained; at 15s, force a
   *  short explore-burst to break the camp. Resets when bot moves normally. */
  campTimer: number;
  /** BETA 15.57 — when > 0, bot is in forced-explore mode. Decremented per
   *  frame; while active, currentGoal locks to 'explore' regardless of camp-
   *  enticing conditions. Duration 4s — enough to walk 800wu and find new
   *  ground; not so long that it ruins long-range builds (RPK can keep firing). */
  forceExploreTimer: number;
  /** BETA 15.57 — total wu moved over a 1-second sliding window. Used as the
   *  "are we camping?" metric (averaged) instead of per-frame velocity which
   *  spikes/drops too quickly. */
  recentMoveDist: number;
  recentMoveTime: number;
  /** BETA 17.57 — snapshot of player position at the START of the current
   *  sliding window. At window end, compute NET displacement vs this anchor
   *  to detect corner-flicker (oscillating between two positions = high
   *  total distance but ~0 net displacement). Dylan: "the corner of the
   *  rooms. it will just go in and sit going back and forth super fast
   *  like flickering for like 5 - 10 seconds. so much time wasted." */
  windowStartPos: { x: number; y: number } | null;
  stuckCount: number;         // how many consecutive stuck events at this spot
  stuckOrigin: { x: number; y: number } | null; // where the streak started
  // BETA 13.56: drift-circle b-line failsafe (replaces 13.55 stuck-escape)
  driftCenter: { x: number; y: number } | null;  // anchor that follows the bot when it moves > DRIFT_RADIUS
  driftStartTime: number;     // runTime (sec) the bot has been near driftCenter since
  blineUntil: number;         // runTime (sec) the current b-line ends — 0 = not b-lining
  blineTarget: { x: number; y: number } | null; // world coord the b-line is heading toward
  // BETA 13.59: invisible-behavior trackers (per user spec — if it's invisible, count it)
  blineTriggerCount: number;       // total b-line activations this run
  trailTeleportCount: number;      // total trail-enemy teleports this run
  namedEliteFleeCount: number;     // total Timmy/CaseOh awareness triggers this run
  // BETA 13.80 (main-dev side): room-time enforcement. User direction: "bot needs to
  // be forced to escape a room to the mainland if they are inside a room for more than
  // 10 seconds and continue and do not run back into that same room for at least 30
  // seconds."
  /** Room id the bot is currently inside, or null when in the corridor "mainland". */
  currentRoomId: string | null;
  /** Run-time (sec) when the bot entered currentRoomId. Reset every room change. */
  currentRoomEnteredAt: number;
  /** Map<roomId, runTime when cooldown ends>. If a room is in this map and now < value, the bot avoids it. */
  roomCooldowns: Map<string, number>;
  /** True while the bot is being force-escaped (overrides goal targeting). Cleared once the bot exits the room. */
  forceEscapeRoomId: string | null;
  /** Counter for the CSV — how many times the room-time limit kicked in this run. */
  roomEscapeCount: number;
  // BETA 13.80 (bot-training side): pathfinding Fix 1 wire-up + hitbox near-miss tracker
  trapCellsMarkedCount: number;    // times we called flowField.markCellAsTrap this run
  // BETA 16.27 — PSBOT cycle-30 D.2: anti-tunnel pick counter. Tracks how
  // many times each name-keyword bucket has been picked in this run so
  // bot scoring can deprioritize over-picked categories.
  statCategoryPickCount?: Record<string, number>;
  lastTrapCellKey: string | null;  // dedupe — don't spam markCellAsTrap on the same cell every frame
  // BETA 14.82 — flow-field deadlock escape (PSBOT RUN cycle-11 chad fix)
  deadlockEscapeUntil: number;       // game-time when escape mode ends (0 = not in escape)
  deadlockEscapeTarget: { x: number; y: number } | null;  // distant random target
  deadlockEscapeStartPos: { x: number; y: number } | null; // where we entered escape (so we can detect we've moved enough)
  enemyNearMissFrameCount: number; // frames where an enemy was hugging player but no damage applied
  weaponNearMissCount: number;     // projectiles that came close to enemies but never hit one
  _projTracking: Map<number, { wasClose: boolean; hitAny: boolean }>; // per-projectile lifetime state
  // BETA 13.82: item coverage tracker — counts picks of items never picked before
  noveltyPickCount: number;
  // Pre-run nav graph
  navGraph: NavGraph | null;
  waypointPath: { x: number; y: number }[];
  waypointIdx: number;
  frameCount: number;
  // Restart handling
  restartTimer: number;
  // Phase lock — prevents scheduling multiple timeouts for the same screen
  phaseHandledThisEntry: boolean;
}

// ─── Singleton ──────────────────────────────────────────────────────
let bot: BotState | null = null;

// BETA 17.49 — Dylan: "bot keeps standing still not on shrines. that
// will kill you. just wasting seconds." Need to diagnose every stop
// frame. This exposes the live bot state via a window hook so Chrome
// MCP can sample currentGoal / recentMoveDist / position / forceExplore
// at high frequency and identify exactly which goal is producing a
// zero-magnitude move vector.
export function getBotDebug(): {
  currentGoal: string;
  goalLockTimer: number;
  recentMoveDist: number;
  forceExploreTimer: number;
  currentRoomId: string | null;
  pos: { x: number; y: number };
  lastPos: { x: number; y: number };
  forceEscapeRoomId: string | null;
  blineUntil: number;
  phaseHandledThisEntry: boolean;
  buildType: string;
  // BETA 17.59 — map-context telemetry. Dylan: "track all movement of the
  // bot and in relevance to where it is in the map near shrines, rooms,
  // doors, corners of rooms, chest, chick magnets." Each frame, sample
  // distance to nearest of each type so the diagnosis layer can see WHERE
  // the bot is failing (next to chest? next to wall corner? in horde?).
  nearestShrine: { x: number; y: number; d: number } | null;
  nearestChestPaid: { x: number; y: number; d: number; cost: number } | null;
  nearestChestFree: { x: number; y: number; d: number } | null;
  nearestBarrel: { x: number; y: number; d: number } | null;
  nearestEnemyDist: number;        // -1 if no enemies
  enemiesIn200: number;
  enemiesIn400: number;
  hordeAhead: boolean;             // true if 5+ enemies within 300wu in the direction of motion
} | null {
  if (!bot) return null;
  return {
    currentGoal: bot.currentGoal,
    goalLockTimer: bot.goalLockTimer,
    recentMoveDist: bot.recentMoveDist,
    forceExploreTimer: bot.forceExploreTimer,
    currentRoomId: bot.currentRoomId,
    pos: { x: 0, y: 0 },  // filled by caller via __getPlayer
    lastPos: { ...bot.lastPos },
    forceEscapeRoomId: bot.forceEscapeRoomId ?? null,
    blineUntil: (bot as unknown as { blineUntil?: number }).blineUntil ?? 0,
    phaseHandledThisEntry: bot.phaseHandledThisEntry ?? false,
    buildType: bot.config?.buildType ?? 'unknown',
    // map-context fields filled by the caller via getMapContext; default null
    nearestShrine: null,
    nearestChestPaid: null,
    nearestChestFree: null,
    nearestBarrel: null,
    nearestEnemyDist: -1,
    enemiesIn200: 0,
    enemiesIn400: 0,
    hordeAhead: false,
  };
}

// BETA 17.59 — diagnostic helper for live observation. Returns map-context
// snapshot relative to the player's current position. Called from the
// __getBotDebug hook in GameCanvas so Chrome MCP sees the world the bot
// sees each frame: what's near, how far, how clustered.
export function getMapContext(state: GameState): {
  nearestShrine: { x: number; y: number; d: number } | null;
  nearestChestPaid: { x: number; y: number; d: number; cost: number } | null;
  nearestChestFree: { x: number; y: number; d: number } | null;
  nearestBarrel: { x: number; y: number; d: number } | null;
  nearestEnemyDist: number;
  enemiesIn200: number;
  enemiesIn400: number;
  hordeAhead: boolean;
} {
  const px = state.player.x, py = state.player.y;
  // Shrines
  let nearestShrine: { x: number; y: number; d: number } | null = null;
  for (const s of state.map.roomMap.shrines ?? []) {
    const sAny = s as { id?: string; x: number; y: number; activated?: boolean; used?: boolean; state?: string };
    if (sAny.activated || sAny.used) continue;
    if (sAny.state === 'spent_lit' || sAny.state === 'boss_spawned' || sAny.state === 'boss_defeated') continue;
    const d = Math.hypot(sAny.x - px, sAny.y - py);
    if (!nearestShrine || d < nearestShrine.d) nearestShrine = { x: sAny.x, y: sAny.y, d };
  }
  // Chests — split by type so we can see "FREE chest at 800wu, paid at 200wu"
  let nearestChestPaid: { x: number; y: number; d: number; cost: number } | null = null;
  let nearestChestFree: { x: number; y: number; d: number } | null = null;
  for (const c of state.map.roomMap.chests ?? []) {
    const cAny = c as { id?: string; x: number; y: number; type?: string; opened?: boolean; collected?: boolean; cost?: number };
    if (cAny.opened || cAny.collected) continue;
    const d = Math.hypot(cAny.x - px, cAny.y - py);
    if (cAny.type === 'gold') {
      if (!nearestChestFree || d < nearestChestFree.d) nearestChestFree = { x: cAny.x, y: cAny.y, d };
    } else {
      if (!nearestChestPaid || d < nearestChestPaid.d) nearestChestPaid = { x: cAny.x, y: cAny.y, d, cost: cAny.cost ?? 0 };
    }
  }
  // Barrels
  let nearestBarrel: { x: number; y: number; d: number } | null = null;
  for (const b of state.map.roomMap?.barrels ?? []) {
    const bAny = b as { x: number; y: number; state?: string };
    if (bAny.state === 'broken') continue;
    const d = Math.hypot(bAny.x - px, bAny.y - py);
    if (!nearestBarrel || d < nearestBarrel.d) nearestBarrel = { x: bAny.x, y: bAny.y, d };
  }
  // Enemy proximity
  const enemies = state.enemies ?? [];
  let nearestEnemyDist = -1;
  let count200 = 0, count400 = 0;
  for (const e of enemies) {
    if (!e.alive) continue;
    const d = Math.hypot(e.x - px, e.y - py);
    if (d < 200) count200++;
    if (d < 400) count400++;
    if (nearestEnemyDist < 0 || d < nearestEnemyDist) nearestEnemyDist = d;
  }
  // Horde-ahead: enemies in the bot's facing direction within 300wu
  let hordeAhead = false;
  if (count400 >= 5 && state.player.vx !== undefined && state.player.vy !== undefined) {
    const vx = state.player.vx ?? 0;
    const vy = state.player.vy ?? 0;
    const vMag = Math.hypot(vx, vy);
    if (vMag > 1) {
      const dirX = vx / vMag, dirY = vy / vMag;
      let aheadCount = 0;
      for (const e of enemies) {
        if (!e.alive) continue;
        const ex = e.x - px, ey = e.y - py;
        const dist = Math.hypot(ex, ey);
        if (dist > 300) continue;
        // Dot product to project onto facing direction (cos angle)
        const dot = (ex * dirX + ey * dirY) / Math.max(1, dist);
        if (dot > 0.5) aheadCount++; // within ~60° cone ahead
      }
      hordeAhead = aheadCount >= 5;
    }
  }
  return {
    nearestShrine,
    nearestChestPaid,
    nearestChestFree,
    nearestBarrel,
    nearestEnemyDist,
    enemiesIn200: count200,
    enemiesIn400: count400,
    hordeAhead,
  };
}

// BETA 14.05 — module-level accumulators for floor-effect + kitten-trap tracking.
// Reset in startNewRun. Snapshotted into RunLog at finalizeRun.
const _seenGroundEffectIds = new Set<number>();
const _triggeredGroundEffectIds = new Set<number>();
const _groundEffectSourceCounts = new Map<string, number>();
// BETA 16.55 — PSBOT cycle-30l: per-source SPAWN counter (separate from
// step-on counter above). Banana auto-drop "regression" diagnosis was
// confused by `groundEffectSources` actually counting step-ons; need a
// distinct spawn-count to verify whether banana/whoopie are even
// spawning their ground effects at all.
const _groundEffectSpawnCounts = new Map<string, number>();
const _seenKittenIds = new Set<number>();
const _activeKittenIds = new Set<number>();

// BETA 14.06 — per-projectile first-hit-time tracking. User: "time it takes
// for projectile to hit. all that. these are real adjustments needed if
// overpowered. too slow and needs to be smart if its a gun or perk or buncha
// item doing it. context is key." Each projectile captured ONCE on the frame
// its hitEnemyIds.size transitions from 0 → ≥1. proj.age at that moment is
// the time from spawn to first hit. Aggregated per-weapon for analyst pass.
const _firstHitProjIds = new Set<number>();      // projectiles whose first hit we've already counted
const _weaponHitTimeSum = new Map<string, number>(); // sum of first-hit ages per weaponId
const _weaponHitTimeCount = new Map<string, number>(); // count of first-hit events per weaponId

// BETA 14.06 (audit pass) — revive detection via HP-transition watcher. Engine
// has _sheeeshUsedThisRun + _liarUsedThisRun module-private flags that aren't
// exposed to the bot. Workaround: watch player.hp ratio per-frame; if it
// transitions from <0.05 to ≥0.20 within ~2 frames AND the relevant item is
// in inventory, that's a revive. Sheeesh = 25% HP refill, Liar = 1 HP + 5s
// invuln (so ratio ~0.01 → 0.20+ for Sheeesh, ratio ~0.01 → ~0.05 for Liar).
let _lastFrameHpFraction = 1.0;
let _sheeeshFiredThisRun = 0;
let _liarFiredThisRun = 0;

// BETA 14.07 — projectile chase/homing tracking. User: "the projectile or
// bullet sometimes follows an enemy when it should just shoot straight... the
// ice gun one shoots and the bullet will chase. i don't want you to change
// that right now. just something to track to see if its supposed to do it.
// how long did it chase, and such." Per-frame, compare each projectile's
// current velocity angle to its previous frame's angle. Delta > 5° = turn
// event = projectile is homing/chasing. Aggregated per-weapon for analyst pass.
const _projLastAngle = new Map<number, number>();
// BETA 14.82 — recent trap-cell markings (rolling window for deadlock detection)
const _recentTraps: Array<{ x: number; y: number; t: number }> = [];
const _weaponTurnEvents = new Map<string, number>(); // total turns observed per weaponId
const _weaponMaxChaseDurationByProj = new Map<string, number>(); // max chase duration (frames) seen for any single projectile
const _projChaseFrames = new Map<number, number>(); // per-projectile consecutive turn-frame count

// BETA 15.42 (re-add post-rollback) — CSV-side long-frame counters. The
// existing telemetry block at line ~2092 (PS Major's BETA 15.25) already
// detects long frames + posts to perfTelemetry. We piggy-back here to also
// track count + worst-ms + context for the CSV (so per-run freeze data is
// queryable without joining against the perf_events stream). Module-scope so
// they survive across updateBot calls; reset in startNewRun.
let _longFrameCount = 0;
let _longestFrameMs = 0;
let _longFrameLastContext = '';

// BETA 14.07 — additional precision metrics ("can you think of anything else
// we need to track to be extremely precise?"). All bot-side observation, no
// engine hooks needed. Reset on startNewRun, snapshotted at finalizeRun.
let _timeToFirstKillSec = -1;        // game time when killCount first hit 1
let _phaseTimePlaying = 0;
let _phaseTimeLevelup = 0;
let _phaseTimeChest = 0;
let _phaseTimeShrine = 0;
let _distanceTraveled = 0;            // sum of frame-to-frame player position deltas (wu)
let _lastFramePlayerX = 0;
let _lastFramePlayerY = 0;
let _enemiesAliveSum = 0;             // for avg enemies on screen calc
let _enemiesAliveSamples = 0;
let _kiteDistanceSum = 0;             // for avg kite distance calc
let _kiteDistanceSamples = 0;

function createBotState(config: Partial<BotConfig> = {}): BotState {
  return {
    config: { ...DEFAULT_CONFIG, ...config },
    currentRun: null,
    allRuns: [],
    runCount: loadRunCount(), // restore global run counter so IDs never repeat
    targetPos: null,
    currentGoal: 'explore',
    goalTimer: 0,
    goalLockTimer: 0,
    reactiveFleeUntil: 0,
    lastHp: 100,
    lastEnemyCount: 0,
    lastDamageFrame: 0,
    damageDealtAccum: 0,
    damageTakenAccum: 0,
    healingAccum: 0,
    killsByType: {},
    roomsVisitedSet: new Set(),
    upgradesPicked: [],
    upgradesSkipped: [],
    choiceLog: [],
    itemsCollected: [],
    weaponsUsed: new Set(),
    lastPhase: 'loading',
    stuckTimer: 0,
    lastPos: { x: 0, y: 0 },
    campTimer: 0,                  // BETA 15.57 — anti-camp accumulator
    forceExploreTimer: 0,          // BETA 15.57 — forced-explore countdown
    recentMoveDist: 0,             // BETA 15.57 — sliding 1s window
    recentMoveTime: 0,
    windowStartPos: null,          // BETA 17.57 — net-displacement anchor
    stuckCount: 0,
    stuckOrigin: null,
    driftCenter: null,
    driftStartTime: 0,
    blineUntil: 0,
    blineTarget: null,
    blineTriggerCount: 0,
    trailTeleportCount: 0,
    namedEliteFleeCount: 0,
    // BETA 13.80 (main-dev side) — room-time enforcement
    currentRoomId: null,
    currentRoomEnteredAt: 0,
    roomCooldowns: new Map(),
    forceEscapeRoomId: null,
    roomEscapeCount: 0,
    // BETA 13.80 (bot-training side) — Fix 1 wire-up + hitbox near-miss tracker
    trapCellsMarkedCount: 0,
    lastTrapCellKey: null,
    // BETA 16.27 — PSBOT cycle-30 D.2: anti-tunnel pick counter init
    statCategoryPickCount: {},
    // BETA 14.82 — flow-field deadlock escape state
    deadlockEscapeUntil: 0,
    deadlockEscapeTarget: null,
    deadlockEscapeStartPos: null,
    enemyNearMissFrameCount: 0,
    weaponNearMissCount: 0,
    _projTracking: new Map(),
    noveltyPickCount: 0,
    navGraph: null,
    waypointPath: [],
    waypointIdx: 0,
    frameCount: 0,
    restartTimer: 0,
    phaseHandledThisEntry: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════

/** Initialize the bot. Call once at game start. */
export function initBot(config: Partial<BotConfig> = {}): void {
  // BETA 13.56: pull persisted build from localStorage if caller didn't pass one,
  // and apply build-specific BotConfig overrides (kite/flee/heal thresholds).
  if (!config.buildType) config.buildType = loadStoredBuildType();
  const overrides = BUILD_CONFIG_OVERRIDES[config.buildType];
  if (overrides) config = { ...overrides, ...config };

  // Don't recreate if already running — just update config
  if (bot) {
    Object.assign(bot.config, config);
    return;
  }
  bot = createBotState(config);
  brain = loadBrain(); // always reload from storage so brain persists across page refreshes

  // BETA 17.22 — PSBOT cycle-30ai B3: server-side aggregated brain. Fetch
  // global stats from Supabase bot_run rows on bot init. Blends into the
  // local brain at 40% weight. Fire-and-forget — if fetch fails, bot
  // works on local brain only.
  //
  // BETA 17.29 — PSBOT cycle-30ap Candidate #1. initBot is ALWAYS called
  // (even for solo play with config.enabled=false). The 200-row Supabase
  // fetch + JSON parse + aggregate was blocking solo player startup.
  // Skip when bot mode is disabled — solo player doesn't need bot brain.
  if (bot.config.enabled) {
    void _fetchAndBlendGlobalBrain(brain);
  }
  // Reload previously completed runs so they survive page refresh / restart
  const stored = loadRunsFromStorage();
  if (stored.length > 0) {
    bot.allRuns = stored;
    // Safety: ensure runCount is at least as high as the highest stored run number
    // so the counter never resets and produces duplicate run IDs
    const maxRunNum = Math.max(...stored.map(r => r.runNumber));
    if (bot.runCount < maxRunNum) {
      bot.runCount = maxRunNum;
      saveRunCount(bot.runCount);
    }
  }
  log(`Bot initialized. Brain has ${brain.totalRunsLearned} runs of memory. ${bot.allRuns.length} stored runs loaded. Next run: #${bot.runCount + 1}`);
}

/**
 * Finalize the current in-progress run as a "quit" so its data is saved even
 * if the bot didn't die. Call this when the user navigates away mid-run.
 */
export function finalizeRunOnQuit(state: GameState): void {
  if (!bot || !bot.currentRun) return;
  if (bot.currentRun.endTime > 0) return; // already finalized
  bot.currentRun.deathCause = 'quit';
  finalizeRun(state, 'gameover');
}

/** Toggle bot on/off. Returns new enabled state. */
export function toggleBot(): boolean {
  if (!bot) bot = createBotState();
  bot.config.enabled = !bot.config.enabled;
  log(`Bot ${bot.config.enabled ? 'ENABLED' : 'DISABLED'}`);
  return bot.config.enabled;
}

/** Check if bot is active */
/** BETA 13.56: change the bot's build archetype from the browser console.
 *  Usage: window.__setBotBuild('chad_aura' | 'peanut_sniper' | ...). Persists via
 *  localStorage so the next run picks it up. Pass 'adaptive' to clear. */
// BETA 20.68 — BRAIN v2 visibility (Dylan: "its not learning from itself").
// Now you can SEE it learn. Browser console: __getBotBrainReport()
if (typeof window !== 'undefined') {
  (window as unknown as { __getBotBrainReport?: () => unknown }).__getBotBrainReport = () => {
    const cs = Object.entries(brain.cardScores ?? {})
      .filter(([, v]) => (v as { count: number }).count >= 3)
      .map(([k, v]) => ({ keyword: k, avgSurvival: Math.round((v as { total: number; count: number }).total / (v as { total: number; count: number }).count), picks: (v as { count: number }).count }))
      .sort((a, b) => b.avgSurvival - a.avgSurvival);
    return {
      runsLearned: brain.totalRunsLearned,
      avgSurvivalSec: Math.round(brain.avgSurvivalSec),
      bestSurvivalSec: Math.round(brain.bestSurvivalSec),
      fleeThreshold: +brain.fleeThreshold.toFixed(3),
      kiteAggression: +brain.kiteAggression.toFixed(3),
      healSeekThreshold: +brain.healSeekThreshold.toFixed(3),
      last10Survivals: (brain.recentSurvivalTimes ?? []).map((t) => Math.round(t)),
      perCharacter: brain.perCharacter,
      bestKeywords: cs.slice(0, 5),
      worstKeywords: cs.slice(-5),
      stallBreaksThisSession: (bot as unknown as { _stallBreaks?: number } | null)?._stallBreaks ?? 0,
    };
  };
}

export function setBotBuild(b: BuildType): void {
  saveStoredBuildType(b);
  if (bot) {
    bot.config.buildType = b;
    const overrides = BUILD_CONFIG_OVERRIDES[b];
    if (overrides) Object.assign(bot.config, overrides);
  }
  log(`Bot build set to: ${b}`);
}

if (typeof window !== 'undefined') {
  (window as unknown as { __setBotBuild?: typeof setBotBuild }).__setBotBuild = setBotBuild;
}

/** BETA 13.59: engine calls this every time the trail-enemy teleport fires.
 *  Lets the invisible behavior show up as a real CSV column. */
export function noteTrailTeleport(): void {
  if (bot) bot.trailTeleportCount++;
}

/** BETA 13.67: orchestrator-side helper to reset bot.currentRun between programmatic
 *  character switches. The standard run lifecycle finalizes via handleGameOver (death)
 *  or handleWon (victory), both of which press 'r' to restart. When the orchestrator
 *  calls __returnToMenu without a death, bot.currentRun stays live and startNewRun
 *  skips on the next game start (because the !bot.currentRun guard fails) — which
 *  means the next character's first run inherits the previous character's RunLog
 *  (wrong runNumber, wrong buildType, wrong character data).
 *  Call this AFTER __returnToMenu and BEFORE __startGame to force a clean break. */
export function resetBotRun(): void {
  if (!bot || !bot.currentRun) return;
  // If still mid-flight, mark it as quit so it's persisted (don't lose the data)
  if (bot.currentRun.endTime === 0) {
    bot.currentRun.deathCause = 'quit';
    // Best-effort fill of timing fields without a state reference
    bot.currentRun.endTime = Date.now();
    bot.currentRun.wallClockSec = (bot.currentRun.endTime - bot.currentRun.startTime) / 1000;
    bot.allRuns.push(bot.currentRun);
    saveRunsToStorage(bot.allRuns);
  }
  bot.currentRun = null;
}

if (typeof window !== 'undefined') {
  (window as unknown as { __resetBotRun?: typeof resetBotRun }).__resetBotRun = resetBotRun;
  // BETA 13.82 — coverage report + reset helpers, accessible from dev console:
  //   __getCoverageReport()  → { pickedCount, offeredCount, neverPickedFromOffered, ... }
  //   __resetCoverage()      → wipes both sets so the next run rebiases toward novel picks
  (window as unknown as { __getCoverageReport?: typeof getCoverageReport }).__getCoverageReport = getCoverageReport;
  (window as unknown as { __resetCoverage?: typeof resetCoverage }).__resetCoverage = resetCoverage;
  // BETA 13.83 — tracker self-validation. Call at any time to verify the
  // _snapshotPlayerStats keys match every numeric Player field. Run once
  // automatically at the first run start (see startNewRun); also exposed on
  // window so PS Major can `__validateTracker()` after adding new Player fields.
  (window as unknown as { __validateTracker?: (p: Record<string, unknown>) => unknown }).__validateTracker = validateTrackerCoverage;
  // BETA 13.84 — bot-farm helpers. The bot-farm dashboard at /bot-farm loads each
  // game iframe with `?botfarm=true&character=X&build=Y&runs=N&muted=true`. The
  // iframe auto-starts the requested character + build, runs N games at 1× speed,
  // exports the CSV string, and postMessages it to the dashboard parent. Each
  // game keeps its own localStorage isolated (separate origins via query params
  // doesn't actually isolate — we use sessionStorage for transient run buffer).
  (window as unknown as { __exportCSVAsString?: typeof exportCSVAsString }).__exportCSVAsString = exportCSVAsString;
}

/** BETA 13.60: per-system frame-time tracker. Engine calls this with cumulative ms
 *  for each subsystem each frame. We aggregate into bot.currentRun.perfBuckets so
 *  the CSV can show "ms per second of game time" per system at finalize. Per
 *  user spec: "if things are invisible anytime create something so you can see it." */
export function notePerf(bucket: 'enemies' | 'weapons' | 'items' | 'bot' | 'render' | 'flowfield', ms: number): void {
  if (!bot || !bot.currentRun) return;
  if (!bot.currentRun.perfBuckets) {
    bot.currentRun.perfBuckets = { enemies: 0, weapons: 0, items: 0, bot: 0, render: 0, flowfield: 0 };
  }
  // Older runs may have a perfBuckets object without 'flowfield' yet — guard the increment.
  if (bot.currentRun.perfBuckets[bucket] === undefined) bot.currentRun.perfBuckets[bucket] = 0;
  bot.currentRun.perfBuckets[bucket] += ms;
  // BETA 13.70 FPS: also feed performanceTracker for the dev panel's avg/worst breakdown.
  notePerfFrame(bucket, ms);
}

export function isBotEnabled(): boolean {
  return bot?.config.enabled ?? false;
}

/** Get all completed run logs */
export function getBotRunLogs(): RunLog[] {
  return bot?.allRuns ?? [];
}

/** Get the current run log (in progress) */
export function getCurrentRunLog(): RunLog | null {
  return bot?.currentRun ?? null;
}

// BETA 13.84 — programmatic CSV export. The bot-farm dashboard postMessages
// each iframe at run-batch-complete time and concats all CSVs into a master.
// Identical schema to the DebugPanel download button so cycle-N analysis
// scripts work unchanged. Call with runs=[] to get just the header row.
// BETA 14.70 — CSV-escape helper for JSON tracker strings. Wraps in double
// quotes + escapes inner quotes (CSV-standard double-up).
function csvEscapeJson(s: string | undefined): string {
  return `"${(s ?? '').replace(/"/g, '""')}"`;
}

export function exportCSVAsString(runs?: RunLog[]): string {
  const rows = runs ?? bot?.allRuns ?? [];
  // BETA 13.99b — 7 new columns appended at the end of the schema (cycle-6
  // adoption tracking for 11 weapons + 8 items shipped 13.94-99). Older cycle
  // analysis scripts that key on column NAMES still work; scripts that key on
  // positional index (1..30) need to bump to 1..37.
  // BETA 14.05 — added newPercPicks (38) + 5 floor-effect/kitten columns (39-43).
  // BETA 14.06 — added weaponTelemetry (44), percLevels (45), finalStats (46),
  // sheeeshTriggered (47), liarTriggered (48). Total schema 48 columns.
  // Per user audit: "double check all percs and all buncha bullshit items that
  // all of these new tracking is going to track them as well." — full coverage
  // confirmed for all 28 percs (T01-T14 + N01-N14) via percLevels + finalStats,
  // and for ALL 27 buncha-bullshit items via finalStats (passive stat mods),
  // groundEffectSources (floor-trap items), synergyDracsBoth (gas mask combo),
  // sheeeshTriggered/liarTriggered (revive items), freezeFrames/slowFrames/
  // wetFrames (status-effect items), and weaponTelemetry hit-time (weapon
  // accuracy + projectile speed context per user "context is key").
  // BETA 14.07 — added 9 precision columns (49-57): timeToFirstKillSec,
  // phaseTimePlaying/Levelup/Chest/Shrine, distanceTraveled, maxItemStack,
  // avgEnemiesAlive, avgKiteDistance. weaponTelemetry now also includes
  // turn= + maxChase= per weapon (folded into the existing column).
  // Total schema 48 → 57 columns.
  // BETA 14.70 — v1 CSV converge: appended 36 new cols (58→94) so master.csv
  // has the same per-mechanic data as WL CSV. PSBOT RUN cycle-8 unblocked
  // without v1/v2 mode-switching.
  const header = 'run,character,build,survivalSec,level,floor,kills,killsPerMin,chests,gold,damageTaken,damageDealt,deathCause,anomalies,bugs,blineTriggers,trailTeleports,namedEliteFlees,trapMarks,enemyNearMissFrames,weaponNearMisses,noveltyPicks,botMs,enemiesMs,weaponsMs,itemsMs,picksTracked,brokenPicks,brokenPickNames,weapons,newWeapPicks,newItemPicks,newPickNames,freezeFrames,slowFrames,wetFrames,synergyDracsBoth,newPercPicks,groundEffectsSpawned,groundEffectsTriggered,groundEffectSources,groundEffectSpawnSources,kittensPlaced,kittensTriggered,weaponTelemetry,percLevels,finalStats,sheeeshTriggered,liarTriggered,timeToFirstKillSec,phaseTimePlaying,phaseTimeLevelup,phaseTimeChest,phaseTimeShrine,distanceTraveled,maxItemStack,avgEnemiesAlive,avgKiteDistance,'
    + 'percTracker,itemTracker,shrineTracker,floorEffectTracker,weaponEnemyMatrix,synergyTracker,eventLog,deathContext,nearMissEventsLog,retreatEventsLog,'
    + 'topWeaponVsZombie,topWeaponVsTimmy,topWeaponVsCaseoh,topWeaponVsElite,weakestWeaponOverall,'
    + 'reviveEvents,healEvents,dodgeEvents,phaseEvents,knockbackEvents,'
    + 'enemyAttacksAttempted,enemyAttacksHit,enemyAttacksMissedByEvasion,enemyAttacksMissedByIframe,enemyAttacksMissedByPhase,enemyAttacksMissedByOther,enemySpriteOverlapFrames,enemySpriteOnlyTouchFrames,enemyTouchCooldownGated,'
    + 'firstBloodTime,firstDamageTakenTime,longestStretchBetweenHitsMs,engineFramePerf,'
    + 'finalDamagePct,finalCritChancePct,finalAttackSpeedPct,finalMaxHP,runGoldSpent,'
    // BETA 15.13 N-perk stat-anchor verification cols (11 cols)
    + 'finalLifestealPct,finalArmor,finalThornsPct,finalCritDamagePct,finalProjectileCount,finalOverheal,finalProjectilePierce,finalPickupRangePct,finalEliteDamagePct,finalProjectileBounce,finalPhaseChancePct,'
    // BETA 14.72 — bot diagnostics (4 new cols, total 94 → 98)
    + 'botPositionTrail,stuckEvents,botMapCellVisits,decisionTrace,'
    // BETA 15.14 — N10 Debt mechanic trackers (4 cols, 98 → 102)
    + 'debtSavesTriggered,debtGoldSpent,debtHitsAbsorbed,debtForceEnded,'
    // BETA 15.15 — N13 Not Xanax + N06 Let's Ride trackers (3 cols, 102 → 105)
    + 'notXanaxSlowsApplied,notXanaxTotalSlowSeconds,perkBungulatorsSpawned,'
    // BETA 15.42 — long-frame / freeze detection (re-add post-rollback, 3 cols, 105 → 108)
    + 'longFrameCount,longestFrameMs,longFrameContext,'
    // BETA 15.26 — character passive trackers (5 cols, 105 → 110)
    + 'characterPassiveLayer1Final,signatureProcsThisRun,signatureMaxStacks,signatureActiveSeconds,signatureBonusDamage,'
    // BETA 15.28 — spawn timing + final luck (10 cols, 110 → 120)
    + 'enemiesSpawned_0to30s,enemiesSpawned_30to60s,enemiesSpawned_60to120s,enemiesSpawned_120to300s,'
    + 'peakAliveCount,peakAliveTime,firstZombieSpawnTime,firstTimmySpawnTime,firstCaseohSpawnTime,finalLuckPct,'
    // BETA 15.31 — perc-pick rarity counts (6 cols, 120 → 126)
    + 'percPicks_common,percPicks_uncommon,percPicks_rare,percPicks_epic,percPicks_legendary,percPicks_total,'
    // BETA 15.34 — full pick audit (13 cols, 126 → 139). Summary + full JSON.
    + 'pickAuditCount,pickAuditLegendaryCount,pickAuditEpicPlusCount,pickAuditMaxBuffValue,pickAuditMaxBuffSource,pickAuditMaxBuffId,pickAuditMaxBuffRarity,pickAuditPickCounts_levelupPerc,pickAuditPickCounts_levelupWeapon,pickAuditPickCounts_shrine,pickAuditPickCounts_chestFree,pickAuditPickCounts_chestPaid,pickAuditJson';
  const csvRows = rows.map(r => {
    const kpm = r.survivalTimeSec > 0 ? (r.killCount / r.survivalTimeSec * 60).toFixed(1) : '0';
    const weapons = (r.weaponsUsed ?? []).join('|') || 'none';
    const brokenNames = (r.brokenPickNames ?? []).join('|') || 'none';
    const newPicks = (r.newPickNames ?? []).join('|') || 'none';
    const pb = r.perfBuckets;
    const survSec = r.survivalTimeSec || 1;
    const msPerSec = (raw: number | undefined) => raw !== undefined ? (raw / survSec).toFixed(2) : '0';
    return [r.runNumber, r.characterId ?? 'unknown', r.buildType ?? 'adaptive', r.survivalTimeSec.toFixed(1), r.levelReached, r.floorReached,
     r.killCount, kpm, r.chestsOpened, r.goldCollected, r.damageTaken.toFixed(0),
     r.damageDealt.toFixed(0), r.deathCause,
     r.anomalies.length, r.anomalies.filter(a => a.severity === 'bug').length,
     r.blineTriggers ?? 0, r.trailTeleports ?? 0, r.namedEliteFlees ?? 0,
     r.trapCellsMarked ?? 0, r.enemyNearMissFrames ?? 0, r.weaponNearMisses ?? 0,
     r.noveltyPicks ?? 0,
     msPerSec(pb?.bot), msPerSec(pb?.enemies), msPerSec(pb?.weapons), msPerSec(pb?.items),
     r.totalPicksTracked ?? 0, r.brokenPickCount ?? 0, `"${brokenNames}"`,
     `"${weapons}"`,
     r.newWeapPicks ?? 0, r.newItemPicks ?? 0, `"${newPicks}"`,
     r.freezeFrames ?? 0, r.slowFrames ?? 0, r.wetFrames ?? 0,
     r.synergyDracsBoth ?? 0, r.newPercPicks ?? 0,
     r.groundEffectsSpawned ?? 0, r.groundEffectsTriggered ?? 0,
     `"${(r.groundEffectSources ?? []).join('|') || 'none'}"`,
     `"${(r.groundEffectSpawnSources ?? []).join('|') || 'none'}"`,
     r.kittensPlaced ?? 0, r.kittensTriggered ?? 0,
     `"${(r.weaponTelemetry ?? []).join('|') || 'none'}"`,
     `"${(r.percLevels ?? []).join('|') || 'none'}"`,
     `"${r.finalStats ?? ''}"`,
     r.sheeeshTriggered ?? 0, r.liarTriggered ?? 0,
     r.timeToFirstKillSec ?? -1,
     r.phaseTimePlaying ?? 0, r.phaseTimeLevelup ?? 0,
     r.phaseTimeChest ?? 0, r.phaseTimeShrine ?? 0,
     r.distanceTraveled ?? 0, r.maxItemStack ?? 0,
     r.avgEnemiesAlive ?? 0, r.avgKiteDistance ?? 0,
     // BETA 14.70 — appended converge cols. Helper escapes JSON for CSV.
     csvEscapeJson(r.percTrackerJson), csvEscapeJson(r.itemTrackerJson),
     csvEscapeJson(r.shrineTrackerJson), csvEscapeJson(r.floorEffectTrackerJson),
     csvEscapeJson(r.weaponEnemyMatrixJson), csvEscapeJson(r.synergyTrackerJson),
     csvEscapeJson(r.eventLogJson), csvEscapeJson(r.deathContextJson),
     csvEscapeJson(r.nearMissEventsLogJson), csvEscapeJson(r.retreatEventsLogJson),
     r.topWeaponVsZombie ?? '', r.topWeaponVsTimmy ?? '',
     r.topWeaponVsCaseoh ?? '', r.topWeaponVsElite ?? '',
     r.weakestWeaponOverall ?? '',
     r.reviveEventsThisRun ?? 0, r.healEventsThisRun ?? 0,
     r.dodgeEventsThisRun ?? 0, r.phaseEventsThisRun ?? 0,
     r.knockbackEventsThisRun ?? 0,
     r.enemyAttacksAttempted ?? 0, r.enemyAttacksHit ?? 0,
     r.enemyAttacksMissedByEvasion ?? 0, r.enemyAttacksMissedByIframe ?? 0,
     r.enemyAttacksMissedByPhase ?? 0, r.enemyAttacksMissedByOther ?? 0,
     r.enemySpriteOverlapFrames ?? 0, r.enemySpriteOnlyTouchFrames ?? 0, r.enemyTouchCooldownGated ?? 0,
     r.firstBloodTime ?? 0, r.firstDamageTakenTime ?? 0,
     r.longestStretchBetweenHitsMs ?? 0,
     csvEscapeJson(r.engineFramePerfJson),
     r.finalDamagePct ?? 0, r.finalCritChancePct ?? 0,
     r.finalAttackSpeedPct ?? 0, r.finalMaxHP ?? 0,
     r.runGoldSpent ?? 0,
     // BETA 15.13 N-perk stat-anchor verification (11 cols)
     r.finalLifestealPct ?? 0, r.finalArmor ?? 0, r.finalThornsPct ?? 0,
     r.finalCritDamagePct ?? 0, r.finalProjectileCount ?? 0, r.finalOverheal ?? 0,
     r.finalProjectilePierce ?? 0, r.finalPickupRangePct ?? 0, r.finalEliteDamagePct ?? 0,
     r.finalProjectileBounce ?? 0, r.finalPhaseChancePct ?? 0,
     // BETA 14.72 — bot diagnostics 4 new cols
     csvEscapeJson(r.botPositionTrailJson),
     csvEscapeJson(r.stuckEventsJson),
     csvEscapeJson(r.botMapCellVisitsJson),
     csvEscapeJson(r.decisionTraceJson),
     // BETA 15.14 — N10 Debt mechanic trackers (4 cols)
     r.debtSavesTriggered ?? 0,
     r.debtGoldSpent ?? 0,
     r.debtHitsAbsorbed ?? 0,
     r.debtForceEnded ?? 0,
     // BETA 15.15 — N13 Not Xanax + N06 Let's Ride (3 cols)
     r.notXanaxSlowsApplied ?? 0,
     (r.notXanaxTotalSlowSeconds ?? 0).toFixed(2),
     r.perkBungulatorsSpawned ?? 0,
     // BETA 15.42 — long-frame detection (3 cols)
     r.longFrameCount ?? 0,
     Math.round(r.longestFrameMs ?? 0),
     csvEscapeJson(r.longFrameLastContext),
     // BETA 15.26 — character passive trackers (5 cols)
     r.characterPassiveLayer1Final ?? 0,
     r.signatureProcsThisRun ?? 0,
     r.signatureMaxStacks ?? 0,
     (r.signatureActiveSeconds ?? 0).toFixed(2),
     r.signatureBonusDamage ?? 0,
     // BETA 15.28 — spawn timing + final luck (10 cols)
     r.enemiesSpawned_0to30s ?? 0,
     r.enemiesSpawned_30to60s ?? 0,
     r.enemiesSpawned_60to120s ?? 0,
     r.enemiesSpawned_120to300s ?? 0,
     r.peakAliveCount ?? 0,
     r.peakAliveTime ?? 0,
     r.firstZombieSpawnTime ?? -1,
     r.firstTimmySpawnTime ?? -1,
     r.firstCaseohSpawnTime ?? -1,
     r.finalLuckPct ?? 0,
     // BETA 15.31 — perc-pick rarity (6 cols)
     r.percPicks_common ?? 0,
     r.percPicks_uncommon ?? 0,
     r.percPicks_rare ?? 0,
     r.percPicks_epic ?? 0,
     r.percPicks_legendary ?? 0,
     r.percPicks_total ?? 0,
     // BETA 15.34 — pick audit summary (12) + full JSON (1) = 13 cols
     r.pickAuditCount ?? 0,
     r.pickAuditLegendaryCount ?? 0,
     r.pickAuditEpicPlusCount ?? 0,
     (r.pickAuditMaxBuffValue ?? 0).toFixed(2),
     `"${r.pickAuditMaxBuffSource ?? ''}"`,
     `"${(r.pickAuditMaxBuffId ?? '').replace(/"/g, '""')}"`,
     `"${r.pickAuditMaxBuffRarity ?? ''}"`,
     r.pickAuditPickCounts_levelupPerc ?? 0,
     r.pickAuditPickCounts_levelupWeapon ?? 0,
     r.pickAuditPickCounts_shrine ?? 0,
     r.pickAuditPickCounts_chestFree ?? 0,
     r.pickAuditPickCounts_chestPaid ?? 0,
     csvEscapeJson(r.pickAuditJson),
    ].join(',');
  });
  return [header, ...csvRows].join('\n');
}

/** Print a summary report to console */
export function printBotReport(): void {
  if (!bot || bot.allRuns.length === 0) {
    console.log('[BOT] No completed runs yet.');
    return;
  }
  const report = generateReport(bot.allRuns);
  console.log(report);
}

/** Export all run data as JSON string */
export function exportBotData(): string {
  return JSON.stringify(bot?.allRuns ?? [], null, 2);
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN UPDATE — Called every frame from engine.ts updateGame()
// ═══════════════════════════════════════════════════════════════════════

// BETA 15.25 — telemetry pacing state. Reset at startNewRun.
// _lastBotUpdateWallTime: wall-clock at the END of last updateBot, used to
//   detect long frames (current call - last = real elapsed). When delta is
//   way bigger than expected, the JS event loop was blocked = lag spike.
// _lastInProgressSnapshotMs: wall-clock of last RUN_PARTIAL Supabase send.
//   Throttled to once per 5 min so the network spam doesn't itself become
//   the freeze cause (the bug we hit in 15.30/15.31 with 30s intervals).
// _prevTelemetryPhase: SEPARATE from bot.lastPhase (which is consumed by
//   onPhaseChange at line ~2113) — we only need it for the long-frame guard
//   and don't want to interfere with the phase-change detection.
// _longFrameSpikeThresholdMs: minimum delta to count as a lag spike. 1000ms
//   = 1s freeze. Anything shorter is normal jitter / GC pause.
let _lastBotUpdateWallTime = 0;
let _lastInProgressSnapshotMs = 0;
let _prevTelemetryPhase: string = '';
const _longFrameSpikeThresholdMs = 1000;
const _inProgressSnapshotIntervalMs = 5 * 60 * 1000; // 5 minutes

// BETA 15.28 — Spawn timing tracker. Per Dylan: "I need to find a correlation
// on when and if we are getting overwhelmed too soon." Tracks new-enemy IDs
// per frame, buckets spawns by gameTime window, captures peak alive count +
// first-spawn time per enemy type. Reset in startNewRun.
const _seenEnemyIds = new Set<number>();
let _spawnBucket0to30   = 0;
let _spawnBucket30to60  = 0;
let _spawnBucket60to120 = 0;
let _spawnBucket120to300 = 0;
let _peakAliveCount = 0;
let _peakAliveTime = 0;
let _firstZombieSpawnTime = -1;
// E1 (enemy rebuild) — _firstTimmySpawnTime/_firstCaseohSpawnTime trackers
// deleted (always -1 since the purges). CSV columns firstTimmySpawnTime/
// firstCaseohSpawnTime stay in the header and emit -1 forever via the
// `?? -1` row fallbacks (PSBOT retired-column law).

export function updateBot(state: GameState, dt: number): void {
  if (!bot || !bot.config.enabled) return;

  bot.frameCount++;

  // BETA 20.67 — cohort integrity (deep-dive HIGH #3): mark the run as
  // bot-controlled ON THE LIVE STATE so every finalize path (incl. live
  // pause-quit) stamps botEnabled and perfTelemetry routes the row to
  // bot_run even in a normal tab without ?botfarm=true.
  (state as unknown as { _botEnabledRun?: boolean })._botEnabledRun = true;

  // BETA 15.25 — telemetry: long-frame detection + 5-min in-progress snapshot.
  // Both are fire-and-forget into perfTelemetry which is async-deferred via
  // setTimeout(0). Net per-frame cost: 2 Date.now() calls + 1 number compare
  // when nothing fires. Fires only when the right conditions hit.
  try {
    const _wallNow = Date.now();
    const _wallDelta = _lastBotUpdateWallTime > 0 ? (_wallNow - _lastBotUpdateWallTime) : 0;

    // Long-frame: previous frame was 'playing' (not menu/levelup/chest), current
    // call is 'playing', AND wall delta exceeds threshold. Both-sides 'playing'
    // guard is critical — without it cross-phase gaps (e.g. levelup modal open
    // for 5s) get falsely counted as a 5s freeze.
    const _prevWasPlaying = _prevTelemetryPhase === 'playing';
    const _curIsPlaying = state.phase === 'playing';
    if (_prevWasPlaying && _curIsPlaying && _wallDelta >= _longFrameSpikeThresholdMs) {
      // BETA 15.42 (re-add) — CSV-side per-run counters. Lets us answer
      // "how many freezes did this run have, and how bad was the worst" without
      // joining against perf_events. Worst-frame context snapshot includes
      // bot.currentGoal so we can still confirm the cycle-16 finding (46% of
      // freezes were goal=seek_shrine).
      _longFrameCount++;
      const _ctx = {
        ms: Math.round(_wallDelta),
        gameTime: Math.round((state.time ?? 0) * 10) / 10,
        x: Math.round(state.player?.x ?? 0),
        y: Math.round(state.player?.y ?? 0),
        hp: Math.round(state.player?.hp ?? 0),
        level: state.player?.level ?? 0,
        kills: state.killCount ?? 0,
        enemiesAlive: state.enemies?.filter(e => e.alive).length ?? 0,
        goal: bot?.currentGoal ?? '?',
        weapons: bot?.weaponsUsed.size ?? 0,
        chests: state.chestsOpenedThisRun ?? 0,
        char: bot?.currentRun?.characterId ?? '?',
        build: bot?.config.buildType ?? '?',
      };
      if (_wallDelta > _longestFrameMs) {
        _longestFrameMs = _wallDelta;
        try { _longFrameLastContext = JSON.stringify(_ctx); }
        catch (_e) { _longFrameLastContext = `err:${Math.round(_wallDelta)}ms`; }
      }
      recordPerfEvent(ERR.LONG_FRAME, {
        deltaMs: Math.round(_wallDelta),
        gameTime: Math.round((state.time ?? 0) * 100) / 100,
        kills: state.killCount ?? 0,
        enemies: state.enemies?.filter(e => e.alive).length ?? 0,
        playerHp: Math.round(state.player?.hp ?? 0),
        playerLevel: state.player?.level ?? 0,
        character: (state as unknown as { _characterId?: string })._characterId ?? '?',
        runNumber: bot?.currentRun?.runNumber ?? -1,
        goal: bot?.currentGoal ?? '?',
      });
    }

    // 5-min in-progress snapshot: only fires while a run is alive and playing.
    // Sends current run state so a freeze that kills the tile mid-run still
    // leaves us recoverable data within 5 min of the freeze.
    if (bot?.currentRun && _curIsPlaying) {
      if (_lastInProgressSnapshotMs === 0) {
        _lastInProgressSnapshotMs = _wallNow;
      } else if (_wallNow - _lastInProgressSnapshotMs >= _inProgressSnapshotIntervalMs) {
        _lastInProgressSnapshotMs = _wallNow;
        recordBotRunEvent(bot.currentRun, 'RUN_PARTIAL');
      }
    }

    _lastBotUpdateWallTime = _wallNow;
    _prevTelemetryPhase = state.phase;
  } catch { /* never let telemetry crash the bot loop */ }

  // BETA 15.28 — Spawn timing tracker. Per Dylan: balance question is "are
  // bots getting overwhelmed TOO SOON?" Per-frame scan for new enemy IDs;
  // bucket spawns by gameTime window. Cheap: one Set.has lookup per alive
  // enemy per frame. Only runs while playing so menu time doesn't pollute
  // first-spawn timestamps.
  if (bot.currentRun && state.phase === 'playing') {
    try {
      const t = state.time ?? 0;
      let aliveNow = 0;
      for (const e of state.enemies) {
        if (!e.alive) continue;
        aliveNow++;
        if (!_seenEnemyIds.has(e.id)) {
          _seenEnemyIds.add(e.id);
          // Bucket by gameTime window
          if      (t < 30)  _spawnBucket0to30++;
          else if (t < 60)  _spawnBucket30to60++;
          else if (t < 120) _spawnBucket60to120++;
          else if (t < 300) _spawnBucket120to300++;
          // First-spawn time per named enemy type
          if (e.type === 'zombie' && _firstZombieSpawnTime < 0) _firstZombieSpawnTime = t;
        }
      }
      // Peak concurrent alive count + when it happened
      if (aliveNow > _peakAliveCount) {
        _peakAliveCount = aliveNow;
        _peakAliveTime = t;
      }
    } catch { /* never let spawn tracking crash the bot loop */ }
  }

  // BETA 13.99b — per-frame enemy status sweep. Cheap O(N) over alive enemies;
  // counts ENEMY-FRAMES of each status. Stratifies frozen vs slowed (freeze
  // implies slow but we only count it as frozen — they're different mechanics).
  // Used by cycle-6 analyst pass to measure adoption of freeze/slow/wet weapons.
  if (bot.currentRun && state.phase === 'playing') {
    let freeze = 0, slow = 0, wet = 0;
    for (const e of state.enemies) {
      if (!e.alive) continue;
      if ((e as { frozenUntil?: number }).frozenUntil && (e as { frozenUntil?: number }).frozenUntil! > 0) { freeze++; continue; }
      if ((e as { slowedUntil?: number }).slowedUntil && (e as { slowedUntil?: number }).slowedUntil! > 0) slow++;
      if ((e as { wetUntil?: number }).wetUntil && (e as { wetUntil?: number }).wetUntil! > 0) wet++;
    }
    bot.currentRun.freezeFrames = (bot.currentRun.freezeFrames ?? 0) + freeze;
    bot.currentRun.slowFrames   = (bot.currentRun.slowFrames   ?? 0) + slow;
    bot.currentRun.wetFrames    = (bot.currentRun.wetFrames    ?? 0) + wet;

    // BETA 14.72 — bot diagnostics (position trail + map cell visits).
    // Tracker functions self-gate to avoid bloat (5s position interval,
    // 0.5s map cell sample interval). Catches every frame where bot is
    // playing so trail/heatmap covers full run.
    try {
      const t = state.time ?? 0;
      trackBotPosition(t, state.player.x, state.player.y, state.player.hp,
        state.player.level ?? 1, state.killCount ?? 0, state.enemies.filter(e => e.alive).length);
      trackBotMapCell(t, state.player.x, state.player.y);
    } catch (_e) { /* never let diagnostics crash gameplay */ }

    // BETA 14.06 (audit pass) — revive detection. Watch player HP ratio
    // per-frame for a near-death-then-jump pattern. Sheeesh refills to 25%,
    // Liar Deceiver to 1 HP with invuln. Disambiguate by which item is in
    // inventory + magnitude of jump.
    const p = state.player;
    const curHpFrac = p.maxHp > 0 ? p.hp / p.maxHp : 0;
    if (_lastFrameHpFraction < 0.05 && curHpFrac >= 0.10) {
      // HP jumped meaningfully — likely a revive
      try {
        const inv = getItemState()?.inventory ?? [];
        const hasSheeesh = inv.some(s => s.item.id === 'item_sheeesh');
        const hasLiar = inv.some(s => s.item.id === 'item_liar_deceiver_false_prophet');
        if (curHpFrac >= 0.20 && hasSheeesh && _sheeeshFiredThisRun === 0) {
          _sheeeshFiredThisRun = 1;
        } else if (hasLiar && _liarFiredThisRun === 0) {
          _liarFiredThisRun = 1;
        }
      } catch { /* noop */ }
    }
    _lastFrameHpFraction = curHpFrac;

    // BETA 14.05 — per-frame ground-effect sweep. Reads weaponSystemState.groundEffects
    // (item_shit / banana / whoopie / poop sling impact / shitty cannon impact /
    // caseoh slam crack). Counts unique IDs (spawned), unique IDs flipped to
    // wasSteppedOn=true (triggered), and per-source breakdown.
    const ws = getWeaponSystemState();
    if (ws) {
      for (const ge of ws.groundEffects) {
        if (!_seenGroundEffectIds.has(ge.id)) {
          _seenGroundEffectIds.add(ge.id);
          // BETA 16.55 — also bump spawn-side per-source count on first sight.
          // PSBOT cycle-30l: gives us a clean answer to "did banana spawn at
          // all?" instead of having to infer from step-on counts.
          const spawnSrc = ge.weaponId || 'unknown';
          _groundEffectSpawnCounts.set(spawnSrc, (_groundEffectSpawnCounts.get(spawnSrc) ?? 0) + 1);
        }
        if (ge.wasSteppedOn && !_triggeredGroundEffectIds.has(ge.id)) {
          _triggeredGroundEffectIds.add(ge.id);
          const src = ge.weaponId || 'unknown';
          _groundEffectSourceCounts.set(src, (_groundEffectSourceCounts.get(src) ?? 0) + 1);
        }
      }
    }

    // BETA 14.05 — per-frame Ugly Kittens trap sweep. The kitten weapon spawns
    // projectiles with speed 0 + lifetime 999, so they sit on the ground. When
    // an enemy contacts one, the projectile is removed from the list. We count
    // unique IDs ever seen (placed) and IDs that disappeared (triggered).
    // BETA 14.06 — also capture per-projectile first-hit time for any projectile
    // here in the same sweep (cheap — just one extra check per projectile).
    if (ws) {
      const liveKittenIds = new Set<number>();
      for (const proj of ws.projectiles) {
        // BETA 14.06: first-hit time capture. Once a projectile's hitEnemyIds
        // becomes non-empty, lock in (proj.age, proj.weaponId) and never re-count.
        if (proj.hitEnemyIds.size > 0 && !_firstHitProjIds.has(proj.id)) {
          _firstHitProjIds.add(proj.id);
          const wid = proj.weaponId || 'unknown';
          _weaponHitTimeSum.set(wid, (_weaponHitTimeSum.get(wid) ?? 0) + proj.age);
          _weaponHitTimeCount.set(wid, (_weaponHitTimeCount.get(wid) ?? 0) + 1);
        }
        // Kitten-specific tracking
        if (proj.weaponId === 'wpn_ugly_kittens') {
          liveKittenIds.add(proj.id);
          if (!_seenKittenIds.has(proj.id)) {
            _seenKittenIds.add(proj.id);
            _activeKittenIds.add(proj.id);
          }
        }
      }
      // Any kitten in _activeKittenIds but NOT in liveKittenIds = died this frame
      // Array.from() to avoid downlevelIteration TS error (same pattern as line 832).
      for (const id of Array.from(_activeKittenIds)) {
        if (!liveKittenIds.has(id)) {
          _activeKittenIds.delete(id);
          bot.currentRun.kittensTriggered = (bot.currentRun.kittensTriggered ?? 0) + 1;
        }
      }

      // BETA 14.07 — projectile chase tracking. For each projectile, compute
      // current velocity angle and compare to the previous frame. A delta > 5°
      // (0.087 rad) per frame is a "turn event" — projectile is homing/chasing
      // rather than flying straight. Cumulative count per weaponId. Folded into
      // weaponTelemetry as turn= field at finalize. User: "the bullet sometimes
      // follows an enemy when it should just shoot straight... how long did it
      // chase, and such." Boomerang (Terminator) + orbital weapons (Karen,
      // Iranian Bird drone) are EXPECTED to turn — high counts there are by
      // design. Frozen Darts / Squirter / straight projectiles SHOULDN'T turn.
      const liveProjIds2 = new Set<number>();
      for (const proj of ws.projectiles) {
        liveProjIds2.add(proj.id);
        const curAngle = Math.atan2(proj.velocityY, proj.velocityX);
        const lastAngle = _projLastAngle.get(proj.id);
        if (lastAngle !== undefined) {
          let delta = Math.abs(curAngle - lastAngle);
          if (delta > Math.PI) delta = 2 * Math.PI - delta;
          if (delta > 0.087) {
            const wid = proj.weaponId || 'unknown';
            _weaponTurnEvents.set(wid, (_weaponTurnEvents.get(wid) ?? 0) + 1);
            const chaseFrames = (_projChaseFrames.get(proj.id) ?? 0) + 1;
            _projChaseFrames.set(proj.id, chaseFrames);
            const curMax = _weaponMaxChaseDurationByProj.get(wid) ?? 0;
            if (chaseFrames > curMax) _weaponMaxChaseDurationByProj.set(wid, chaseFrames);
          }
        }
        _projLastAngle.set(proj.id, curAngle);
      }
      // GC dead projectile entries
      for (const id of Array.from(_projLastAngle.keys())) {
        if (!liveProjIds2.has(id)) {
          _projLastAngle.delete(id);
          _projChaseFrames.delete(id);
        }
      }
    }

    // BETA 14.07 — time-to-first-kill capture
    if (_timeToFirstKillSec < 0 && (state.killCount ?? 0) >= 1) {
      _timeToFirstKillSec = (state.time ?? 0) - (bot.currentRun.gameTimeStart ?? 0);
    }

    // BETA 14.07 — distance traveled (sum frame-to-frame player position deltas)
    {
      const dx = state.player.x - _lastFramePlayerX;
      const dy = state.player.y - _lastFramePlayerY;
      _distanceTraveled += Math.hypot(dx, dy);
      _lastFramePlayerX = state.player.x;
      _lastFramePlayerY = state.player.y;
    }

    // BETA 14.07 — avg enemies alive + avg kite distance (only sample during 'playing')
    {
      let aliveCount = 0;
      let nearestDist = Infinity;
      for (const e of state.enemies) {
        if (!e.alive) continue;
        aliveCount++;
        const d = Math.hypot(e.x - state.player.x, e.y - state.player.y);
        if (d < nearestDist) nearestDist = d;
      }
      _enemiesAliveSum += aliveCount;
      _enemiesAliveSamples++;
      if (nearestDist < Infinity) {
        _kiteDistanceSum += nearestDist;
        _kiteDistanceSamples++;
      }
    }
  }

  // BETA 14.07 — phase-time accumulator (runs OUTSIDE the playing-only block
  // so we capture levelup / chest / shrine time too).
  if (bot.currentRun) {
    switch (state.phase) {
      case 'playing': _phaseTimePlaying += dt; break;
      case 'levelup': _phaseTimeLevelup += dt; break;
      case 'chest_choice':
      case 'chest_spin': _phaseTimeChest += dt; break;
      case 'shrine_choice': _phaseTimeShrine += dt; break;
    }
  }

  // ── Handle game phase transitions ──
  if (state.phase !== bot.lastPhase) {
    onPhaseChange(state, bot.lastPhase, state.phase);
    bot.lastPhase = state.phase;
  }

  switch (state.phase) {
    case 'playing':
      updatePlaying(state, dt);
      break;
    case 'levelup':
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleLevelUp(state); }
      break;
    case 'shrine_choice':
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleShrineChoice(state); }
      break;
    case 'chest_spin':
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleChestChoice(state); }
      break;
    case 'chest_choice':
      // chest_choice can arrive after chest_spin OR directly — only handle once
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleChestChoiceDirect(state); }
      break;
    case 'ultra_chest':
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleUltraChest(state); }
      break;
    case 'paused':
      // Do nothing — let the user pause and unpause freely; bot resumes on its own when phase returns to 'playing'
      break;
    case 'gameover':
      handleGameOver(state);  // called every frame intentionally (restart timer)
      break;
    case 'won':
      if (!bot.phaseHandledThisEntry) { bot.phaseHandledThisEntry = true; handleWon(state); }
      break;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// PLAYING STATE — Movement, combat, and decision-making
// ═══════════════════════════════════════════════════════════════════════

function updatePlaying(state: GameState, dt: number): void {
  if (!bot) return;
  const { player, enemies } = state;

  // ── Track stats ──
  trackStats(state, dt);

  // ── Anomaly detection ──
  detectAnomalies(state, dt);

  // ── BETA 13.80: Room-time enforcement (main-dev side) ──
  // User direction: "bot needs to be forced to escape a room to the mainland if
  // they are inside a room for more than 10 seconds and continue and do not run
  // back into that same room for at least 30 seconds."
  _updateRoomTracking(state);

  // ── BETA 13.80 — hitbox near-miss tracker (bot-training side, per pathfinding handoff §6) ──
  trackNearMisses(state);

  // ── Decide current goal (thresholds come from adaptive brain) ──
  const hpPct = player.hp / player.maxHp;
  const nearbyEnemies = getEnemiesInRange(enemies, player.x, player.y, 400);
  const dangerousEnemies = getEnemiesInRange(enemies, player.x, player.y, 200);

  // BETA 17.25 — PSBOT cycle-30ak flaw #7: in-run brain adaptation.
  // Previously brain only adapted at finalizeRun. If bot was bleeding
  // damage mid-run from a tongue every 3s, it didn't realize until next
  // run. Now: every 5s, check damage-taken-per-sec vs maxHp budget. If
  // exceeded, bump fleeThreshold + kiteAggression for the rest of this
  // run (revert at finalizeRun).
  {
    const sb = bot as unknown as { _lastInRunAdapt?: number; _inRunDmgAccum?: number; _inRunDmgLast?: number };
    if (sb._lastInRunAdapt === undefined) {
      sb._lastInRunAdapt = state.time;
      sb._inRunDmgLast = bot.currentRun?.damageTaken ?? 0;
    }
    if (state.time - (sb._lastInRunAdapt ?? 0) >= 5.0) {
      const currentDmg = bot.currentRun?.damageTaken ?? 0;
      const dmgDelta = currentDmg - (sb._inRunDmgLast ?? 0);
      const dmgPerSec = dmgDelta / Math.max(1, state.time - (sb._lastInRunAdapt ?? 0));
      // BETA 20.68 — BRAIN v2: /15 → /12 per-character budget (recon #5 —
      // fragile characters stop panic-adapting after 3 hits), and the adapt
      // is now BIDIRECTIONAL. It was a one-way ratchet: only ever more flee,
      // less aggression — every long session ended with a cowardly bot
      // (Dylan: "they pause... rigid... not learning"). Comfortable sustain
      // now re-aggresses mid-run.
      const budget = (state.player.maxHp ?? 100) / 12;
      if (dmgPerSec > budget) {
        brain.fleeThreshold = Math.min(0.55, brain.fleeThreshold + 0.05);
        brain.kiteAggression = Math.max(0.2, brain.kiteAggression - 0.05);
        log(`[BOT] in-run adapt: ${dmgPerSec.toFixed(1)} dmg/s > budget ${budget.toFixed(1)}, bumping flee → ${brain.fleeThreshold.toFixed(2)}`);
      } else if (dmgPerSec < budget * 0.4) {
        brain.kiteAggression = Math.min(0.9, brain.kiteAggression + 0.04);
        brain.fleeThreshold = Math.max(0.12, brain.fleeThreshold - 0.02);
      }
      sb._lastInRunAdapt = state.time;
      sb._inRunDmgLast = currentDmg;
    }
  }

  // BETA 17.22 — PSBOT cycle-30ai B7: safe-zone tracking. When HP recovers
  // from <50% to >70%, remember this position as a safe zone. Top 5 LRU.
  // Used by calcSeekHealthVector as a fallback when no health orb exists.
  {
    const sb = bot as unknown as { _lastLowHpMark?: { x: number; y: number; hp: number; t: number } };
    if (hpPct < 0.5 && !sb._lastLowHpMark) {
      sb._lastLowHpMark = { x: player.x, y: player.y, hp: hpPct, t: state.time };
    } else if (hpPct > 0.7 && sb._lastLowHpMark) {
      // Recovered — register this spot as a safe zone
      const recoveredAmount = hpPct - sb._lastLowHpMark.hp;
      if (recoveredAmount > 0.2) {
        if (!brain.safeZones) brain.safeZones = [];
        brain.safeZones.push({
          x: player.x,
          y: player.y,
          quality: recoveredAmount,
          lastUsed: state.time,
        });
        // LRU prune to top 5 by quality
        brain.safeZones.sort((a, b) => b.quality - a.quality);
        if (brain.safeZones.length > 5) brain.safeZones.length = 5;
        saveBrain(brain);
      }
      sb._lastLowHpMark = undefined;
    }
  }

  // BETA 13.59: track currently equipped weapons each frame so the CSV weapons
  // column actually populates (was always exporting "none" because the Set was
  // initialized but never written to).
  const wsState = getWeaponSystemState();
  if (wsState?.weapons) {
    for (const w of wsState.weapons) bot.weaponsUsed.add(w.definitionId);
  }

  // E1 (enemy rebuild) — the 13.59/13.81 named-elite (timmy/caseoh) flee
  // awareness was deleted: provably always-false since the 23.39/23.55 purges
  // (ENEMY_DATABASE holds only zombie). bot.namedEliteFleeCount plumbing stays
  // untouched so the CSV column namedEliteFlees keeps emitting 0 forever
  // (PSBOT retired-column law). Phase C threat awareness will be tag-based.

  // Early-game (first 2 min): always prioritize defense regardless of brain
  const runTime = state.time ?? 0;
  const earlyGame = runTime < 120;
  // BETA 13.38 cycle-7 fix: very-early-game safety. Cycle-7 data showed roughly half
  // of all player_stuck events fired in the first 2-3s of a run — bot boxed in by
  // zombies spawning on top of it. Raise flee threshold to 0.85 in the first 5s so
  // any damage triggers relocation before the bot gets trapped at spawn.
  // BETA 13.66: extend the safety window for SOLO-WEAPON aura builds (chad_aura).
  // Cycle-5 audit: 2 of 7 Chad runs died at LV 5-9 in 41-59s with only 1 weapon
  // because chad_aura's solo DPS can't out-trade base zombie touch damage. Extend
  // the 0.85 flee window to 60s when bot has only its starter weapon AND build is
  // chad_aura. Solo aura needs help kiting until first weapon pickup compounds DPS.
  // BETA 13.66: Chad solo-aura early-game safety window (extended to 60s when only 1 weapon)
  const soloAuraEarly = bot && bot.config.buildType === 'chad_aura' && bot.weaponsUsed.size <= 1 && runTime < 60;
  // BETA 13.72: extend the same pattern to spatula_melee (close-range starter weapon —
  // 4 INSTANT + 9 EARLY deaths in 15-run dataset = 87% died <60s) and rpk_suppressor
  // (53% died <60s). Solo-weapon early game = no DPS support = swarm kills.
  // BETA 13.94: cycle-5b sponge 5/5 sub-60s, pirate 4/5 sub-60s, wizard 5/5 sub-60s
  // — 60s window not enough. Bumped spatula/fireball/tank to 90s.
  const soloMeleeEarly = bot && bot.config.buildType === 'spatula_melee' && bot.weaponsUsed.size <= 1 && runTime < 90;
  const soloRpkEarly = bot && bot.config.buildType === 'rpk_suppressor' && bot.weaponsUsed.size <= 1 && runTime < 45;
  const soloKiteEarly = bot && bot.config.buildType === 'fireball_kite' && bot.weaponsUsed.size <= 1 && runTime < 90;
  const soloTankEarly = bot && bot.config.buildType === 'tank_sustain' && bot.weaponsUsed.size <= 1 && runTime < 90;
  const soloTrinityEarly = bot && bot.config.buildType === 'holy_trinity' && bot.weaponsUsed.size <= 1 && runTime < 60;
  const veryEarlyGame = runTime < 5 || soloAuraEarly || soloMeleeEarly || soloRpkEarly || soloKiteEarly || soloTankEarly || soloTrinityEarly;
  // BETA 17.53 — Dylan: "bot should survive 10 minutes without nerfs."
  // 10-min test showed bot dying ~every 2 min. Threshold drops at the
  // 2-minute mark (earlyGame boundary) coincide with brain's late-game
  // values (sometimes as low as 0.20 flee / 0.30 heal for non-archetype
  // brains). Bot then tanks too many hits before fleeing/healing and
  // dies to swarm. Fix: floor BOTH thresholds at 0.30 flee / 0.55 heal
  // GLOBALLY (was: only during earlyGame). No matter what the brain
  // learned, bot always flees at 30%+ HP and seeks heal at 55%+ HP.
  // The 0.85 flee during veryEarlyGame still applies for the first 5 sec.
  // BETA 19.40 — Glass-cannon flee floor.
  // Dylan: "work on [killa] for now. make the bot smarter. pretty much
  // make the bot cheat."
  // Killa bot run on 19.39 died at 75s with only 20 kills + just 3 alive
  // enemies at minute 1 (telemetry: aliveSamplesPerMinute=[0,3]). Killa
  // is a glass cannon — base maxHp 100 with no defensive starter passive.
  // At 30% HP (the global flee floor) Killa is 30 HP. One zombie hit
  // does 25-50 dmg = dead. The flee trigger fires the SAME frame death
  // arrives. Bot can't escape in time.
  // Fix: detect fragile characters (maxHp ≤ 110 OR character id in
  // ['killa', 'cowboy', 'mr_silly']) and floor flee threshold at 0.50
  // instead of 0.30. Gives bot the buffer to actually disengage before
  // the next hit lands.
  const _charIdLower = (state.character?.id ?? '').toLowerCase();
  const _isFragileChar = (player.maxHp <= 110) ||
    _charIdLower === 'killa' || _charIdLower === 'cowboy' || _charIdLower === 'mr_silly';
  const _fragileFleeFloor = _isFragileChar ? 0.50 : 0.30;
  const fleeThr   = veryEarlyGame ? 0.85 : Math.max(brain.fleeThreshold, _fragileFleeFloor);
  const healThr   = Math.max(brain.healSeekThreshold, _isFragileChar ? 0.65 : 0.55);
  void earlyGame;  // BETA 17.53 — kept for telemetry but no longer drops thresholds

  // Loot orb count near player (gold + XP)
  const nearbyLootCount = countNearbyLoot(state, 600);

  // ── Shrine channel state (needed by both priority block and movement block) ──
  // BETA 17.62 — CRITICAL BUG FIX. Dylan: "this bot you have will not stay on a
  // shrine to activate it fully. why???" Root cause: state.interactTarget is
  // NEVER set to 'shrine' anywhere in the engine — only 'chest' / 'barrel' /
  // 'chick_magnet' / 'ultra_chest' / 'boss_shrine' / 'portal'. (Verified by
  // grep across all src/ files.) So `state.interactTarget === 'shrine'` has
  // been ALWAYS FALSE since the day the line was written. The whole shrine-
  // commitment system has been a no-op for regular shrines — burst-flee,
  // force-explore, HP-flee, and heal-seek have all been walking the bot off
  // shrines mid-channel since BETA 13.74.
  //
  // Real source of truth: each shrine has its own `state` field that the
  // engine sets to 'channeling' on every frame the player is within
  // SHRINE_ACTIVATION_RADIUS (100wu). Read that directly. Boss shrines still
  // use the interactTarget mechanism (they don't have a channel-state field).
  const isChannelingShrine = (
    state.interactTarget === 'boss_shrine' ||
    (state.map.roomMap.shrines ?? []).some(s => (s as { state?: string }).state === 'channeling')
  );

  // BETA 13.74/13.76: shrine commitment lock. User direction: "bot should still
  // move inside the shrine exactly to try and dodge enemies but not step off unless
  // at 2 percent health. If that is the case, ignore that shrine and move on."
  // BETA 13.74 lowered flee threshold mid-channel to 15%. BETA 13.76 lowers further
  // to 2% per the user's exact spec — bot eats almost all damage to complete the buff.
  // The actual movement now dodges INSIDE the shrine radius (see channel block below).
  const channelFleeThr = isChannelingShrine ? 0.02 : fleeThr;

  // ── BETA 17.54 / 17.59 — Burst-threat pre-flee (rebalanced again) ──
  // Live death sample at 17.58 caught: bot in force-explore mode (from a
  // stuck-flicker break-out), random escape direction walked it INTO 2
  // enemies at 45wu, took 48 dmg in 2 sec. Solo-enemy contact at LV 2
  // with 0 armor = 48-damage burst. The "weapons auto-shoot, push through"
  // logic breaks when the bot is NAKED and an enemy actually TOUCHES it.
  //
  // 17.59: add a TOUCH-THREAT trigger — any enemy within 80wu = imminent
  // contact, flee regardless. Plus tightened swarm threshold 4+ → 3+ at
  // 200wu. Plus burst-flee now preempts force-explore (the random escape
  // direction can't be allowed to override real danger).
  const armorVal = (player as { armor?: number }).armor ?? 0;
  const evasionVal = (player as { evasion?: number }).evasion ?? 0;
  const shieldVal = (player as { shield?: number }).shield ?? 0;
  // BETA 17.68 — Tactical Peanut starts with 30 shield baseline, which made
  // every Tactical bot look "well-defended" to the old defenseScore. Split:
  // armorScore (permanent mitigation) vs defenseScore (including transient
  // shield). Fragile check uses ARMOR only — shield isn't enough to survive
  // sustained pressure since it gets consumed.
  const armorScore = armorVal + (evasionVal * 0.5);
  const defenseScore = armorScore + shieldVal;
  const isFragile = armorScore < 10;
  const enemiesIn80  = getEnemiesInRange(enemies, player.x, player.y, 80);
  const enemiesIn130 = getEnemiesInRange(enemies, player.x, player.y, 130);
  const enemiesIn200 = getEnemiesInRange(enemies, player.x, player.y, 200);
  const enemiesIn400 = getEnemiesInRange(enemies, player.x, player.y, 400);
  // Touch-threat: bot is about to be HIT in <0.5 sec. Skip the touch-threat
  // when bot is on/near a power-up (shrine within 80wu, chest within 80wu)
  // so it can COMMIT to grabbing it rather than bailing at the last moment.
  const _shrineWithin80 = (state.map.roomMap.shrines ?? []).some(s => {
    const sAny = s as { x: number; y: number; activated?: boolean; used?: boolean; state?: string };
    if (sAny.activated || sAny.used) return false;
    if (sAny.state === 'boss_spawned' || sAny.state === 'spent_lit') return false;
    return Math.hypot(sAny.x - player.x, sAny.y - player.y) < 80;
  });
  const _chestWithin80 = (state.map.roomMap.chests ?? []).some(c => {
    const cAny = c as { x: number; y: number; opened?: boolean; collected?: boolean };
    if (cAny.opened || cAny.collected) return false;
    return Math.hypot(cAny.x - player.x, cAny.y - player.y) < 80;
  });
  const onPowerUp = _shrineWithin80 || _chestWithin80;
  // BETA 17.64 — defense-aware threat thresholds. Dylan: "do not stop
  // until it plays better than me." His 4:43 run hit LV 23 in 4:43;
  // bot at 17.63 only LV 10 in 10 game-min — bot was building defense
  // but fleeing every 3-enemy contact even when it could trade hits.
  // Now the swarm/touch/horde thresholds SCALE with the bot's defense
  // score. Fragile = same panic as before; well-armored = can trade
  // 1-2 hits to grab a kill / power-up.
  // touch-threat: only fires when bot is REALLY naked (defenseScore < 5)
  const touchThreat = !isChannelingShrine && !onPowerUp && enemiesIn80.length >= 1 && defenseScore < 5;
  // horde-avoid: still fragile-only (gated by isFragile = defenseScore < 10)
  const hordeNearby = !isChannelingShrine && !onPowerUp && isFragile && enemiesIn400.length >= 8;
  // Swarm threshold scales: defenseScore 0-9 → 3 enemies, 10-24 → 5, 25+ → 7.
  const swarmThreshold = defenseScore < 10 ? 3 : (defenseScore < 25 ? 5 : 7);
  // BETA 19.60 — Chad-aura offensive override. Dylan live: "chad should know
  // how powerful he is so he isnt so scared of enemies and knows he can kill
  // them. he runs away for 1 enemy in the map instead of going to the
  // shrine. so he spends his time running. its like he is still dumb."
  // Chad's aura ALWAYS DPSes nearby enemies — being IN melee range is the
  // optimal state, not a danger. With aura equipped + HP > 60%, skip ALL
  // burst-threat triggers EXCEPT the named-elite + low-HP combo (real
  // boss-near-death case). Chad walks INTO clusters, aura cooks them.
  const _wsBurst = getWeaponSystemState();
  const _chadAuraActive = !!_wsBurst?.weapons?.some(w => w.definitionId === 'chad_aura') && hpPct > 0.6;
  // BETA 19.90 — Late-game aggression. Live 91s replica watch (lvl 85,
  // 4 weapons L8-9, 9 projectile count, 14% lifesteal, 4M DPS) showed
  // bot fleeing 60% of the time despite 0 enemies threatening it.
  // When the player has CLEARLY out-leveled the spawn pressure (high lvl
  // + healthy HP + sustainable lifesteal), burst-threat triggers should
  // step aside — bot should engage, not retreat. Mirrors chad_aura
  // lawnmower override but generalized to any late-game build.
  const _lateGameAggressive = (player.level ?? 0) >= 20
    && hpPct > 0.5
    && (player.lifesteal ?? 0) >= 8;
  const burstThreat = !isChannelingShrine && !_chadAuraActive && !_lateGameAggressive && (
    touchThreat ||                                            // 17.59 / 17.64: about-to-touch flee (naked only)
    hordeNearby ||                                            // 17.63: fragile + 8+ in 400wu = horde
    (enemiesIn200.length >= swarmThreshold) ||                // 17.64: defense-scaled swarm
    (enemiesIn130.length >= 2 && defenseScore < 15)           // 17.64: tight cluster — only flee when light defense
  );

  // BETA 13.49 (PS Major Pattern 6): goal hysteresis. After a goal change, lock
  // the new goal for 300ms so the bot can't thrash between two priorities frame-
  // by-frame as HP/threat signals oscillate. flee/seek_health (HP-critical) can
  // still preempt the lock.
  if (bot.goalLockTimer > 0) bot.goalLockTimer -= dt;
  const _previousGoal = bot.currentGoal;
  const _allowSwitch = bot.goalLockTimer <= 0
    || (hpPct < channelFleeThr && dangerousEnemies.length > 0) // emergency flee always wins (lower bar mid-channel)
    || (hpPct < healThr && !isChannelingShrine)                 // emergency heal always wins
    || burstThreat;  // BETA 17.54 — burst-threat pre-flee always breaks goal lock

  // BETA 13.51: priority chain REORDERED. Old chain had `kite` above shrines/chests/
  // chick magnets — bot was kiting forever and never grabbing power-ups. User
  // observation: "the bot is trying to survive too hard." Power-ups (shrines, chests,
  // chick magnets) compound permanently and ARE the path to survival, so when HP is
  // healthy (>50%) they outrank pure defensive kiting. Kite still wins when HP is
  // moderate (35-50%) and there's no power-up reachable.
  //
  // New priority:
  //   1. flee            — HP critical
  //   2. seek_health     — HP medium-critical
  //   3. seek_shrine     — permanent buff, COMMIT (existing)
  //   4. seek_chick_magnet — vacuum every gold+XP orb on the map, MASSIVE power spike
  //   5. seek_chest      — permanent items
  //   6. seek_loot (4+ orbs no danger)
  //   7. kite            — moved DOWN; defensive but produces no power
  //   8. seek_loot (2+ orbs HP > 40%)
  //   9. seek_portal
  //  10. explore
  // BETA 15.48 — opportunistic chest detour (re-add post-rollback) + paid-first
  // preference. Cycle-17 finding: chest_paid = 0 across 255 runs even though
  // bots ended with median 491g gold. Root cause: priority chain has shrine
  // check with no distance cap. Cycle-18: my detour got bots opening chests
  // (0→1 median) but ALL of them were FREE gold chests — bot picks closest of
  // any type, gold chests are usually closer. chest_paid still 0/48 runs.
  //
  // Fix: prefer PAID goop chests over FREE gold chests within 350wu. Only fall
  // back to gold chests if no paid chest is in range. Paid chests give items
  // (real upgrade); gold chests just give gold (which would feed back into
  // more chests = circular). Slightly wider range (350 vs 300) for paid since
  // they're rarer — bot should reach for them harder.
  const veryCloseChest = (() => {
    const chests = state.map.roomMap.chests ?? [];
    let bestPaid: { x: number; y: number; d: number } | null = null;
    let bestGold: { x: number; y: number; d: number } | null = null;
    for (const c of chests) {
      if ((c as { opened?: boolean; collected?: boolean }).opened) continue;
      if ((c as { opened?: boolean; collected?: boolean }).collected) continue;
      if (!canAffordChest(state, c)) continue;
      const d = Math.hypot(c.x - player.x, c.y - player.y);
      const isGold = (c as { type?: string }).type === 'gold';
      const range = isGold ? 300 : 350; // paid chests get wider range
      if (d > range) continue;
      if (isGold) {
        if (!bestGold || d < bestGold.d) bestGold = { x: c.x, y: c.y, d };
      } else {
        if (!bestPaid || d < bestPaid.d) bestPaid = { x: c.x, y: c.y, d };
      }
    }
    return bestPaid ?? bestGold; // PAID chests preferred; gold fallback
  })();

  // ── BETA 15.57 / 17.57 — Anti-camp + corner-flicker tracking ──
  // 17.57 rewrite per Dylan: "the corner of the rooms. it will just go in
  // and sit going back and forth super fast like flickering for like 5-10
  // seconds. so much time wasted." Old detector used per-frame distance
  // (recentMoveDist) which a bouncing bot SPIKES (vx flips +200/-200 each
  // frame = 200wu/s total distance, but ~0 NET displacement). Detector
  // missed the flicker entirely. Plus 15s threshold was way too forgiving.
  //
  // New: 1-second window, NET DISPLACEMENT from window-start anchor.
  //   - Net < 50wu in 1 sec  → STUCK (flicker, wall-press, frozen). Trigger
  //     immediate break-out (forceExplore 3s, fresh target).
  //   - Net 50-100wu in 1 sec → wall-sliding but moving. No action.
  //   - Net 100wu+ → fine.
  //
  // Channeling shrine is exempt — bot SHOULD be still on a shrine.
  {
    const dx = state.player.x - bot.lastPos.x;
    const dy = state.player.y - bot.lastPos.y;
    const distMoved = Math.hypot(dx, dy);
    bot.recentMoveDist += distMoved;
    bot.recentMoveTime += dt;
    // Initialize the window-start anchor on first frame of a new window
    if (bot.windowStartPos === null) {
      bot.windowStartPos = { x: state.player.x, y: state.player.y };
    }
    // BETA 19.59 — Dylan live: "the bot is wasting so much time running into
    // the corner of a room and just sitting for a second or two." Stuck
    // window was 1.0s — bot sits visibly stuck before the detector fires.
    // Shortened to 0.4s so corner-press kicks in escape-mode 2.5× faster.
    // Threshold scales too (50wu/1s → 20wu/0.4s = same effective velocity).
    if (bot.recentMoveTime >= 0.4) {
      // Net displacement from window start (catches flicker that avgVel misses)
      const netDx = state.player.x - bot.windowStartPos.x;
      const netDy = state.player.y - bot.windowStartPos.y;
      const netDisp = Math.hypot(netDx, netDy);
      // 17.57: STUCK = net displacement < 50wu in 1 sec AND not on a shrine.
      // Channeling shrine is exempt — bot should stay still while channeling.
      // BETA 17.60 — Dylan: "it is now teleporting at the start of a run."
      // Guard: skip stuck-flicker for the first 2 seconds of a run.
      // BETA 17.87 — Dylan caught bot stuck in corner during LUDICROUS bulk
      // test (lab min-10 time-lock). Old runAge used state.time -
      // gameTimeStart, but lab time-lock clamps state.time = 600 every frame
      // AND gameTimeStart was set to 600 at run-start → runAge = 0 forever
      // → stuck detector NEVER fired during time-locked tests. Now use
      // wall-clock (bot.currentRun.startTime) which is unaffected by the lab
      // time-lock. Lab respawns still get correct runAge since startTime is
      // set once per run, not per frame.
      const startMs = bot.currentRun?.startTime ?? Date.now();
      const runAge = (Date.now() - startMs) / 1000;
      // BETA 19.59: threshold scales with window — 50wu/1.0s old = 20wu/0.4s now (same effective velocity).
      const isStuckFlicker = netDisp < 20 && !isChannelingShrine && runAge > 2.0;
      if (isStuckFlicker) {
        if (bot.forceExploreTimer <= 0) {
          // Net-stuck for 1 sec → immediate break-out (no 15-sec wait).
          // BETA 17.58 — use deadlock-escape mode (direct vector, no waypoints)
          // pointed 800wu in a random direction. Same trick BETA 14.82 used for
          // trap-cell clusters. Previous "set forceExploreTimer + null target"
          // wasn't enough — bot re-picked waypoint path that included the stuck
          // cell, returning to the same flicker spot within 1 sec.
          bot.forceExploreTimer = 3.0;  // 3s of forced exploration
          bot.campTimer = 0;
          bot.targetPos = null;          // clear any waypoint target
          const angle = Math.random() * Math.PI * 2;
          const dist = 800;
          bot.deadlockEscapeTarget = {
            x: state.player.x + Math.cos(angle) * dist,
            y: state.player.y + Math.sin(angle) * dist,
          };
          bot.deadlockEscapeUntil = (state.time ?? 0) + 3.0;
          bot.deadlockEscapeStartPos = { x: state.player.x, y: state.player.y };
          // BETA 17.58 — ALSO blacklist the goal-target. Live 17.57 console
          // showed bot escape successfully (>300wu moved), THEN goal pulls
          // bot back to same spot (seek_chest target at the stuck area).
          // Bot flickered → escaped → re-pursued → flickered again → loop.
          // Blacklist the nearest shrine/chest/barrel so bot picks a different
          // target after the escape ends.
          const stuckGoal = bot.currentGoal;
          if (stuckGoal === 'seek_shrine') _blacklistNearestShrine(state, 30_000);
          else if (stuckGoal === 'seek_chest') _blacklistNearestChest(state, 30_000);
          else if (stuckGoal === 'seek_barrel') _blacklistNearestBarrel(state, 30_000);
          log(`[STUCK-FLICKER] net=${netDisp.toFixed(0)}wu in 0.4s — escape to (${bot.deadlockEscapeTarget.x.toFixed(0)},${bot.deadlockEscapeTarget.y.toFixed(0)}) from (${state.player.x.toFixed(0)},${state.player.y.toFixed(0)}) goal=${stuckGoal}`);
        }
        // BETA 17.49 — stuck-on-unreachable-shrine detector.
        // BETA 17.50 — extended to chest + barrel goals. Live 60s sample
        // after 17.49 showed bot frozen 16.8s on seek_chest (28%) at the
        // same coords. Same pattern as shrines — fixates on unreachable
        // target, presses into wall, engine clamps movement. Blacklist
        // the relevant target type after 2 sec of low-velocity stuck.
        const sb = bot as unknown as { _stuckTargetSec?: number };
        const stuckGoal = bot.currentGoal;
        const isStuckEligible = !isChannelingShrine && (
          stuckGoal === 'seek_shrine' || stuckGoal === 'seek_chest' || stuckGoal === 'seek_barrel'
        );
        if (isStuckEligible) {
          sb._stuckTargetSec = (sb._stuckTargetSec ?? 0) + bot.recentMoveTime;
          if (sb._stuckTargetSec >= 2.0) {
            if (stuckGoal === 'seek_shrine') _blacklistNearestShrine(state, 30_000);
            else if (stuckGoal === 'seek_chest') _blacklistNearestChest(state, 30_000);
            else if (stuckGoal === 'seek_barrel') _blacklistNearestBarrel(state, 30_000);
            sb._stuckTargetSec = 0;
            bot.goalLockTimer = 0;  // force re-evaluation next frame
          }
        } else {
          sb._stuckTargetSec = 0;
        }
      } else {
        bot.campTimer = 0; // moved normally — reset counter
        const sb = bot as unknown as { _stuckTargetSec?: number };
        sb._stuckTargetSec = 0;  // bot is moving fine — reset stuck timer too
      }
      bot.recentMoveDist = 0;
      bot.recentMoveTime = 0;
      // 17.57: reset window-start anchor for next 1-second window
      bot.windowStartPos = { x: state.player.x, y: state.player.y };
    }
    if (bot.forceExploreTimer > 0) bot.forceExploreTimer -= dt;
  }

  if (_allowSwitch) {
    // BETA 17.18 — Dylan: "BOTS STAND STILL AND DONT DO THE MISSION SET OUT
    // TO THEM. THEY SHOULD BE XP HUNGRY AND LEVEL UP HUNGRY, AND CHEST
    // HUNGRY, AND FREE CHEST HUNGRY, AND SHRINE HUNGRY AND ALL BALANCED IN
    // DONT DIE." Complete rewrite of priority chain.
    //
    // BETA 17.23 — PSBOT cycle-30ak fix #6: re-wire forceExploreTimer.
    // It still increments at line 2804 but was dead-code since 17.18.
    // PSBOT data showed anti-camp firing in response to stand-still bugs
    // (NaN cascade etc, fixed in #1-#5 above). With those fixed, anti-
    // camp can be re-wired safely. It only fires after 15s of < 30wu/s
    // velocity — bot SHOULD genuinely be camping if this trips. Survival
    // gates still preempt so camp-break doesn't kill a low-HP bot.
    if (burstThreat) {
      // BETA 17.59 — burst-threat preempts force-explore. Live death sample
      // at 17.58 caught bot in force-explore mode, random escape direction
      // walked into 2 enemies, took 48 dmg. Force-explore is for breaking
      // OUT of a stuck spot; if the bot is now in a real burst situation
      // while breaking out, the burst-flee must win. Otherwise the random
      // escape direction can lead straight into a swarm.
      bot.currentGoal = 'flee';
    } else if (bot.reactiveFleeUntil > (state.time ?? 0)) {
      // BETA 19.45 — Hit-reactive flee. Bot just took damage in the last
      // 1.5s, so abandon whatever goal it was committed to and create
      // distance. trackStats sets reactiveFleeUntil = time + 1.5 on any
      // dmg event. Beats normal goal-priority but loses to burstThreat
      // (a real swarm that needs the wider flee radius).
      bot.currentGoal = 'flee';
    } else if (bot.forceExploreTimer > 0) {
      // BETA 17.58 — stuck-flicker break-out wins over normal priority
      // (but not over burst-threat — see above). Previously gated on
      // HP >= 45% AND no dangerous enemies, but the bot only gets stuck
      // WHEN enemies are pinning it, so the gate blocked every actual
      // break-out. Now: unconditional UNLESS a real burst is happening.
      bot.currentGoal = 'explore';
    } else if (hpPct < channelFleeThr && dangerousEnemies.length > 0 && !isChannelingShrine) {
      // BETA 17.45 — Dylan: "it just walked off a shrine when it was almost
      // done cause an enemy showed up. the enemy was far away. that was dumb."
      // Added `!isChannelingShrine` guard so a low-HP+enemy combo no longer
      // breaks an active channel. The bot commits to the shrine.
      // (E1 enemy rebuild — the named-elite flee preempt that sat above this
      // branch was deleted; it was always-false since the 23.39/23.55 purges.)
      bot.currentGoal = 'flee';
    } else if (hpPct < healThr && !isChannelingShrine) {
      bot.currentGoal = 'seek_health';
    } else if (isChannelingShrine) {
      bot.currentGoal = 'seek_shrine'; // never abandon channel
    } else if ((() => {
      // ─── BETA 19.48 — POWER-UP PRIORITY (proximity-aware tier chain) ─
      // Dylan live: "the fucking bots run right past barrels.... easy XP
      // and gold." First strict-tier cut (19.47) had bot always pick the
      // far shrine over a 200wu barrel — because shrine is tier-1 and
      // there's always a shrine SOMEWHERE in 1400wu range.
      //
      // New behavior:
      //   1. If ANY power-up is within CLOSE_RANGE (350wu) → grab the
      //      CLOSEST of those (free immediate value beats abstract
      //      tier preference). This is the "don't walk past free XP"
      //      fix Dylan asked for.
      //   2. Otherwise use Dylan's strict tier order — shrines first,
      //      then barrels, then chests — picking the CLOSEST in each
      //      tier among options in tier range.
      //
      // Per Dylan's spec: get shrines → barrels → chests → kill →
      // gather → level → DO NOT GET HIT.
      const px = state.player.x, py = state.player.y;
      const CLOSE_RANGE = 350;

      // BETA 19.52 — Dylan live: "sponge peanut is being fucking stupid."
      // Live evidence: bot at corner of map (9589, 7814), goal seek_shrine
      // but nearestShrine = 1506wu away, recentMoveDist = 0 (wall-stuck).
      // The BETA 19.47 range bumps (shrine 700→1400, barrel 500→900, chest
      // 800/1200→1600) pulled bots across the WHOLE MAP into corners chasing
      // distant power-ups. Reverted to tighter ranges so bot focuses on
      // power-ups in the IMMEDIATE area + kills enemies between them
      // (rather than ignoring 17 nearby enemies to trek 1500wu for one
      // shrine). Total ranges now: shrine 700wu, barrel 500wu, chest 1000wu.
      // BETA 19.57 — Dylan live: "why is chad being stupid and not b lining
      // to everything like shrines and chest." Chad at L1 gold=0 had a
      // paid chest at 446wu but canAffordChest=false → chest excluded,
      // shrine 985wu (outside 700) + barrel 1104wu (outside 500) also
      // out → all power-ups skipped → fell through to explore → moveDist=0
      // stuck. Widened: shrine 700→1200, barrel 500→800, chest 1000→1500.
      // Plus: include UNAFFORDABLE chests so bot walks toward them (earns
      // gold from enemies on the way, can afford by arrival).
      const SHRINE_RANGE = 1200;
      const BARREL_RANGE = 800;
      const CHEST_RANGE = 1500;

      // Collect every valid power-up with type + distance
      const allPowerUps: Array<{ goal: 'seek_shrine' | 'seek_barrel' | 'seek_chest'; d: number; tier: number }> = [];

      const shrines = state.map.roomMap.shrines ?? [];
      for (const s of shrines) {
        const sAny = s as { id?: string; x: number; y: number; activated?: boolean; used?: boolean; state?: string; type?: string };
        if (sAny.activated || sAny.used) continue;
        if (sAny.state === 'spent_lit' || sAny.state === 'boss_spawned' || sAny.state === 'boss_defeated') continue;
        if (sAny.type === 'boss' && brain.avoidBossShrines) continue;
        if (_isShrineBlacklisted(sAny)) continue;
        const sRoom = _roomAtPoint(state, sAny.x, sAny.y);
        if (sRoom && _isRoomOnCooldown(sRoom.id, state)) continue;
        const d = Math.hypot(sAny.x - px, sAny.y - py);
        if (d < SHRINE_RANGE) allPowerUps.push({ goal: 'seek_shrine', d, tier: 1 });
      }

      const barrels = state.map.roomMap?.barrels ?? [];
      for (const b of barrels) {
        const bAny = b as { id?: string; x: number; y: number; state?: string };
        if (bAny.state === 'broken') continue;
        if (_isBarrelBlacklisted(bAny)) continue;
        const d = Math.hypot(bAny.x - px, bAny.y - py);
        if (d < BARREL_RANGE) allPowerUps.push({ goal: 'seek_barrel', d, tier: 2 });
      }

      const chests = state.map.roomMap.chests ?? [];
      for (const c of chests) {
        if ((c as { opened?: boolean; collected?: boolean }).opened) continue;
        if ((c as { opened?: boolean; collected?: boolean }).collected) continue;
        if (_isChestBlacklisted(c as { id?: string; x?: number; y?: number })) continue;
        // BETA 19.57 — DON'T exclude unaffordable chests. Bot walks toward
        // them, picks up gold from enemies on the way, can afford by
        // arrival. Better than aimless explore (Chad live evidence).
        // canAffordChest is checked at PICKUP time by the engine, not at
        // pathing time.
        const cRoom = _roomAtPoint(state, c.x, c.y);
        if (cRoom && _isRoomOnCooldown(cRoom.id, state)) continue;
        const d = Math.hypot(c.x - px, c.y - py);
        if (d < CHEST_RANGE) allPowerUps.push({ goal: 'seek_chest', d, tier: 3 });
      }

      if (allPowerUps.length === 0) return false;

      // ─── STEP 1: close-proximity wins ──────────────────────────────
      // Any power-up within 350wu? Grab the CLOSEST one regardless of
      // tier — Dylan: "run right past barrels" = bug; barrel at 200wu
      // must beat shrine at 800wu.
      const closeOnes = allPowerUps.filter(p => p.d < CLOSE_RANGE);
      if (closeOnes.length > 0) {
        closeOnes.sort((a, b) => a.d - b.d);
        bot.currentGoal = closeOnes[0].goal;
        return true;
      }

      // ─── STEP 2: strict tier preference at distance ────────────────
      // Nothing in close range — fall back to Dylan's tier preference:
      // shrines first, then barrels, then chests. Within a tier, pick
      // the CLOSEST candidate.
      for (let tier = 1; tier <= 3; tier++) {
        const tierOnes = allPowerUps.filter(p => p.tier === tier);
        if (tierOnes.length === 0) continue;
        tierOnes.sort((a, b) => a.d - b.d);
        bot.currentGoal = tierOnes[0].goal;
        return true;
      }
      return false;
    })()) {
      // goal set inside the IIFE above
    } else if ((() => {
      // ─── PRIORITY 4: ENGAGE_MELEE (kill enemies) ──────────────────
      // Was preempting power-up seeking in BETA 19.44-19.46; Dylan's spec
      // moves it BELOW shrines/barrels/chests. Bot only engages melee
      // when no power-up is reachable AND a close enemy is in range.
      // BETA 19.44 — Dylan live: "make the bot fucking smart as fuck"
      // with the new sprite-collision spatula. Live evidence (Sponge, 60s
      // runs): bot kept enemies at 87-150wu hovering OUTSIDE arc reach
      // (65wu sprite reach + ~30wu sprite-collision tolerance ≈ 95wu real
      // hitbox) while pursuing chests/shrines. With melee weapons equipped,
      // the bot should actively ENGAGE a close enemy instead of seeking
      // loot past it — the swing on a moving target produces kills, while
      // walking around a 100wu enemy wastes the equipped weapon entirely.
      //
      // Trigger conditions (all must hold):
      //   - At least one MELEE weapon equipped (spatula_slash or wpn_dracs_fang)
      //   - Nearest enemy within MELEE_ENGAGE_RANGE (160wu — covers both
      //     spatula 65wu reach + fang 65wu reach + the bot's natural
      //     hover-distance behaviour, plus some lead to chase)
      //   - HP healthy enough to take a hit on the way in (≥ 55%)
      //   - Not currently fleeing/seeking-health (those gates run before)
      //
      // Returns true to claim the goal slot; false to fall through to the
      // existing loot-picking IIFE.
      const _ws = getWeaponSystemState();
      const _hasMelee = !!_ws?.weapons?.some(w =>
        w.definitionId === 'spatula_slash' || w.definitionId === 'wpn_dracs_fang',
      );
      // BETA 19.58 — Aura weapons (chad_aura) ALSO benefit from engage_melee:
      // they need enemies within ~150wu aura ring to proc. Without this branch,
      // Chad's aura sits cold at 0 enemies in range while bot seeks distant
      // chests. With aura equipped, engage_melee fires when nearest enemy is
      // within MELEE_ENGAGE_RANGE — bot walks into the cluster + lawnmowers.
      const _hasAura = !!_ws?.weapons?.some(w => w.definitionId === 'chad_aura');
      if (!_hasMelee && !_hasAura) return false;
      if (hpPct < 0.55) return false;
      // BETA 19.44 — Live evidence (t=55-70 samples): bot kept enemies at
      // 170-200wu — JUST outside the 160wu threshold so engage_melee never
      // fired. Bumped to 240wu so the bot starts chasing before the enemy
      // hovers at its preferred distance. Spatula sweep is wide (110°→360°)
      // + the bot move-speed (200wu/s) closes the gap in ~1s.
      const _MELEE_ENGAGE_RANGE = 240;
      // BETA 19.46 — Tightened swarm bail-out from 180→130wu. Live evidence
      // (Sponge 19.45): 7 hits at 120-150s window when 11+ enemies on screen,
      // because the 180wu swarm-bail was permissive enough to engage with
      // 2 enemies at 175wu and a 3rd zombie crashing in mid-strafe. 130wu is
      // tight to the bot's actual standoff zone (80-105wu); any 2 enemies
      // already INSIDE that zone = guaranteed multi-front damage.
      const _MELEE_SWARM_RADIUS = 130;
      // BETA 19.46 — Elite/boss + high-dmg enemies skip engage_melee entirely.
      // First cut used _DANGER_DMG = 35 — that blocked EVERY scaled normal
      // zombie (~50-57 dmg at 85s) and crashed Sponge's kill rate. Raised
      // to 75 so the gate only catches genuine elites (96-105 dmg) + bosses,
      // not normal-zombie scaling. Pair with the engine-side scaling tone-
      // down so normals stay <60 dmg through 3 min.
      const _DANGER_DMG = 75;  // any enemy whose `damage` >= this is "do not engage"
      let _closestDist = Infinity;
      let _inSwarm = 0;
      let _closestIsDanger = false;
      for (const e of enemies) {
        if (!e.alive) continue;
        const _dx = e.x - state.player.x;
        const _dy = e.y - state.player.y;
        const _d = Math.hypot(_dx, _dy);
        if (_d < _closestDist) {
          _closestDist = _d;
          // The abstracted Enemy type in types.ts doesn't expose `variant`;
          // the real EnemyInstance (enemies.ts) does. Read it via an unknown
          // cast so we still benefit from variant detection without bloating
          // the public Enemy interface.
          const _ev = (e as unknown as { variant?: string }).variant;
          _closestIsDanger = _ev === 'elite' || _ev === 'boss' || (e.damage ?? 0) >= _DANGER_DMG;
        }
        if (_d < _MELEE_SWARM_RADIUS) _inSwarm++;
      }
      if (_closestDist > _MELEE_ENGAGE_RANGE) return false;
      if (_inSwarm >= 2) return false;  // 2+ enemies in melee range = swarm, bail
      if (_closestIsDanger) return false;  // elite/boss/high-dmg = let kite/flee handle
      bot.currentGoal = 'engage_melee';
      return true;
    })()) {
      // goal set inside the IIFE above
    } else if (hpPct > 0.4 && hasNearbyChickMagnet(state) && countMapOrbs(state) >= 40) {
      // BETA 17.60 — Dylan: "its activating chick magnets when there is no
      // large amount of xp on the map. the bot needs to know how much gold
      // and xp is laying on the map so it knows its worth getting the chick
      // magnet." Bumped 15 → 40 orbs. Chick magnet should only fire when
      // there's a REAL payload to vacuum, not just a few stragglers.
      // Chick magnet — was 25, now 15 orbs (vacuum sooner).
      bot.currentGoal = 'seek_chick_magnet';
    } else if (hasAnyLootOrb(state)) {
      // XP/level-up hunger — even 1 orb anywhere on the map.
      bot.currentGoal = 'seek_loot';
    } else if (nearbyEnemies.length >= 5 || dangerousEnemies.length >= 3) {
      bot.currentGoal = 'kite';
    } else if (state.portal && state.portal.active && state.portalDiscovered) {
      bot.currentGoal = 'seek_portal';
    } else {
      bot.currentGoal = 'explore';
    }
    // BETA 17.74 — Dylan's gap #5 (floor progression). Bot stays on FL 1
    // farming because loot orbs are always present → seek_loot always
    // wins over seek_portal. But Dylan progresses through floors; FL 2+
    // has higher XP/kill + better item tiers. ELEVATE portal-seek when
    // bot has a STRONG build (LV 8+, armor 10+, 3+ weapons) AND the
    // portal is active+discovered. Overrides whatever goal was set above
    // EXCEPT survival gates (which run before this block).
    const isStrongBuild = (player.level ?? 0) >= 8
      && armorScore >= 10
      && (getWeaponSystemState()?.weapons?.length ?? 0) >= 3;
    if (
      isStrongBuild
      && state.portal && state.portal.active && state.portalDiscovered
      && bot.currentGoal !== 'flee'
      && bot.currentGoal !== 'seek_health'
      && bot.currentGoal !== 'kite'
      && !isChannelingShrine
    ) {
      bot.currentGoal = 'seek_portal';
    }
    if (bot.currentGoal !== _previousGoal) bot.goalLockTimer = 0.3;
  }

  // ── Execute goal ──
  let moveX = 0;
  let moveY = 0;

  switch (bot.currentGoal) {
    case 'flee':
      // BETA 17.54 — when burst-threat fired, use a wider radius so the flee
      // direction accounts for the actual threats. BETA 17.56: bumped 280 → 320
      // to match wider proximity triggers (named elites up to 300wu, fragile
      // single-enemy at 200wu, fragile 2-enemy at 250wu, swarm 200wu).
      ({ moveX, moveY } = calcFleeVector(state, burstThreat
        ? getEnemiesInRange(enemies, player.x, player.y, 320)
        : dangerousEnemies));
      break;
    case 'kite':
      ({ moveX, moveY } = calcKiteVector(state, nearbyEnemies));
      break;
    case 'seek_health':
      ({ moveX, moveY } = blendAvoid(state, calcSeekHealthVector(state), nearbyEnemies));
      break;
    case 'seek_chest':
      ({ moveX, moveY } = blendAvoid(state, calcSeekChestVector(state), nearbyEnemies));
      break;
    case 'seek_shrine':
      ({ moveX, moveY } = blendAvoid(state, calcSeekShrineVector(state), nearbyEnemies));
      break;
    case 'seek_portal':
      ({ moveX, moveY } = blendAvoid(state, calcSeekPortalVector(state), nearbyEnemies));
      break;
    case 'seek_loot':
      // seek_loot already considers enemies internally — leave unwrapped.
      ({ moveX, moveY } = calcSeekLootVector(state, nearbyEnemies));
      break;
    case 'seek_chick_magnet':
      ({ moveX, moveY } = blendAvoid(state, calcSeekChickMagnetVector(state), nearbyEnemies));
      break;
    case 'engage_melee': {
      // BETA 19.44 — Smart melee engagement. Live evidence (3 hits in 25.8s
      // on Sponge): walking straight at the enemy puts the bot at 8-42wu
      // distance INSIDE the contact-damage radius (~36wu). The spatula
      // swing reach is ~110wu — there's a 60+wu "kill zone" where bot can
      // hit enemy but enemy CAN'T hit bot. Park there.
      //
      // Distance behaviour:
      //   d > MELEE_KEEP_MAX (95wu)  →  approach (close the gap so swing lands)
      //   d in keep window (50–95wu) →  strafe perpendicular (let the swing
      //                                 sweep + don't drift into contact range)
      //   d < MELEE_KEEP_MIN (50wu)  →  back away (enemy in contact range)
      //
      // Constants chosen against:
      //   contact-damage radius ≈ player.size(16) + enemy.hitbox(20) = 36wu
      //   spatula effective reach ≈ 65wu sprite-radius + 27wu sprite-radius
      //                            + ~20wu enemy-hitbox = ~112wu
      //
      // Live evidence (54.7s Sponge run, BETA 19.44 first cut): 4 hits all
      // at recorded dist 46-55wu — right at the previous MIN=50 edge. By the
      // time bot reads enemy at dist=49, the zombie moving 80wu/s has closed
      // ~24wu since last sample → already in contact. Two fixes:
      //   1. MIN bumped 50 → 80wu (44wu buffer above contact-damage)
      //   2. Distance check uses PREDICTED enemy position 0.3s ahead via
      //      _projectedEnemyPos — so we start backing away BEFORE the
      //      zombie has actually closed to MIN.
      // BETA 19.58 — Aura char (Chad) wants enemies INSIDE the 130-150wu aura
      // ring, not at melee standoff distance. When chad_aura is equipped,
      // collapse the keep window to (0, 50) — bot walks INTO the enemy, lets
      // the aura cook them. Spatula/Fang keeps the (80, 105) standoff so it
      // doesn't trade contact damage.
      const _wsEM = getWeaponSystemState();
      const _isAuraChar = !!_wsEM?.weapons?.some(w => w.definitionId === 'chad_aura');
      const MELEE_KEEP_MIN = _isAuraChar ? 0  : 80;
      const MELEE_KEEP_MAX = _isAuraChar ? 50 : 105;
      let _nearestE: { x: number; y: number; dist: number } | null = null;
      let _bestD = Infinity;
      for (const e of enemies) {
        if (!e.alive) continue;
        // Predicted enemy position 0.3s ahead — anticipates the close
        const { px: epx, py: epy } = _projectedEnemyPos(e);
        const _dx = epx - player.x;
        const _dy = epy - player.y;
        const _d = Math.hypot(_dx, _dy);
        if (_d < _bestD) { _bestD = _d; _nearestE = { x: epx, y: epy, dist: _d }; }
      }
      if (_nearestE) {
        const dx = _nearestE.x - player.x;
        const dy = _nearestE.y - player.y;
        const dist = _nearestE.dist || 1;
        const nx = dx / dist;
        const ny = dy / dist;
        if (dist > MELEE_KEEP_MAX) {
          // Too far — close the gap straight in (probeMove respects walls)
          const _vec = probeMove(state, player.x, player.y, _nearestE.x, _nearestE.y);
          moveX = _vec.moveX;
          moveY = _vec.moveY;
        } else if (dist < MELEE_KEEP_MIN) {
          // Too close — back away in line from enemy
          moveX = -nx;
          moveY = -ny;
        } else {
          // In kill zone — strafe perpendicular so swing sweeps across the
          // enemy without drifting into contact range. Pick rotation that
          // points AWAY from any second-nearest enemy to avoid sandwich.
          let perpSign = 1;
          let secondNearest: { dx: number; dy: number } | null = null;
          let secondD = Infinity;
          for (const e of enemies) {
            if (!e.alive) continue;
            const edx = e.x - player.x;
            const edy = e.y - player.y;
            const ed = Math.hypot(edx, edy);
            if (ed < 0.1 || ed === dist) continue;
            if (ed < secondD) { secondD = ed; secondNearest = { dx: edx, dy: edy }; }
          }
          if (secondNearest) {
            // Strafe direction (perpendicular CCW = -ny, nx): if it points
            // INTO the second enemy (positive dot product), flip to CW.
            const ccwX = -ny, ccwY = nx;
            const dot = ccwX * secondNearest.dx + ccwY * secondNearest.dy;
            perpSign = dot > 0 ? -1 : 1;
          }
          moveX = -ny * perpSign;
          moveY = nx * perpSign;
        }
      } else {
        ({ moveX, moveY } = blendAvoid(state, calcExploreVector(state, dt), nearbyEnemies));
      }
      break;
    }
    case 'seek_barrel': {
      // BETA 17.48 — head toward nearest un-broken barrel. Bot walks INTO
      // the barrel hitbox; engine handles the "player touches barrel"
      // break in updateGame. Standard avoid-blend so enemies still nudge.
      const _bar = _nearestBarrel(state);
      if (_bar) {
        const _bdx = _bar.x - player.x;
        const _bdy = _bar.y - player.y;
        const _bdist = Math.hypot(_bdx, _bdy) || 1;
        const _vec = probeMove(state, player.x, player.y, _bar.x, _bar.y);
        void _bdist; void _bdx; void _bdy;
        ({ moveX, moveY } = blendAvoid(state, _vec, nearbyEnemies));
      } else {
        ({ moveX, moveY } = blendAvoid(state, calcExploreVector(state, dt), nearbyEnemies));
      }
      break;
    }
    case 'explore':
    default:
      // BETA 17.09 — explore was previously enemy-blind. Now blends in
      // an avoid term so the bot doesn't wander straight into zombies
      // while exploring a fresh room.
      ({ moveX, moveY } = blendAvoid(state, calcExploreVector(state, dt), nearbyEnemies));
      break;
  }

  // ── BETA 19.53: UNIVERSAL projectile dodge ──
  // Was previously only inside blendAvoid → ONLY some goals had dodge.
  // Live death (Killa BETA 19.52, t=183-185): boss Timmy threw penguin from
  // 212wu while bot was in `flee` goal → 99 dmg landed → another 99 dmg
  // 2s later → bot died despite having "dodge" code in the codebase.
  // Now: every goal's final move vector passes through applyProjectileDodge
  // so flee/kite/engage_melee all benefit. If an incoming projectile will
  // hit within 0.5s + 250wu, override the move with a perpendicular dodge.
  {
    const _afterDodge = applyProjectileDodge(state, { moveX, moveY });
    moveX = _afterDodge.moveX;
    moveY = _afterDodge.moveY;
  }

  // ── BETA 13.80: Room-escape force override ──
  // If the bot has been inside a single room past the time limit, override the
  // goal vector with a head-toward-nearest-doorway vector. Survival goals
  // (flee / seek_health) still preempt the override so the bot can still escape
  // if it's about to die. The override clears automatically once the bot has
  // physically left the room (handled in _updateRoomTracking on room change).
  if (
    bot.forceEscapeRoomId !== null &&
    bot.currentGoal !== 'flee' &&
    bot.currentGoal !== 'seek_health'
  ) {
    const escape = _calcRoomEscapeVector(state);
    if (escape.moveX !== 0 || escape.moveY !== 0) {
      // Hard override (90/10 blend toward escape) — strong enough to reliably
      // exit but still respects nudges from the doorway magnet / wall probes
      // below so the bot doesn't punch into a corner.
      moveX = escape.moveX * 0.9 + moveX * 0.1;
      moveY = escape.moveY * 0.9 + moveY * 0.1;
      const len = Math.hypot(moveX, moveY) || 1;
      moveX /= len; moveY /= len;
    }
  }

  // ── Flow Field Pathfinding (DELETED in BETA 13.46, per PS Major guide) ──
  // The flow field is built FROM the player outward — at the player's own cell
  // the flow vector is (0,0) or points BACKWARD due to bilinear interp on stale
  // adjacent cells. Bot was vibrating because this block kept reading useless
  // direction data and overriding good goal vectors with garbage.
  // The bot already has 4 working obstacle-avoidance systems below this point:
  //   1. Doorway magnet (snaps toward gap centers)
  //   2. Green zone redirect (pushes off corners)
  //   3. Room center pull (reorients in open space)
  //   4. probeMove() — 11-angle wall avoidance probe used by movement vectors
  // None of those need the flow field. Flow field stays available for ENEMIES
  // (whose getFlowDirection lookup at their own cell DOES point at the player).
  // ── Doorway magnet (BETA 13.27, Manus): if the straight-line path from current
  //    position toward our movement target would clip a wall corner, redirect to
  //    the doorway center. Checked BEFORE the older gap-snap and green-zone hooks.
  {
    const rooms = state.map.roomMap?.rooms ?? [];
    if (rooms.length > 0) {
      const lookahead = 200; // px ahead in current move direction = synthetic target
      const tx = state.player.x + moveX * lookahead;
      const ty = state.player.y + moveY * lookahead;
      const magnet = getDoorwayMagnet(rooms, state.player.x, state.player.y, tx, ty);
      if (magnet) {
        // BETA 17.52 — OLD-CODE FIX. Was: raw `moveX = dx / len`. Now:
        // probeMove-wrapped so the doorway magnet respects walls — if
        // the gap center is behind a wall the bot can't reach, probeMove
        // deflects to a walkable angle instead of walking into wall.
        const probe = probeMove(state, state.player.x, state.player.y, magnet.x, magnet.y);
        moveX = probe.moveX;
        moveY = probe.moveY;
      }
    }
  }

  // ── Doorway magnet — snap movement through gap centers when nearby ──
  // When the bot is within 300px of any gap center AND heading roughly toward it,
  // strongly override direction toward the gap center (88/12 blend).
  // This prevents clipping doorway edges when approaching at an angle.
  {
    const rooms = state.map.roomMap?.rooms ?? [];
    let nearestGap: { x: number; y: number } | null = null;
    let nearestDist = 300; // enlarged from 220 — activates earlier
    for (const room of rooms) {
      for (const gap of (room.gaps ?? [])) {
        const gx = gap.x + gap.w / 2;
        const gy = gap.y + gap.h / 2;
        const d = Math.hypot(gx - player.x, gy - player.y);
        if (d < nearestDist) {
          // BETA 13.48 (PS Major Pattern 3): if we're already AT the gap center,
          // don't snap-back. Bot was oscillating right inside doorways because the
          // first magnet pulled in and this one snapped back to dead center.
          if (d < 40) continue;
          // Only snap if goal direction has at least a small component toward this gap
          const gapAngle = Math.atan2(gy - player.y, gx - player.x);
          const dotGoal = moveX * Math.cos(gapAngle) + moveY * Math.sin(gapAngle);
          if (dotGoal > 0.20) { // goal is within ~78° of gap direction (slightly wider)
            nearestDist = d;
            nearestGap = { x: gx, y: gy };
          }
        }
      }
    }
    if (nearestGap && bot) {
      // Redirect probeMove to aim straight through the gap center
      const snapped = probeMove(state, player.x, player.y, nearestGap.x, nearestGap.y);
      // Blend: 88% snap toward gap center, 12% original goal — strong pull
      const blendX = snapped.moveX * 0.88 + moveX * 0.12;
      const blendY = snapped.moveY * 0.88 + moveY * 0.12;
      const len = Math.sqrt(blendX * blendX + blendY * blendY) || 1;
      moveX = blendX / len;
      moveY = blendY / len;
    }
  }

  // ── Navigation (3-zone collision system, BETA 13.29) ──
  // The doorway magnet block above already snaps the bot to a doorway center
  // when its straight-line path would clip a wall corner. Here we layer the
  // green-zone redirect and a gentle room-center pull on top.
  let greenPush: { dx: number; dy: number } | null = null;
  const greenTarget = getGreenZoneRedirect(state, state.player.x, state.player.y);
  if (greenTarget) {
    const dx = greenTarget.x - state.player.x;
    const dy = greenTarget.y - state.player.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    greenPush = { dx: dx / len, dy: dy / len };
  }
  // Gentle room-center pull — prevents drifting into corners in the first place
  const centerPull = getRoomCenterPull(state, moveX, moveY);

  const triPush = greenPush;
  if (triPush) {
    // BETA 13.48 (PS Major Pattern 4): 85/15 → 95/5. Bot was getting stuck deep in
    // green corners — old blend let the goal vector keep dragging it back into the
    // wall while probeMove couldn't find any clear angle. 95/5 = nearly pure escape.
    const blendX = triPush.dx * 0.95 + moveX * 0.05;
    const blendY = triPush.dy * 0.95 + moveY * 0.05;
    const len = Math.sqrt(blendX * blendX + blendY * blendY) || 1;
    moveX = blendX / len;
    moveY = blendY / len;
  } else if (centerPull) {
    // Gentle nudge: 20% center pull / 80% goal direction (reduced from 30/70)
    const blendX = centerPull.dx * 0.20 + moveX * 0.80;
    const blendY = centerPull.dy * 0.20 + moveY * 0.80;
    const len = Math.sqrt(blendX * blendX + blendY * blendY) || 1;
    moveX = blendX / len;
    moveY = blendY / len;
  }

  // ── BETA 13.56: drift-circle b-line failsafe (replaces 13.55 stuck-escape) ──
  // Per user spec: not just stuck-in-spot, but stayed within a circle slightly larger
  // than a shrine (175 wu — shrine activation radius is 100) for >10 seconds. When
  // triggered, b-line straight toward the closest meaningful goal target (chest /
  // shrine / chick magnet / loot orb) for up to 3 seconds, then resume normal goal
  // logic. Survival goals (flee / seek_health) preempt the override so the bot
  // never ignores HP-critical situations to chase a chest.
  const tNow = state.time ?? 0;
  const survivalCriticalGoal = bot.currentGoal === 'flee' || bot.currentGoal === 'seek_health';
  if (!isChannelingShrine && !survivalCriticalGoal) {
    // BETA 13.59: cycle-14 run #4 logged 65 anomalies but survived 377s — failsafe
    // was firing too often when bot was actually fine. Loosen both thresholds.
    const DRIFT_RADIUS = 200;          // was 175 — wider tolerance for normal kiting
    const DRIFT_TIMEOUT_SEC = 12;      // was 10 — extra 2s of grace before b-line
    const BLINE_DURATION_SEC = 3;
    const BLINE_REACH_DIST = 60;

    // Drift anchor: re-anchor whenever the bot moves outside the circle
    if (!bot.driftCenter) {
      bot.driftCenter = { x: state.player.x, y: state.player.y };
      bot.driftStartTime = tNow;
    } else {
      const ddx = state.player.x - bot.driftCenter.x;
      const ddy = state.player.y - bot.driftCenter.y;
      if (ddx * ddx + ddy * ddy > DRIFT_RADIUS * DRIFT_RADIUS) {
        bot.driftCenter = { x: state.player.x, y: state.player.y };
        bot.driftStartTime = tNow;
        // Movement out of the circle clears any active b-line — bot is unstuck on its own
        bot.blineUntil = 0;
        bot.blineTarget = null;
      }
    }

    // Trigger a b-line if drifted in this circle for > 10s and not already b-lining
    if (bot.blineUntil === 0 && tNow - bot.driftStartTime > DRIFT_TIMEOUT_SEC) {
      const target = findBestBlineTarget(state);
      if (target) {
        bot.blineTarget = target;
        bot.blineUntil = tNow + BLINE_DURATION_SEC;
        bot.blineTriggerCount++; // BETA 13.59: track invisible behavior
      } else {
        // No good target on the map — just reset the drift timer so we don't fire
        // every frame. Goal logic will eventually unblock or another b-line fires.
        bot.driftStartTime = tNow;
      }
    }

    // While b-lining, override moveX/moveY toward target.
    // BETA 17.52 — OLD-CODE FIX (Dylan: "old code making bot dumb").
    // Was: raw `moveX = dx / len; moveY = dy / len` — bypassed probeMove +
    // blendAvoid entirely, so the bot walked INTO WALLS toward the
    // random b-line target. Now: probeMove-wrapped so walls deflect
    // the b-line direction the same way they deflect goal movement.
    if (bot.blineUntil > 0 && bot.blineTarget) {
      if (tNow < bot.blineUntil) {
        const dx = bot.blineTarget.x - state.player.x;
        const dy = bot.blineTarget.y - state.player.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > BLINE_REACH_DIST) {
          const probe = probeMove(state, state.player.x, state.player.y, bot.blineTarget.x, bot.blineTarget.y);
          moveX = probe.moveX;
          moveY = probe.moveY;
        } else {
          // Reached target — clear b-line + drift, resume normal logic
          bot.blineUntil = 0;
          bot.blineTarget = null;
          bot.driftCenter = null;
        }
      } else {
        // Burst expired — clear and let normal goal logic re-evaluate
        bot.blineUntil = 0;
        bot.blineTarget = null;
        bot.driftCenter = null;
      }
    }
  } else {
    // Survival-critical goal active OR channeling — pause drift tracking so the bot
    // doesn't trigger a b-line right when flee/seek_health takes over.
    bot.driftCenter = null;
    bot.blineUntil = 0;
    bot.blineTarget = null;
  }

  // ── BETA 13.76: shrine-channel "dodge inside" instead of freeze ──
  // User direction: "bot should still move inside the shrine to dodge enemies but
  // not step off unless at 2 percent health. If 2% HP, ignore that shrine and move
  // on to the next task." Old code froze the bot entirely (movement was assumed to
  // cancel the channel). Reality (verified in shrines.ts): channel only resets
  // when player LEAVES the activation radius. So dodge inside is fine.
  //
  // Strategy: while channeling, compute an avoid-vector from the nearest enemy
  // and apply it BUT clamp the resulting position to stay within radius - safety.
  // BETA 17.23 — PSBOT cycle-30ak CRITICAL fix #4: boss_shrine dead-code
  // freeze. engine.ts ONLY assigns interactTarget='boss_shrine' (verified
  // by grep — never 'shrine'). But this block enters whenever
  // interactTarget is either string, then looks for a shrine with
  // state==='channeling' which boss shrines NEVER enter (they go
  // idle → boss_spawned per shrines.ts:330). Result: foundShrine = false
  // every frame near a boss shrine → applyMovement(0,0) → freeze until
  // 'e' interaction triggers. Fix: only enter the dodge-inside block if
  // a real channeling shrine exists. Otherwise fall through to normal
  // movement.
  const _channelingShrineExists = isChannelingShrine && (state.map.roomMap.shrines ?? [])
    .some(sh => (sh as { state?: string }).state === 'channeling');
  if (_channelingShrineExists) {
    const SHRINE_INNER_SAFETY = 60; // wu — stay this far from edge to avoid accidentally stepping out
    // Find the shrine we're channeling on (closest one within activation radius)
    let shrineX = state.player.x;
    let shrineY = state.player.y;
    let foundShrine = false;
    for (const sh of (state.map.roomMap.shrines ?? [])) {
      if ((sh as { state?: string }).state !== 'channeling') continue;
      shrineX = sh.x; shrineY = sh.y;
      foundShrine = true;
      break;
    }
    if (foundShrine && nearbyEnemies.length > 0) {
      // Compute avoid-vector from nearest enemy (centroid weighted by closeness)
      let avX = 0, avY = 0, total = 0;
      for (const e of nearbyEnemies.slice(0, 5)) { // 5 closest
        const dx = state.player.x - e.x;
        const dy = state.player.y - e.y;
        const d = Math.hypot(dx, dy) || 1;
        const w = 1 / d;
        avX += (dx / d) * w;
        avY += (dy / d) * w;
        total += w;
      }
      if (total > 0) {
        avX /= total; avY /= total;
        const len = Math.hypot(avX, avY) || 1;
        avX /= len; avY /= len;
        // Project: where would we end up after this frame's move?
        const projDist = state.player.speed * dt;
        const futureX = state.player.x + avX * projDist;
        const futureY = state.player.y + avY * projDist;
        // Distance from shrine center after the move
        const futureDist = Math.hypot(futureX - shrineX, futureY - shrineY);
        const safeRadius = SHRINE_INNER_SAFETY; // tight: only roam in inner 60wu of 100wu radius
        if (futureDist > safeRadius) {
          // Would leave safe zone — instead, blend with center-pull to stay inside
          const cdx = shrineX - state.player.x;
          const cdy = shrineY - state.player.y;
          const cdLen = Math.hypot(cdx, cdy) || 1;
          // 70% center-pull, 30% avoid
          avX = (cdx / cdLen) * 0.7 + avX * 0.3;
          avY = (cdy / cdLen) * 0.7 + avY * 0.3;
          const blendLen = Math.hypot(avX, avY) || 1;
          avX /= blendLen; avY /= blendLen;
        }
        applyMovement(state, avX, avY);
      } else {
        applyMovement(state, 0, 0);
      }
    } else {
      applyMovement(state, 0, 0); // no enemies near — just stand still
    }
    simulateKey(state, 'e'); // keep holding E to interact
  } else {
    // ── Normal movement ──
    applyMovement(state, moveX, moveY);
    // ── Auto-interact (E key) when near interactables ──
    // BETA 17.73 — threat-aware chest opens. Chest spin locks the bot
    // stationary for 3.5 seconds. If 4+ enemies are converging within
    // 200wu and the bot doesn't have armor 15+, that's a guaranteed
    // burst-death scenario. Suppress the 'e' press AND temp-blacklist
    // the chest for 3 sec so the bot's priority chain falls to kite/
    // explore. Once cluster thins or bot relocates, blacklist expires.
    if (state.interactTarget === 'chest' && enemiesIn200.length >= 4 && armorScore < 15) {
      // Don't open — temp-blacklist and let bot kite
      _blacklistNearestChest(state, 3_000);
    } else if (state.interactTarget) {
      simulateKey(state, 'e');
    }

    // ── BETA 22.47 — BOT DASH ESCAPE (Dylan: "bots do not know about dash yet...
    // so they can use dash now"). When genuinely in danger — fleeing, low HP, or
    // tightly swarmed — and a dash charge is up, tap Shift to blink out with the
    // dash i-frames. The engine reads 'shift' as the dash input and fires it in the
    // bot's current move direction (applyMovement above already set the flee keys);
    // the 22.47 engine change removed the old `!isBotEnabled()` block that hard-
    // refused bot dashes. Throttled by _lastDashT so it doesn't burn both charges
    // in one frame. ADDITIVE hook — does not touch target scoring / movement vectors.
    const _botDashW = bot as unknown as { _lastDashT?: number };
    const _dashHpPct = player.maxHp > 0 ? player.hp / player.maxHp : 1;
    const _closestE = nearbyEnemies.length
      ? Math.hypot(player.x - nearbyEnemies[0].x, player.y - nearbyEnemies[0].y)
      : Infinity;
    const _wantDash = bot.currentGoal === 'flee' || _dashHpPct < 0.30 || (enemiesIn200.length >= 5 && _closestE < 75);
    if (
      _wantDash &&
      (player.dashCharges ?? 0) > 0 &&
      (player.dashActiveTimer ?? 0) <= 0 &&
      state.time - (_botDashW._lastDashT ?? -10) > 1.4
    ) {
      simulateKey(state, 'shift');
      _botDashW._lastDashT = state.time;
    }
  }

  // ── Point mouse at most DANGEROUS enemy (BETA 17.24 — PSBOT cycle-30ak
  // intelligence flaw #1). Was: `nearbyEnemies[0]` = closest by distance.
  // Now: score by `damage*5 + isBoss*200 + isElite*80 + (maxHp - hp)*1 -
  // distance*0.5`. Picks a low-HP boss over a full-HP zombie.
  // BETA 17.24 flaw #4: aim LEADS the target — use _projectedEnemyPos
  // (existing 0.7s extrapolator) so slow-projectile weapons (Viking Axe
  // speed 380, Blunderbuss) actually hit moving zombies.
  if (nearbyEnemies.length > 0) {
    let bestScore = -Infinity;
    let bestTarget: Enemy = nearbyEnemies[0];
    for (const e of nearbyEnemies) {
      if (!e.alive) continue;
      const dx = e.x - player.x, dy = e.y - player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const eAny = e as unknown as {
        damage?: number;
        currentHP?: number;
        maxHP?: number;
        variant?: string;
        definitionId?: string;
      };
      const damage = eAny.damage ?? 10;
      const maxHp = eAny.maxHP ?? 50;
      const curHp = eAny.currentHP ?? maxHp;
      // E1 (enemy rebuild) — dead caseoh_mega/caseoh/timmy id checks deleted
      // (purged ids can never appear); variant-based scoring stays.
      const isBoss = eAny.variant === 'boss';
      const isElite = eAny.variant === 'elite';
      // BETA 17.27 — PSBOT cycle-30an P0 regression fix. The 17.26 universal
      // distance penalty 1.0 + near-kill 50 was meant for snipers but broke
      // Sponge -60% / Killa -39% / Mr Silly -15% by making tank+AoE builds
      // shoot at face-zombies and ignore distant elites. Now
      // archetype-conditional: only peanut_sniper (LeBron, Tactical) gets
      // the sniper-tuned values (+17% LeBron in 17.26 proved that's right
      // for actual snipers). Everything else reverts to the 17.24-era
      // values that worked for 7/10 characters.
      // PSBOT originally suggested rpk_suppressor (Killa) be sniper-treated
      // but cycle-30an proved Killa with sniper values regressed -39%.
      // RPK is mid-range AoE-burst, NOT sniper. Keep it on default values.
      const isSniperBuild = bot?.config.buildType === 'peanut_sniper';
      const distPenalty = isSniperBuild ? 1.0 : 0.5;
      const nearKillBonus = (curHp > 0 && curHp <= maxHp / 8)
        ? (isSniperBuild ? 50 : 20)
        : 0;
      // BETA 22.21 (Dylan): the bot was aiming at high-value enemies BEHIND WALLS
      // its weapon can't shoot through, ignoring ones it could actually kill. Heavily
      // deprioritize any enemy the bullet can't reach — same 16wu wall-step the
      // projectile wall-kill uses (types.hasLineOfSight). If EVERY enemy is walled
      // off they all share the penalty, so it still picks the best one to approach.
      const _losPenalty = hasProjectileLOS(state, player.x, player.y, e.x, e.y) ? 0 : -10000;
      const score =
        damage * 5 +
        (isBoss ? 300 : 0) +
        (isElite ? 100 : 0) +
        (maxHp - curHp) * 1 +
        nearKillBonus +
        -dist * distPenalty +
        _losPenalty;
      if (score > bestScore) {
        bestScore = score;
        bestTarget = e;
      }
    }
    // BETA 17.26 — PSBOT cycle-30am: weapon-specific aim leading. Was:
    // constant `projSpeed=600` which over-led fast weapons (Revolver speed
    // 900 → projectile passed BEHIND moving zombie).
    // BETA 17.29 — PSBOT cycle-30ao P0: Chad collapsed -60% / Sponge -57%
    // because the leading was applied universally — chad_aura is an AURA
    // with no projectile (falls to 600 default) and spatula_slash is melee.
    // Aim leading on aura/melee weapons makes the bot aim AHEAD of moving
    // targets → bot moves PAST the target instead of into kill range. Now:
    // walk active weapons, pick the highest-damage one ("main weapon"). If
    // it has a projectile.speed, use that. If no projectile (aura/melee),
    // disable leading entirely (leadT=0, aim at current target position).
    const targetDist = Math.hypot(bestTarget.x - player.x, bestTarget.y - player.y);
    let projSpeed = 600; // fallback for first few frames before any damage logged
    let isMeleeOrAura = false;
    try {
      const ws = getWeaponSystemState();
      if (ws?.weapons) {
        let maxDmg = -1;
        let mainDefSpeed: number | undefined;
        let mainDefIsMeleeOrAura = false;
        for (const w of ws.weapons) {
          if (w.totalDamageDealt > maxDmg) {
            const def = _getWeaponDef(w.definitionId);
            if (def) {
              maxDmg = w.totalDamageDealt;
              const sp = def.projectile?.speed;
              if (sp && sp > 0) {
                mainDefSpeed = sp;
                mainDefIsMeleeOrAura = false;
              } else {
                mainDefSpeed = undefined;
                mainDefIsMeleeOrAura = true;
              }
            }
          }
        }
        if (mainDefSpeed && mainDefSpeed > 0) projSpeed = mainDefSpeed;
        isMeleeOrAura = mainDefIsMeleeOrAura;
      }
    } catch { /* fallback to projSpeed=600, no melee skip */ }
    const leadT = isMeleeOrAura ? 0 : Math.min(0.8, targetDist / projSpeed);
    const { px, py } = _projectedEnemyPos(bestTarget, leadT);
    state.mouse.worldX = px;
    state.mouse.worldY = py;
    state.mouse.down = true; // Auto-fire
  } else {
    state.mouse.down = false;
  }

  // ── Track room visits ──
  if (player.currentRoom) {
    bot.roomsVisitedSet.add(String(player.currentRoom));
  }
}

// ─── BETA 13.80: Room-time enforcement helpers ──────────────────────
// Limits how long the bot is allowed inside a room before being force-escaped,
// and locks that room out of goal-targeting for a cooldown after exit. Built
// as a per-frame side-effect so `_currentRoomId` stays in sync with reality.

const ROOM_TIME_LIMIT_SEC = 10;
const ROOM_REENTER_COOLDOWN_SEC = 30;

interface RoomLite { id: string; x: number; y: number; size: number;
  gaps: ReadonlyArray<{ x: number; y: number; w: number; h: number; dir: string }> }

function _findContainingRoom(state: GameState): RoomLite | null {
  const rooms = (state.map.roomMap?.rooms ?? []) as ReadonlyArray<RoomLite>;
  const px = state.player.x, py = state.player.y;
  for (const r of rooms) {
    if (px >= r.x && px < r.x + r.size && py >= r.y && py < r.y + r.size) return r;
  }
  return null;
}

function _updateRoomTracking(state: GameState): void {
  if (!bot) return;
  const runTime = state.time ?? 0;
  const room = _findContainingRoom(state);
  const newId = room?.id ?? null;

  // Detect a room change (enter, exit, or A→B transition).
  if (newId !== bot.currentRoomId) {
    // If we were inside a room and now we're not (or in a different one), it
    // counts as exiting the previous one — start its re-entry cooldown.
    if (bot.currentRoomId !== null) {
      bot.roomCooldowns.set(bot.currentRoomId, runTime + ROOM_REENTER_COOLDOWN_SEC);
      // If we exited the room we were force-escaping, the escape is complete.
      if (bot.forceEscapeRoomId === bot.currentRoomId) bot.forceEscapeRoomId = null;
    }
    bot.currentRoomId = newId;
    bot.currentRoomEnteredAt = newId !== null ? runTime : 0;
  }

  // If we've been in the same room past the limit, mark it for force-escape.
  if (
    bot.forceEscapeRoomId === null &&
    bot.currentRoomId !== null &&
    runTime - bot.currentRoomEnteredAt > ROOM_TIME_LIMIT_SEC
  ) {
    bot.forceEscapeRoomId = bot.currentRoomId;
    bot.roomEscapeCount++;
  }

  // Garbage-collect expired cooldowns to keep the map tiny.
  if (bot.roomCooldowns.size > 0) {
    const expired: string[] = [];
    bot.roomCooldowns.forEach((exp, rid) => { if (exp <= runTime) expired.push(rid); });
    for (const rid of expired) bot.roomCooldowns.delete(rid);
  }
}

/**
 * Returns true if the room is currently on a re-entry cooldown — used by the
 * goal selector to filter out chests/shrines/orbs that would force the bot
 * back into a freshly-escaped room.
 */
function _isRoomOnCooldown(roomId: string | null, state: GameState): boolean {
  if (!bot || roomId === null) return false;
  const runTime = state.time ?? 0;
  const exp = bot.roomCooldowns.get(roomId);
  return exp !== undefined && runTime < exp;
}

/** Find which room (if any) contains a given world point. */
function _roomAtPoint(state: GameState, x: number, y: number): RoomLite | null {
  const rooms = (state.map.roomMap?.rooms ?? []) as ReadonlyArray<RoomLite>;
  for (const r of rooms) {
    if (x >= r.x && x < r.x + r.size && y >= r.y && y < r.y + r.size) return r;
  }
  return null;
}

/**
 * Returns a unit-length move vector aimed at the nearest doorway gap exit on
 * the room containing the bot. Used by the force-escape override to push the
 * bot to the corridor "mainland" past the room edge.
 */
function _calcRoomEscapeVector(state: GameState): { moveX: number; moveY: number } {
  const room = _findContainingRoom(state);
  if (!room || !room.gaps || room.gaps.length === 0) {
    // No gap data? Push toward the world center as a last resort.
    return _normalizeMove(state.map.WORLD_SIZE / 2 - state.player.x, state.map.WORLD_SIZE / 2 - state.player.y);
  }
  const px = state.player.x, py = state.player.y;
  let bestDx = 0, bestDy = 0, bestDist = Infinity;
  for (const g of room.gaps) {
    // Aim a few units PAST the gap so the path crosses the room boundary.
    const cx = g.x + g.w / 2;
    const cy = g.y + g.h / 2;
    let tx = cx, ty = cy;
    const PAD = 64;
    if (g.dir === 'north') ty = g.y - PAD;
    else if (g.dir === 'south') ty = g.y + g.h + PAD;
    else if (g.dir === 'west')  tx = g.x - PAD;
    else if (g.dir === 'east')  tx = g.x + g.w + PAD;
    const dx = tx - px, dy = ty - py;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestDist) { bestDist = d2; bestDx = dx; bestDy = dy; }
  }
  return _normalizeMove(bestDx, bestDy);
}

function _normalizeMove(dx: number, dy: number): { moveX: number; moveY: number } {
  const len = Math.hypot(dx, dy);
  if (len < 1) return { moveX: 0, moveY: 0 };
  return { moveX: dx / len, moveY: dy / len };
}

/**
 * BETA 19.53 — UNIVERSAL projectile dodge layer. Was previously embedded in
 * blendAvoid which only wraps SOME goals (seek_chest, explore, seek_health,
 * etc.). Flee / kite / engage_melee / seek_loot / seek_barrel did NOT use
 * blendAvoid → those goals had NO projectile dodge. Live death pattern
 * (Killa BETA 19.52 at t=183-185): boss Timmy threw penguin projectile
 * from 212wu while bot was in `flee` goal → 99 dmg landed → another 99 dmg
 * 2s later → bot died despite "dodge" code existing.
 *
 * Now called from the goal-dispatch TAIL so every goal benefits.
 *
 * Returns:
 *  - `goalVec` unchanged if no incoming projectile threats
 *  - a dodged vector (90% perpendicular to projectile, 10% original goal)
 *    if any projectile within 250wu will hit the bot in next 0.5s
 */
function applyProjectileDodge(
  state: GameState,
  goalVec: { moveX: number; moveY: number },
): { moveX: number; moveY: number } {
  const { player } = state;

  // BETA 19.55 — SLAM DODGE (Caseoh Mega boss-kill enabler).
  // Live death (Killa BETA 19.54): hit for 57 dmg at 106wu + 90.6 dmg at
  // 112wu from CASEOH_MEGA boss — those are SLAM attacks, not projectiles.
  // Slam = enemy goes airborne (slamPhase=1, slamTargetX/Y set), lands at
  // target after airborneDuration → AOE damage in slam radius. Bot's
  // projectile-dodge never catches this because there's no projectile entity.
  //
  // Detection: enemy.slamPhase >= 1 + slamTargetX/Y within 200wu of player
  // = bot is the target. Action: sprint perpendicular to the slam vector
  // (player → slam target) to get out of the AOE before impact.
  for (const e of state.enemies) {
    if (!e.alive) continue;
    const eAny = e as unknown as { slamPhase?: number; slamTargetX?: number; slamTargetY?: number };
    if (!eAny.slamPhase || eAny.slamPhase < 1) continue;
    if (eAny.slamTargetX === undefined || eAny.slamTargetY === undefined) continue;
    const tdx = eAny.slamTargetX - player.x;
    const tdy = eAny.slamTargetY - player.y;
    const tdist = Math.hypot(tdx, tdy);
    if (tdist > 200) continue;  // not aimed at us
    // Sprint perpendicular to the slam-aim vector, biased outward from target
    const aimAng = Math.atan2(tdy, tdx);
    // Dodge in the +/- perpendicular direction farther from slam target
    const perpX = -Math.sin(aimAng);
    const perpY =  Math.cos(aimAng);
    // Pick perpendicular dir that takes us FURTHER from slam target
    const candAx = player.x + perpX * 100;
    const candAy = player.y + perpY * 100;
    const candBx = player.x - perpX * 100;
    const candBy = player.y - perpY * 100;
    const dA = Math.hypot(eAny.slamTargetX - candAx, eAny.slamTargetY - candAy);
    const dB = Math.hypot(eAny.slamTargetX - candBx, eAny.slamTargetY - candBy);
    const dodgeX = dA > dB ? perpX : -perpX;
    const dodgeY = dA > dB ? perpY : -perpY;
    // 95% dodge, 5% original goal — slam damage is high enough to abandon goal
    const mx = dodgeX * 0.95 + goalVec.moveX * 0.05;
    const my = dodgeY * 0.95 + goalVec.moveY * 0.05;
    const mlen = Math.sqrt(mx * mx + my * my) || 1;
    return { moveX: mx / mlen, moveY: my / mlen };
  }

  // BETA 19.56 — THROW WINDUP DODGE.
  // Live death (Killa BETA 19.55): one-shot kill from Timmy boss throw at
  // dist=126wu, dmg=98.7. The projectile was inside the 400wu dodge
  // threshold but ETA was probably ~0.16s — too short for the bot to
  // move out of the 45wu hit zone (45wu / 200wu/s = 0.225s minimum to
  // clear). Solution: dodge during the throw WINDUP — enemy has throwWindupTimer
  // > 0 and throwTargetX/Y locked. Move OUT of the projectile path
  // BEFORE the projectile spawns. Gives bot a full windup-duration of
  // travel time (usually 0.5-1.0s) instead of 0.2s of reaction time.
  for (const e of state.enemies) {
    if (!e.alive) continue;
    const eAny = e as unknown as { throwWindupTimer?: number; throwTargetX?: number; throwTargetY?: number };
    if (!eAny.throwWindupTimer || eAny.throwWindupTimer <= 0) continue;
    if (eAny.throwTargetX === undefined || eAny.throwTargetY === undefined) continue;
    // Is the bot near the throw target? (target is where the THROWER thinks
    // the player will be when projectile arrives, not bot's current pos —
    // but for a thrown projectile in flight that's close enough)
    const tdx = eAny.throwTargetX - player.x;
    const tdy = eAny.throwTargetY - player.y;
    const tdist = Math.hypot(tdx, tdy);
    if (tdist > 80) continue; // throw aimed elsewhere
    // The projectile will travel FROM enemy.x/y TOWARD throwTargetX/Y.
    // Compute perpendicular to that direction and sprint OUT.
    const fromX = e.x;
    const fromY = e.y;
    const aimDx = eAny.throwTargetX - fromX;
    const aimDy = eAny.throwTargetY - fromY;
    const aimLen = Math.hypot(aimDx, aimDy) || 1;
    const perpX = -aimDy / aimLen;
    const perpY = aimDx / aimLen;
    // Pick perpendicular direction that takes us further from the throw target
    const candAx = player.x + perpX * 60;
    const candAy = player.y + perpY * 60;
    const candBx = player.x - perpX * 60;
    const candBy = player.y - perpY * 60;
    const dA = Math.hypot(eAny.throwTargetX - candAx, eAny.throwTargetY - candAy);
    const dB = Math.hypot(eAny.throwTargetX - candBx, eAny.throwTargetY - candBy);
    const dodgeX = dA > dB ? perpX : -perpX;
    const dodgeY = dA > dB ? perpY : -perpY;
    // 90% dodge, 10% goal — windup dodge is preventative, not panic
    const mx = dodgeX * 0.9 + goalVec.moveX * 0.1;
    const my = dodgeY * 0.9 + goalVec.moveY * 0.1;
    const mlen = Math.sqrt(mx * mx + my * my) || 1;
    return { moveX: mx / mlen, moveY: my / mlen };
  }

  const projectiles = _getActiveEnemyProjectiles();
  for (const proj of projectiles) {
    const dx = proj.x - player.x;
    const dy = proj.y - player.y;
    const distSq = dx * dx + dy * dy;
    // BETA 19.54 — Killa BETA 19.53 death: 86.9 dmg projectile at 286wu
    // (just outside the 250wu threshold). Boss Timmy throw speed ~700wu/s
    // = covers 250wu in 0.36s — barely inside the 0.5s window but our
    // distance gate killed the check first. Bumped to 400wu so a 700wu/s
    // projectile gets caught at ~0.57s ETA (still inside meaningful dodge
    // window — bot move speed 200wu/s = 100wu of avoidance over 0.5s).
    // ETA window also bumped 0.5 → 0.7s to match.
    if (distSq > 400 * 400) continue;
    const speedSq = proj.vx * proj.vx + proj.vy * proj.vy;
    if (speedSq < 100) continue;
    const t = (-dx * proj.vx + -dy * proj.vy) / speedSq;
    if (t < 0 || t > 0.7) continue;
    const closestX = proj.x + proj.vx * t;
    const closestY = proj.y + proj.vy * t;
    const ddx = closestX - player.x;
    const ddy = closestY - player.y;
    const closeDistSq = ddx * ddx + ddy * ddy;
    const hitRadiusSq = (proj.radius + 25) * (proj.radius + 25);
    if (closeDistSq > hitRadiusSq) continue;
    // Projectile will hit — dodge perpendicular to its velocity
    const speedInv = 1 / Math.sqrt(speedSq);
    const perpX = -proj.vy * speedInv;
    const perpY = proj.vx * speedInv;
    const dotGoal = perpX * goalVec.moveX + perpY * goalVec.moveY;
    const dodgeX = dotGoal >= 0 ? perpX : -perpX;
    const dodgeY = dotGoal >= 0 ? perpY : -perpY;
    const mx = dodgeX * 0.9 + goalVec.moveX * 0.1;
    const my = dodgeY * 0.9 + goalVec.moveY * 0.1;
    const mlen = Math.sqrt(mx * mx + my * my) || 1;
    return { moveX: mx / mlen, moveY: my / mlen };
  }
  return goalVec;
}

// ─── Movement Vectors ───────────────────────────────────────────────

// BETA 17.09 — Bot AI fix (Dylan: "make bots smart as me"). The seek_*
// vectors (seek_chest, seek_shrine, seek_portal, seek_health, explore)
// previously walked straight at their target through ANY enemy crowd in
// the way. That's why bots die in 60-72s while humans get 309s — humans
// instinctively path around clusters. This helper blends the goal vector
// with a kite-away component when enemies are within `threshold` wu.
// Default 60% goal / 40% kite when 2+ enemies are close, else 100% goal.
function blendAvoid(
  state: GameState,
  goalVec: { moveX: number; moveY: number },
  nearbyEnemies: Enemy[],
  // BETA 17.46 — Dylan: "should be way smarter at not getting hit. i mean
  // its a computer bot. it should not ever get close enough to get hit."
  // 17.69/17.70 attempted combat-aware dynamic threshold but 3 consecutive
  // test runs landed at LV 5-7 vs 17.68's LV 9-15 baseline. Reverted to
  // proven static 300wu/70-30 in 17.71. blendAvoid combat-tuning needs
  // more careful analysis (likely interacts with explore/goal-vector
  // chains in ways we haven't fully traced).
  threshold: number = 300,
): { moveX: number; moveY: number } {
  const { player } = state;

  // BETA 19.53 — Projectile dodge moved to standalone applyProjectileDodge,
  // called UNIVERSALLY from the goal-dispatch tail (autoPlaytester.ts ~line
  // 3540). Previously the dodge ONLY fired when a goal flowed through
  // blendAvoid, which meant flee/kite/engage_melee had NO dodge. Live death
  // pattern (Killa 19.52, t=183-185): boss Timmy threw penguin from 212wu
  // away while bot was in flee goal → 99 dmg hit landed → second 99 dmg
  // 2s later. Universal dodge layer prevents this — every goal now ducks
  // incoming projectiles at the end of the dispatch.
  const _afterDodge = applyProjectileDodge(state, goalVec);
  if (_afterDodge !== goalVec) return _afterDodge;

  // BETA 19.58 — AURA CHAR LAWNMOWER MODE. Live evidence (Chad L11 / 178s
  // / 0 enemies in 151wu aura ring) confirms PSBOT cycle-30av #2: chad_aura
  // hit rate dropped 64% → 28% because the BETA 19.49 tiered dodge keeps
  // enemies OUT of the 130-180wu aura range. Aura weapons NEED enemies
  // inside that ring to deal damage — bot must walk THROUGH clusters,
  // not around them. When any aura weapon (chad_aura) is equipped AND
  // HP > 60%, drop the dodge weight to 5/95 (basically no dodge). Bot
  // becomes a lawnmower — chad's aura cooks everything in its 150wu ring.
  const _ws = getWeaponSystemState();
  const _hasAuraWeapon = !!_ws?.weapons?.some(w => w.definitionId === 'chad_aura');
  const _hpPct = player.maxHp > 0 ? player.hp / player.maxHp : 1;
  if (_hasAuraWeapon && _hpPct > 0.6) {
    return { moveX: goalVec.moveX, moveY: goalVec.moveY };
  }

  // BETA 19.49 — Dylan re-prioritized: "they need to level up and fast as
  // fucking possible." 19.48's aggressive 30/70 dodge below 120wu was
  // SAFE but bots were L1 at 45s because enemies were dodged out of
  // weapon range — no kills, no XP. Re-tuned to keep enemies INSIDE
  // weapon range while still ducking the worst incoming:
  //   - any enemy < 60wu (literally about to touch): 40/60 (dodge wins)
  //   - any enemy < 150wu (inside spatula/RPK kill range): 70/30 (goal-bias, let weapon hit)
  //   - any enemy ≥ 150wu in threshold: 80/20 (mostly goal-seek)
  // Kills come BEFORE don't-get-hit in Dylan's priority chain — bot must
  // tolerate close enemies so its weapons can fire on them.
  let nClose = 0;
  let cx = 0, cy = 0;
  let minDist = Infinity;
  for (const e of nearbyEnemies) {
    if (!e.alive) continue;
    const { px, py } = _projectedEnemyPos(e);
    const dx = px - player.x;
    const dy = py - player.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < threshold * threshold) {
      cx += px; cy += py; nClose++;
      if (d2 < minDist) minDist = d2;
    }
  }
  if (nClose < 1) return goalVec;
  cx /= nClose; cy /= nClose;
  const dx = player.x - cx;
  const dy = player.y - cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 30) return goalVec;
  const awayX = dx / dist;
  const awayY = dy / dist;
  const goalDot = goalVec.moveX * awayX + goalVec.moveY * awayY;
  if (goalDot < -0.3) return goalVec;
  // Distance-tiered avoid weight (BETA 19.49 retune)
  const minD = Math.sqrt(minDist);
  let goalW: number;
  let avoidW: number;
  if (minD < 60) { goalW = 0.40; avoidW = 0.60; }       // touching range — dodge wins
  else if (minD < 150) { goalW = 0.70; avoidW = 0.30; } // weapon range — let weapons fire
  else { goalW = 0.80; avoidW = 0.20; }                 // distant — pursue goal
  const mx = goalVec.moveX * goalW + awayX * avoidW;
  const my = goalVec.moveY * goalW + awayY * avoidW;
  const mlen = Math.sqrt(mx * mx + my * my) || 1;
  return { moveX: mx / mlen, moveY: my / mlen };
}

// BETA 17.21 — PSBOT cycle-30ai B1: predictive enemy position helper.
// Extrapolates enemy 0.7s ahead based on velocity so kite/flee math
// targets where enemies WILL BE, not where they are now. Zombies move
// 80wu/s — by the time bot finishes a kite tick, the centroid has
// already shifted 56wu toward the bot. Bot's escape was stale-targeted.
function _projectedEnemyPos(e: Enemy, projT: number = 0.7): { px: number; py: number } {
  const eAny = e as unknown as { vx?: number; vy?: number };
  return {
    px: e.x + (eAny.vx ?? 0) * projT,
    py: e.y + (eAny.vy ?? 0) * projT,
  };
}

// BETA 17.21 — PSBOT cycle-30ai B6: formation awareness via angular spread.
// If enemies are spread >115° around the player (surround / flank), the
// kite centroid is roughly at player position → near-zero kite vector →
// bot stands still in the kill zone. Detect surround and force flee.
function _isFlanked(state: GameState, enemies: Enemy[]): boolean {
  if (enemies.length < 3) return false;
  const { player } = state;
  const angles = enemies.map(e => Math.atan2(e.y - player.y, e.x - player.x));
  // Sort + find max gap. Spread = 2π - largest gap (smaller gap = more surrounded).
  angles.sort((a, b) => a - b);
  let maxGap = (angles[0] + Math.PI * 2) - angles[angles.length - 1];
  for (let i = 1; i < angles.length; i++) {
    const g = angles[i] - angles[i - 1];
    if (g > maxGap) maxGap = g;
  }
  const spread = Math.PI * 2 - maxGap;
  return spread > 2.0; // ≈ 115° spread — significantly surrounded
}

function calcFleeVector(state: GameState, threats: Enemy[]): { moveX: number; moveY: number } {
  // BETA 17.23 — PSBOT cycle-30ak CRITICAL fix #1: zero-threats guard.
  if (threats.length === 0) return calcExploreVector(state, 0.016);
  const { player } = state;
  let cx = 0, cy = 0;
  // BETA 17.21 — B1: project enemy positions 0.7s ahead so we flee toward
  // where it'll actually be safe, not where it WAS safe.
  for (const e of threats) {
    const { px, py } = _projectedEnemyPos(e);
    cx += px; cy += py;
  }
  cx /= threats.length; cy /= threats.length;
  const dx = player.x - cx;
  const dy = player.y - cy;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  // Natural flee direction: away from threat centroid.
  const naturalFleeX = dx / dist;
  const naturalFleeY = dy / dist;

  // BETA 17.51 — Dylan: bot died at 60s while fleeing. Pattern: bot in
  // corner, threats between bot and open space → centroid-flee direction
  // points INTO the wall → probeMove tries ±90° but ALL bracket angles
  // also into wall → bot wedged in corner dies to swarm.
  // Fix: check if the natural flee direction is actually walkable at
  // 100wu probe distance. If not, use the largest-gap-between-enemies
  // direction instead (same logic calcKiteVector uses for flanked, but
  // always-on for flee since fleeing IS the panic case). The largest
  // gap is the bot's true escape route — picks the direction with the
  // most clearance from threats AND, since we pick it dynamically per
  // frame, will track to a real opening as enemies shift.
  const r = (player.size ?? 16);
  const probeX = player.x + naturalFleeX * 100;
  const probeY = player.y + naturalFleeY * 100;
  const naturalDirWalkable = canMoveTo(state, probeX, probeY, r, true);

  if (!naturalDirWalkable && threats.length >= 2) {
    // Largest gap between threats — same pattern as calcKiteVector flank.
    const angles = threats.map(e => Math.atan2(e.y - player.y, e.x - player.x));
    angles.sort((a, b) => a - b);
    let maxGap = (angles[0] + Math.PI * 2) - angles[angles.length - 1];
    let gapMidAngle = (angles[angles.length - 1] + (angles[0] + Math.PI * 2)) / 2;
    for (let i = 1; i < angles.length; i++) {
      const g = angles[i] - angles[i - 1];
      if (g > maxGap) {
        maxGap = g;
        gapMidAngle = (angles[i - 1] + angles[i]) / 2;
      }
    }
    // Only use the gap if it's actually walkable; otherwise fall back to
    // probeMove on natural direction (probeMove\'s own fallbacks kick in).
    const gapX = player.x + Math.cos(gapMidAngle) * 300;
    const gapY = player.y + Math.sin(gapMidAngle) * 300;
    if (canMoveTo(state, gapX, gapY, r, true)) {
      return probeMove(state, player.x, player.y, gapX, gapY);
    }
  }

  // Default: natural-direction flee with probeMove safety. probeMove
  // (17.47) handles wall avoidance + jiggle as last resort.
  const fleeTargetX = player.x + naturalFleeX * 400;
  const fleeTargetY = player.y + naturalFleeY * 400;
  return probeMove(state, player.x, player.y, fleeTargetX, fleeTargetY);
}

// BETA 20.68 — BOT BRAIN v2: threat-aware kiting (recon upgrade #1). Real
// players kite THROUGH what they can kill and AWAY from what they can't.
// Each enemy's pull on the flee-centroid is weighted by (its damage) × (how
// badly MY equipped weapons hit its type), sampled from the live
// weapon×enemy accuracy matrix every 2s. Unknown types fall back to
// damage-only weighting — never worse than the old uniform centroid.
let _threatAccCache: Record<string, number> = {}; // enemyType → avg accuracy 0..1
let _threatAccCacheT = -10;
function _refreshThreatAccCache(state: GameState): void {
  if (state.time - _threatAccCacheT < 2 && state.time >= _threatAccCacheT) return;
  _threatAccCacheT = state.time;
  _threatAccCache = {};
  try {
    const matrix = _getWpnEnemyMatrixForBrain();
    const equipped = new Set(getWeaponSystemState()?.weapons?.map(w => w.definitionId) ?? []);
    const agg: Record<string, { shots: number; hits: number }> = {};
    for (const [wid, perEnemy] of Object.entries(matrix)) {
      if (!equipped.has(wid)) continue;
      for (const [etype, entry] of Object.entries(perEnemy)) {
        const a = agg[etype] ?? (agg[etype] = { shots: 0, hits: 0 });
        a.shots += entry.shots; a.hits += entry.hits;
      }
    }
    for (const [etype, a] of Object.entries(agg)) {
      if (a.shots >= 10) _threatAccCache[etype] = Math.min(1, a.hits / Math.max(1, a.shots));
    }
  } catch { /* matrix optional — damage-only weighting still applies */ }
}
function _threatWeightFor(e: Enemy): number {
  const dmgW = Math.max(0.5, ((e as unknown as { damage?: number }).damage ?? 25) / 25);
  const etype = (e as unknown as { definitionId?: string; type?: string });
  const acc = _threatAccCache[etype.definitionId ?? etype.type ?? ''];
  const accW = acc === undefined ? 1 : (1.6 - acc); // can't-hit-it ⇒ 1.6× scarier; we shred it ⇒ 0.6×
  return dmgW * accW;
}

function calcKiteVector(state: GameState, enemies: Enemy[]): { moveX: number; moveY: number } {
  const { player } = state;
  if (!bot) return { moveX: 0, moveY: 0 };

  // BETA 17.23 — PSBOT cycle-30ak CRITICAL fix #2: zero-enemies guard.
  // Same NaN cascade pattern as calcFleeVector. If goal-locked to 'kite'
  // and enemies empty, cx /= 0 → NaN → freeze. Fall back to explore.
  if (enemies.length === 0) return calcExploreVector(state, 0.016);

  // BETA 17.21 — B6 flank detection: if surrounded (>115° spread), the
  // centroid math degenerates. Pick the LARGEST-gap direction as escape
  // route instead of centroid-flee.
  if (enemies.length >= 3 && _isFlanked(state, enemies)) {
    const angles = enemies.map(e => Math.atan2(e.y - player.y, e.x - player.x));
    angles.sort((a, b) => a - b);
    let maxGap = (angles[0] + Math.PI * 2) - angles[angles.length - 1];
    let gapMidAngle = (angles[angles.length - 1] + (angles[0] + Math.PI * 2)) / 2;
    for (let i = 1; i < angles.length; i++) {
      const g = angles[i] - angles[i - 1];
      if (g > maxGap) {
        maxGap = g;
        gapMidAngle = (angles[i - 1] + angles[i]) / 2;
      }
    }
    // Run through the biggest gap between enemies
    const targetX = player.x + Math.cos(gapMidAngle) * 300;
    const targetY = player.y + Math.sin(gapMidAngle) * 300;
    return probeMove(state, player.x, player.y, targetX, targetY);
  }

  _refreshThreatAccCache(state);
  let cx = 0, cy = 0, wSum = 0;
  // BETA 17.21 — B1: project enemy positions ahead.
  // BETA 20.68 — BRAIN v2: threat-weighted centroid — the flee direction
  // prioritizes escaping what actually kills us, and yields ground last to
  // enemies our build shreds (real-player kiting instead of uniform retreat).
  for (const e of enemies) {
    const { px, py } = _projectedEnemyPos(e);
    const w = _threatWeightFor(e);
    cx += px * w; cy += py * w; wSum += w;
  }
  cx /= (wSum || enemies.length); cy /= (wSum || enemies.length);

  const dx = player.x - cx;
  const dy = player.y - cy;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const fleeX = dx / dist;
  const fleeY = dy / dist;
  const strafeX = -fleeY;
  const strafeY = fleeX;

  // Use brain's adaptive kite aggression. BETA 17.21 B5 — HP buffer state:
  // at HP <30% push 90% flee / 10% strafe (just survive). At HP 30-60% use
  // brain.kiteAggression normally. At HP >80% allow more strafe (aggressive
  // close-pass) since bot can absorb a hit.
  let k = brain.kiteAggression;
  const hpPct = player.hp / Math.max(1, player.maxHp);
  if (hpPct < 0.3) k = Math.min(k, 0.10);       // emergency: pure retreat
  else if (hpPct < 0.6) k = Math.min(k, 0.55);  // measured: ≤55% strafe
  // else hpPct >= 0.6: use full brain.kiteAggression (aggressive build)
  const rawX = fleeX * (1 - k) + strafeX * k;
  const rawY = fleeY * (1 - k) + strafeY * k;

  // Project 300 units in the kite direction and run through probeMove for wall safety
  const targetX = player.x + rawX * 300;
  const targetY = player.y + rawY * 300;
  return probeMove(state, player.x, player.y, targetX, targetY);
}

function calcSeekHealthVector(state: GameState): { moveX: number; moveY: number } {
  // Look for health orbs in lootOrbs — navigate toward nearest health orb
  const { player } = state;
  if (state.lootOrbs?.orbs) {
    const healthOrbs = state.lootOrbs.orbs.filter(o => o.alive && !o.vacuuming && o.type === 'health');
    if (healthOrbs.length > 0) {
      let nearest = healthOrbs[0];
      let nearestDist = Math.hypot(nearest.x - player.x, nearest.y - player.y);
      for (const orb of healthOrbs) {
        const d = Math.hypot(orb.x - player.x, orb.y - player.y);
        if (d < nearestDist) { nearest = orb; nearestDist = d; }
      }
      return probeMove(state, player.x, player.y, nearest.x, nearest.y);
    }
    // BETA 17.22 — PSBOT cycle-30ai B7: no health orb — route to nearest
    // remembered safe zone instead of wandering. Bot now navigates back to
    // corners/positions where it recovered HP before, prioritized by
    // recovery quality (how much HP regained there last time).
    if (brain.safeZones && brain.safeZones.length > 0) {
      let bestZone = brain.safeZones[0];
      let bestScore = -Infinity;
      for (const z of brain.safeZones) {
        const d = Math.hypot(z.x - player.x, z.y - player.y);
        // Prefer closer zones with higher quality.
        const score = z.quality * 1000 - d;
        if (score > bestScore) { bestScore = score; bestZone = z; }
      }
      bestZone.lastUsed = state.time;
      return probeMove(state, player.x, player.y, bestZone.x, bestZone.y);
    }
    // No safe zone either — flee from enemies while waiting
    const closeEnemies = getEnemiesInRange(state.enemies, player.x, player.y, 300);
    if (closeEnemies.length > 0) return calcFleeVector(state, closeEnemies);
  }
  return calcExploreVector(state, 0.016);
}

/** Count gold + XP orbs within range — used for loot-seeking priority */
function countNearbyLoot(state: GameState, range: number): number {
  if (!state.lootOrbs?.orbs) return 0;
  const { player } = state;
  return state.lootOrbs.orbs.filter(o =>
    o.alive && !o.vacuuming &&
    (o.type === 'gold' || o.type === 'xp') &&
    Math.hypot(o.x - player.x, o.y - player.y) < range
  ).length;
}

/**
 * Navigate toward the centroid of the nearest loot cluster (gold + XP orbs).
 * When enemies are also nearby, blend in a kite direction so the bot doesn't
 * run straight into enemies while collecting.
 */
function calcSeekLootVector(state: GameState, nearbyEnemies: Enemy[]): { moveX: number; moveY: number } {
  const { player } = state;
  if (!state.lootOrbs?.orbs) return calcExploreVector(state, 0.016);

  // BETA 17.18 — Dylan: "XP HUNGRY AND LEVEL UP HUNGRY." Removed the 700wu
  // distance cap. If seek_loot was selected, there IS an orb on the map
  // (hasAnyLootOrb gated the selection). Find the BEST single orb by
  // inverse-distance-weighted score (gold weight 3, XP weight 2). Chase
  // the nearest valuable orb directly instead of moving toward a
  // centroid that may be empty.
  const orbs = state.lootOrbs.orbs.filter(o =>
    o.alive && !o.vacuuming && (o.type === 'gold' || o.type === 'xp')
  );
  if (orbs.length === 0) return calcExploreVector(state, 0.016);

  // Single-target chase: pick the highest score = (typeWeight × valueWeight × 1000 / distance)
  let bestScore = -Infinity;
  let bestX = orbs[0].x, bestY = orbs[0].y;
  for (const orb of orbs) {
    const d = Math.max(50, Math.hypot(orb.x - player.x, orb.y - player.y));
    const typeW = orb.type === 'gold' ? 3 : 2;
    const valueW = Math.min(orb.value || 1, 5);
    const score = (typeW * valueW * 1000) / d;
    if (score > bestScore) {
      bestScore = score;
      bestX = orb.x;
      bestY = orb.y;
    }
  }
  const lootVec = probeMove(state, player.x, player.y, bestX, bestY);

  // BETA 17.18 — kite blend only when truly swarmed (4+ instead of 3+). 3
  // enemies in 400wu doesn't justify abandoning XP — let proactive kite at
  // HP<70% handle that path. Softer 70/30 (was 55/45).
  // BETA 19.61 — Dylan: "being slow does not work." Blended vector wasn't
  // normalized — if loot + kite point opposite, sum magnitude < 1.0 →
  // bot moves slowly. Normalize after blend so speed stays at 1.0.
  if (nearbyEnemies.length >= 4) {
    const kite = calcKiteVector(state, nearbyEnemies);
    const mx = lootVec.moveX * 0.7 + kite.moveX * 0.3;
    const my = lootVec.moveY * 0.7 + kite.moveY * 0.3;
    const mlen = Math.hypot(mx, my) || 1;
    return { moveX: mx / mlen, moveY: my / mlen };
  }
  return lootVec;
}

function calcSeekChestVector(state: GameState): { moveX: number; moveY: number } {
  const { player } = state;
  const chests = state.map.roomMap.chests ?? [];
  // BETA 15.48 — paid-first preference. Was "closest of any type"; bots
  // gravitated to gold chests because they're often closer. cycle-18 had
  // chest_paid=0 across 48 runs because of this. Now: find closest PAID
  // (goop) chest first; only fall back to gold chest if no paid available.
  // This gets the actual item-upgrade economy moving; gold chests are
  // fallback enrichment, not the primary chest goal.
  let bestPaid: { x: number; y: number } | null = null;
  let bestPaidDist = Infinity;
  let bestGold: { x: number; y: number } | null = null;
  let bestGoldDist = Infinity;
  for (const c of chests) {
    if ((c as any).opened || (c as any).collected) continue;
    if (!canAffordChest(state, c)) continue;
    // BETA 13.80: skip chests inside a freshly-escaped room until its cooldown clears.
    const cRoom = _roomAtPoint(state, c.x, c.y);
    if (cRoom && _isRoomOnCooldown(cRoom.id, state)) continue;
    // BETA 17.58 — respect chest stuck-blacklist (same fix as shrine).
    if (_isChestBlacklisted(c as { id?: string; x?: number; y?: number })) continue;
    const d = Math.hypot(c.x - player.x, c.y - player.y);
    if ((c as { type?: string }).type === 'gold') {
      if (d < bestGoldDist) { bestGoldDist = d; bestGold = c; }
    } else {
      if (d < bestPaidDist) { bestPaidDist = d; bestPaid = c; }
    }
  }
  const target = bestPaid ?? bestGold;
  if (target) return probeMove(state, player.x, player.y, target.x, target.y);
  return calcExploreVector(state, 0.016);
}

function calcSeekShrineVector(state: GameState): { moveX: number; moveY: number } {
  const { player } = state;
  const shrines = state.map.roomMap.shrines ?? [];
  let closest: { x: number; y: number } | null = null;
  let closestDist = Infinity;
  for (const s of shrines) {
    if ((s as any).activated || (s as any).used) continue;
    const st = (s as any).state;
    if (st === 'spent_lit' || st === 'boss_spawned' || st === 'boss_defeated') continue;
    if ((s as any).type === 'boss' && brain.avoidBossShrines) continue;
    // BETA 13.80: skip shrines in a cooldown room — bot just escaped, give it 30s before re-entry.
    const sRoom = _roomAtPoint(state, s.x, s.y);
    if (sRoom && _isRoomOnCooldown(sRoom.id, state)) continue;
    // BETA 17.58 — respect the stuck blacklist. Previously the goal-decision
    // helper hasAnyShrine() filtered blacklisted shrines, but THIS vector did
    // not — so once goal=seek_shrine was locked in, the bot pulled toward the
    // blacklisted shrine anyway and re-stuck on the same wall.
    if (_isShrineBlacklisted(s as { id?: string; x?: number; y?: number })) continue;
    const d = Math.hypot(s.x - player.x, s.y - player.y);
    if (d < closestDist) { closestDist = d; closest = s; }
  }
  if (closest) return probeMove(state, player.x, player.y, closest.x, closest.y);
  return calcExploreVector(state, 0.016);
}

function calcSeekPortalVector(state: GameState): { moveX: number; moveY: number } {
  if (!state.portal) return calcExploreVector(state, 0.016);
  return probeMove(state, state.player.x, state.player.y, state.portal.x, state.portal.y);
}

function calcExploreVector(state: GameState, dt: number): { moveX: number; moveY: number } {
  if (!bot) return { moveX: 0, moveY: 0 };
  bot.goalTimer -= dt;

  const player = state.player;

  // BETA 14.82 — flow-field deadlock escape mode (PSBOT RUN cycle-11 chad fix).
  // When markCellAsTrap detected ≥5 traps clustered, deadlockEscapeUntil is set.
  // Override navigation: use direct movement toward distant target (no flow field,
  // no waypoints) so the bot can break out of the trap-cell cluster. Exit early
  // if bot has moved >300wu from where escape began (we got out successfully).
  const now = state.time ?? 0;
  if (bot.deadlockEscapeTarget && now < bot.deadlockEscapeUntil) {
    const tgt = bot.deadlockEscapeTarget;
    if (bot.deadlockEscapeStartPos) {
      const movedFromStart = Math.hypot(
        player.x - bot.deadlockEscapeStartPos.x,
        player.y - bot.deadlockEscapeStartPos.y,
      );
      if (movedFromStart > 300) {
        // Successfully escaped the deadlock cluster — exit escape mode early
        bot.deadlockEscapeUntil = 0;
        bot.deadlockEscapeTarget = null;
        bot.deadlockEscapeStartPos = null;
        log('[FLOW FIELD DEADLOCK] escaped — moved >300wu from cluster, resuming normal nav');
      } else {
        // Direct vector toward distant target, bypassing flow field + waypoints
        const dx = tgt.x - player.x;
        const dy = tgt.y - player.y;
        const d = Math.hypot(dx, dy) || 1;
        return { moveX: dx / d, moveY: dy / d };
      }
    }
  } else if (bot.deadlockEscapeUntil !== 0) {
    // Timeout reached — exit escape mode
    bot.deadlockEscapeUntil = 0;
    bot.deadlockEscapeTarget = null;
    bot.deadlockEscapeStartPos = null;
  }

  const atTarget = bot.targetPos
    ? Math.hypot(player.x - bot.targetPos.x, player.y - bot.targetPos.y) < 80
    : true;

  // Pick a new target when timer expires, we've arrived, or we got stuck
  if (!bot.targetPos || bot.goalTimer <= 0 || atTarget) {
    const rooms = state.map.roomMap?.rooms;
    const { ROOM_SIZE } = state.map;
    if (rooms && rooms.length > 0) {
      // BETA 13.80: filter rooms currently on a re-entry cooldown so explore
      // doesn't immediately yank the bot back into a freshly-escaped room.
      const eligible = rooms.filter(r => !_isRoomOnCooldown((r as RoomLite).id, state));
      const pool = eligible.length > 0 ? eligible : rooms; // fallback if every room is cooled down
      // BETA 17.67 — Dylan: "do not stop until it plays like me." Bot's kill
      // rate gap vs Dylan was largely because random-room explore landed in
      // empty rooms while enemies clustered elsewhere. Now: when bot has
      // any DPS (damageBonus >= 20% or held weapons >= 2), bias the room
      // choice toward rooms with MORE ENEMIES inside (count enemies whose
      // position falls in each room). Weighted random where each room's
      // weight = (enemiesInRoom + 1). Empty rooms still pickable but rare;
      // dense rooms picked ~5-10× more often. Fragile/low-DPS bots fall
      // through to the original random pick.
      const armorVal2 = (state.player as { armor?: number }).armor ?? 0;
      const evaVal2 = (state.player as { evasion?: number }).evasion ?? 0;
      const shVal2 = (state.player as { shield?: number }).shield ?? 0;
      const damBonus2 = (state.player as { damageBonus?: number }).damageBonus ?? 0;
      const wsW2 = (getWeaponSystemState()?.weapons?.length ?? 0);
      const isBotCombatReady = (armorVal2 + evaVal2 * 0.5 + shVal2 >= 10) || damBonus2 >= 20 || wsW2 >= 2;
      let room: { x: number; y: number };
      if (isBotCombatReady && state.enemies && state.enemies.length > 0) {
        // Count alive enemies per room
        const roomCounts: Array<{ r: typeof pool[number]; count: number }> = pool.map(r => ({ r, count: 0 }));
        for (const e of state.enemies) {
          if (!e.alive) continue;
          // Find which room this enemy is in (if any)
          for (const rc of roomCounts) {
            if (e.x >= rc.r.x && e.x < rc.r.x + ROOM_SIZE && e.y >= rc.r.y && e.y < rc.r.y + ROOM_SIZE) {
              rc.count++;
              break;
            }
          }
        }
        // Weighted random pick: weight = count + 1 (so empty rooms still pickable but rare)
        const totalWeight = roomCounts.reduce((sum, rc) => sum + (rc.count + 1), 0);
        let pick = Math.random() * totalWeight;
        let chosen = roomCounts[0];
        for (const rc of roomCounts) {
          pick -= (rc.count + 1);
          if (pick <= 0) { chosen = rc; break; }
        }
        room = chosen.r;
      } else {
        room = pool[Math.floor(Math.random() * pool.length)];
      }
      bot.targetPos = {
        x: room.x + ROOM_SIZE / 2 + (Math.random() - 0.5) * 100,
        y: room.y + ROOM_SIZE / 2 + (Math.random() - 0.5) * 100,
      };
    } else {
      const { WORLD_SIZE } = state.map;
      const margin = 800;
      bot.targetPos = {
        x: margin + Math.random() * (WORLD_SIZE - margin * 2),
        y: margin + Math.random() * (WORLD_SIZE - margin * 2),
      };
    }
    // Compute nav-graph waypoint path to the new target
    if (bot.navGraph) {
      bot.waypointPath = findNavPath(bot.navGraph, player.x, player.y, bot.targetPos.x, bot.targetPos.y);
      bot.waypointIdx  = 0;
    }
    bot.goalTimer = 4 + Math.random() * 3;
    bot.stuckTimer = 0;
  }

  // Advance along waypoint path — move toward next waypoint instead of final target directly.
  // Radius 40px (was 80px): bot must pass closer to gap center before advancing,
  // ensuring it goes through the middle of the doorway not the edge.
  if (bot.waypointPath.length > 0) {
    const wp = bot.waypointPath[bot.waypointIdx];
    if (wp && Math.hypot(player.x - wp.x, player.y - wp.y) < 40) {
      bot.waypointIdx = Math.min(bot.waypointIdx + 1, bot.waypointPath.length - 1);
    }
  }
  const navTarget = bot.waypointPath.length > 0
    ? bot.waypointPath[bot.waypointIdx]
    : bot.targetPos!;

  const exploreVec = probeMove(state, state.player.x, state.player.y, navTarget.x, navTarget.y);

  // BETA 13.48 (PS Major Pattern 7): if probeMove returned near-zero, the target
  // is unreachable (all 11 angles blocked). Force a new target next frame instead
  // of vibrating toward the wall.
  if (Math.abs(exploreVec.moveX) < 0.1 && Math.abs(exploreVec.moveY) < 0.1) {
    bot.goalTimer = 0;
  }

  // BETA 13.48 (PS Major Pattern 5): border avoidance. calcExploreVector picks
  // random room centers; if the path strays within 400px of any world edge,
  // probeMove can get boxed between border collision and a room wall. Push the
  // bot back inward (70/30 blend) so it never drifts to the boundary.
  {
    const BORDER_MARGIN = 400;
    const { WORLD_SIZE } = state.map;
    let bx = 0, by = 0;
    if (state.player.x < BORDER_MARGIN) bx = 1;
    if (state.player.x > WORLD_SIZE - BORDER_MARGIN) bx = -1;
    if (state.player.y < BORDER_MARGIN) by = 1;
    if (state.player.y > WORLD_SIZE - BORDER_MARGIN) by = -1;
    if (bx !== 0 || by !== 0) {
      const blen = Math.sqrt(bx * bx + by * by) || 1;
      exploreVec.moveX = (bx / blen) * 0.7 + exploreVec.moveX * 0.3;
      exploreVec.moveY = (by / blen) * 0.7 + exploreVec.moveY * 0.3;
    }
  }

  // While exploring, if there's any loot in the wider area, bias toward it
  // (gold/XP within 900px — the bot should always drift toward dropped loot)
  if (state.lootOrbs?.orbs) {
    const { player } = state;
    const orbs = state.lootOrbs.orbs.filter(o =>
      o.alive && !o.vacuuming &&
      (o.type === 'gold' || o.type === 'xp') &&
      Math.hypot(o.x - player.x, o.y - player.y) < 900
    );
    if (orbs.length >= 3) {
      // Nearest orb
      const nearest = orbs.reduce((best, o) => {
        const d = Math.hypot(o.x - player.x, o.y - player.y);
        const bd = Math.hypot(best.x - player.x, best.y - player.y);
        return d < bd ? o : best;
      }, orbs[0]);
      const lootVec = probeMove(state, player.x, player.y, nearest.x, nearest.y);
      // Blend explore with loot pull — 40% loot bias, 60% explore plan.
      // BETA 19.61 — Normalize after blend so bot doesn't move at fractional
      // speed when explore + loot vectors point opposite directions.
      const mx = exploreVec.moveX * 0.6 + lootVec.moveX * 0.4;
      const my = exploreVec.moveY * 0.6 + lootVec.moveY * 0.4;
      const mlen = Math.hypot(mx, my) || 1;
      return { moveX: mx / mlen, moveY: my / mlen };
    }
  }

  return exploreVec;
}

// ─── Helpers ────────────────────────────────────────────────────────

function moveToward(from: { x: number; y: number }, to: { x: number; y: number }): { moveX: number; moveY: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  return { moveX: dx / dist, moveY: dy / dist };
}

/**
 * Wall-aware movement: mirrors the enemy 11-angle probe from enemySystem.ts.
 * Tries the direct angle first, then fans outward in ±15°, ±31°, ±47°, ±63°, ±90°
 * steps until it finds a direction not blocked by a wall. Falls back to 8-direction
 * escape cycling if fully trapped. Only used in bot mode — human movement unchanged.
 */

// Legacy triangle helpers removed in BETA 13.29 — fully replaced by getDoorwayMagnet
// (top of move loop) + getGreenZoneRedirect (3-zone collision). See roomCollisionGrid.ts.

/**
 * When the bot is near a room wall, return a gentle pull toward room center.
 * Only applies when the bot is drifting toward the wall unintentionally —
 * skips if the goal direction is already pointing toward the wall (doorway approach).
 */
function getRoomCenterPull(state: GameState, goalX: number, goalY: number): { dx: number; dy: number } | null {
  const rooms = state.map.roomMap?.rooms as PlacedRoom[] | undefined;
  if (!rooms || rooms.length === 0) return null;

  const { x, y } = state.player;
  const WALL_MARGIN = 64; // reduced from 120 — only kick in very close to wall

  for (const room of rooms) {
    if (x >= room.x && x < room.x + room.size &&
        y >= room.y && y < room.y + room.size) {
      const lx = x - room.x;
      const ly = y - room.y;

      const nearWall = lx < WALL_MARGIN || lx > room.size - WALL_MARGIN ||
                       ly < WALL_MARGIN || ly > room.size - WALL_MARGIN;
      if (!nearWall) return null;

      const cx = room.x + room.size / 2;
      const cy = room.y + room.size / 2;
      const toCenterX = cx - x;
      const toCenterY = cy - y;
      const len = Math.sqrt(toCenterX * toCenterX + toCenterY * toCenterY) || 1;

      // Skip if goal is pointing AWAY from center (bot is heading toward wall intentionally
      // — i.e. approaching a doorway). Only pull back if bot is drifting sideways into wall.
      const goalDotCenter = goalX * (toCenterX / len) + goalY * (toCenterY / len);
      if (goalDotCenter < -0.2) return null; // goal is toward wall → don't fight it

      return { dx: toCenterX / len, dy: toCenterY / len };
    }
  }
  return null;
}

/** probeMove — exactly mirrors enemySystem.ts steerTowardPlayer.
 *
 * Uses TWO step sizes (small then large) so the bot clears both tight hallways
 * (need small step to see the immediate path) and open corners (large step to
 * look further ahead).  Matching the enemy logic means the bot navigates rooms
 * exactly as well as zombies do.
 */
function probeMove(
  state: GameState,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): { moveX: number; moveY: number } {
  const dx = toX - fromX;
  const dy = toY - fromY;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return { moveX: 0, moveY: 0 };

  const baseAngle = Math.atan2(dy, dx);
  const r = (state.player.size ?? 16);

  // Same 11-angle probe the enemy system uses (enemySystem.ts steerTowardPlayer)
  const probeAngles = [
    baseAngle,
    baseAngle - 0.27, baseAngle + 0.27,   // ±15°
    baseAngle - 0.55, baseAngle + 0.55,   // ±31°
    baseAngle - 0.82, baseAngle + 0.82,   // ±47°
    baseAngle - 1.10, baseAngle + 1.10,   // ±63°
    baseAngle - 1.57, baseAngle + 1.57,   // ±90°
  ];

  // Pass 1: small step (matches enemy system — catches walls right in front of player)
  const smallStep = r * 0.9; // ~14px — almost identical to enemy's speed*dt
  for (const angle of probeAngles) {
    const nx = fromX + Math.cos(angle) * smallStep;
    const ny = fromY + Math.sin(angle) * smallStep;
    if (canMoveTo(state, nx, ny, r, true)) {
      return { moveX: Math.cos(angle), moveY: Math.sin(angle) };
    }
  }

  // Pass 2: larger step (looks further ahead for corridor navigation)
  const largeStep = r * 2.0; // ~32px
  for (const angle of probeAngles) {
    const nx = fromX + Math.cos(angle) * largeStep;
    const ny = fromY + Math.sin(angle) * largeStep;
    if (canMoveTo(state, nx, ny, r, true)) {
      return { moveX: Math.cos(angle), moveY: Math.sin(angle) };
    }
  }

  // Fully boxed in — cycle 8 escape directions by time bucket (same as enemy fallback)
  // BETA 19.61 — Dylan: "why do i ever see the bot moving slowly... being
  // slow does not work." Was returning 0.6× magnitude → bot moved at 60%
  // speed during boxed-in escapes. Restored to full 1.0 — engine clamps
  // movement to walls anyway, no reason to soft-pedal the escape.
  const timeBucket = Math.floor(Date.now() / 400);
  for (let i = 0; i < 8; i++) {
    const escAngle = (((timeBucket + i) % 8) / 8) * Math.PI * 2;
    const nx = fromX + Math.cos(escAngle) * smallStep;
    const ny = fromY + Math.sin(escAngle) * smallStep;
    if (canMoveTo(state, nx, ny, r, true)) {
      return { moveX: Math.cos(escAngle), moveY: Math.sin(escAngle) };
    }
  }

  // BETA 17.47 — PSBOT cycle-30ak Path #5 fix ("probeMove walled-in").
  // Dylan: "did you fix what i asked earlier about the bot getting stuck?"
  // The previous fallback returned (0,0) when every probe angle was
  // blocked — bot froze in tight corners. Last-resort: try the same 16
  // angles WITHOUT the exterior-green-polygon block (`isEntity=false`).
  // This lets the bot cut a corner through a green zone when otherwise
  // walled in. Green polys are meant to prevent enemies from corner-
  // skipping; for an actually-stuck bot, breaking that rule for ONE
  // frame is better than freezing. Player can cut corners freely
  // anyway, so the visual result matches normal play.
  for (let i = 0; i < 16; i++) {
    const escAngle = (((timeBucket + i) % 16) / 16) * Math.PI * 2;
    const nx = fromX + Math.cos(escAngle) * smallStep;
    const ny = fromY + Math.sin(escAngle) * smallStep;
    if (canMoveTo(state, nx, ny, r, false)) {  // isEntity=false: allow green cut
      // BETA 19.61 — Was 0.5× magnitude → 50% speed during green-cut escapes.
      // Dylan: "being slow does not work." Full magnitude — corner-cut should
      // be FAST since we're already breaking the green-poly rule.
      return { moveX: Math.cos(escAngle), moveY: Math.sin(escAngle) };
    }
  }

  // BETA 17.47 — TRULY trapped (walls on all 16 sides even ignoring green).
  // Last resort: pick a tiny random nudge so the bot at least JIGGLES
  // until conditions change (e.g., enemy moves, wall destroyed). Returning
  // (0,0) here was the stuck-pattern Dylan kept seeing — bot frozen until
  // a new path opens. Tiny random direction breaks that.
  // BETA 19.61 — Was 0.15× magnitude (slow jitter). Per Dylan: "being slow
  // does not work." Full magnitude — if all 16 angles blocked even ignoring
  // walls, the bot might as well move fast in the random direction; walls
  // will clamp the actual displacement anyway. Slow jitter just made the
  // stuck-state visible to the player.
  const jitterAngle = (Date.now() % 1000) / 1000 * Math.PI * 2;
  return { moveX: Math.cos(jitterAngle), moveY: Math.sin(jitterAngle) };
}

function getEnemiesInRange(enemies: Enemy[], px: number, py: number, range: number): Enemy[] {
  return enemies
    .filter(e => e.alive && Math.hypot(e.x - px, e.y - py) < range)
    .sort((a, b) => Math.hypot(a.x - px, a.y - py) - Math.hypot(b.x - px, b.y - py));
}

/** Returns true if the bot can afford to open this chest (gold chests are always free) */
function canAffordChest(state: GameState, chest: any): boolean {
  if (chest.type === 'gold') return true; // gold chests always free
  // BETA 16.20 — PSBOT cycle-23: synced to new engine formula 1.7^N (was 3^N
  // stale, was 1.5^N at engine — both out-of-date). Bot predictor must match
  // engine.getChestCost or bot walks to chests it can't afford. Engine line
  // ~2974: Math.round(CHEST_BASE_COST × Math.pow(1.7, chestsOpenedThisRun)).
  const cost = Math.round(15 * Math.pow(1.7, state.chestsOpenedThisRun ?? 0));
  return state.player.gold >= cost;
}

/** BETA 13.51: returns true if ANY un-activated chick magnet statue exists on the map. */
function hasNearbyChickMagnet(state: GameState): boolean {
  const magnets = state.map.roomMap?.chickMagnets ?? [];
  return magnets.some((m: { activated?: boolean }) => !m.activated);
}

/**
 * BETA 13.52: count the gold + XP orbs currently on the map.
 * Used to decide WHEN to commit to a chick magnet — burning a magnet for 5 orbs
 * is a waste, but if 30+ are sitting around (the bot just kited through a horde
 * and left a trail), now is the moment to grab one.
 */
function countMapOrbs(state: GameState): number {
  const orbs = state.lootOrbs?.orbs ?? [];
  return orbs.filter((o: { alive?: boolean; vacuuming?: boolean; type?: string }) =>
    o.alive && !o.vacuuming && (o.type === 'gold' || o.type === 'xp')
  ).length;
}

/** Total integer value of all alive gold orbs on the map. */
function totalMapGoldValue(state: GameState): number {
  const orbs = state.lootOrbs?.orbs ?? [];
  let total = 0;
  for (const o of orbs as Array<{ alive?: boolean; vacuuming?: boolean; type?: string; value?: number }>) {
    if (o.alive && !o.vacuuming && o.type === 'gold') total += (o.value ?? 0);
  }
  return total;
}

/** BETA 13.51: pathfind to the nearest un-activated chick magnet. */
function calcSeekChickMagnetVector(state: GameState): { moveX: number; moveY: number } {
  const player = state.player;
  const magnets = (state.map.roomMap?.chickMagnets ?? []) as Array<{ x: number; y: number; activated?: boolean }>;
  const target = magnets
    .filter(m => !m.activated)
    .reduce<{ x: number; y: number; d: number } | null>((best, m) => {
      const d = Math.hypot(m.x - player.x, m.y - player.y);
      if (!best || d < best.d) return { x: m.x, y: m.y, d };
      return best;
    }, null);
  if (!target) return calcExploreVector(state, 0.016);
  return probeMove(state, player.x, player.y, target.x, target.y);
}

// BETA 13.56: pick the closest meaningful target for a b-line escape, in priority order.
// Chest > shrine > chick magnet > loot orb cluster. No distance gate — if the bot has
// drifted in a circle for 10s, even a far target is worth heading for to break the loop.
function findBestBlineTarget(state: GameState): { x: number; y: number } | null {
  const { player } = state;
  // 1. Closest unopened chest
  let best: { d: number; x: number; y: number } | null = null;
  for (const c of (state.map.roomMap.chests ?? [])) {
    if ((c as { opened?: boolean }).opened || (c as { collected?: boolean }).collected) continue;
    const d = Math.hypot(c.x - player.x, c.y - player.y);
    if (!best || d < best.d) best = { d, x: c.x, y: c.y };
  }
  if (best) return { x: best.x, y: best.y };
  // 2. Closest unspent shrine
  for (const s of (state.map.roomMap.shrines ?? [])) {
    const sa = s as { activated?: boolean; used?: boolean; state?: string; type?: string };
    if (sa.activated || sa.used) continue;
    if (sa.state === 'boss_spawned' || sa.state === 'spent_lit' || sa.state === 'boss_defeated') continue;
    const d = Math.hypot(s.x - player.x, s.y - player.y);
    if (!best || d < best.d) best = { d, x: s.x, y: s.y };
  }
  if (best) return { x: best.x, y: best.y };
  // 3. Closest unactivated chick magnet
  for (const m of ((state.map.roomMap?.chickMagnets ?? []) as Array<{ x: number; y: number; activated?: boolean }>)) {
    if (m.activated) continue;
    const d = Math.hypot(m.x - player.x, m.y - player.y);
    if (!best || d < best.d) best = { d, x: m.x, y: m.y };
  }
  if (best) return { x: best.x, y: best.y };
  // 4. Largest cluster of loot orbs — just pick the closest live orb as anchor
  for (const o of (state.lootOrbs?.orbs ?? [])) {
    const oa = o as { alive?: boolean; vacuuming?: boolean; x: number; y: number };
    if (oa.alive === false || oa.vacuuming) continue;
    const d = Math.hypot(oa.x - player.x, oa.y - player.y);
    if (!best || d < best.d) best = { d, x: oa.x, y: oa.y };
  }
  if (best) return { x: best.x, y: best.y };
  return null;
}

function hasNearbyChest(state: GameState): boolean {
  // BETA 13.53: distance-gated. Old logic returned true for ANY affordable chest
  // anywhere on the map, so the bot would divert toward a chest 4 rooms away,
  // take damage from enemies on the trip, then bail when HP dropped — net result:
  // ran past the close chest, took hits, never opened anything. New: chest must
  // be within 1100 wu (≈ one room hop) to count as a "seek now" target. Farther
  // chests get visited organically through explore.
  const player = state.player;
  const chests = state.map.roomMap.chests ?? [];
  return chests.some(c => {
    if ((c as any).opened || (c as any).collected) return false;
    if (!canAffordChest(state, c)) return false;
    // BETA 13.59: cycle-14 runs 1-3 got 0 chests. 1100 was too tight.
    // Loosened to 1300 — still gated, just more forgiving on early-game routing.
    return Math.hypot(c.x - player.x, c.y - player.y) < 1300;
  });
}

// BETA 17.49 — Dylan: "it keeps standing still not on shrines. that
// will kill you. just wasting seconds." Live 60s sample showed the bot
// frozen for 10.8s (18% of run) trying to reach unreachable shrines.
// Pattern: bot picks closest shrine, can't reach (wall blocks path),
// stands still pressing against wall. probeMove jiggle (17.47) doesn't
// trigger because the goal vector ISN'T (0,0) — engine collision just
// clamps movement to 0. Solution: track shrines we've been stuck on
// and blacklist them for 30 seconds so the hunger chain falls through
// to chest/barrel/loot instead.
// BETA 17.49 — stuck-shrine blacklist.
// BETA 17.50 — Same pattern extended to chests + barrels. Live 60s sample
// after 17.49 showed bot frozen 16.8s (28% of run) at (5400, 7554) trying
// to reach a chest. Bot fixates on closest unreachable target.
const _stuckTargetBlacklist = new Map<string, number>();  // key → expiry timestamp
function _targetKey(prefix: 'shr' | 'cst' | 'brl', s: { id?: string; x?: number; y?: number }): string {
  return `${prefix}:${s.id ?? `${Math.round(s.x ?? 0)}|${Math.round(s.y ?? 0)}`}`;
}
function _isTargetBlacklisted(prefix: 'shr' | 'cst' | 'brl', s: { id?: string; x?: number; y?: number }): boolean {
  const key = _targetKey(prefix, s);
  const exp = _stuckTargetBlacklist.get(key);
  if (exp == null) return false;
  if (Date.now() > exp) {
    _stuckTargetBlacklist.delete(key);
    return false;
  }
  return true;
}
// Convenience wrappers for the call-sites.
function _isShrineBlacklisted(s: { id?: string; x?: number; y?: number }): boolean { return _isTargetBlacklisted('shr', s); }
function _isChestBlacklisted(s: { id?: string; x?: number; y?: number }): boolean { return _isTargetBlacklisted('cst', s); }
function _isBarrelBlacklisted(s: { id?: string; x?: number; y?: number }): boolean { return _isTargetBlacklisted('brl', s); }

function _blacklistNearestShrine(state: GameState, durationMs: number = 30_000): void {
  const shrines = state.map.roomMap.shrines ?? [];
  const px = state.player.x, py = state.player.y;
  let best: { s: typeof shrines[number]; d: number } | null = null;
  for (const s of shrines) {
    const sAny = s as { id?: string; x: number; y: number; state?: string; activated?: boolean; used?: boolean; type?: string };
    if (sAny.activated || sAny.used) continue;
    const st = sAny.state;
    if (st === 'boss_spawned' || st === 'spent_lit' || st === 'boss_defeated') continue;
    const d = Math.hypot(sAny.x - px, sAny.y - py);
    if (!best || d < best.d) best = { s, d };
  }
  if (best) {
    const key = _targetKey('shr', best.s as { id?: string; x?: number; y?: number });
    _stuckTargetBlacklist.set(key, Date.now() + durationMs);
    log(`[STUCK] blacklisted ${key} for ${durationMs / 1000}s (bot froze at ${px.toFixed(0)},${py.toFixed(0)})`);
  }
}
function _blacklistNearestChest(state: GameState, durationMs: number = 30_000): void {
  const chests = state.map.roomMap.chests ?? [];
  const px = state.player.x, py = state.player.y;
  let best: { s: typeof chests[number]; d: number } | null = null;
  for (const c of chests) {
    const cAny = c as { id?: string; x: number; y: number; opened?: boolean; collected?: boolean };
    if (cAny.opened || cAny.collected) continue;
    const d = Math.hypot(cAny.x - px, cAny.y - py);
    if (!best || d < best.d) best = { s: c, d };
  }
  if (best) {
    const key = _targetKey('cst', best.s as { id?: string; x?: number; y?: number });
    _stuckTargetBlacklist.set(key, Date.now() + durationMs);
    log(`[STUCK] blacklisted ${key} for ${durationMs / 1000}s (bot froze at ${px.toFixed(0)},${py.toFixed(0)})`);
  }
}
function _blacklistNearestBarrel(state: GameState, durationMs: number = 30_000): void {
  const barrels = state.map.roomMap?.barrels ?? [];
  const px = state.player.x, py = state.player.y;
  let best: { s: typeof barrels[number]; d: number } | null = null;
  for (const b of barrels) {
    const bAny = b as { id?: string; x: number; y: number; state?: string };
    if (bAny.state === 'broken') continue;
    const d = Math.hypot(bAny.x - px, bAny.y - py);
    if (!best || d < best.d) best = { s: b, d };
  }
  if (best) {
    const key = _targetKey('brl', best.s as { id?: string; x?: number; y?: number });
    _stuckTargetBlacklist.set(key, Date.now() + durationMs);
    log(`[STUCK] blacklisted ${key} for ${durationMs / 1000}s`);
  }
}
export function _clearStuckShrineBlacklist(): void { _stuckTargetBlacklist.clear(); }

/** Returns true if ANY un-activated shrine exists anywhere on the map. No distance cap.
 *  BETA 17.49 — also skips shrines on the stuck-shrine blacklist so the
 *  hunger chain falls through when the closest shrine is unreachable. */
function hasAnyShrine(state: GameState): boolean {
  const shrines = state.map.roomMap.shrines ?? [];
  return shrines.some(s => {
    const sAny = s as { id?: string; x?: number; y?: number; state?: string; activated?: boolean; used?: boolean; type?: string };
    const st = sAny.state;
    if (sAny.activated || sAny.used) return false;
    if (st === 'boss_spawned' || st === 'spent_lit' || st === 'boss_defeated') return false;
    if (sAny.type === 'boss' && brain.avoidBossShrines) return false;
    if (_isShrineBlacklisted(sAny)) return false;  // 17.49
    return true;
  });
}

/** BETA 17.18 — Returns true if any affordable chest exists ANYWHERE on the
 * map (no distance cap). Replaces the hasNearbyChest 1300wu gate that was
 * blocking chest hunger. Player Dylan: "chest hungry. free chest hungry." */
function hasAffordableChestAnywhere(state: GameState): boolean {
  const chests = state.map.roomMap.chests ?? [];
  return chests.some(c => {
    if ((c as any).opened || (c as any).collected) return false;
    if (!canAffordChest(state, c)) return false;
    return true;
  });
}

/** BETA 17.19 — PSBOT cycle-30ah balance fix: nearest distance to closest
 * activatable shrine. Returns Infinity if none exists. Used by the hunger
 * chain to pick CLOSER of shrine vs chest (instead of always preferring
 * shrine which starved chest pickups 19:1). */
function _nearestShrineDistance(state: GameState): number {
  const shrines = state.map.roomMap.shrines ?? [];
  const px = state.player.x, py = state.player.y;
  let best = Infinity;
  for (const s of shrines) {
    const sAny = s as { id?: string; x: number; y: number; state?: string; activated?: boolean; used?: boolean; type?: string };
    if (sAny.activated || sAny.used) continue;
    const st = sAny.state;
    if (st === 'boss_spawned' || st === 'spent_lit' || st === 'boss_defeated') continue;
    if (sAny.type === 'boss' && brain.avoidBossShrines) continue;
    if (_isShrineBlacklisted(sAny)) continue;  // 17.49 — skip stuck-on shrines
    const d = Math.hypot(sAny.x - px, sAny.y - py);
    if (d < best) best = d;
  }
  return best;
}

/** BETA 17.19 — PSBOT cycle-30ah balance fix: nearest distance to closest
 * affordable un-opened chest. Returns Infinity if none exists. */
function _nearestAffordableChestDistance(state: GameState): number {
  const chests = state.map.roomMap.chests ?? [];
  const px = state.player.x, py = state.player.y;
  let best = Infinity;
  for (const c of chests) {
    const cAny = c as { id?: string; x: number; y: number; opened?: boolean; collected?: boolean };
    if (cAny.opened || cAny.collected) continue;
    if (!canAffordChest(state, c)) continue;
    if (_isChestBlacklisted(cAny)) continue;  // 17.50
    const d = Math.hypot(cAny.x - px, cAny.y - py);
    if (d < best) best = d;
  }
  return best;
}

/** BETA 17.45 — Dylan: "NEVER pass up a free chest." Returns true if any
 * un-opened gold chest (type === 'gold' = always free) exists within
 * `radius` world units. Default 1200wu = roughly a screen-diagonal walk. */
function _hasFreeChestNearby(state: GameState, radius: number = 1200): boolean {
  const chests = state.map.roomMap.chests ?? [];
  const px = state.player.x, py = state.player.y;
  for (const c of chests) {
    const cAny = c as { id?: string; x: number; y: number; opened?: boolean; collected?: boolean; type?: string };
    if (cAny.opened || cAny.collected) continue;
    if (cAny.type !== 'gold') continue;
    if (_isChestBlacklisted(cAny)) continue;  // 17.50
    const d = Math.hypot(cAny.x - px, cAny.y - py);
    if (d <= radius) return true;
  }
  return false;
}

/** BETA 17.45 — has ANY affordable PAID chest (cost > 0 but bot has gold). */
function _hasAffordablePaidChest(state: GameState): boolean {
  const chests = state.map.roomMap.chests ?? [];
  for (const c of chests) {
    const cAny = c as { id?: string; x?: number; y?: number; opened?: boolean; collected?: boolean; type?: string };
    if (cAny.opened || cAny.collected) continue;
    if (cAny.type === 'gold') continue;  // gold chests handled separately
    if (!canAffordChest(state, c)) continue;
    if (_isChestBlacklisted(cAny)) continue;  // 17.50
    return true;
  }
  return false;
}

/** BETA 17.48 — Dylan: "its also passing all barrels. that is free xp and
 * free gold. HUGE early game needs." Barrels drop XP + gold on break.
 * Bot should grab any un-broken barrel within reach. Returns true if
 * one exists within `radius` world units. */
function _hasBarrelNearby(state: GameState, radius: number = 1000): boolean {
  const barrels = state.map.roomMap?.barrels ?? [];
  const px = state.player.x, py = state.player.y;
  for (const b of barrels) {
    const bAny = b as { id?: string; x: number; y: number; state?: string };
    if (bAny.state === 'broken') continue;
    if (_isBarrelBlacklisted(bAny)) continue;  // 17.50
    const d = Math.hypot(bAny.x - px, bAny.y - py);
    if (d <= radius) return true;
  }
  return false;
}

/** BETA 17.48 — nearest un-broken barrel position for the seek_barrel goal. */
function _nearestBarrel(state: GameState): { x: number; y: number } | null {
  const barrels = state.map.roomMap?.barrels ?? [];
  const px = state.player.x, py = state.player.y;
  let best: { x: number; y: number; d: number } | null = null;
  for (const b of barrels) {
    if ((b as { state?: string }).state === 'broken') continue;
    // BETA 17.58 — respect barrel stuck-blacklist (same fix as shrine/chest).
    if (_isBarrelBlacklisted(b as { id?: string; x?: number; y?: number })) continue;
    const bx = (b as { x: number }).x;
    const by = (b as { y: number }).y;
    const d = Math.hypot(bx - px, by - py);
    if (!best || d < best.d) best = { x: bx, y: by, d };
  }
  return best ? { x: best.x, y: best.y } : null;
}

/** BETA 17.18 — Returns true if any alive non-vacuuming XP/gold orb exists
 * anywhere on the map. Replaces the 2+/4+ cluster gates that left the bot
 * standing on 1 orb because it didn't count. Player Dylan: "XP hungry." */
function hasAnyLootOrb(state: GameState): boolean {
  const stateAny = state as unknown as { lootOrbs?: { orbs?: Array<{ alive?: boolean; vacuuming?: boolean; type?: string }> } };
  const orbs = stateAny.lootOrbs?.orbs;
  if (!orbs) return false;
  return orbs.some(o =>
    o.alive && !o.vacuuming && (o.type === 'gold' || o.type === 'xp')
  );
}

/** Legacy alias kept for any callers — now delegates to hasAnyShrine. */
function hasNearbyShrine(state: GameState): boolean {
  return hasAnyShrine(state);
}

function applyMovement(state: GameState, mx: number, my: number): void {
  // Inject movement by manipulating the keys Set
  state.keys.delete('w'); state.keys.delete('a');
  state.keys.delete('s'); state.keys.delete('d');
  // BETA 17.23 — PSBOT cycle-30ak CRITICAL fix #3: isFinite guard. If
  // any upstream NaN snuck through (calcFleeVector / calcKiteVector /
  // probeMove / blendAvoid edge cases), don't silently freeze the bot —
  // route to a default movement so it keeps progress. Worst case: bot
  // momentarily moves randomly, which is better than standing still.
  // BETA 20.68 — BRAIN v2 anti-pause watchdog (Dylan: "they pause... rigid").
  // The recon mapped 6 separate zero-magnitude code paths; rather than patch
  // each, ANY sub-deadzone movement intent sustained >0.6s — outside
  // intentional shrine channeling — is overridden with an explore drift.
  // Breaks are counted for the __getBotBrainReport.
  if (bot) {
    const sbW = bot as unknown as { _lastRealMoveT?: number; _stallBreaks?: number };
    const _mag = Math.hypot(mx, my);
    if (Number.isFinite(_mag) && _mag >= 0.05) {
      sbW._lastRealMoveT = state.time;
    } else if (bot.currentGoal !== 'seek_shrine' &&
               state.time - (sbW._lastRealMoveT ?? state.time) > 0.6) {
      const ev = calcExploreVector(state, 0.016);
      mx = ev.moveX; my = ev.moveY;
      sbW._stallBreaks = (sbW._stallBreaks ?? 0) + 1;
      sbW._lastRealMoveT = state.time;
    }
  }
  if (!Number.isFinite(mx) || !Number.isFinite(my)) {
    // NaN/Infinity in intent — log a soft warning and press an arbitrary key
    // so the bot keeps moving rather than freezing.
    log(`[BOT] NaN/Infinity movement intent — fallback to random press`);
    state.keys.add(Math.random() < 0.5 ? 'w' : 'd');
    return;
  }
  // BETA 17.18 — Dylan: "BOTS ARE REALLY DUMB. STAND STILL." Root cause from
  // agent audit: every blend in the movement pipeline (doorway magnet 88/12,
  // green-zone redirect 95/5, center pull 20/80, blendAvoid 60/40) shrinks
  // per-axis components. A unit vector at 80° (0.17, 0.98) survives — but
  // anything diagonal after 4 layered blends drops to (0.10, 0.12) which
  // falls under the 0.15 deadzone and presses NO keys. Bot stands still
  // despite having a valid intent vector.
  //
  // Fix: NORMALIZE the vector first, then threshold on the normalized
  // components. Guarantees any non-zero intent vector presses at least one
  // key matching its direction. True-zero vectors (genuine "stand still")
  // still fall through the magnitude < 0.05 short-circuit.
  const mag = Math.sqrt(mx * mx + my * my);
  if (mag < 0.05) return; // genuinely zero intent — stand still is correct
  const nx = mx / mag;
  const ny = my / mag;
  // Threshold 0.35 on the normalized vector = press a cardinal key for any
  // angle within 70° of it. Diagonal moves (45°) press 2 keys → smooth.
  // Pure cardinal (0°/90°) press 1 key.
  if (ny < -0.35) state.keys.add('w');
  if (ny > 0.35) state.keys.add('s');
  if (nx < -0.35) state.keys.add('a');
  if (nx > 0.35) state.keys.add('d');
}

function simulateKey(state: GameState, key: string): void {
  state.keys.add(key);
  // Remove next frame
  setTimeout(() => state.keys.delete(key), 50);
}

// ═══════════════════════════════════════════════════════════════════════
// DECISION HANDLERS — Level-up, shrine, chest choices
// ═══════════════════════════════════════════════════════════════════════

// ─── Rarity priority for smart card picking ─────────────────────────
const RARITY_RANK: Record<string, number> = {
  legendary: 5, epic: 4, rare: 3, uncommon: 2, common: 1,
};

interface CardContext { hpPct: number; wave: number; weaponsHeld: string[]; characterId?: string; survivalSec?: number; isFragile?: boolean; damageBonusPct?: number; defenseScore?: number; armorScore?: number; buildType?: BuildType; }

/** Returns the index (0-based) of the best upgrade card to pick, using brain preferences. */
function pickBestCardIndex(
  cards: Array<{ rarity?: string; name?: string; label?: string; percName?: string; description?: string; synergy?: string }>,
  ctx?: CardContext,
): number {
  if (cards.length === 0) return 0;

  const hpPct       = ctx?.hpPct       ?? 1;
  const wave        = ctx?.wave        ?? 1;
  const weaponsHeld = ctx?.weaponsHeld ?? [];
  const survivalSec = ctx?.survivalSec ?? 0;
  const isFragile   = ctx?.isFragile   ?? false;
  // BETA 19.45 — Dylan live: "the true key though is for them to get max
  // weapons as soon as possible and not pass them up." Dropped the previous
  // 180s time gate (BETA 17.44's "4 weapons by 3 min mark") — if a slot is
  // open, the bot ALWAYS prioritizes new weapons regardless of run time.
  // A 4-weapon loadout always out-DPS a 3-weapon-plus-items loadout, and
  // the bot was passing up weapon offers at 3:01+ even with empty slots.
  void survivalSec; // keep readable name available for future per-run scaling
  const needsMoreWeapons = weaponsHeld.length < 4;
  // Build a lowercased set of held weapon names for "is this an upgrade?"
  // detection inside the bonus function below.
  const _heldSet = new Set(weaponsHeld.map(w => w.toLowerCase().replace(/_/g, ' ')));

  // BETA 13.32: shrine cards expose `.label`, level-up cards expose `.percName`,
  // chest cards expose `.name`. Normalize so scoring sees a populated name on all paths.
  // (Previously every shrine/levelup pick scored as name=2 — the empty-string fallback —
  // which made the bot effectively pick by rarity alone, ignoring all preferences.)
  // BETA 17.63 — also concatenate `description` so scoring sees the stat text.
  // Chest items like "Peanut's Underwear" have the actual stat ("+12 armor") in
  // description, not name. Without this, fragileDefenseBonus + baseNameScore
  // never matched 'armor' keyword on chest cards.
  const cardName = (c: typeof cards[number]): string =>
    `${(c.name as string | undefined) ?? c.label ?? c.percName ?? ''} ${c.description ?? ''}`.trim();

  // Base keyword priority (before brain adjustments)
  const baseNameScore = (name: string = '') => {
    const n = name.toLowerCase();

    // ── HP-aware survival priority: when near death, heavily prefer sustain ──
    if (hpPct < 0.35) {
      if (n.includes('lifesteal') || n.includes('life steal')) return 18;
      if (n.includes('health') || n.includes('hp') || n.includes('heal') || n.includes('regen')) return 16;
      if (n.includes('armor') || n.includes('shield') || n.includes('evasion')) return 13;
    } else if (hpPct < 0.55) {
      if (n.includes('health') || n.includes('hp') || n.includes('heal') || n.includes('regen') || n.includes('lifesteal')) return 11;
      if (n.includes('armor') || n.includes('shield') || n.includes('evasion')) return 9;
    }

    // ── BETA 13.31: hard penalty for negative buffs (bot was blindly picking "+8% Difficulty") ──
    if (n.includes('difficulty') || n.includes('-')) {
      // "-" inside a name often means a penalty stat (e.g. "-10% damage"); guard with stronger negative
      if (n.includes('difficulty')) return -20;
    }

    // ── Standard scoring ──
    // BETA 13.31: early-wave survival bias (bot was dying in 27s — sustain > offense until level 5+)
    // BETA 13.38 cycle-7 strengthening: cycle-7 data showed bimodal outcomes — most runs die at
    // 23-27s with 6-12 kills, a few snowball to 26+ levels. The early survival bias was too weak
    // (only 1 point above damage) — survival items lost ties with same-rarity damage. Bumped the
    // gap so common/uncommon survival decisively beats same-rarity damage, while legendary damage
    // still beats common survival (40 vs 114). Goal: the bot survives past level 1 to start the snowball.
    if (wave <= 5) {
      if (n.includes('lifesteal') || n.includes('life steal')) return 25;
      if (n.includes('health') || n.includes('hp') || n.includes('heal') || n.includes('regen')) return 22;
      if (n.includes('armor') || n.includes('shield') || n.includes('evasion') || n.includes('overheal')) return 20;
      if (n.includes('damage') || n.includes('crit') || n.includes('attack') || n.includes('dmg')) return 14;
      if (n.includes('fire rate') || n.includes('attack speed') || n.includes('reload')) return 12;
      // BETA 13.60: XP/Luck/Level early-game bias bumped 2→9. Cycle-14 run #3 LV6 shrine
      // picked "+20% Pickup Range" over "+20% XP Gain" because both scored 2 (tiebreaker
      // = first option). XP and luck compound — they ARE the snowball — but they were
      // tied with pickup-range default. Bumped to 9 (between damage 14 and movespeed 8)
      // so XP/luck reliably wins against neutral picks but still loses to true survival.
      if (n.includes('xp') || n.includes('experience') || n.includes('level') || n.includes('ambition')) return 9;
      if (n.includes('luck') || n.includes('lady luck') || n.includes('horseshoe') || n.includes('rabbit foot')) return 9;
      if (n.includes('movement speed') || n.includes('move speed')) return 8;
      if (n.includes('gold') || n.includes('magnet')) return 4; // gold lifts but less than XP
      return 2;
    }
    // BETA 17.53 — late-game survival weights bumped. Was: hp/regen/armor/
    // shield all scored 5 (dwarfed by damage 10) in mid-late game (wave > 5).
    // Live 10-min sample showed bot peakLvl 3 with 5 deaths in 10-game-min —
    // bot was picking damage over survival as difficulty scaled, then dying
    // to swarms it couldn't outheal. Per Dylan: "bot should survive 10 min
    // without nerfs." Survival now scores 12-15 throughout — still loses
    // to damage when offered damage of equal rarity, but no longer
    // catastrophically devalued. HP-aware branch (line 4441-4452 above)
    // STILL adds emergency boost when bot is already low-HP.
    if (n.includes('damage') || n.includes('crit') || n.includes('attack') || n.includes('dmg')) return 13;
    if (n.includes('lifesteal') || n.includes('life steal')) return 18;  // 17.53 — lifesteal sustains thru mid/late game
    if (n.includes('health') || n.includes('hp') || n.includes('heal') || n.includes('regen')) return 15;
    if (n.includes('armor') || n.includes('shield') || n.includes('defense') || n.includes('evasion')) return 14;
    if (n.includes('fire rate') || n.includes('speed') || n.includes('reload')) return 9;
    // Late-game: utility like gold magnet and XP gets a small bump (already rich in offense)
    if (n.includes('gold') || n.includes('xp') || n.includes('magnet') || n.includes('luck')) return wave > 20 ? 6 : 4;
    return 2;
  };

  // Brain preference modifiers (applied on top of base)
  const brainBonus = (name: string = '') => {
    const n = name.toLowerCase();
    const keywords = extractKeywords(n);
    let bonus = 0;
    for (const kw of keywords) {
      // Learned score from past runs
      const mult = brainKeywordMultiplier(brain, kw);
      bonus += (mult - 1.0) * 8; // learned weight contribution
      // Strategic flag bonuses
      if (brain.preferDefense && (kw === 'health' || kw === 'defense')) bonus += 6;
      if (brain.preferOffense && kw === 'damage') bonus += 6;
    }
    return bonus;
  };

  // Weapon upgrade bonus: upgrading a weapon you already hold is usually better
  // than picking up a new one (avoids DPS dilution). More valuable in early waves.
  // BETA 17.09 — Bot AI investigation (Dylan: "make bots smart as me").
  // Dylan's Killa run leveled rpk_machine_gun to lvl 5 (his main DPS, 63% of
  // total damage) instead of spreading picks. The bot was previously
  // tied/losing to "new legendary weapon" offers because legendary base
  // rarity bonus = 80 dominated upgrade = 10. Bumped to 35 (wave<20) /
  // 25 (wave 20+) so an upgrade for a held weapon beats most new-weapon
  // offers below legendary. Legendary new weapons still win (worth picking
  // up something rare) but the bot now commits to its main DPS.
  const weaponUpgradeBonus = (name: string = '') => {
    const n = name.toLowerCase();
    for (const held of weaponsHeld) {
      const heldLower = held.toLowerCase().replace(/_/g, ' ');
      if (n.includes(heldLower) || heldLower.includes(n.replace(/ (level|upgrade|lv\.|tier)/g, '').trim())) {
        return wave < 20 ? 35 : 25;
      }
    }
    return 0;
  };

  // BETA 17.44 — Dylan: "you want 4 weapons by 3 minute mark. to do that
  // you must level up!" When the bot has < 4 weapons in the first 3 min,
  // hard-prefer NEW WEAPON cards over everything (percs, items, upgrades).
  // Detection: card.name matches a weapon in WEAPON_DATABASE, AND the
  // weapon's ID is NOT in weaponsHeld. Bonus is +100 — big enough to beat
  // legendary perc/item base rarity bonuses (80 max) but stacks below the
  // emergency-survival score (which sits at 25 in this scoring layout).
  const newWeaponBonus = (name: string = '') => {
    if (!needsMoreWeapons) return 0;
    const n = name.toLowerCase().trim();
    for (const def of _WEAPON_DATABASE) {
      const defName = def.name.toLowerCase();
      if (n === defName || n.includes(defName)) {
        // Matched a weapon. Is it new (not held)?
        const defLower = def.name.toLowerCase().replace(/_/g, ' ');
        if (!_heldSet.has(defLower) && !_heldSet.has(def.id.toLowerCase())) {
          // BETA 19.41 — Was: unconditional +100 for ANY new weapon.
          // Live Killa run on rpk_suppressor picked Chad Aura + Revolver
          // + Ex Girlfriend on top of starter RPK — DPS got diluted across
          // 4 unsynergistic weapons. The original +100 swamped the brain's
          // build-keyword tier scoring (max ~20 contribution).
          // Now: gate the +100 on the weapon matching the bot's build
          // tier1/tier2 keywords. Off-build new weapons drop to +30 (still
          // beats most perc/item picks but loses to a tier-matching weapon
          // OR a same-tier upgrade). Live data showed bot picking the
          // first new weapon offered regardless of build fit.
          // BETA 19.45 — Dylan: "max weapons as soon as possible and not
          // pass them up." Tier alignment still gives a top-up so the bot
          // PREFERS on-build weapons when multiple are offered, but the
          // floor for ANY new weapon when a slot is open is now +110 —
          // dominant over every item/perc pick (epic items max ~80, brain
          // bonuses cap around 25). Was +30 for off-build (BETA 19.41 hedge
          // against off-build weapon dilution) — that turned out to lose to
          // mediocre items at chest time, leaving slots empty.
          const buildTiers = ctx?.buildType ? BUILD_KEYWORD_TIERS[ctx.buildType] : null;
          if (buildTiers) {
            const inTier1 = Object.keys(buildTiers.tier1).some(kw => defName.includes(kw) || defLower.includes(kw));
            const inTier2 = Object.keys(buildTiers.tier2).some(kw => defName.includes(kw) || defLower.includes(kw));
            if (inTier1) return 140; // ideal weapon for build
            if (inTier2) return 120; // adjacent-tier weapon
            return 110; // off-build but slot-filling — still beats items
          }
          return 130; // no build (adaptive) — always grab a weapon
        }
      }
    }
    return 0;
  };
  // Silence unused-when-needsMoreWeapons-false warning:
  void _heldSet;

  // BETA 13.56: tiered build-archetype bias. Tier 1 = ideal (+12), Tier 2 = good (+6),
  // Tier 3 = generic survival fallback (+3). When the offer pool lacks ideal cards,
  // tier 2/3 still produces a sensible "second-best" pick instead of falling back to
  // pure rarity. 'adaptive' build = empty tiers = no change vs pre-13.56 behavior.
  const buildType = bot?.config?.buildType ?? 'adaptive';
  const buildTiers = BUILD_KEYWORD_TIERS[buildType];
  const buildBonus = (name: string): number => {
    const n = name.toLowerCase();
    let bonus = 0;
    for (const [kw, val] of Object.entries(buildTiers.tier1)) if (n.includes(kw)) bonus += val;
    for (const [kw, val] of Object.entries(buildTiers.tier2)) if (n.includes(kw)) bonus += val;
    for (const [kw, val] of Object.entries(buildTiers.tier3)) if (n.includes(kw)) bonus += val;
    return bonus;
  };

  // BETA 13.82 — coverage bias. Only apply when we can afford to "test" — past
  // first wave AND HP > 50% (don't sabotage early-game or low-HP picks for
  // coverage). +15 sits between Tier 2 (+10) and Tier 1 (+20) so it breaks
  // ties between similar-tier picks but never overrides a build keystone or
  // an emergency heal.
  const coverageEligible = wave > 1 && hpPct > 0.5;
  const coverageBonus = (name: string): number => {
    if (!coverageEligible) return 0;
    // BETA 13.94: noveltyPicks dropped 0.66 → 0.30 cycle-over-cycle (bot
    // converging on familiar picks). Raised bonus +15 → +20 to break ties
    // toward exploration. Still capped below build-keystone tier weight so
    // it never overrides a build-defining pick.
    return isNoveltyItem(name) ? 20 : 0;
  };

  // BETA 16.27 — PSBOT cycle-30 D.1: CHARACTER DIVERSITY BONUS. Per cycle-30
  // pacing audit: 6 of 12 stats have median = 0 across runs because bot
  // tunnels damage/atkSpd. Each character now has must-include stat-name
  // keywords that bump matching cards by +18 (between coverage and Tier 1
  // weight). Forces Cowboy to pick crit-damage cards, Sponge to stack
  // armor/lifesteal, Mr Silly to pick luck. Per Dylan-greenlit Option D.
  const charId = ctx?.characterId ?? 'unknown';
  const charDiversityKeywords: Record<string, string[]> = {
    cowboy:   ['vision', 'adderall', 'crit', 'reflexes'],
    sponge:   ['hutch', 'armor', 'shield', 'tum', 'sponge', 'vitamin', 'lifesteal'],
    mr_silly: ['lady luck', 'lucky', 'horseshoe', 'silly', 'lucky belt'],
    wizard:   ['not viagra', 'puberty', 'projectile', 'big brain', 'vitamin'],
    killa:    ['steroid', 'adderall', 'vision', 'reflexes'],
    viking:   ['adderall', 'steroid', 'vitamin', 'caffeine'],
    chad:     ['caffeine', 'sticky', 'pickup', 'not crack'],
    pirate:   ['greed', 'gold', 'lifesteal', 'lucky', 'horseshoe'],
    lebron:   ['laxative', 'caffeine', 'adderall', 'knockback'],
    tactical: ['caffeine', 'projectile', 'bounce', 'steroid'],
  };
  const charKeywords = charDiversityKeywords[charId] ?? [];
  const charDiversityBonus = (name: string): number => {
    if (charKeywords.length === 0) return 0;
    const n = name.toLowerCase();
    for (const kw of charKeywords) {
      if (n.includes(kw)) return 18;
    }
    return 0;
  };

  // BETA 16.27 — PSBOT cycle-30 D.2: ANTI-TUNNEL PENALTY. When the bot
  // has picked 5+ cards whose name shares a keyword (e.g. all damage),
  // deprioritize more-of-the-same. Reads bot.statCategoryPickCount which
  // tracks lifetime picks per category-keyword for this run. -15 penalty
  // at 5+, -30 at 8+. Encourages diverse builds without hard-blocking.
  const antiTunnelPenalty = (name: string): number => {
    const n = name.toLowerCase();
    const buckets = ['steroid', 'adderall', 'vitamin', 'crack', 'reflexes', 'vision', 'greed', 'laxative', 'caffeine', 'tum', 'horseshoe', 'sponge', 'puberty'];
    for (const bucket of buckets) {
      if (n.includes(bucket)) {
        const cnt = bot?.statCategoryPickCount?.[bucket] ?? 0;
        if (cnt >= 8) return -30;
        if (cnt >= 5) return -15;
        return 0;
      }
    }
    return 0;
  };

  // BETA 16.28 — PSBOT cycle-30f §3A: SYNERGY PAIRS BONUS. Per cycle-28
  // audit: 0 of 8 designed item pairs were both-equipped across 296 runs.
  // Bot AI didn't recognize designed synergies. Now: when scoring an
  // offer, if the player ALREADY OWNS one half of a designed pair AND
  // the offered card is the OTHER half, multiply score by 1.4.
  const SYNERGY_PAIRS: Array<[string, string]> = [
    ['drac', 'dracs gas mask'],          // wpn_dracs_fang + item_dracs_gas_mask
    ['big mac', 'stay frosty'],          // burn DoT + slow
    ['stompatine', 'hutch'],             // sponge build
    ['ugly kittens', 'frozen darts'],    // zone control
    ['poop sling', 'shit'],              // SHIT trail + slow projectile
    ['poop sling', 'shitty cannon'],     // dual-poop combo
    ['voogoo', 'big brain'],             // R09 XP burst + voogoo
  ];
  const synergyPairBonus = (cardName: string): number => {
    const cn = cardName.toLowerCase();
    if (!bot) return 0;
    // Build owned-set from weapons + items the bot has picked up.
    const ownedNames: string[] = [];
    try {
      const ws = getWeaponSystemState();
      if (ws) for (const w of ws.weapons) ownedNames.push(w.definitionId);
      const _items = getItemState();
      if (_items) for (const s of _items.inventory) ownedNames.push(s.item.id, s.item.name.toLowerCase());
    } catch { /* ignore */ }
    const ownedJoined = ownedNames.join(' ').toLowerCase();
    for (const [a, b] of SYNERGY_PAIRS) {
      // If card matches one half AND owned set has the other half
      if (cn.includes(a) && ownedJoined.includes(b) && !cn.includes(b)) return 25;
      if (cn.includes(b) && ownedJoined.includes(a) && !cn.includes(a)) return 25;
    }
    return 0;
  };

  // BETA 16.28 — PSBOT cycle-30f §3B: WEAPON TIER BUMP. Under-picked
  // high-DPS weapons (Drac's Fang, Squirter, Frozen Darts, Ugly Kittens).
  // +12 score nudge so they're more competitive in scoring vs the
  // entrenched Rifle/RPK/Spatula/Revolver tier-1 picks.
  const WEAPON_TIER_BUMPS = ['dracs_fang', 'dracs fang', 'squirter', 'frozen darts', 'frozen_darts', 'ugly kittens', 'ugly_kittens'];
  const weaponTierBump = (name: string): number => {
    const n = name.toLowerCase();
    for (const kw of WEAPON_TIER_BUMPS) {
      if (n.includes(kw)) return 12;
    }
    return 0;
  };

  // BETA 17.72 — BUILD COHESION via weapon-synergy bonus.
  // Items have a `synergy` tag (damage/health/defense/luck/xp/utility).
  // Map the bot's held weapons → primary synergy of the build, then
  // give items matching that synergy a +8 bonus. This makes the bot's
  // build COMPOUND instead of scattering picks across unrelated stats.
  // Dylan's runs show coherent builds (sniper crits + crit-damage items)
  // outperform random picks by 2-3x because of compound effects.
  //
  // Mapping by weapon ID prefix (covers the starter weapon for each char,
  // which tends to anchor the build identity):
  //   peanut_rifle / sniper        → 'damage' / 'crit' (crit synergy)
  //   spatula_slash / melee arc    → 'health' (defense-tank synergy)
  //   revolver / cowboy            → 'damage' (high-crit burst)
  //   rpk_machine_gun              → 'damage' (suppress / sustained)
  //   chad_aura / aura             → 'utility' (size / area scaling)
  //   ugly_kittens / wizard        → 'luck' (crit + drops)
  //   dracs_fang                   → 'damage' (boss melt)
  //   blunderbuss / shitty_cannon  → 'damage'
  // Falls back to 'damage' if unknown.
  const weaponToSynergy: Record<string, string> = {
    peanut_rifle: 'damage',
    spatula_slash: 'health',
    revolver: 'damage',
    rpk_machine_gun: 'damage',
    chad_aura: 'utility',
    ugly_kittens: 'luck',
    dracs_fang: 'damage',
    blunderbuss: 'damage',
    shitty_cannon: 'damage',
    fireball: 'damage',
    squirter: 'damage',
    frozen_darts: 'damage',
    ex_girlfriend: 'damage',
    the_terminator: 'damage',
    ultra_beam: 'damage',
    voogoo_rager_toss: 'damage',
    tactical_basketball: 'utility',
    hurray: 'damage',
  };
  // Determine bot's primary synergy from FIRST weapon held (build anchor).
  const primarySynergy: string | null = (() => {
    if (weaponsHeld.length === 0) return null;
    const firstWeapon = weaponsHeld[0].toLowerCase().replace(/^wpn_/, '');
    for (const [id, syn] of Object.entries(weaponToSynergy)) {
      if (firstWeapon.includes(id) || firstWeapon === id) return syn;
    }
    return null;
  })();
  const buildSynergyBonus = (card: { synergy?: string }): number => {
    if (!primarySynergy || !card.synergy) return 0;
    return card.synergy === primarySynergy ? 8 : 0;
  };

  // BETA 17.54 / 17.65 / 17.66 — Defense bonus, two tiers (rebalanced).
  // 17.65 test: bot stacked 35 armor with 0% damage, leveled to only LV 5
  // in 10 game-min because pure-defense build can't kill fast enough.
  // 17.66 fix: Tier-1 is now MILDER and turns off as soon as bot has SOME
  // armor/shield. Goal: bot grabs 1-2 defensive picks early (enough to
  // tank burst), then normal scoring takes over and damage gets picked.
  //
  // Tier 1 (TRULY NAKED): defenseScore < 5. Modest bonus to defensive picks
  //   (was +30 armor; now +18). Damage at base 14 still loses to armor 38,
  //   but once bot has any armor the tier turns off and damage wins.
  // Tier 2 (DAMAGE-PIVOT): damageBonus >= 50% AND defenseScore < 25.
  //   Same as 17.65 — nudge bot toward defense once damage is already strong.
  const _damageBonusPct = ctx?.damageBonusPct ?? 0;
  const _defScoreCtx = ctx?.defenseScore ?? 0;
  const _armorScoreCtx = ctx?.armorScore ?? _defScoreCtx; // back-compat fallback
  // BETA 17.68 — fragile/naked checks use ARMOR-ONLY score so Tactical
  // Peanut's 30 baseline shield doesn't disable defense picks.
  const isTrulyNaked = _armorScoreCtx < 5;
  const isDamagePivot = !isFragile && _damageBonusPct >= 50 && _armorScoreCtx < 25;
  const fragileDefenseBonus = (name: string = '') => {
    const n = name.toLowerCase();
    // BETA 19.42 — KITE-SYNERGY BONUS for fragile characters (Killa,
    // Cowboy, Mr Silly, anyone with maxHp ≤ 110). Watching a live Killa
    // run: bot offered Stay Frosty (50% slow on hit — perfect kite for
    // glass cannon RPK) vs Nutty T-Bag, picked Nutty (slot-1 tiebreak)
    // because neither item name/desc hit any scored keyword. "slow",
    // "freeze", "stun", "knockback", "stagger", "poison" are crowd-
    // control effects that keep enemies OFF a fragile bot — equivalent
    // to defense for kiting characters. Score these +12 to +18 when
    // fragile so they beat random offensive picks like exploding T-bags.
    if (isFragile) {
      if (n.includes('slow') || n.includes('freeze') || n.includes('frosty')) return 18;
      if (n.includes('stun') || n.includes('stagger')) return 16;
      if (n.includes('knockback')) return 14;
      if (n.includes('poison') || n.includes('gas') || n.includes('toxic')) return 12;
    }
    if (isTrulyNaked) {
      // 17.66 — tightened from <10 to <5, magnitudes reduced from 30/28 → 18/16.
      if (n.includes('armor')) return 18;
      if (n.includes('shield')) return 16;
      if (n.includes('evasion') || n.includes('dodge')) return 14;
      if (n.includes('overheal')) return 12;
      if (n.includes('hp') || n.includes('health') || n.includes('max hp')) return 10;
      if (n.includes('regen') || n.includes('heal')) return 8;
      if (n.includes('lifesteal') || n.includes('life steal')) return 8;
      if (n.includes('thorns')) return 5;
      return 0;
    }
    if (isDamagePivot) {
      // 17.65 — nudge bot toward defense once damage is already strong.
      if (n.includes('armor')) return 18;
      if (n.includes('shield')) return 16;
      if (n.includes('evasion') || n.includes('dodge')) return 12;
      if (n.includes('overheal')) return 10;
      if (n.includes('lifesteal') || n.includes('life steal')) return 10;
      if (n.includes('damage reduction')) return 14;
      return 0;
    }
    return 0;
  };
  void isDamagePivot;

  let bestIdx = 0;
  let bestScore = -1;
  let bestBreakdown = '';
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    const cn = cardName(c);                          // BETA 13.32: works for shrine/levelup/chest
    const rarityScore  = (RARITY_RANK[c.rarity ?? 'common'] ?? 1) * 20;
    const ns = baseNameScore(cn);
    const bb = brainBonus(cn);
    const wu = weaponUpgradeBonus(cn);
    const newW = newWeaponBonus(cn);                 // BETA 17.44: 4 weapons by 3 min
    const buildScore   = buildBonus(cn);             // BETA 13.56: tiered archetype bias
    const covScore     = coverageBonus(cn);          // BETA 13.82: novelty bias for coverage
    // BETA 16.27 — PSBOT cycle-30 D.1+D.2: character diversity + anti-tunnel.
    const charDiv      = charDiversityBonus(cn);
    const antiTun      = antiTunnelPenalty(cn);
    // BETA 16.28 — PSBOT cycle-30f §3A+§3B: synergy pairs + weapon tier bumps.
    const synPair      = synergyPairBonus(cn);
    const wepTier      = weaponTierBump(cn);
    const fragD        = fragileDefenseBonus(cn);    // BETA 17.54: naked bot → +defense
    const buildSyn     = buildSynergyBonus(c);       // BETA 17.72: weapon-synergy build cohesion
    const nameScore    = ns + bb + wu + newW + buildScore + covScore + charDiv + antiTun + synPair + wepTier + fragD + buildSyn;
    const memoryScore  = memoryScoreBonus(cn);       // ±6 from StrategyMemory correlation
    const total = rarityScore + nameScore + memoryScore;
    if (total > bestScore) {
      bestScore = total;
      bestIdx = i;
      bestBreakdown = `rarity=${rarityScore} name=${ns} brain=${bb} weapUp=${wu} newW=${newW} build=${buildScore}(${buildType}) cov=${covScore} charDiv=${charDiv} antiTun=${antiTun} synPair=${synPair} wepTier=${wepTier} fragD=${fragD}(frag=${isFragile}/pivot=${isDamagePivot}) buildSyn=${buildSyn}(${primarySynergy}) mem=${memoryScore} hp=${(hpPct*100).toFixed(0)}% need4W=${needsMoreWeapons}`;
    }
  }
  _lastPickReason = bestBreakdown;
  return bestIdx;
}

// BETA 13.56: name normalizer — level-up cards expose `percName`, shrine cards
// expose `label`, chest cards expose `name`. Without this, every offered/pickedName
// log was 'unknown' because logChoice only read `o.name`. The bot was picking by
// rarity alone and the CSV had ZERO upgrade telemetry.
function _resolveCardName(o: { name?: string; label?: string; percName?: string }): string {
  return (o.name as string | undefined) ?? o.label ?? o.percName ?? 'unknown';
}

// BETA 13.70: stat-effect verification helpers. Snapshot the player's relevant
// computed stats so we can diff them before vs after each pick. If a perk says
// "+10% Damage" but damageBonus delta is 0, the upgrade pipeline is broken for
// that perk — we'll see it in the CSV's topStatDelta column.
function _snapshotPlayerStats(p: { size?: number; maxHp: number; hp: number; speed: number; armor?: number; evasion?: number; lifesteal?: number; luck?: number; critChance?: number; critDamage?: number; damageBonus?: number; attackSpeedBonus?: number; hpRegen?: number; shield?: number; maxShield?: number; pickupRange?: number; xpMultiplier?: number; goldMultiplier?: number; knockbackBonus?: number; projectileCountBonus?: number; rangeBonus?: number; thorns?: number; damageMultiplier?: number; eliteDamageBonus?: number; projectileSpeedBonus?: number; attackAreaBonus?: number; projectileBounceBonus?: number; shieldRegen?: number; overheal?: number; projectilePierceBonus?: number; contactPhaseChance?: number; }): Record<string, number> {
  return {
    // BETA 14.71 — BUG-11 from cycle-8: `size` field was triggering tracker
    // self-validation warnings on every bot startup. Now included so any
    // perc/item that modifies player.size won't false-positive as "broken".
    size: p.size ?? 0,
    maxHp: p.maxHp,
    hp: p.hp,
    speed: p.speed,
    armor: p.armor ?? 0,
    evasion: p.evasion ?? 0,
    lifesteal: p.lifesteal ?? 0,
    luck: p.luck ?? 0,
    critChance: p.critChance ?? 0,
    critDamage: p.critDamage ?? 0,
    damageBonus: p.damageBonus ?? 0,
    attackSpeedBonus: p.attackSpeedBonus ?? 0,
    hpRegen: p.hpRegen ?? 0,
    shield: p.shield ?? 0,
    maxShield: p.maxShield ?? 0,
    pickupRange: p.pickupRange ?? 0,
    xpMultiplier: p.xpMultiplier ?? 0,
    goldMultiplier: p.goldMultiplier ?? 0,
    knockbackBonus: p.knockbackBonus ?? 0,
    projectileCountBonus: p.projectileCountBonus ?? 0,
    rangeBonus: p.rangeBonus ?? 0,
    thorns: p.thorns ?? 0,
    damageMultiplier: p.damageMultiplier ?? 0,
    // BETA 13.78: track the 3 new fields PS Major added in 13.77 so the stat-effect
    // tracker stops false-flagging the shrines/perks that target them as "broken".
    eliteDamageBonus:     p.eliteDamageBonus ?? 0,
    projectileSpeedBonus: p.projectileSpeedBonus ?? 0,
    attackAreaBonus:      p.attackAreaBonus ?? 0,
    // BETA 13.83: track projectileBounceBonus (PS Major shipped this in 13.82 to wire
    // the +1 Projectile Bounce shrine end-to-end). The wire is correct, but cycle-4
    // surfaced 27 false-positive "broken" flags because the snapshot didn't include
    // this field — the stat moved but the tracker couldn't see it. Add it here so
    // the tracker's signal stays clean. Same pattern as the 13.78 fix for evasion/
    // projectileSpeedBonus/attackAreaBonus.
    projectileBounceBonus: p.projectileBounceBonus ?? 0,
    // BETA 19.83 — Tracker self-validation warning surfaced 4 missing fields
    // during a live replica run. shieldRegen wired by 16.36 + 16.43 shrines,
    // overheal by 17.x items, projectilePierceBonus + contactPhaseChance also
    // live stat fields. Adding so picks targeting them don't silently
    // false-flag as "broken" in the bot CSV / signature damage analyzer.
    shieldRegen:           p.shieldRegen ?? 0,
    overheal:              p.overheal ?? 0,
    projectilePierceBonus: p.projectilePierceBonus ?? 0,
    contactPhaseChance:    p.contactPhaseChance ?? 0,
  };
}

function _diffStats(before: Record<string, number>, after: Record<string, number>): Record<string, number> {
  const delta: Record<string, number> = {};
  for (const k of Object.keys(after)) {
    const d = (after[k] ?? 0) - (before[k] ?? 0);
    // ignore noise — only record changes > 0.001 (handles float HP regen ticks)
    if (Math.abs(d) > 0.001) delta[k] = parseFloat(d.toFixed(3));
  }
  return delta;
}

function logChoice(state: GameState, type: ChoiceRecord['type'], opts: Array<{ name?: string; label?: string; percName?: string; rarity?: string }>, idx: number): void {
  if (!bot) return;
  const { player } = state;
  // BETA 13.58: stamp WHEN this pick was made so finalizeRun can back-fill outcomes
  const pickedAtSurvivalSec = bot.currentRun
    ? Math.max(0, (state.time ?? 0) - (bot.currentRun.gameTimeStart ?? 0))
    : 0;
  // BETA 13.70: capture stats BEFORE the pick is applied. The deferred snapshot
  // (set up below) will compare against these to compute the actual effect.
  const statsBefore = _snapshotPlayerStats(player);
  const offeredNames = opts.map(_resolveCardName);
  const pickedName = _resolveCardName(opts[idx] ?? {});
  const record: ChoiceRecord = {
    type,
    wave: state.wave ?? 1,
    level: player.level ?? 1,
    hpPct: player.maxHp > 0 ? Math.round((player.hp / player.maxHp) * 100) / 100 : 1,
    offered: offeredNames,
    pickedIndex: idx,
    pickedName,
    pickedRarity: opts[idx]?.rarity,
    pickedAtSurvivalSec,
    pickedAtKills: state.killCount ?? 0,
    statsBefore,
  };
  bot.currentRun?.choiceLog.push(record);

  // BETA 13.82 — item coverage tracking. Mark every item the bot saw as
  // OFFERED, and the chosen one as PICKED. Both sets persist across runs in
  // localStorage so the next run's pick scoring biases toward items never
  // seen / never picked. New-pick increments the per-run noveltyPicks counter
  // for the CSV.
  markItemsOffered(offeredNames);
  if (pickedName && pickedName !== 'unknown') {
    if (isNoveltyItem(pickedName)) {
      if (bot) bot.noveltyPickCount++;
    }
    markItemPicked(pickedName);
    // BETA 13.99b — count adoption of the 11 new weapons (BETA 13.94-97) and
    // 8 new items (BETA 13.98-99). Per-pick increment, plus name list capped at
    // 10 entries so the CSV cell stays bounded.
    // BETA 14.05 — extended for the 14 new percs (BETA 13.103) via newPercPicks.
    if (bot && bot.currentRun) {
      if (isNewWeaponName(pickedName)) bot.currentRun.newWeapPicks = (bot.currentRun.newWeapPicks ?? 0) + 1;
      if (isNewItemName(pickedName))   bot.currentRun.newItemPicks = (bot.currentRun.newItemPicks ?? 0) + 1;
      if (isNewPercName(pickedName))   bot.currentRun.newPercPicks = (bot.currentRun.newPercPicks ?? 0) + 1;
      if (isNewWeaponName(pickedName) || isNewItemName(pickedName) || isNewPercName(pickedName)) {
        bot.currentRun.newPickNames ??= [];
        if (bot.currentRun.newPickNames.length < 12) bot.currentRun.newPickNames.push(pickedName);
      }
    }
    // BETA 16.27 — PSBOT cycle-30 D.2: increment statCategoryPickCount
    // for the keyword bucket the picked card belongs to. Used by the
    // anti-tunnel scoring penalty above.
    if (bot && pickedName) {
      const lowerName = pickedName.toLowerCase();
      const buckets = ['steroid', 'adderall', 'vitamin', 'crack', 'reflexes', 'vision', 'greed', 'laxative', 'caffeine', 'tum', 'horseshoe', 'sponge', 'puberty'];
      for (const bucket of buckets) {
        if (lowerName.includes(bucket)) {
          bot.statCategoryPickCount = bot.statCategoryPickCount ?? {};
          bot.statCategoryPickCount[bucket] = (bot.statCategoryPickCount[bucket] ?? 0) + 1;
          break;
        }
      }
    }
  }

  // BETA 13.70: 500ms delayed snapshot — by then handle*Selection has run AND
  // itemsAdapter has had a frame or two to recalc. Diff vs statsBefore to verify
  // the pick actually had an effect. Picks that show statDeltas={} are broken.
  setTimeout(() => {
    if (!bot?.currentRun) return;
    const target = bot.currentRun.choiceLog[bot.currentRun.choiceLog.length - 1];
    // If a newer choice was already logged, find ours by reference
    const myRecord = (target === record) ? target : bot.currentRun.choiceLog.find(r => r === record);
    if (!myRecord || !myRecord.statsBefore) return;
    const after = _snapshotPlayerStats(state.player);
    myRecord.statsAfter = after;
    const deltas = _diffStats(myRecord.statsBefore, after);
    myRecord.statDeltas = deltas;
    // Find the strongest change for CSV summary (excluding hp churn from regen ticks)
    let topStat = '';
    let topMag = 0;
    for (const [k, v] of Object.entries(deltas)) {
      if (k === 'hp') continue; // hp churns from regen, not a meaningful pick effect
      const mag = Math.abs(v);
      if (mag > topMag) { topMag = mag; topStat = k; }
    }
    if (topStat) {
      myRecord.topStatDelta = { stat: topStat, delta: deltas[topStat] };
    }
  }, 500);
  // ── Pick bridge: export for v2 ──
  _lastPick = {
    type,
    pickedIndex: idx,
    pickedName: _resolveCardName(opts[idx] ?? {}),
    pickedRarity: opts[idx]?.rarity,
    offered: opts.map(_resolveCardName),
    reason: _lastPickReason,
    timestamp: state.time ?? 0,
  };
}
function _makeCardCtx(state: GameState): CardContext {
  const { player } = state;
  // BETA 17.44 — pass survival time so card scoring can implement
  // Dylan's "4 weapons by 3 minute mark" rule (new-weapon priority
  // when weaponsHeld < 4 and survivalSec < 180).
  const survivalSec = bot?.currentRun
    ? Math.max(0, (state.time ?? 0) - (bot.currentRun.gameTimeStart ?? 0))
    : 0;
  // BETA 17.54 — fragility flag for card scoring. Same defenseScore math
  // as the priority-chain burst-threat check. When the bot has nearly no
  // defensive stats it gets a big card-picking nudge toward armor / hp /
  // shield / evasion so it actually builds defenses instead of stacking
  // damage and getting one-shot.
  const p = player as { armor?: number; evasion?: number; shield?: number; maxShield?: number; damageBonus?: number };
  const armorVal   = p.armor   ?? 0;
  const evasionVal = p.evasion ?? 0;
  const shieldVal  = p.shield  ?? 0;
  // BETA 17.68 — Dylan: balance log observation. Tactical Peanut starts
  // with maxShield 30 baseline. Old defenseScore (armor + eva/2 + shield)
  // treated that 30 starting shield as "well-defended", so isTrulyNaked
  // never fired for Tactical bots → Tier-1 fragile bonus never picked
  // armor → bot ran whole game with 0 armor. Now: separate "armor score"
  // (armor + eva/2, permanent mitigation) from total defenseScore. Tier-1
  // fires when ARMOR is missing, regardless of shield.
  const armorScore = armorVal + (evasionVal * 0.5);
  const defenseScore = armorScore + shieldVal;
  const isFragile = armorScore < 10;
  return {
    hpPct:        player.maxHp > 0 ? player.hp / player.maxHp : 1,
    wave:         state.wave ?? 1,
    weaponsHeld:  bot ? Array.from(bot.weaponsUsed) : [],
    // BETA 16.27 — PSBOT cycle-30 D.1: character ID for diversity bonus.
    characterId:  state.character?.id ?? 'unknown',
    survivalSec,
    isFragile,
    // BETA 17.65 / 17.68 — Tier-2 damage-pivot defense bias inputs.
    damageBonusPct: p.damageBonus ?? 0,
    defenseScore,
    armorScore,
    // BETA 19.41 — Surface bot's build assignment so newWeaponBonus can
    // gate the +100 on tier-matching weapons only. Live Killa run had
    // bot picking off-build weapons (chad_aura, revolver) on rpk_suppressor.
    buildType: bot?.config?.buildType,
  };
}

// BETA 17.23 — PSBOT cycle-30ak fix #7: setTimeout cancellation across
// runs. handleLevelUp/Shrine/Chest schedule chained setTimeouts that
// fire 350-500ms later. When bot dies + restarts, the OLD state object
// is replaced; the OLD setTimeout closures still fire against dead
// state. Phase checks don't help because the dead state's phase is
// frozen (no longer updated). Track all pending timers in a module-
// level array and clear on startNewRun / phase-change.
const _pendingPhaseTimers: number[] = [];
function _clearPendingPhaseTimers(): void {
  for (const t of _pendingPhaseTimers) clearTimeout(t);
  _pendingPhaseTimers.length = 0;
}

// BETA 17.55 — has-defense detector for reroll logic. Returns true if any
// card in the offered set has a TRUE-MITIGATION keyword in its name/label/percName.
// BETA 17.56 — tightened: only armor / shield / evasion / dodge count.
// Max HP, regen, heal, lifesteal DO NOT mitigate burst damage — they sustain.
// A fragile bot needs to survive a single 69-damage burst, which HP / regen
// can't do (extra HP just gets eaten by the same burst). Previous loose list
// counted "max hp" as defense — bot picked a maxhp card and STAYED naked.
const _DEF_KEYWORDS = ['armor', 'shield', 'evasion', 'dodge'];
function _hasDefensiveOption(opts: Array<{ name?: string; label?: string; percName?: string; description?: string }>): boolean {
  // BETA 17.63 — Dylan: deaths at LV 4-7 ALL had 0 armor. Bot was rerolling
  // when offers had no defensive name keyword, but CHEST item names are
  // things like "Peanut's Underwear", "Cloakzy's Championship Belt", "The
  // Wyfe Beater" — none of which match 'armor'/'shield'/'evasion'/'dodge'.
  // The actual stat info is in `description` ("+12 armor", "+8% evasion").
  // Now check BOTH name AND description so chest items get correctly
  // identified as defensive.
  for (const o of opts) {
    const haystack = `${o.name ?? ''} ${o.label ?? ''} ${o.percName ?? ''} ${o.description ?? ''}`.toLowerCase();
    if (!haystack.trim()) continue;
    for (const kw of _DEF_KEYWORDS) {
      if (haystack.includes(kw)) return true;
    }
  }
  return false;
}
// BETA 17.55 / 17.63 — decide whether to reroll the current set.
// Spend at most 2 of the 3 rerolls hunting defense (keeps 1 in reserve).
// BETA 17.63 — Dylan: "do not stop until it plays like me." Death-cause
// analysis of 4 deaths at 17.62 showed bot dying at LV 4-7 with 0 armor,
// 0 evasion, 0 shield EVERY TIME. Root cause: the 17.55 HP gate
// `if (!isFragile && hpPct >= 0.7) return false` only allowed reroll
// when fragile OR HP < 70%. But the bot was usually at FULL HP when
// taking the offer (it heals up between encounters), so reroll never
// fired even though bot was naked. Fix: reroll on fragile, REGARDLESS
// of HP. Defense > healing — armor/shield mitigate ALL incoming damage,
// HP just dies to a single burst.
function _shouldRerollForDefense(state: GameState, opts: Array<{ name?: string; label?: string; percName?: string; description?: string }>, rerollSpentThisOffer: number): boolean {
  if (state.rerollsLeft <= 1) return false; // keep at least 1 reroll in reserve
  if (rerollSpentThisOffer >= 2) return false; // cap at 2 per offer
  const p = state.player as { armor?: number; evasion?: number; shield?: number; hp: number; maxHp: number };
  const armorVal   = p.armor   ?? 0;
  const evasionVal = p.evasion ?? 0;
  // BETA 17.68 — armor-only fragility check (matches _makeCardCtx + burstThreat).
  // Old check included shield, which counted Tactical's 30-baseline shield as
  // defense and stopped reroll-for-defense from ever firing on Tactical bots.
  const armorScore = armorVal + (evasionVal * 0.5);
  const isTrulyNaked = armorScore < 5;
  const hpPct = p.maxHp > 0 ? p.hp / p.maxHp : 1;
  if (!isTrulyNaked && hpPct >= 0.7) return false;
  if (_hasDefensiveOption(opts)) return false;
  return true;
}

function handleLevelUp(state: GameState): void {
  if (!bot) return;
  // Short settle delay then call the selection functions directly (no key simulation)
  const t1 = setTimeout(() => {
    if (state.phase !== 'levelup') return;
    let opts = getLevelUpOptionDisplayData();
    if (opts.length === 0) {
      state.phase = 'playing'; // no options — escape the screen
      return;
    }
    // BETA 17.55 — reroll if fragile + low HP + no defensive option offered.
    let rerollsSpent = 0;
    while (_shouldRerollForDefense(state, opts, rerollsSpent)) {
      state.rerollsLeft--;
      rerollLevelUpOptions();
      rerollsSpent++;
      opts = getLevelUpOptionDisplayData();
      log(`[REROLL] level-up — hunting defense (rerolls left: ${state.rerollsLeft}, attempt ${rerollsSpent})`);
    }
    const idx = pickBestCardIndex(opts, _makeCardCtx(state));
    logChoice(state, 'levelup', opts, idx);
    setLevelUpSelectedIndex(idx);
    const t2 = setTimeout(() => {
      if (state.phase !== 'levelup') return;
      handleLevelUpSelection(idx);
      state.phase = 'playing'; // ← CRITICAL: GameCanvas keyboard handler normally does this
    }, 150) as unknown as number;
    _pendingPhaseTimers.push(t2);
  }, 350) as unknown as number;
  _pendingPhaseTimers.push(t1);
}

function handleShrineChoice(state: GameState): void {
  // BETA 19.27 — Was: raw setTimeout calls with no _pendingPhaseTimers
  // tracking. The 5770-5775 comment block explicitly documented the
  // stale-timer race for level-up; the same race exists here and in
  // handleChestChoice/Direct/UltraChest. Timer IDs now tracked so
  // startNewRun's _clearPendingPhaseTimers() cancels them on restart.
  const t1 = setTimeout(() => {
    if (state.phase !== 'shrine_choice') return;
    let opts = getShrineBuffDisplayOptions();
    if (opts.length === 0) {
      state.phase = 'playing';
      return;
    }
    // BETA 17.55 — reroll if fragile + low HP + no defensive option offered.
    let rerollsSpent = 0;
    while (_shouldRerollForDefense(state, opts, rerollsSpent)) {
      state.rerollsLeft--;
      rerollShrineOptions();
      rerollsSpent++;
      opts = getShrineBuffDisplayOptions();
      log(`[REROLL] shrine — hunting defense (rerolls left: ${state.rerollsLeft}, attempt ${rerollsSpent})`);
    }
    const idx = pickBestCardIndex(opts, _makeCardCtx(state));
    logChoice(state, 'shrine', opts, idx);
    setShrineSelectedIndex(idx);
    const t2 = setTimeout(() => {
      if (state.phase !== 'shrine_choice') return;
      handleShrineSelection(idx);
      state.phase = 'playing'; // ← CRITICAL
    }, 150) as unknown as number;
    _pendingPhaseTimers.push(t2);
  }, 350) as unknown as number;
  _pendingPhaseTimers.push(t1);
}

function handleChestChoice(state: GameState): void {
  // Entered via chest_spin — wait for the spin animation to fully finish.
  // BETA 19.27 — Track timer in _pendingPhaseTimers (see handleShrineChoice).
  const t = setTimeout(() => {
    if (state.phase !== 'chest_choice') return;
    _doChestPick(state);
  }, 3500) as unknown as number;
  _pendingPhaseTimers.push(t);
}

function handleChestChoiceDirect(state: GameState): void {
  // Entered directly (no spin) — short settle delay.
  // BETA 19.27 — Track timer in _pendingPhaseTimers (see handleShrineChoice).
  const t = setTimeout(() => {
    if (state.phase !== 'chest_choice') return;
    _doChestPick(state);
  }, 350) as unknown as number;
  _pendingPhaseTimers.push(t);
}

function _doChestPick(state: GameState): void {
  let opts = getChestOptionDisplayData();
  if (opts.length === 0) {
    state.phase = 'playing'; // no items — escape
    return;
  }
  // BETA 17.55 — reroll if fragile + low HP + no defensive option offered.
  let rerollsSpent = 0;
  while (_shouldRerollForDefense(state, opts, rerollsSpent)) {
    state.rerollsLeft--;
    rerollChestOptions();
    rerollsSpent++;
    opts = getChestOptionDisplayData();
    log(`[REROLL] chest — hunting defense (rerolls left: ${state.rerollsLeft}, attempt ${rerollsSpent})`);
  }
  const idx = pickBestCardIndex(opts, _makeCardCtx(state));
  logChoice(state, 'chest', opts, idx);
  setChestHoverIndex(idx);
  // BETA 19.27 — Track timer in _pendingPhaseTimers (see handleShrineChoice).
  const t = setTimeout(() => {
    if (state.phase !== 'chest_choice') return;
    handleChestItemSelection(idx);
    state.phase = 'playing'; // ← CRITICAL: GameCanvas keyboard handler normally does this
  }, 150) as unknown as number;
  _pendingPhaseTimers.push(t);
}

function handleUltraChest(state: GameState): void {
  // BETA 19.27 — Track both nested timers in _pendingPhaseTimers so
  // stale callbacks don't fire against a dead state after restart.
  const t1 = setTimeout(() => {
    if (state.phase !== 'ultra_chest') return;
    simulateKey(state, '1');
    const t2 = setTimeout(() => {
      if (state.phase === 'ultra_chest') simulateKey(state, 'enter');
    }, 200) as unknown as number;
    _pendingPhaseTimers.push(t2);
  }, 450) as unknown as number;
  _pendingPhaseTimers.push(t1);
}

function handleGameOver(state: GameState): void {
  if (!bot) return;
  finalizeRun(state, 'gameover');

  if (bot.config.autoRestart && (bot.config.maxRuns === 0 || bot.runCount < bot.config.maxRuns)) {
    bot.restartTimer += 1;
    if (bot.restartTimer > bot.config.restartDelayMs / 16) {
      bot.restartTimer = 0;
      simulateKey(state, 'r'); // Restart
    }
  } else if (bot.runCount >= bot.config.maxRuns && bot.config.maxRuns > 0) {
    bot.config.enabled = false;
    log('=== ALL RUNS COMPLETE ===');
    printBotReport();
  }
}

function handleWon(state: GameState): void {
  if (!bot) return;
  finalizeRun(state, 'won');
  if (bot.config.autoRestart && (bot.config.maxRuns === 0 || bot.runCount < bot.config.maxRuns)) {
    setTimeout(() => simulateKey(state, 'r'), 2000);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// STAT TRACKING & ANOMALY DETECTION
// ═══════════════════════════════════════════════════════════════════════

function trackStats(state: GameState, _dt: number): void {
  if (!bot || !bot.currentRun) return;
  const { player } = state;

  // Track damage taken
  if (player.hp < bot.lastHp) {
    const dmg = bot.lastHp - player.hp;
    bot.damageTakenAccum += dmg;
    bot.lastDamageFrame = bot.frameCount;
    // BETA 19.45 — Reactive flee. ANY damage event triggers a 1.5s force-flee
    // so the bot bails out of engage_melee / seek_chest before the next hit
    // lands. Live cascade evidence (Sponge): 5 hits in 6s because bot kept
    // engaging after the first 32-dmg hit. The override is read in the goal-
    // selection block (lines ~3300) before normal priority — only the
    // burstThreat / forceExploreTimer pre-emption can beat it.
    if (dmg > 0.5) {
      // BETA 19.47 — Dylan: bots taking too long to grab shrines/barrels.
      // The 1.5s reactive flee window was eating 30% of the bot's seek time
      // (any hit pushed it into 1.5s of pure flee). Shortened to 0.5s —
      // just long enough to break a one-shot cascade pattern, not derail
      // loot pursuit. Dylan's priority chain: SHRINES > BARRELS > CHESTS
      // > KILL > GATHER > LEVEL > don't get hit. Don't-get-hit lives in
      // dodge logic + blendAvoid; reactive flee is the emergency brake
      // only, not a goal-killer.
      bot.reactiveFleeUntil = (state.time ?? 0) + 0.5;
    }
  }
  // Track healing
  if (player.hp > bot.lastHp) {
    bot.healingAccum += player.hp - bot.lastHp;
  }
  bot.lastHp = player.hp;

  // Track kill count changes
  // BETA 20.73 — audit fix: killsByType was declared, reset, and exported to
  // the run record but NEVER incremented (empty {} in all 123 audited runs).
  // Attribute kills the same way v2's trackCombatData does: when killCount
  // rises, any enemy id that left the alive set since last frame died —
  // credit its last-seen type (+ variant suffix for elite/boss now that
  // 20.72 pipes variant through toEngineEnemy).
  if (state.killCount > (bot.currentRun.killCount || 0)) {
    const _aliveNow = new Set<number>();
    for (const e of state.enemies) { if (e.alive) _aliveNow.add(e.id); }
    // Array.from — tsconfig target predates direct Map iteration (same
    // pattern as v2's trackCombatData). Only allocates on kill frames.
    for (const [eid, ekey] of Array.from(_prevAliveForKillsByType)) {
      if (!_aliveNow.has(eid)) {
        bot.killsByType[ekey] = (bot.killsByType[ekey] ?? 0) + 1;
      }
    }
    bot.currentRun.killCount = state.killCount;
  }
  // Refresh the alive snapshot every frame (cheap: clear + re-set, no allocs
  // beyond map entries; enemy list is capped ~180).
  _prevAliveForKillsByType.clear();
  for (const e of state.enemies) {
    if (e.alive) {
      const v = (e as { variant?: string }).variant;
      _prevAliveForKillsByType.set(e.id, v && v !== 'normal' ? `${e.type}:${v}` : e.type);
    }
  }
}

// BETA 20.73 — last-frame alive-enemy snapshot (id → type[:variant]) backing
// the killsByType attribution above. Cleared on each run start.
const _prevAliveForKillsByType = new Map<number, string>();

/**
 * BETA 13.80 — hitbox near-miss tracker (pathfinding handoff §6).
 *
 * Two signals, both are CSV columns at run end:
 *
 *   enemyNearMissFrames — frames where ANY enemy is within (enemy.size +
 *     player.size + 5wu) of the player but no damage was applied this frame
 *     (i.e. player.hp didn't drop). Could mean: bot dodging well (good signal,
 *     iframe timing tight) OR enemy hitboxes too small (bad — bot phases through).
 *
 *   weaponNearMisses — projectiles that despawn having passed within (proj.size
 *     + 10wu) of an enemy but never connected. Lifetime check via `_projTracking`
 *     map: each frame we mark wasClose / hitAny per projectile id. When a proj id
 *     disappears from the live list, if wasClose && !hitAny → near-miss++.
 *
 * Per-weapon breakdown is in the CSV via the existing `weapons` column — analysis
 * cross-tabs nearMiss / shotsFired per weapon to find tuning candidates.
 */
function trackNearMisses(state: GameState): void {
  if (!bot || !bot.currentRun) return;
  const { player, enemies } = state;

  // ── ENEMY NEAR-MISS: any enemy hugging player but no damage this frame ──
  // We use bot.lastHp as the "damage this frame" check (already tracked). If hp
  // dropped, damage was applied → not a near-miss frame for any enemy.
  const damageAppliedThisFrame = player.hp < bot.lastHp;
  if (!damageAppliedThisFrame) {
    const playerSize = player.size ?? 16;
    let anyClose = false;
    for (const e of enemies) {
      if (!e.alive) continue;
      const ed = Math.hypot(player.x - e.x, player.y - e.y);
      const threshold = (e.size ?? 16) + playerSize + 5;
      if (ed < threshold) { anyClose = true; break; }
    }
    if (anyClose) bot.enemyNearMissFrameCount++;
  }

  // ── WEAPON NEAR-MISS: per-projectile lifetime tracking ──
  const wsState = getWeaponSystemState();
  if (!wsState) return;
  const projs = wsState.projectiles;
  const liveIds = new Set<number>();

  for (const proj of projs) {
    if (!proj.alive) continue;
    liveIds.add(proj.id);

    let entry = bot._projTracking.get(proj.id);
    if (!entry) {
      entry = { wasClose: false, hitAny: false };
      bot._projTracking.set(proj.id, entry);
    }

    // Once a projectile has hit anything, it's no longer a near-miss candidate.
    if (proj.hitEnemyIds.size > 0) entry.hitAny = true;

    // Cheap proximity check: scan enemies once. If any was within (proj.size +
    // 10wu) of proj this frame, mark wasClose. Only run if not already marked
    // (saves work on long-lived close-flying projectiles).
    if (!entry.wasClose && !entry.hitAny) {
      // BETA 13.94: raised threshold +10 → +30 wu. Cycle-3/4/5b all
      // produced 0 weaponNearMisses across 600+ runs; +10 is too tight
      // for auto-aim weapons, which rarely miss this close. +30 captures
      // grazes that DON'T hit and gives us first hitbox-tuning signal.
      const closeThr = proj.size + 30;
      for (const e of enemies) {
        if (!e.alive) continue;
        const ed = Math.hypot(proj.x - e.x, proj.y - e.y);
        if (ed < closeThr) { entry.wasClose = true; break; }
      }
    }
  }

  // Sweep: any tracked projectile that's no longer in liveIds has despawned.
  // If it was close and never hit anything → register a near-miss.
  // Array.from() to avoid downlevelIteration TS error (same pattern as line 832).
  for (const [id, entry] of Array.from(bot._projTracking)) {
    if (liveIds.has(id)) continue;
    if (entry.wasClose && !entry.hitAny) bot.weaponNearMissCount++;
    bot._projTracking.delete(id);
  }
}

function detectAnomalies(state: GameState, dt: number): void {
  if (!bot || !bot.currentRun) return;
  const { player, enemies } = state;

  // ── Stuck detection ──
  // Fires when bot wiggles in place — tries progressively different escape directions
  // instead of hammering the same blocked angle.
  // BETA 16.90 — PSBOT cycle-30k §11B: 5 of 10 Sponge runs stuck at
  // fixed coords. Sponge has a larger hitbox than default chars
  // (sprite-matched 32 → ~36-40 effective). Tighter stuck params for
  // Sponge specifically: detect after 2s instead of 3s, and at a 30wu
  // movement threshold instead of 20wu — bigger sprite = more wall
  // contact = more "wiggle in place but technically moving" patterns
  // that the default thresholds miss.
  const _isSponge = (state.character?.id ?? '') === 'sponge';
  const _stuckDistThreshold = _isSponge ? 30 : 20;
  const _stuckTimeThreshold = _isSponge ? 2 : 3;
  const dist = Math.hypot(player.x - bot.lastPos.x, player.y - bot.lastPos.y);
  if (dist < _stuckDistThreshold) {
    bot.stuckTimer += dt;
    if (bot.stuckTimer > _stuckTimeThreshold) {
      bot.stuckTimer = 0;

      // Track whether this is the same stuck spot or a new one
      const sameSpot = bot.stuckOrigin != null &&
        Math.hypot(player.x - bot.stuckOrigin.x, player.y - bot.stuckOrigin.y) < 250;
      if (sameSpot) {
        bot.stuckCount++;
      } else {
        bot.stuckCount = 1;
        bot.stuckOrigin = { x: player.x, y: player.y };
      }

      addAnomaly('warning', 'movement',
        `Bot stuck at (${Math.round(player.x)}, ${Math.round(player.y)}) attempt ${bot.stuckCount}`,
        { x: player.x, y: player.y });

      // BETA 13.80 — pathfinding handoff Fix 1 wire-up. PS Major shipped
      // markCellAsTrap(worldX, worldY) in flowField.ts as part of BETA 13.79;
      // this is the bot side that calls it. When the bot has been stuck > 4
      // times at the same coord (cluster pattern from the (2960, 4631) /
      // (9100, 2620) smoking guns), penalize that cell in the flow field so
      // subsequent BFS rebuilds route AROUND it. Dedupe by cell key so we only
      // mark each unique cell once per "streak" — the API stacks penalty
      // automatically when the streak resets and re-fires.
      if (bot.stuckCount > 4) {
        const cellKey = `${Math.floor(player.x / 64)},${Math.floor(player.y / 64)}`;
        if (cellKey !== bot.lastTrapCellKey) {
          markCellAsTrap(player.x, player.y);
          bot.trapCellsMarkedCount++;
          bot.lastTrapCellKey = cellKey;
          log(`[Trap-cell] Marked (${Math.round(player.x)}, ${Math.round(player.y)}) — flow field will route around. Total this run: ${bot.trapCellsMarkedCount}`);

          // BETA 14.82 — Fix 1 (PSBOT RUN cycle-11 chad infinite-loop bug).
          // When the bot accumulates ≥5 trap-cell markings within 100wu radius
          // within 30s, it's in a flow-field deadlock (every neighboring cell
          // is also a "trap" so flow field has nowhere to route). Force ESCAPE
          // mode: pick a random distant target + direct movement (skip flow
          // field) for 8 seconds. After 8s the trap cluster fades and bot
          // resumes normal navigation.
          _recentTraps.push({ x: player.x, y: player.y, t: state.time ?? 0 });
          // Cap the recent-trap list to 50 entries (drop-oldest)
          while (_recentTraps.length > 50) _recentTraps.shift();
          const now = state.time ?? 0;
          // Count traps within 100wu radius within last 30 seconds
          const _RECENT_TRAP_WINDOW_SEC = 30;
          const _DEADLOCK_RADIUS_WU = 100;
          const _DEADLOCK_THRESHOLD = 5;
          let cluster = 0;
          for (const tr of _recentTraps) {
            if (now - tr.t > _RECENT_TRAP_WINDOW_SEC) continue;
            const dx = tr.x - player.x;
            const dy = tr.y - player.y;
            if (dx * dx + dy * dy <= _DEADLOCK_RADIUS_WU * _DEADLOCK_RADIUS_WU) cluster++;
          }
          if (cluster >= _DEADLOCK_THRESHOLD && bot.deadlockEscapeUntil < now) {
            // Pick a distant random target to break the deadlock. Direct movement
            // (no flow field) for the next 8s. Distance 800-1200wu in a random
            // direction so we definitely leave the trap zone.
            const ang = Math.random() * Math.PI * 2;
            const dist = 800 + Math.random() * 400;
            bot.deadlockEscapeTarget = {
              x: player.x + Math.cos(ang) * dist,
              y: player.y + Math.sin(ang) * dist,
            };
            bot.deadlockEscapeUntil = now + 8;
            bot.deadlockEscapeStartPos = { x: player.x, y: player.y };
            log(`[FLOW FIELD DEADLOCK] cluster=${cluster} traps in 100wu within 30s — entering ESCAPE mode for 8s. Target: (${Math.round(bot.deadlockEscapeTarget.x)}, ${Math.round(bot.deadlockEscapeTarget.y)})`);

            // Fire stuck event with new category so analyst can see this in CSV
            try {
              const enemiesInRange: { type: string; dist: number }[] = [];
              let nearestDist = Infinity;
              for (const e of state.enemies ?? []) {
                if (!e.alive) continue;
                const ed = Math.sqrt((e.x - player.x) ** 2 + (e.y - player.y) ** 2);
                if (ed < 200) enemiesInRange.push({ type: e.type, dist: Math.round(ed) });
                if (ed < nearestDist) nearestDist = ed;
              }
              const enemyTypesNearby: Record<string, number> = {};
              for (const er of enemiesInRange) enemyTypesNearby[er.type] = (enemyTypesNearby[er.type] ?? 0) + 1;
              const hpFrac = state.player.maxHp > 0 ? state.player.hp / state.player.maxHp : 0;
              trackStuckEvent({
                t: now,
                x: Math.round(player.x), y: Math.round(player.y),
                hp: state.player.hp, hpFrac,
                level: state.player.level ?? 1,
                kills: state.killCount ?? 0,
                enemyCount: enemiesInRange.length,
                enemyTypesNearby,
                hasWeaponInRange: nearestDist < 300,
                hasLoSToNearestEnemy: nearestDist < 400,
                nearestEnemyDist: Math.round(nearestDist === Infinity ? 9999 : nearestDist),
                category: 'flow_field_deadlock',
              });
            } catch (_e) { /* never let diagnostics crash gameplay */ }
          }
        }
      }

      // Angle the bot was TRYING to go — this direction is blocked
      const blockedAngle = bot.targetPos
        ? Math.atan2(bot.targetPos.y - player.y, bot.targetPos.x - player.x)
        : Math.random() * Math.PI * 2;

      // Escape sequence: opposite → left 90° → right 90° → back-left 135° → back-right 135°
      // Each consecutive stuck event at the same spot rotates to the next escape angle
      const escapeAngles = [
        blockedAngle + Math.PI,           // attempt 1: just go the other way
        blockedAngle + Math.PI / 2,       // attempt 2: left
        blockedAngle - Math.PI / 2,       // attempt 3: right
        blockedAngle + Math.PI * 3 / 4,   // attempt 4: back-left diagonal
        blockedAngle - Math.PI * 3 / 4,   // attempt 5: back-right diagonal
      ];
      const escapeAngle = escapeAngles[(bot.stuckCount - 1) % escapeAngles.length];

      // Find the best gap to escape through:
      // Priority 1 — any gap within 300px (including one we're right next to — push through it)
      // Priority 2 — nearest gap in the escape direction within 1200px
      // Priority 3 — raw 400px move in escape direction
      const rooms = state.map.roomMap?.rooms ?? [];
      const { ROOM_SIZE } = state.map;

      let nearestGap: { x: number; y: number; dist: number } | null = null;
      let bestAlignedGap: { x: number; y: number } | null = null;
      let bestAlignScore = Infinity;

      for (const room of rooms) {
        for (const gap of (room.gaps ?? [])) {
          const gx = gap.x + gap.w / 2;
          const gy = gap.y + gap.h / 2;
          const d = Math.hypot(gx - player.x, gy - player.y);
          if (d > 1200) continue;
          // Track the absolute nearest gap (no exclusion zone — if stuck AT doorway, go through it)
          if (!nearestGap || d < nearestGap.dist) {
            nearestGap = { x: gx, y: gy, dist: d };
          }
          // Also track best angle-aligned gap
          const gapAngle = Math.atan2(gy - player.y, gx - player.x);
          const angDiff = Math.abs(((gapAngle - escapeAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
          if (angDiff < 1.0 && angDiff < bestAlignScore) {
            bestAlignScore = angDiff;
            bestAlignedGap = { x: gx, y: gy };
          }
        }
      }

      if (nearestGap && nearestGap.dist < 300) {
        // Right next to a gap — just push straight through its center
        bot.targetPos = { x: nearestGap.x, y: nearestGap.y };
      } else if (bestAlignedGap) {
        // Nearest gap in escape direction
        bot.targetPos = bestAlignedGap;
      } else if (nearestGap) {
        // Any gap at all
        bot.targetPos = { x: nearestGap.x, y: nearestGap.y };
      } else {
        // No gaps found — move 400px in escape direction
        bot.targetPos = {
          x: player.x + Math.cos(escapeAngle) * 400,
          y: player.y + Math.sin(escapeAngle) * 400,
        };
      }

      // Re-route via nav graph to the escape target
      if (bot.navGraph && bot.targetPos) {
        bot.waypointPath = findNavPath(bot.navGraph, player.x, player.y, bot.targetPos.x, bot.targetPos.y);
        bot.waypointIdx  = 0;
      }
      bot.goalTimer = 4;
      bot.currentGoal = 'explore';
    }
  } else {
    bot.stuckTimer = 0;
    // Clear streak if bot has moved well away from where it was stuck
    if (bot.stuckOrigin != null &&
        Math.hypot(player.x - bot.stuckOrigin.x, player.y - bot.stuckOrigin.y) > 300) {
      bot.stuckCount = 0;
      bot.stuckOrigin = null;
      // BETA 13.80: also clear the last-trap-cell dedupe so if the bot returns
      // to the same coord later in the run and gets stuck again, we re-mark it
      // (penalty stacks in flowField, capped at 1000).
      bot.lastTrapCellKey = null;
    }
  }
  bot.lastPos = { x: player.x, y: player.y };

  // ── HP overflow ──
  if (player.hp > player.maxHp + 1) {
    addAnomaly('bug', 'hp_overflow', `HP ${player.hp.toFixed(0)} exceeds maxHP ${player.maxHp}`, { hp: player.hp, maxHp: player.maxHp });
  }

  // ── Enemy inside wall ──
  for (const e of enemies) {
    if (!e.alive) continue;
    // Check if enemy is inside the world border
    if (e.x < 100 || e.y < 100 || e.x > state.map.WORLD_SIZE - 100 || e.y > state.map.WORLD_SIZE - 100) {
      addAnomaly('warning', 'enemy_oob', `Enemy ${e.type}#${e.id} at (${Math.round(e.x)}, ${Math.round(e.y)}) near/outside world border`, { enemy: e.type, x: e.x, y: e.y });
    }
  }

  // ── Negative gold ──
  if (player.gold < 0) {
    addAnomaly('bug', 'negative_gold', `Player gold is ${player.gold}`, { gold: player.gold });
  }

  // ── XP regression ──
  if (player.xp < 0) {
    addAnomaly('bug', 'negative_xp', `Player XP is ${player.xp}`, { xp: player.xp });
  }

  // ── Invincibility stuck ──
  if (player.invincibleTimer > 10) {
    addAnomaly('bug', 'invincible_stuck', `Invincibility timer stuck at ${player.invincibleTimer.toFixed(1)}s`, { timer: player.invincibleTimer });
  }

  // ── Enemy count spike ──
  if (enemies.length > 200) {
    addAnomaly('warning', 'enemy_flood', `${enemies.length} enemies alive — possible spawn leak`, { count: enemies.length });
  }

  // ── Zero-damage enemies ──
  for (const e of enemies) {
    if (e.alive && e.damage <= 0) {
      addAnomaly('info', 'zero_damage_enemy', `Enemy ${e.type}#${e.id} has 0 damage`, { enemy: e.type });
    }
  }
}

function addAnomaly(severity: Anomaly['severity'], category: string, description: string, data?: any): void {
  if (!bot || !bot.currentRun) return;
  // Deduplicate — don't log the same category more than once per 5 seconds
  const recent = bot.currentRun.anomalies;
  const last = recent.filter(a => a.category === category).pop();
  if (last && Date.now() - last.timestamp < 5000) return;

  const anomaly: Anomaly = { timestamp: Date.now(), severity, category, description, data };
  bot.currentRun.anomalies.push(anomaly);
  if (bot.config.logLevel === 'verbose') {
    console.warn(`[BOT ${severity.toUpperCase()}] ${category}: ${description}`);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// RUN LIFECYCLE
// ═══════════════════════════════════════════════════════════════════════

function onPhaseChange(state: GameState, from: string, to: string): void {
  if (!bot) return;

  // Reset per-phase handler lock so the new phase's handler fires once
  bot.phaseHandledThisEntry = false;

  // Start a new run when entering 'playing' from a non-playing state
  if (to === 'playing' && !bot.currentRun) {
    startNewRun(state);
  }

  if (bot.config.logLevel === 'verbose') {
    log(`Phase: ${from} → ${to}`);
  }
}

function startNewRun(state: GameState): void {
  if (!bot) return;
  // BETA 17.22 — PSBOT cycle-30ai B4: apply per-character brain at run
  // start now that character is known. Re-fires global blend cache so
  // hot-reloaded brains get the latest Supabase aggregate when stale.
  const _charId = (state as unknown as { _characterId?: string })._characterId;
  _applyPerCharacterBrain(_charId);
  void _fetchAndBlendGlobalBrain(brain);

  // BETA 15.25 — telemetry pacing reset per-run. Without this, run 2's
  // long-frame detector would compute delta against run 1's last frame
  // (could be 30+ seconds of menu time → false-positive freeze report).
  // 5-min snapshot timer also resets so each run gets at most 1 snapshot
  // until 5 min in (instead of inheriting near-trigger state from prior run).
  _lastBotUpdateWallTime = 0;
  _lastInProgressSnapshotMs = 0;
  _prevTelemetryPhase = '';
  // BETA 15.34 — Pick audit reset. Without this, run 2's audit log would
  // contain run 1's shrine/perc/chest picks. Reset clears the in-module
  // array. Reset MUST happen here, not in finalizeRun, because the perc
  // pick auditor fires DURING gameplay (before finalizeRun runs).
  resetPickAudit();

  // BETA 15.28 — spawn-timing tracker reset per-run. Without this, run 2's
  // first-spawn timestamps would carry over from run 1 (showing time-since-
  // botfarm-launch instead of time-into-this-run), and bucket counts would
  // accumulate across all runs.
  _seenEnemyIds.clear();
  _spawnBucket0to30 = 0;
  _spawnBucket30to60 = 0;
  _spawnBucket60to120 = 0;
  _spawnBucket120to300 = 0;
  _peakAliveCount = 0;
  _peakAliveTime = 0;
  _firstZombieSpawnTime = -1;
  // BETA 15.31 — perc-pick rarity reset (delegated to peanutPercSystem
  // module owns the counters; we just trigger the clear).
  resetPercPickRarityCounts();
  // BETA 14.74 — Tracker per-run reset. weaponLab module-level trackers
  // (percTracker, itemTracker, shrineTracker, eventLog, floorEffectTracker,
  // weaponEnemyMatrix, synergyTracker, deathContext, nearMissEventsLog,
  // retreatEventsLog, AND BETA 14.72 bot diagnostics: botPositionTrail,
  // stuckEvents, botMapCellVisits, decisionTrace) are MODULE-level singletons.
  // Without an explicit clear, run 2's CSV row contained run 1's events;
  // run 3's row contained run 1+2's events; etc. Phase 0 sanity check on
  // cycle-9.5 CSV (master_50runs_1777661697950.csv) confirmed the bleed.
  // clearTrackers() also internally calls clearBotDiagnostics() so all
  // 14.72 streams reset too. PSBOT RUN's analyst pass needs per-run data
  // to compute kill rates / death rates / median survival accurately.
  clearTrackers();
  // BETA 13.58: auto-pick build from selected character UNLESS the user has
  // manually overridden via window.__setBotBuild() (which writes localStorage).
  // No localStorage entry → fall back to character mapping. Mapping miss →
  // keep whatever's currently set (likely 'adaptive').
  const userOverride = (() => { try { return localStorage.getItem('goopzone_bot_build'); } catch { return null; } })();
  if (!userOverride) {
    const charId = state.character?.id;
    const autoBuild = charId ? CHARACTER_BUILD[charId] : undefined;
    if (autoBuild && autoBuild !== bot.config.buildType) {
      bot.config.buildType = autoBuild;
      const overrides = BUILD_CONFIG_OVERRIDES[autoBuild];
      if (overrides) Object.assign(bot.config, overrides);
      log(`Auto-build: character "${charId}" → "${autoBuild}"`);
    }
  }

  // BETA 17.23 — PSBOT cycle-30ak CRITICAL fix #5: cross-run state reset.
  // 14+ flags persisted between runs without reset, causing the "works at
  // first then stays still" pattern across runs 2+. Each flag below was
  // verified to either lock movement, NaN-cascade coordinates, or trigger
  // false anti-camp / b-line / room-cooldown logic at fresh run start.
  bot.forceEscapeRoomId = null;
  bot.forceExploreTimer = 0;
  bot.goalLockTimer = 0;
  bot.blineUntil = 0;
  bot.blineTarget = null;
  (bot as unknown as { driftCenter?: unknown }).driftCenter = null;
  (bot as unknown as { driftStartTime?: number }).driftStartTime = 0;
  bot.currentRoomId = null;
  (bot as unknown as { currentRoomEnteredAt?: number }).currentRoomEnteredAt = 0;
  if (bot.roomCooldowns && typeof bot.roomCooldowns.clear === 'function') {
    bot.roomCooldowns.clear();
  }
  bot.campTimer = 0;
  bot.recentMoveDist = 0;
  bot.recentMoveTime = 0;
  bot.currentGoal = 'explore';
  bot.lastPos = { x: state.player.x, y: state.player.y };
  (bot as unknown as { statCategoryPickCount?: Record<string, number> }).statCategoryPickCount = {};
  // Module-level traps from previous run's deadlock-escape would trigger
  // immediate "trap detected" on respawn at the same death position.
  _recentTraps.length = 0;
  // Clear stale per-frame caches that referenced the dead state
  bot.phaseHandledThisEntry = false;
  // Cancel pending setTimeout handles from the previous run's
  // handleLevelUp/Shrine/Chest. Without this, the OLD closures fire
  // 350-500ms after a fresh run starts and either no-op or write to
  // the dead state. Closes Path #4 from cycle-30ak.
  _clearPendingPhaseTimers();

  bot.runCount++;
  saveRunCount(bot.runCount); // persist so run numbers are global across page reloads
  bot.currentRun = {
    runNumber: bot.runCount,
    gameVersion: GAME_VERSION,
    buildType: bot.config.buildType, // BETA 13.56: tag every run with its archetype
    characterId: state.character?.id, // BETA 13.73: character id (cowboy, killa, viking, etc.)
    timeScale: debugTimeScale,
    startTime: Date.now(),
    endTime: 0,
    gameTimeStart: state.time ?? 0,
    survivalTimeSec: 0,
    wallClockSec: 0,
    deathCause: 'unknown',
    floorReached: state.map.level,
    waveReached: state.wave,
    killCount: 0,
    killsByType: {},
    damageDealt: 0,
    damageTaken: 0,
    healingReceived: 0,
    goldCollected: 0,
    chestsOpened: 0,
    shrinesActivated: 0,
    itemsCollected: [],
    weaponsUsed: [],
    levelReached: state.player.level,
    upgradesPicked: [],
    upgradesSkipped: [],
    choiceLog: [],
    roomsVisited: 0,
    deathPosition: { x: 0, y: 0 },
    hpAtDeath: state.player.hp,
    anomalies: [],
    // BETA 16.28 — PSBOT cycle-30f §1.6 Bug 8: always-write zero-defaults
    // for the final*Pct fields PSBOT audit found missing in 90% of
    // RUN_PARTIAL snapshots. Pre-finalize, fields stay 0 — better than
    // null because PSBOT's audit math `?.field` returns undefined and
    // the field gets categorized as "missing" instead of "zero."
    // finalize() overwrites with real values once a run ends. Only
    // typed-on-RunLog fields here; finalShield/Evasion/MoveSpeedPct flow
    // through buildExtendedTelemetryFields (cycle-27 shared helper).
    finalPhaseChancePct: 0,
    finalProjectileBounce: 0,
    finalLuckPct: 0,
  };
  // Reset accumulators
  bot.lastHp = state.player.hp;
  bot.damageDealtAccum = 0;
  resetRunDamageDealt(); // reset weaponAdapter's live damage counter too
  bot.damageTakenAccum = 0;
  bot.healingAccum = 0;
  bot.killsByType = {};
  _prevAliveForKillsByType.clear(); // BETA 20.73 — fresh snapshot per run
  bot.roomsVisitedSet = new Set();
  bot.upgradesPicked = [];
  bot.upgradesSkipped = [];
  bot.itemsCollected = [];
  bot.weaponsUsed = new Set();
  // BETA 13.59: reset invisible-behavior counters for the new run
  bot.blineTriggerCount = 0;
  bot.trailTeleportCount = 0;
  bot.namedEliteFleeCount = 0;
  // BETA 13.80: reset pathfinding-fix-1 + hitbox near-miss counters for the new run
  bot.trapCellsMarkedCount = 0;
  bot.lastTrapCellKey = null;
  bot.enemyNearMissFrameCount = 0;
  bot.weaponNearMissCount = 0;
  bot._projTracking.clear();
  // BETA 13.82: reset novelty-pick counter for the new run
  bot.noveltyPickCount = 0;
  // BETA 14.05: reset floor-effect + kitten-trap accumulators for the new run
  _seenGroundEffectIds.clear();
  _triggeredGroundEffectIds.clear();
  _groundEffectSourceCounts.clear();
  _groundEffectSpawnCounts.clear(); // BETA 16.55 — PSBOT cycle-30l
  _seenKittenIds.clear();
  _activeKittenIds.clear();
  // BETA 14.06: reset per-weapon first-hit-time accumulators for the new run
  _firstHitProjIds.clear();
  _weaponHitTimeSum.clear();
  _weaponHitTimeCount.clear();
  // BETA 14.06: reset revive-detection state
  _lastFrameHpFraction = 1.0;
  _sheeeshFiredThisRun = 0;
  _liarFiredThisRun = 0;
  // BETA 14.07: reset projectile chase + precision metric accumulators
  _projLastAngle.clear();
  _weaponTurnEvents.clear();
  _weaponMaxChaseDurationByProj.clear();
  _projChaseFrames.clear();
  _timeToFirstKillSec = -1;
  _phaseTimePlaying = 0;
  _phaseTimeLevelup = 0;
  _phaseTimeChest = 0;
  _phaseTimeShrine = 0;
  _distanceTraveled = 0;
  _lastFramePlayerX = state.player.x;
  _lastFramePlayerY = state.player.y;
  _enemiesAliveSum = 0;
  _enemiesAliveSamples = 0;
  _kiteDistanceSum = 0;
  _kiteDistanceSamples = 0;
  // BETA 15.42 (re-add) — long-frame CSV-counter reset (PS Major's
  // _lastBotUpdateWallTime is reset elsewhere in the same lifecycle).
  _longFrameCount = 0;
  _longestFrameMs = 0;
  _longFrameLastContext = '';
  bot.stuckTimer = 0;
  bot.lastPos = { x: state.player.x, y: state.player.y };
  // Build nav graph from actual map layout so bot knows real doorway paths
  bot.navGraph    = buildNavGraph(state);
  bot.waypointPath = [];
  bot.waypointIdx  = 0;
  log(`Nav graph built: ${bot.navGraph.nodes.size} nodes`);

  // BETA 13.83 — once per session, validate that _snapshotPlayerStats covers every
  // numeric Player field. Cycle-4 caught a 27-instance false-positive flagging on
  // +1 Projectile Bounce because projectileBounceBonus wasn't in the snapshot.
  // This auto-runs on the first run start so future field additions get caught
  // immediately in the console rather than 27 cycles later.
  if (!_trackerValidated) {
    validateTrackerCoverage(state.player as unknown as Record<string, unknown>);
  }

  log(`=== RUN #${bot.runCount} STARTED ===`);
}

function finalizeRun(state: GameState, outcome: 'gameover' | 'won'): void {
  if (!bot || !bot.currentRun) return;
  // Only finalize once
  if (bot.currentRun.endTime > 0) return;

  const run = bot.currentRun;
  run.endTime = Date.now();
  run.wallClockSec    = (run.endTime - run.startTime) / 1000;
  // Use game time (state.time delta) so speed-scale doesn't corrupt survival stats
  run.survivalTimeSec = Math.max(0, (state.time ?? 0) - (run.gameTimeStart ?? 0));
  run.deathCause = outcome === 'won' ? 'won' : guessDeathCause(state);
  // BETA 17.27 — PSBOT cycle-30an P0: broken-state guard. Viking ghost run
  // (1075 kills, 0 survival, endTime stuck) polluted Supabase. Pattern:
  // game freezes mid-run, finalizeRun fires, state.time stuck → survival=0
  // but killCount accumulated to real number. Detect: meaningful killCount
  // but trivial survivalTimeSec. Mark deathCause='interrupted' and use
  // wallClockSec as the survival fallback so analyst rows aren't garbage.
  // BETA 17.29 — PSBOT cycle-30ao: 2 more crashes had wallClockSec=0 too
  // (both timers frozen). Strengthened fallback to use Date.now() - startTime
  // as the final-resort survival estimate. Drops the `wallClockSec > 5`
  // gate from the predicate so the guard fires for the harder crash too.
  if (run.killCount > 5 && run.survivalTimeSec < 1) {
    run.deathCause = 'interrupted';
    if (run.wallClockSec > 5) {
      run.survivalTimeSec = run.wallClockSec;
    } else if (run.startTime > 0) {
      // wallClockSec also stuck — derive from Date.now() vs startTime stamp.
      run.survivalTimeSec = Math.max(0, (Date.now() - run.startTime) / 1000);
    }
  }
  run.floorReached = state.map.level;
  // BETA 17.25 — PSBOT cycle-30al P0 schema fix. state.wave is dead (only
  // initialized to 0 in createInitialState, never incremented). Derive
  // waveReached from survival time so PSBOT can correlate picks to game
  // progression. Convention: 60s = 1 wave.
  run.waveReached = Math.max(1, Math.floor((run.survivalTimeSec ?? 0) / 60) + 1);
  run.killCount = state.killCount;
  run.killsByType = { ...bot.killsByType };
  run.damageTaken = bot.damageTakenAccum;
  run.damageDealt = getRunDamageDealt(); // reads from weaponAdapter's live accumulator
  run.healingReceived = bot.healingAccum;
  // BETA 20.67 — deep-dive HIGH #9: this was the post-spend WALLET (deflated
  // by chest buys + Debt drain), while live rows use the lifetime accumulator
  // (the 19.99 fix that never reached the bot side). Bot-vs-human gold was
  // apples-to-oranges; gold/min from bot data underestimated income.
  run.goldCollected = ((state as unknown as { _goldCollectedThisRun?: number })._goldCollectedThisRun ?? state.player.gold);
  run.chestsOpened = state.chestsOpenedThisRun;
  // BETA 16.90 — PSBOT cycle-30f Bug 6: surface shrine count for bot CSV.
  run.shrinesActivated = state.shrinesActivatedThisRun ?? 0;
  run.levelReached = state.player.level;
  // BETA 17.25 — PSBOT cycle-30al P0 schema fix. bot.upgradesPicked/
  // upgradesSkipped/itemsCollected are initialized at startNewRun but were
  // NEVER pushed to anywhere (no .push call sites in the file). Result:
  // PSBOT got empty arrays despite 47 picks logged to choiceLog. Fix: at
  // finalize, derive all 3 summary fields from bot.currentRun.choiceLog
  // (the source of truth for pick events).
  {
    const log = bot.currentRun?.choiceLog ?? [];
    const picked: string[] = [];
    const skipped: string[] = [];
    const items: string[] = [];
    for (const c of log) {
      if (c.pickedName && c.pickedName !== 'unknown') {
        if (c.type === 'chest') items.push(c.pickedName);
        else picked.push(c.pickedName);  // levelup + shrine
      }
      if (Array.isArray(c.offered)) {
        for (let i = 0; i < c.offered.length; i++) {
          if (i !== c.pickedIndex && c.offered[i] && c.offered[i] !== 'unknown') {
            skipped.push(c.offered[i]);
          }
        }
      }
    }
    run.upgradesPicked   = picked;
    run.upgradesSkipped  = skipped;
    run.itemsCollected   = items;
  }
  run.roomsVisited = bot.roomsVisitedSet.size;
  run.deathPosition = { x: state.player.x, y: state.player.y };
  run.hpAtDeath = state.player.hp;
  run.weaponsUsed = Array.from(bot.weaponsUsed);
  // BETA 13.59: snapshot invisible-behavior counters into the run log
  run.blineTriggers = bot.blineTriggerCount;
  run.trailTeleports = bot.trailTeleportCount;
  run.namedEliteFlees = bot.namedEliteFleeCount;
  // BETA 13.80: snapshot pathfinding-fix-1 + hitbox near-miss counters
  run.trapCellsMarked = bot.trapCellsMarkedCount;
  run.enemyNearMissFrames = bot.enemyNearMissFrameCount;
  run.weaponNearMisses = bot.weaponNearMissCount;
  // BETA 13.82: snapshot novelty-pick counter
  run.noveltyPicks = bot.noveltyPickCount;

  // BETA 13.99b — synergy detection: did the bot end the run holding BOTH
  // Drac's Fang weapon AND Drac's Gas Mask item? Lifesteal jumps 8% → 20%
  // when both equipped (BETA 13.98 wire). Probe inventory + weapons-used.
  try {
    const hasFang = bot.weaponsUsed.has('wpn_dracs_fang');
    const inv = getItemState()?.inventory ?? [];
    const hasGasMask = inv.some(s => s.item.id === 'item_dracs_gas_mask');
    run.synergyDracsBoth = (hasFang && hasGasMask) ? 1 : 0;
  } catch { run.synergyDracsBoth = 0; }

  // BETA 15.26 — character passive trackers (Layer 1 final + Layer 2 signature)
  try {
    run.characterPassiveLayer1Final = getPassiveBonusFinalThisRun(state);
    run.signatureProcsThisRun       = getSignatureProcsThisRun(state);
    run.signatureMaxStacks          = getSignatureMaxStacksThisRun(state);
    run.signatureActiveSeconds      = getSignatureActiveSecondsThisRun(state);
    run.signatureBonusDamage        = getSignatureBonusDamageThisRun(state);
  } catch {
    run.characterPassiveLayer1Final = 0;
    run.signatureProcsThisRun = 0;
    run.signatureMaxStacks = 0;
    run.signatureActiveSeconds = 0;
    run.signatureBonusDamage = 0;
  }

  // BETA 15.28 — spawn timing snapshot + finalLuckPct
  try {
    run.enemiesSpawned_0to30s    = _spawnBucket0to30;
    run.enemiesSpawned_30to60s   = _spawnBucket30to60;
    run.enemiesSpawned_60to120s  = _spawnBucket60to120;
    run.enemiesSpawned_120to300s = _spawnBucket120to300;
    run.peakAliveCount           = _peakAliveCount;
    run.peakAliveTime            = Math.round(_peakAliveTime * 10) / 10;
    run.firstZombieSpawnTime     = Math.round(_firstZombieSpawnTime * 10) / 10;
    // (E1 enemy rebuild — firstTimmySpawnTime/firstCaseohSpawnTime are no
    // longer assigned; their CSV columns emit -1 forever via the `?? -1`
    // row fallbacks. Column names are contract — never remove them.)
    run.finalLuckPct             = Math.round(state.player?.luck ?? 0);
  } catch {
    run.enemiesSpawned_0to30s = 0;
    run.enemiesSpawned_30to60s = 0;
    run.enemiesSpawned_60to120s = 0;
    run.enemiesSpawned_120to300s = 0;
    run.peakAliveCount = 0;
    run.peakAliveTime = 0;
    run.firstZombieSpawnTime = -1;
    run.finalLuckPct = 0;
  }

  // BETA 15.34 — Pick audit snapshot
  try {
    const entries = getPickAuditEntries();
    const counts = getPickAuditCountsBySource();
    const spike = getMaxBuffSpike();
    run.pickAuditCount = getPickAuditCount();
    run.pickAuditLegendaryCount = getLegendaryPickCount();
    run.pickAuditEpicPlusCount = getEpicPlusPickCount();
    run.pickAuditMaxBuffValue = spike?.value ?? 0;
    run.pickAuditMaxBuffSource = spike?.source ?? '';
    run.pickAuditMaxBuffId = spike?.id ?? '';
    run.pickAuditMaxBuffRarity = spike?.rarity ?? '';
    run.pickAuditPickCounts_levelupPerc   = counts.levelup_perc;
    run.pickAuditPickCounts_levelupWeapon = counts.levelup_weapon;
    run.pickAuditPickCounts_shrine        = counts.shrine;
    run.pickAuditPickCounts_chestFree     = counts.chest_free;
    run.pickAuditPickCounts_chestPaid     = counts.chest_paid;
    // Full JSON. Bound by per-run picks (~20-50 typical) × ~150 bytes/entry
    // ≈ 3-7KB. Stays under 100KB cap; goes to Supabase as part of description.
    run.pickAuditJson = JSON.stringify(entries);
  } catch {
    run.pickAuditCount = 0;
    run.pickAuditLegendaryCount = 0;
    run.pickAuditEpicPlusCount = 0;
    run.pickAuditMaxBuffValue = 0;
    run.pickAuditMaxBuffSource = '';
    run.pickAuditMaxBuffId = '';
    run.pickAuditMaxBuffRarity = '';
    run.pickAuditPickCounts_levelupPerc = 0;
    run.pickAuditPickCounts_levelupWeapon = 0;
    run.pickAuditPickCounts_shrine = 0;
    run.pickAuditPickCounts_chestFree = 0;
    run.pickAuditPickCounts_chestPaid = 0;
    run.pickAuditJson = '[]';
  }

  // BETA 15.31 — perc-pick rarity counts (paired with finalLuckPct for the
  // luck-system audit). Read module-level counters from peanutPercSystem.
  try {
    const r = getPercPickRarityCounts();
    run.percPicks_common    = r.common;
    run.percPicks_uncommon  = r.uncommon;
    run.percPicks_rare      = r.rare;
    run.percPicks_epic      = r.epic;
    run.percPicks_legendary = r.legendary;
    run.percPicks_total     = r.total;
  } catch {
    run.percPicks_common = 0;
    run.percPicks_uncommon = 0;
    run.percPicks_rare = 0;
    run.percPicks_epic = 0;
    run.percPicks_legendary = 0;
    run.percPicks_total = 0;
  }

  // BETA 14.70 — v1 BotFarm CSV converge. Snapshot all engine-state trackers
  // onto the run record so the master.csv inherits the rich per-mechanic
  // data WeaponLab + v2 BotFarm have had since BETA 14.30+. Per PSBOT RUN's
  // "v1 add" recommendation: ship once, all future bot runs inherit, no
  // mode-switching between v1/v2 CSV formats.
  try {
    const matrix = getLocalWeaponEnemyMatrix();
    run.weaponEnemyMatrixJson = JSON.stringify(matrix);
    const top = computeTopWeaponSummary();
    run.topWeaponVsZombie = top.topVsZombie;
    run.topWeaponVsTimmy = top.topVsTimmy;
    run.topWeaponVsCaseoh = top.topVsCaseoh;
    run.topWeaponVsElite = top.topVsElite;
    run.weakestWeaponOverall = top.weakest;
    run.percTrackerJson = JSON.stringify(getLocalPercTracker());
    run.itemTrackerJson = JSON.stringify(getLocalItemTracker());
    run.shrineTrackerJson = JSON.stringify(getLocalShrineTracker());
    run.floorEffectTrackerJson = JSON.stringify(getLocalFloorEffectTracker());
    run.synergyTrackerJson = JSON.stringify(getLocalSynergyTracker());
    run.eventLogJson = JSON.stringify(getLocalEventLog());
    run.deathContextJson = JSON.stringify(getLocalDeathContext());
    run.nearMissEventsLogJson = JSON.stringify(getLocalNearMissEvents());
    run.retreatEventsLogJson = JSON.stringify(getLocalRetreatEvents());
    // Phase 1 event counters from state
    run.reviveEventsThisRun = state._reviveEventsThisRun ?? 0;
    run.healEventsThisRun = state._healEventsThisRun ?? 0;
    // BETA 16.51 — Last Stand split off from reviveEvents (PSBOT cycle-30k P1)
    {
      const _sAnyLS = state as unknown as Record<string, number | undefined>;
      (run as unknown as Record<string, number>).lastStandTriggersThisRun = _sAnyLS._lastStandTriggersThisRun ?? 0;
    }
    run.dodgeEventsThisRun = state._dodgeEventsThisRun ?? 0;
    run.phaseEventsThisRun = state._phaseEventsThisRun ?? 0;
    run.knockbackEventsThisRun = state._knockbackEventsThisRun ?? 0;
    run.enemyAttacksAttempted = state._enemyAttacksAttemptedThisRun ?? 0;
    run.enemyAttacksHit = state._enemyAttacksHitThisRun ?? 0;
    run.enemyAttacksMissedByEvasion = state._enemyAttacksMissedByEvasion ?? 0;
    run.enemyAttacksMissedByIframe = state._enemyAttacksMissedByIframe ?? 0;
    run.enemyAttacksMissedByPhase = state._enemyAttacksMissedByPhase ?? 0;
    run.enemyAttacksMissedByOther = state._enemyAttacksMissedByOther ?? 0;
    // BETA 14.82 — sprite-touch tracking
    run.enemySpriteOverlapFrames = state._enemySpriteOverlapFrames ?? 0;
    run.enemySpriteOnlyTouchFrames = state._enemySpriteOnlyTouchFrames ?? 0;
    run.enemyTouchCooldownGated = state._enemyTouchCooldownGated ?? 0;
    run.firstBloodTime = state._firstBloodTime ?? 0;
    run.firstDamageTakenTime = state._firstDamageTakenTime ?? 0;
    run.longestStretchBetweenHitsMs = state._longestStretchBetweenHitsMs ?? 0;
    run.engineFramePerfJson = JSON.stringify(state._engineFramePerfThisRun ?? {});
    run.finalDamagePct = state.player.damageBonus ?? 0;
    run.finalCritChancePct = state.player.critChance ?? 0;
    run.finalAttackSpeedPct = state.player.attackSpeedBonus ?? 0;
    run.finalMaxHP = state.player.maxHp ?? 0;
    run.runGoldSpent = state._runGoldSpent ?? 0;
    // BETA 15.13 N-perk stat-anchor verification — read from player (lazy fields use `as any`)
    const playerAny = state.player as unknown as Record<string, number>;
    run.finalLifestealPct = state.player.lifesteal ?? 0;
    run.finalArmor = state.player.armor ?? 0;
    run.finalThornsPct = state.player.thorns ?? 0;
    run.finalCritDamagePct = state.player.critDamage ?? 0;
    run.finalProjectileCount = state.player.projectileCountBonus ?? 0;
    run.finalOverheal = playerAny.overheal ?? 0;                      // lazy field per itemsAdapter:923
    run.finalProjectilePierce = playerAny.projectilePierceBonus ?? 0; // lazy field per itemsAdapter:935
    run.finalPickupRangePct = state.player.pickupRange ?? 0;
    run.finalEliteDamagePct = state.player.eliteDamageBonus ?? 0;
    run.finalProjectileBounce = state.player.projectileBounceBonus ?? 0;
    // BETA 16.27 — PSBOT cycle-30b Bug 2 FIX. `state.player.contactPhaseChance`
    // is a FRACTION (0-0.95) per applyAllBonusesToPlayer convention, but
    // the telemetry field is named `finalPhaseChancePct` (PERCENT
    // semantic — matches other final*Pct fields). Engine.ts:1493 already
    // multiplies by 100 for state.finalPhaseChancePct; autoPlaytester
    // was writing the fraction directly so 296 bot runs all read ~0
    // even when N14 was picked at full stack. Fixed by × 100 to convert.
    run.finalPhaseChancePct = (state.player.contactPhaseChance ?? 0) * 100;
    // BETA 17.31 — PSBOT cycle-30ap genre research: the *threshold-flip*
    // metric is what tells you whether a buff actually mattered. A +5%
    // damage buff that doesn't cross any "kill in N hits → N-1 hits"
    // threshold is the Oblivion treadmill. Per the Escapist piece + VS
    // discussion threads, hits-per-kill is the load-bearing signal.
    // Aggregate hits-per-kill = total shots that landed / total kills
    // this run. Drops if weapon/perc picks reduced kill-cost (good).
    // Surfaces in bot CSV so PSBOT can audit "did this buff cross a
    // threshold?" instead of inferring from DPS/HP totals.
    try {
      const _ws = getWeaponSystemState();
      let _totalHits = 0;
      if (_ws?.weapons) {
        for (const _w of _ws.weapons) _totalHits += (_w.totalShotsHit ?? 0);
      }
      const _kills = run.killCount ?? 0;
      const _runForHpk = run as unknown as Record<string, number>;
      _runForHpk.hitsPerKill = _kills > 0 ? +(_totalHits / _kills).toFixed(2) : 0;
      _runForHpk.totalShotsHitAllWeapons = _totalHits;
    } catch { /* never crash on telemetry */ }
    // BETA 14.72 — bot diagnostics snapshot
    run.botPositionTrailJson = JSON.stringify(getLocalBotPositionTrail());
    run.stuckEventsJson = JSON.stringify(getLocalStuckEvents());
    run.botMapCellVisitsJson = JSON.stringify(getLocalBotMapCellVisits());
    run.decisionTraceJson = JSON.stringify(getLocalDecisionTrace());
    // BETA 15.14 — N10 Debt mechanic trackers
    run.debtSavesTriggered = state._debtSavesTriggeredThisRun ?? 0;
    run.debtGoldSpent = state._debtGoldSpentThisRun ?? 0;
    run.debtHitsAbsorbed = state._debtHitsAbsorbedThisRun ?? 0;
    run.debtForceEnded = state._debtForceEndedThisRun ?? 0;
    // BETA 15.15 — N13 Not Xanax + N06 Let's Ride trackers
    run.notXanaxSlowsApplied = state._notXanaxSlowsAppliedThisRun ?? 0;
    run.notXanaxTotalSlowSeconds = state._notXanaxTotalSlowSecondsThisRun ?? 0;
    run.perkBungulatorsSpawned = state._perkBungulatorsSpawnedThisRun ?? 0;
    // BETA 17.07 — PSBOT cycle-30w Protocol B: Karen Drone diagnostic
    // counter (instrumentation only, fix decision deferred to next cycle
    // after data review). Hook-add only — no scoring change.
    try {
      const _karen = _getKarenDroneTraceForBot();
      const _runForKaren = run as unknown as Record<string, number>;
      _runForKaren.karenPeakDroneCount = _karen.peakDroneCount;
      _runForKaren.karenContacts       = _karen.contacts;
      _runForKaren.karenCooldownSkips  = _karen.cooldownSkips;
      _runForKaren.karenRadiusSkips    = _karen.radiusSkips;
      _runForKaren.karenLosSkips       = _karen.losSkips;
    } catch { /* never crash on telemetry */ }
    // BETA 17.07 — PSBOT cycle-30t Tier C counters (5 of 12 this beta).
    // Same counters surfaced in live_play via livePlayTracker; bot CSV
    // needs the same hook-add for full parity.
    const _sAny17 = state as unknown as Record<string, number | undefined>;
    const _runForT = run as unknown as Record<string, number>;
    _runForT.voogooHealsTriggered  = _sAny17._voogooHealsTriggeredThisRun ?? 0;
    _runForT.voogooHealAmount      = Math.round(_sAny17._voogooHealAmountThisRun ?? 0);
    _runForT.timmyDamageReduced    = Math.round(_sAny17._timmyDamageReducedThisRun ?? 0);
    _runForT.lastResortActivations = _sAny17._lastResortActivationsThisRun ?? 0;
    // BETA 17.11 — A3 + A7 proof counters surfaced to bot CSV.
    _runForT.a3InvulnRegenPausedFrames = _sAny17._a3InvulnRegenPausedFramesThisRun ?? 0;
    _runForT.a7TimmySlowsApplied       = _sAny17._a7TimmySlowsAppliedThisRun ?? 0;
    // BETA 17.12 — Floor 1 progression diagnostic counters.
    _runForT.megaBossKilled = _sAny17._megaBossKilledThisRun ?? 0;
    _runForT.portalReached  = (_sAny17._portalReachedThisRun ? 1 : 0);
    _runForT.floorAdvances  = _sAny17._floorAdvancesThisRun ?? 0;
    try {
      // BETA 20.67 — _itemProcCounts never had a writer (deep-dive HIGH #6);
      // read the real counts from the shared weaponLab item tracker, same as
      // the live_play fix. Adds the other three proc fields for parity.
      const _trkForBot = _getLocalItemTrackerForBot();
      _runForT.stayFrostySlowsApplied = _trkForBot['item_stay_frosty']?.procCount ?? 0;
      _runForT.timmyRepellentProcs    = _trkForBot['item_timmy_repellent_spray']?.procCount ?? 0;
      _runForT.sheeeshTriggered       = _trkForBot['item_sheeesh']?.procCount ?? 0;
      _runForT.liarTriggered          = _trkForBot['item_liar_deceiver_false_prophet']?.procCount ?? 0;
    } catch { /* never crash on telemetry */ }
    // BETA 17.08 — PSBOT cycle-30t Tier C closing batch (6 final counters).
    // Mirrors livePlayTracker surfacing so bot CSV has the same fields.
    try {
      const _itemTrackerBot = _getLocalItemTrackerForBot();
      const _runForBot = run as unknown as Record<string, number>;
      _runForBot.iranianBirdShots       = _itemTrackerBot['item_iranian_bird']?.procCount ?? 0;
      _runForBot.iranianBirdDamage      = Math.round(_itemTrackerBot['item_iranian_bird']?.dmgDealt ?? 0);
      _runForBot.whoopieStunsApplied    = _itemTrackerBot['item_whoopie_cushion_of_doom']?.procCount ?? 0;
      _runForBot.bungulatorsPunches     = _itemTrackerBot['item_bungulators']?.procCount ?? 0;
      _runForBot.bungulatorsDamage      = Math.round(_itemTrackerBot['item_bungulators']?.dmgDealt ?? 0);
      _runForBot.anusRocketsFired       = _getAnusRocketsForBot();
      _runForBot.anusRocketDamage       = Math.round(_itemTrackerBot['R08']?.dmgDealt ?? 0);
      _runForBot.dracsGasMaskOverheal   = Math.round((state as unknown as Record<string, number | undefined>)._dracsGasMaskOverhealThisRun ?? 0);
      _runForBot.sillyJuiceMaxStack     = _getSillyJuicePeakForBot();
    } catch { /* never crash on telemetry */ }
    // BETA 15.42 — long-frame detection snapshot (re-add post-rollback)
    run.longFrameCount = _longFrameCount;
    run.longestFrameMs = Math.round(_longestFrameMs);
    run.longFrameLastContext = _longFrameLastContext;
  } catch (_e) {
    // BETA 14.70 — guard against any missing tracker; CSV still emits
    // even if a single capture fails. Empty fields show as undefined.
  }

  // BETA 14.05 — floor-effect + kitten-trap snapshot. See per-frame sweep in
  // updateBot for the data source. Source breakdown is pipe-separated so the
  // CSV cell stays bounded.
  run.groundEffectsSpawned = _seenGroundEffectIds.size;
  run.groundEffectsTriggered = _triggeredGroundEffectIds.size;
  run.groundEffectSources = Array.from(_groundEffectSourceCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([src, count]) => `${src}:${count}`);
  // BETA 16.55 — PSBOT cycle-30l: separate spawn-side breakdown so we can
  // answer "did banana auto-drop fire at all?" without inferring from
  // step-on counts. groundEffectSources counts step-ons; this counts
  // spawns. If banana spawn=42 step-on=0, that's a player-movement issue
  // (Killa moves fast, traps drop behind, enemies don't reach). If
  // banana spawn=0, the auto-drop is genuinely broken.
  run.groundEffectSpawnSources = Array.from(_groundEffectSpawnCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([src, count]) => `${src}:${count}`);
  run.kittensPlaced = _seenKittenIds.size;
  // kittensTriggered already accumulated per-frame in updateBot

  // BETA 14.06 — per-weapon accuracy + damage rollup + first-hit time. Engine
  // tracks per-weapon counters (BETA 13.43); we snapshot at run-end. Bot tracks
  // per-weapon first-hit-time accumulators per-frame in updateBot (above).
  // Format per weapon:
  //   wpn_id:lvlN,dmg=D,hit=H/F(P%),crit=C,t=Avg(s)
  // Sorted by total damage descending so the dominant weapon comes first.
  // User context: "time it takes for projectile to hit. all that. these are
  // real adjustments needed if overpowered. too slow and needs to be smart if
  // its a gun or perk or buncha item doing it. context is key." — t= field
  // shows whether weapon is "fast hit" (sniper) or "slow hit" (cannon),
  // making per-weapon balance discussions context-aware.
  try {
    const ws = getWeaponSystemState();
    if (ws) {
      const tele = ws.weapons
        .filter(w => w.totalShotsFired > 0 || w.totalDamageDealt > 0)
        .sort((a, b) => b.totalDamageDealt - a.totalDamageDealt)
        .map(w => {
          const hitPct = w.totalShotsFired > 0
            ? Math.round((w.totalShotsHit / w.totalShotsFired) * 100)
            : 0;
          const htCount = _weaponHitTimeCount.get(w.definitionId) ?? 0;
          const htSum = _weaponHitTimeSum.get(w.definitionId) ?? 0;
          const avgT = htCount > 0 ? (htSum / htCount).toFixed(2) : '-';
          // BETA 14.07: per-weapon turn (chase) events + max chase duration
          // for any single projectile from this weapon.
          const turns = _weaponTurnEvents.get(w.definitionId) ?? 0;
          const maxChase = _weaponMaxChaseDurationByProj.get(w.definitionId) ?? 0;
          return `${w.definitionId}:lvl${w.level},dmg=${Math.round(w.totalDamageDealt)},hit=${w.totalShotsHit}/${w.totalShotsFired}(${hitPct}%),crit=${w.totalCrits},t=${avgT},turn=${turns},maxChase=${maxChase}`;
        });
      run.weaponTelemetry = tele;
    }
  } catch { run.weaponTelemetry = []; }

  // BETA 14.06 — per-perc level rollup. Reads percState.slots and emits
  // pipe-separated id:level pairs. Tells the analyst pass which percs the bot
  // ended with at what level. Required for conditional/scaling perc evaluation.
  try {
    const ps = getPercState();
    if (ps?.slots) {
      const levels: string[] = [];
      for (const slot of ps.slots) {
        if (slot.perc && slot.level > 0) {
          levels.push(`${slot.perc.id}:${slot.level}`);
        }
      }
      run.percLevels = levels;
    }
  } catch { run.percLevels = []; }

  // BETA 14.06 — final player stat snapshot. Captures the cumulative result of
  // all scaling mechanics. Packed key=value comma string keeps the CSV cell
  // small but parseable.
  try {
    const p = state.player;
    const pAny = p as { knockbackBonus?: number; thorns?: number; contactPhaseChance?: number; pickupRange?: number };
    const parts = [
      `maxHp=${Math.round(p.maxHp)}`,
      `dmg=${Math.round(p.damageBonus ?? 0)}`,
      `as=${Math.round(p.attackSpeedBonus ?? 0)}`,
      `arm=${Math.round(p.armor ?? 0)}`,
      `eva=${Math.round(p.evasion ?? 0)}`,
      `crit=${Math.round(p.critChance ?? 0)}`,
      `cdmg=${Math.round(p.critDamage ?? 0)}`,
      `ls=${Math.round(p.lifesteal ?? 0)}`,
      `hpr=${(p.hpRegen ?? 0).toFixed(1)}`,
      `spd=${Math.round(p.speed)}`,
      `lck=${(p.luck ?? 0).toFixed(1)}`,
      `xpm=${((p.xpMultiplier ?? 1)).toFixed(2)}`,
      `gldm=${((p.goldMultiplier ?? 1)).toFixed(2)}`,
      `pjct=${Math.round(p.projectileCountBonus ?? 0)}`,
      `rng=${Math.round(p.rangeBonus ?? 0)}`,
      `area=${Math.round(p.attackAreaBonus ?? 0)}`,
      `pjs=${Math.round(p.projectileSpeedBonus ?? 0)}`,
      `bnc=${Math.round(p.projectileBounceBonus ?? 0)}`,
      `eldmg=${Math.round(p.eliteDamageBonus ?? 0)}`,
      `sh=${Math.round(p.shield ?? 0)}/${Math.round(p.maxShield ?? 0)}`,
      // BETA 14.06 (audit pass) — added missing fields surfaced by user audit:
      `kb=${Math.round(pAny.knockbackBonus ?? 0)}`,        // T08 Laxative
      `thn=${Math.round(pAny.thorns ?? 0)}`,                // R03 Wyfe Beater item
      `phs=${Math.round((pAny.contactPhaseChance ?? 0) * 100)}`, // N14 Not Acid (stored as 0-1 fraction; scaled to %)
      `pkup=${Math.round(pAny.pickupRange ?? 0)}`,         // pickup range buff items
    ];
    run.finalStats = parts.join(',');
  } catch { run.finalStats = ''; }

  // BETA 14.06 (audit pass) — revive flags snapshot
  run.sheeeshTriggered = _sheeeshFiredThisRun;
  run.liarTriggered = _liarFiredThisRun;

  // BETA 14.07 — precision metrics snapshot
  run.timeToFirstKillSec = _timeToFirstKillSec;
  run.phaseTimePlaying = +_phaseTimePlaying.toFixed(2);
  run.phaseTimeLevelup = +_phaseTimeLevelup.toFixed(2);
  run.phaseTimeChest = +_phaseTimeChest.toFixed(2);
  run.phaseTimeShrine = +_phaseTimeShrine.toFixed(2);
  run.distanceTraveled = Math.round(_distanceTraveled);
  run.avgEnemiesAlive = _enemiesAliveSamples > 0
    ? +(_enemiesAliveSum / _enemiesAliveSamples).toFixed(1)
    : 0;
  run.avgKiteDistance = _kiteDistanceSamples > 0
    ? Math.round(_kiteDistanceSum / _kiteDistanceSamples)
    : 0;
  // Max item stack — sweep itemState.inventory at run-end
  try {
    const inv = getItemState()?.inventory ?? [];
    let maxStack = 0;
    for (const slot of inv) {
      if (slot.stacks > maxStack) maxStack = slot.stacks;
    }
    run.maxItemStack = maxStack;
  } catch { run.maxItemStack = 0; }

  // BETA 13.71/13.72: stat-effect verification summary. A pick is "broken" if its
  // statsBefore + statsAfter were both captured AND the diff is empty (the
  // upgrade had no measurable effect on player stats). BETA 13.72: weapon picks
  // (Peanut Rifle, Viking Axe, etc.) DON'T change player stats — they add to the
  // weapon system. Skip those to avoid false-positives. Same for known weapon
  // level-ups ("The Chad Aura" leveled means weapon level, not player stat).
  const KNOWN_WEAPON_NAMES = [
    'peanut rifle', 'spatula slash', 'revolver', 'six shooter', 'rpk machine gun',
    'fireball wand', 'viking axe', 'voogoo rager', 'tactical basketball',
    'chad aura', 'blunderbuss', 'rager toss',
    // BETA 14.05 — added the 11 BETA 13.94+ weapons. Without this, every weapon
    // level-up pick (e.g. "levelup:Frozen Darts") false-positives as broken.
    // Cycle-7 surfaced 47 weapon-named broken-picks because of this gap.
    'ultra beam', 'the terminator', 'terminator', 'ugly kittens', 'kittens',
    'karen drones', 'karen', 'poop sling', 'squirter', 'frozen darts',
    "drac's fang", 'shitty cannon', 'the ex girlfriend', 'ex girlfriend', 'hurray',
  ];
  // BETA 13.81: ULTRA / chest items are CONDITIONAL behavior items (Last Resort
  // only fires <25% HP, Silly Juice only with kill stacks, etc.). At pickup time
  // their condition isn't met → 0 stat delta → tracker false-positives them as
  // "broken". Cycle-3 surfaced 19 instances across 5 ultra items (Smashley's
  // Bubble Wrap 5×, Anus Rocket 4×, Silly Juice 3×, Last Resort 2×, Nutty T-Bag
  // 2×). Skip these from broken-pick count to keep the tracker signal clean.
  const KNOWN_ULTRA_NAMES = [
    "smashley's bubble wrap", 'smashley',
    'anus rocket',
    'silly juice',
    'last resort',
    'nutty t-bag', 'nutty tbag',
    'wyfe beater',
    'blood bag',
    'championship belt',
    'hype train',
    'taco',
    // BETA 13.83: cycle-4 surfaced 3 more ultra/chest items as false-positive
    // "broken" picks. These are conditional behavior items, not stat picks.
    'diarrhea gas',
    'little black dress', 'tj maxx',
    "hutch's sausage fingers", 'sausage fingers',
    // BETA 14.05 — added BETA 13.98-99 + 13.103 items that are CONDITIONAL behavior
    // (revives, on-hit triggers, on-kill heals, on-damage reactives, synergies).
    // Cycle-7 surfaced 10× Liar Deceiver + 4× Drac's Gas Mask + 3× Mystery Meat as
    // false-positive broken-picks because of this gap.
    'sheeesh',
    'iranian bird',
    'bungulators',
    "drac's gas mask",
    'liar deceiver', 'false prophet',
    'mystery meat',
    'big mac', 'special sauce',
    'stay frosty',
    "mom's cooking",
    'write that down',
    'voogoo rager',
    "asmon's desk banana", 'banana',
    'whoopie cushion',
    // BETA 14.05 — also add the new percs that have CONDITIONAL/SCALING/REACTIVE
    // mechanics (their stat-delta isn't visible at snapshot time). Without these,
    // cycle-7 false-positived 11× Let's Ride, 10× Not Xanax, 7× Mr Silly Pills.
    "let's ride", 'lets ride',
    'not xanax', 'xanax',
    'mr silly pills', 'silly pills',
    'bungulazine',
    'stompatine',
    'puberty blockers',
    'not blue chew',
    'not ritalin',
    'greedy tums',
    'debt',
    'not acid',
    'sponge me daddy', 'absorbatol',
    "hutch's armor", 'hutchs armor', 'plating',
    "stepmom's pills", 'stepmoms pills',
    // BETA 20.73 — audit fix: the 123-run cycle false-positived 85 picks across
    // 7 behavior-only items (statModifiers: [] — their effects are triggers,
    // not pick-time stat deltas, so the snapshot diff is ALWAYS empty). All 7
    // verified implemented + firing in code. Same gap as 13.81/14.05, new items.
    "drac's helmet", 'dracs helmet',
    'morning wood',
    'big brain',           // Peanut's Big Brain
    'cursed chat',
    'adrenaline pod',
    'nickmerc',            // NickMerc Jerk
  ];
  const isWeaponPick = (name: string): boolean => {
    const n = (name ?? '').toLowerCase();
    return KNOWN_WEAPON_NAMES.some(w => n.includes(w));
  };
  const isUltraItem = (name: string): boolean => {
    const n = (name ?? '').toLowerCase();
    // BETA 20.73 — "SHIT" (behavior item) needs an EXACT match: as a substring
    // it would swallow every name containing the word (e.g. weapons already
    // filtered, but future items too). Exact-name check only.
    if (n.trim() === 'shit') return true;
    return KNOWN_ULTRA_NAMES.some(u => n.includes(u));
  };
  // BETA 13.81: cap-aware filter for movement-speed picks. Speed cap is 200 in
  // itemsAdapter.applyAllBonusesToPlayer (`speed = Math.min(speed, 200)`). When
  // the player is already at cap, +Move Speed picks have ZERO measurable delta —
  // the bonus IS being applied, the cap is just hiding it. Cycle-3 surfaced 35
  // such instances (Not Crack 23×, +8% MS 8×, +16% MS 4×). Skip these from broken-
  // pick count if both before+after speed are at cap.
  const isCappedMoveSpeedPick = (c: ChoiceRecord): boolean => {
    const n = (c.pickedName ?? '').toLowerCase();
    const isMovePick = n.includes('movement speed') || n.includes('move speed') || n.includes('not crack');
    if (!isMovePick) return false;
    const sb = c.statsBefore?.speed ?? 0;
    const sa = c.statsAfter?.speed ?? 0;
    return sb >= 199 && sa >= 199;
  };
  let brokenCount = 0;
  let totalTracked = 0;
  const brokenNames: string[] = [];
  for (const c of run.choiceLog) {
    if (!c.statsBefore || !c.statsAfter) continue;
    if (isWeaponPick(c.pickedName)) continue; // BETA 13.72: skip weapon picks
    if (isUltraItem(c.pickedName)) continue;  // BETA 13.81: skip conditional ultras
    if (isCappedMoveSpeedPick(c)) continue;   // BETA 13.81: skip speed-cap-hit picks
    totalTracked++;
    const empty = !c.statDeltas || Object.keys(c.statDeltas).filter(k => k !== 'hp').length === 0;
    if (empty) {
      brokenCount++;
      brokenNames.push(`${c.type}:${c.pickedName}`);
    }
  }
  run.brokenPickCount = brokenCount;
  run.brokenPickNames = brokenNames;
  run.totalPicksTracked = totalTracked;
  if (brokenCount > 0) {
    log(`[Stat-effect] ${brokenCount}/${totalTracked} non-weapon picks had NO measurable effect: ${brokenNames.slice(0, 5).join(', ')}${brokenNames.length > 5 ? `, +${brokenNames.length - 5} more` : ''}`);
  }

  // BETA 13.58: back-fill outcome on every choice so the regret analyzer can later
  // compute "for build X, when offered [A, B, C], picked B → bot survived 87 more
  // seconds and got 142 more kills before dying to enemy:zombie:elite". Picks made
  // late in the run get small survival deltas; early picks get big deltas — this
  // weighting is what makes the regret signal meaningful.
  for (const choice of run.choiceLog) {
    if (choice.pickedAtSurvivalSec !== undefined) {
      choice.survivalAfterPickSec = Math.max(0, run.survivalTimeSec - choice.pickedAtSurvivalSec);
    }
    if (choice.pickedAtKills !== undefined) {
      choice.killsAfterPick = Math.max(0, run.killCount - choice.pickedAtKills);
    }
    choice.runDeathCause = run.deathCause;
    choice.runBuildType = run.buildType;
  }

  // BETA 13.59: build-alignment analyzer. For every choice the bot made this run,
  // compute the build's tier score for each OFFERED option vs what was actually picked.
  // Log misalignments to console + persist counts to localStorage as 'goopzone_pick_alignment'
  // so the next-push regret analyzer (13.60) can use historical alignment to bias scoring.
  // Per user spec: "go through them all and correct which he should have chosen."
  if (run.buildType !== 'adaptive' && run.choiceLog.length > 0) {
    const tiers = BUILD_KEYWORD_TIERS[run.buildType];
    const scoreFor = (name: string): number => {
      const n = (name ?? '').toLowerCase();
      let s = 0;
      for (const [kw, val] of Object.entries(tiers.tier1)) if (n.includes(kw)) s += val;
      for (const [kw, val] of Object.entries(tiers.tier2)) if (n.includes(kw)) s += val;
      for (const [kw, val] of Object.entries(tiers.tier3)) if (n.includes(kw)) s += val;
      return s;
    };
    let aligned = 0;
    let misaligned = 0;
    const misalignmentLog: Array<{ level: number; type: string; picked: string; should: string; pickedScore: number; idealScore: number }> = [];
    for (const c of run.choiceLog) {
      const offeredScores = c.offered.map(scoreFor);
      const idealScore = Math.max(...offeredScores);
      const idealIdx = offeredScores.indexOf(idealScore);
      const pickedScore = offeredScores[c.pickedIndex] ?? 0;
      if (idealIdx === c.pickedIndex || pickedScore === idealScore) {
        aligned++;
      } else {
        misaligned++;
        misalignmentLog.push({
          level: c.level,
          type: c.type,
          picked: c.pickedName,
          should: c.offered[idealIdx] ?? '?',
          pickedScore,
          idealScore,
        });
      }
    }
    // Persist a rolling alignment store
    try {
      const KEY = 'goopzone_pick_alignment';
      const raw = localStorage.getItem(KEY);
      const store = raw ? JSON.parse(raw) : {};
      const k = run.buildType;
      store[k] = store[k] ?? { totalAligned: 0, totalMisaligned: 0, misalignmentSamples: [] };
      store[k].totalAligned += aligned;
      store[k].totalMisaligned += misaligned;
      // keep only the most recent 50 samples per build to bound storage
      store[k].misalignmentSamples = [...store[k].misalignmentSamples, ...misalignmentLog].slice(-50);
      localStorage.setItem(KEY, JSON.stringify(store));
    } catch { /* private mode / quota */ }
    log(`[Alignment ${run.buildType}] aligned=${aligned} misaligned=${misaligned}` + (misaligned > 0 ? ` first_miss="${misalignmentLog[0]?.picked}" should="${misalignmentLog[0]?.should}"` : ''));
  }

  bot.allRuns.push(run);
  // Persist to localStorage so runs survive page refresh / quit without death
  saveRunsToStorage(bot.allRuns);

  // BETA 16.26 — PSBOT cycle-27 URGENT: bot_run schema catch-up. Mirror the
  // live_play extended telemetry so both cohorts carry identical fields
  // for cross-tab analysis. Per Dylan: "i want all new things being
  // tracked for play runs to be added to the bot run supa base so it is
  // more accurate at tracking. keep them separate. but update both in
  // real time for now on with improvements." The 5 fields land here:
  //   - pickEvents (per-pick log w/ statsAtPick — drives T-perc verification)
  //   - finalShield, finalEvasionPct, finalMoveSpeedPct (separate fields,
  //     not packed into the playerStatSnapshot string — easier SQL)
  //   - finalMaxHpDebug (breadcrumb dict for maxHp anomaly investigations)
  // Bot already has weaponTelemetry, percLevels, itemsCollected,
  // finalLuckPct (added earlier). Going forward: every new live_play
  // tracker must land in buildExtendedTelemetryFields too.
  try {
    const ext = buildExtendedTelemetryFields(state);
    const runAny = run as unknown as Record<string, unknown>;
    runAny.pickEvents = ext.pickEvents;
    runAny.finalShield = ext.finalShield;
    runAny.finalEvasionPct = ext.finalEvasionPct;
    runAny.finalMoveSpeedPct = ext.finalMoveSpeedPct;
    runAny.finalMaxHpDebug = ext.finalMaxHpDebug;
    // BETA 16.36 — shield regen telemetry parity (live_play + bot_run).
    runAny.finalShieldRegen = ext.finalShieldRegen;
    runAny.runShieldDamageAbsorbed = ext.runShieldDamageAbsorbed;
    runAny.runShieldRegenerated = ext.runShieldRegenerated;
    // BETA 16.37 — heal-received parity (PSBOT cycle-30i §7E Bug C)
    runAny.runHealingReceived = ext.runHealingReceived;
    // BETA 16.37 — phase time parity (PSBOT cycle-30i §7E Bug F).
    // Bot already had its own _phaseTime* counters (lines 1609-1612);
    // this overwrites with the engine-side values so live + bot use the
    // same source of truth and the schemas stay in sync.
    runAny.phaseTimePlaying = ext.phaseTimePlaying;
    runAny.phaseTimeLevelup = ext.phaseTimeLevelup;
    runAny.phaseTimeChest = ext.phaseTimeChest;
    runAny.phaseTimeShrine = ext.phaseTimeShrine;
    // BETA 22.47 — new-system telemetry parity (dash / mods / Cloakzy / extraction stash).
    runAny.dashesUsed = ext.dashesUsed;
    runAny.cloakzyDeals = ext.cloakzyDeals;
    runAny.cloakzyGoldSpent = ext.cloakzyGoldSpent;
    runAny.modsSocketedAtEnd = ext.modsSocketedAtEnd;
    runAny.stashScrap = ext.stashScrap;
    runAny.stashCases = ext.stashCases;
  } catch (e) {
    console.warn('[bot finalize] buildExtendedTelemetryFields threw', e);
  }

  // BETA 15.25 — fire-and-forget Supabase send. recordBotRunEvent wraps the
  // localStorage backup + Supabase insert in setTimeout(0) so this call never
  // blocks the frame. If Supabase is down or RLS blocks, the local backup
  // (capped at 50 entries) catches it. No-op if perfTelemetry.ts can't import
  // supabase — try/catch swallows.
  try { recordBotRunEvent(run, 'RUN'); } catch { /* never let telemetry crash finalize */ }

  // ── Brain learns from this run ──
  learnFromRun(run, brain);

  if (bot.config.logLevel !== 'silent') {
    log(`=== RUN #${run.runNumber} ${outcome.toUpperCase()} ===`);
    log(`  Survived: ${run.survivalTimeSec.toFixed(1)}s | Level: ${run.levelReached} | Floor: ${run.floorReached}`);
    log(`  Kills: ${run.killCount} | Chests: ${run.chestsOpened} | Gold: ${run.goldCollected}`);
    log(`  Damage taken: ${run.damageTaken.toFixed(0)} | Death: ${run.deathCause}`);
    log(`  Anomalies: ${run.anomalies.length} (${run.anomalies.filter(a => a.severity === 'bug').length} bugs)`);
    log(`  Brain: flee=${brain.fleeThreshold.toFixed(2)} heal=${brain.healSeekThreshold.toFixed(2)} kite=${brain.kiteAggression.toFixed(2)} | runs learned: ${brain.totalRunsLearned}`);
  }

  bot.currentRun = null;
}

function guessDeathCause(state: GameState): string {
  // BETA 13.56: tag the variant (regular/elite/boss) so CSV can break out elite
  // zombie deaths separately.
  // BETA 13.65: cycle-3 audit (run #18) returned 'unknown' — old 150 wu radius
  // too tight when bot dies AT the moment the killer just walked away. Now:
  //   - Try alive enemies within 300 wu (was 150)
  //   - If none, fall back to NEAREST enemy regardless of distance (the killer
  //     might have died in the same frame, or wandered past). Better wrong guess
  //     than 'unknown' for telemetry.
  const { player, enemies } = state;
  const nearbyAlive = enemies
    .filter(e => e.alive && Math.hypot(e.x - player.x, e.y - player.y) < 300)
    .sort((a, b) => Math.hypot(a.x - player.x, a.y - player.y) - Math.hypot(b.x - player.x, b.y - player.y));
  let candidate = nearbyAlive[0];
  if (!candidate) {
    candidate = enemies
      .slice()
      .sort((a, b) => Math.hypot(a.x - player.x, a.y - player.y) - Math.hypot(b.x - player.x, b.y - player.y))[0];
  }
  if (candidate) {
    const variant = (candidate as { variant?: string }).variant ?? 'normal';
    return `enemy:${candidate.type}:${variant}`;
  }
  return 'unknown';
}

// ═══════════════════════════════════════════════════════════════════════
// REPORT GENERATION
// ═══════════════════════════════════════════════════════════════════════

function generateReport(runs: RunLog[]): string {
  const lines: string[] = [];
  lines.push('╔══════════════════════════════════════════════════════════════╗');
  lines.push('║           GOOP ZONE — AUTOMATED PLAYTESTER REPORT          ║');
  lines.push('╚══════════════════════════════════════════════════════════════╝');
  lines.push('');

  // ── Summary ──
  const totalRuns = runs.length;
  const wins = runs.filter(r => r.deathCause === 'won').length;
  const avgSurvival = runs.reduce((s, r) => s + r.survivalTimeSec, 0) / totalRuns;
  const avgKills = runs.reduce((s, r) => s + r.killCount, 0) / totalRuns;
  const avgLevel = runs.reduce((s, r) => s + r.levelReached, 0) / totalRuns;
  const totalAnomalies = runs.reduce((s, r) => s + r.anomalies.length, 0);
  const totalBugs = runs.reduce((s, r) => s + r.anomalies.filter(a => a.severity === 'bug').length, 0);

  lines.push(`RUNS: ${totalRuns}  |  WINS: ${wins}  |  WIN RATE: ${((wins / totalRuns) * 100).toFixed(1)}%`);
  lines.push(`AVG SURVIVAL: ${avgSurvival.toFixed(1)}s  |  AVG KILLS: ${avgKills.toFixed(0)}  |  AVG LEVEL: ${avgLevel.toFixed(1)}`);
  lines.push(`ANOMALIES: ${totalAnomalies}  |  BUGS: ${totalBugs}`);
  lines.push('');

  // ── Death causes ──
  lines.push('─── DEATH CAUSES ───');
  const deathCounts: Record<string, number> = {};
  for (const r of runs) { deathCounts[r.deathCause] = (deathCounts[r.deathCause] || 0) + 1; }
  for (const [cause, count] of Object.entries(deathCounts).sort((a, b) => b[1] - a[1])) {
    lines.push(`  ${cause}: ${count} (${((count / totalRuns) * 100).toFixed(0)}%)`);
  }
  lines.push('');

  // ── Per-run table ──
  lines.push('─── PER-RUN BREAKDOWN ───');
  lines.push('Run | Time   | Lvl | Floor | Kills | Chests | Gold  | Death Cause         | Bugs');
  lines.push('----+--------+-----+-------+-------+--------+-------+---------------------+-----');
  for (const r of runs) {
    const time = `${r.survivalTimeSec.toFixed(0)}s`.padStart(5);
    const lvl = String(r.levelReached).padStart(3);
    const floor = String(r.floorReached).padStart(5);
    const kills = String(r.killCount).padStart(5);
    const chests = String(r.chestsOpened).padStart(6);
    const gold = String(r.goldCollected).padStart(5);
    const death = r.deathCause.padEnd(19);
    const bugs = String(r.anomalies.filter(a => a.severity === 'bug').length).padStart(4);
    lines.push(` ${String(r.runNumber).padStart(2)}  |${time} | ${lvl} | ${floor} | ${kills} | ${chests} | ${gold} | ${death} | ${bugs}`);
  }
  lines.push('');

  // ── All anomalies ──
  const allAnomalies = runs.flatMap(r => r.anomalies.map(a => ({ ...a, run: r.runNumber })));
  if (allAnomalies.length > 0) {
    lines.push('─── ALL ANOMALIES ───');
    const byCat: Record<string, { count: number; severity: string; example: string }> = {};
    for (const a of allAnomalies) {
      if (!byCat[a.category]) byCat[a.category] = { count: 0, severity: a.severity, example: a.description };
      byCat[a.category].count++;
    }
    for (const [cat, info] of Object.entries(byCat).sort((a, b) => b[1].count - a[1].count)) {
      lines.push(`  [${info.severity.toUpperCase()}] ${cat}: ${info.count}x — "${info.example}"`);
    }
  }

  return lines.join('\n');
}

// ─── Logging ────────────────────────────────────────────────────────
function log(...args: any[]): void {
  console.log('[BOT]', ...args);
}
